/**
 * In-house lightweight i18n catalog. Flat key → translation per locale.
 *
 * Why not `next-intl`: that lib requires a `/[locale]/...` routing
 * rewrite (locale segment + middleware redirect) which would touch
 * dozens of pages and our backend-proxy contract. We can swap to it
 * later if the catalog grows beyond a few hundred keys; for now
 * `useT()` against a flat object is plenty.
 *
 * Convention: dot-separated keys grouped by feature
 * (`footer.live`, `nav.markets`, `modal.deposit.title`). When a key
 * is missing for the active locale we silently fall back to `en`,
 * and if EN is also missing we return the key itself so untranslated
 * strings surface visibly during dev.
 *
 * Spanish style: peninsular, idiomatic — never robotic word-for-word.
 * Proper nouns stay in EN (FrenFlow, Polymarket, BubbleFlow, UMA,
 * Telegram). Crypto-native terms keep their EN form when that's how
 * actual Spanish-speaking traders use them ("wallet", "swap", "P&L",
 * "Watchlist"). Verbs on buttons use the infinitive form ("Comprar",
 * "Vender", "Cancelar"). Spain-flavour where it differs from LatAm
 * ("Ajustes" over "Configuración", "Iniciar sesión").
 */

import type { Locale } from "@/app/components/LanguageSelector";

type Catalog = Record<string, string>;

const en: Catalog = {
  // ── Footer / live ticker ────────────────────────────────────────────
  "footer.live": "Live",
  "footer.blog": "Blog",
  "footer.docs": "Docs",
  "footer.help": "Help",
  "footer.legal": "Legal",
  "footer.tradeFromTelegram": "Trade from Telegram",

  // ── Sidebar primary nav ─────────────────────────────────────────────
  "nav.home": "Home",
  "nav.markets": "Markets",
  "nav.more": "More",
  "nav.activity": "Activity",
  "nav.traders": "Traders",
  "nav.copy": "Copy",
  "nav.degenbox": "Degenbox",
  "nav.bubbleflow": "BubbleFlow",
  "nav.frenlinks": "FrenLinks",
  "nav.uma": "UMA",
  "nav.affiliate": "Affiliate",
  "nav.feedback": "Feedback",
  "nav.portfolio": "Portfolio",
  "nav.settings": "Settings",
  "nav.profile": "Profile",
  "nav.notifications": "Notifications",
  "nav.search": "Search",
  "nav.post": "Post",

  // ── Page titles ────────────────────────────────────────────────────
  "page.markets": "Markets",
  "page.activity": "Activity",
  "page.traders": "Traders",
  "page.copy": "Copy",
  "page.portfolio": "Portfolio",
  "page.settings": "Settings",
  "page.profile": "Profile",
  "page.event": "Event",
  "page.market": "Market",
  "page.trader": "Trader",
  "page.feedback": "Feedback",
  "page.affiliate": "Affiliate",

  // ── Common buttons / actions ────────────────────────────────────────
  "action.buy": "Buy",
  "action.sell": "Sell",
  "action.cancel": "Cancel",
  "action.confirm": "Confirm",
  "action.close": "Close",
  "action.save": "Save",
  "action.continue": "Continue",
  "action.back": "Back",
  "action.next": "Next",
  "action.submit": "Submit",
  "action.retry": "Retry",
  "action.copy": "Copy",
  "action.copied": "Copied",
  "action.share": "Share",
  "action.like": "Like",
  "action.dislike": "Dislike",
  "action.comment": "Comment",
  "action.shareOnX": "Share on X",
  "action.viewOnPolymarket": "View on Polymarket",
  "action.delete": "Delete",
  "action.edit": "Edit",
  "action.search": "Search",
  "action.menu": "Menu",
  "action.closeSearch": "Close search",
  "action.notInterested": "Not interested",
  "action.setPriceAlert": "Set price alert",
  "action.shareAlpha": "Share the alpha",
  "action.openEvent": "Open event page",
  "action.useBalanceAsAmount": "Use balance as amount",
  "action.maybeLater": "Maybe later",
  "action.snapMidpoint": "Snap to midpoint",
  "hlEnable.tooltip.authoriseAgent": "Authorise FrenFlow agent",
  "hlEnable.tooltip.depositUsdc": "Deposit USDC to Hyperliquid",
  "hlEnable.tooltip.moveToSpot": "Move USDC to spot for trading",
  "hlEnable.tooltip.approveBuilderFee": "Approve builder fee (0.1%)",
  "action.timeUntilSettlement": "Time until settlement",
  "action.livestream": "Livestream",
  "sports.serving": "Serving",
  "climate.seismic.title": "Seismic Monitor",
  "climate.seismic.subtitle": "Real-time M4.5+ earthquake data from USGS",
  "climate.hurricane.title": "Hurricane & Cyclone Tracker",
  "climate.hurricane.subtitle": "Active tropical systems from NOAA",
  "climate.weather.title": "Severe Weather Monitor",
  "climate.weather.subtitle": "Active severe weather alerts from NWS",
  "climate.disaster.title": "Natural Disaster Monitor",
  "climate.disaster.subtitle": "Real-time seismic and weather data",
  "climate.section.activeCyclones": "Active Tropical Cyclones",
  "climate.section.hurricaneAlerts": "Hurricane-Related Alerts",
  "climate.section.weatherAlerts": "Active Weather Alerts",
  "climate.section.severeWeatherAlerts": "Severe Weather Alerts",
  "climate.count.thisWeek": "this week",
  "climate.count.active": "active",
  "leadForm.errFillAll": "Please fill in all fields.",
  "leadForm.errAcceptPolicy": "You must accept the privacy policy.",
  "leadForm.errGeneric": "An error occurred.",
  "leadForm.errConnection": "Connection error.",
  "leadForm.success": "Thanks for signing up! We'll notify you soon 🚀",
  "leadForm.nameLabel": "Your name",
  "leadForm.namePlaceholder": "Name or nickname",
  "leadForm.emailLabel": "Your best email",
  "leadForm.acceptPre": "I accept the ",
  "leadForm.privacyPolicy": "privacy policy",
  "leadForm.submitLoading": "Reserving...",
  "leadForm.submit": "Reserve Your Seat",
  "action.signIn": "Sign in",
  "action.signOut": "Sign out",
  "action.connectWallet": "Connect wallet",
  "action.deposit": "Deposit",
  "action.withdraw": "Withdraw",
  "action.send": "Send",
  "action.receive": "Receive",
  "action.swap": "Swap",
  "action.viewMore": "View more",
  "action.viewAll": "View all",
  "action.seeMore": "See more",
  "action.follow": "Follow",

  // Generic relative-time labels used by feed widgets (whale activity,
  // top callers, recent activity, etc). Existing duplicates under
  // `portfolio.activity.*` and `feedback.time.*` are kept for now to
  // avoid touching unrelated call sites — new code should prefer these.
  "time.justNow": "Just now",
  "time.minutesAgo": "{n}m ago",
  "time.hoursAgo": "{n}h ago",
  "time.daysAgo": "{n}d ago",
  "time.inMinutes": "In {n}m",
  "time.inHours": "In {n}h",
  "time.today": "Today {time}",
  "time.tomorrow": "Tomorrow {time}",

  // Whale activity widget — sidebar + full /activity page
  "whale.tier.megaWhale": "Mega Whale",
  "whale.tier.whale": "Whale",
  "whale.tier.dolphin": "Dolphin",
  "whale.tier.trader": "Trader",
  "whale.empty.title": "No whale activity detected recently",
  "whale.empty.subtitle": "Check back soon for whale alerts",
  "whale.accuracy": "{pct}% acc",
  "whale.copyTrade": "Copy Trade",
  "whale.viewMarket": "View Market",
  "whale.viewAll": "View All Whale Activity",

  // Top Callers leaderboard widget — sidebar + /activity?view=leaderboard
  "topCallers.title": "Top Callers",
  "topCallers.subtitle": "Best performing traders",
  "topCallers.empty": "No caller data available",
  "topCallers.pro": "Pro",
  "topCallers.calls": "{n} calls",
  "topCallers.copiers": "{n} copiers",
  "topCallers.accuracy": "accuracy",
  "topCallers.following": "Following",
  "topCallers.copy": "Copy",
  "topCallers.copiedVolume": "Copied Volume",
  "topCallers.avgReturn": "Avg Return",
  "topCallers.bestCall": "Best Call",
  "topCallers.winStreak": "Win Streak",
  "topCallers.viewLeaderboard": "View Full Leaderboard",
  "action.unfollow": "Unfollow",
  "action.following": "Following",

  // ── Status / general ───────────────────────────────────────────────
  "status.loading": "Loading…",
  "status.empty": "Nothing here yet",
  "status.error": "Something went wrong",
  "status.new": "New",
  "status.live": "Live",
  "status.trending": "Trending",
  "status.resolved": "Resolved",
  "status.closed": "Closed",
  "status.open": "Open",
  "status.pending": "Pending",
  "status.expired": "Expired",

  // ── Trading ────────────────────────────────────────────────────────
  "trading.position": "Position",
  "trading.positions": "Positions",
  "trading.openPositions": "Open positions",
  "trading.closedPositions": "Closed positions",
  "trading.profit": "Profit",
  "trading.loss": "Loss",
  "trading.pnl": "P&L",
  "trading.volume": "Volume",
  "trading.liquidity": "Liquidity",
  // Generic open-interest labels reused by EventHero, list-view header,
  // mobile sticky header, etc. Kalshi-only on the read side; we keep
  // the EN/ES copy short because they share row width with stat pills.
  "common.openInterest": "Open Interest",
  "common.oi": "OI",
  "common.24h": "24h",

  // Resolution / market-rules card on every event page
  "resolution.title.market": "Market Rules",
  "resolution.title.event": "Resolution Details",
  "resolution.meta.created": "Created",
  "resolution.meta.resolver": "Resolver",
  "resolution.meta.source": "Source",
  "resolution.meta.marketOpen": "Market open",
  "resolution.meta.marketCloses": "Market closes",
  "resolution.meta.mayCloseEarly": "May close early",
  "resolution.meta.payout": "Payout",
  "resolution.meta.series": "Series",
  "resolution.meta.event": "Event",
  "resolution.meta.market": "Market",
  "resolution.section.timeline": "Timeline & payout",
  "resolution.section.earlyClose": "Early close",
  "resolution.section.outcomeVerified": "Outcome verified from",
  "resolution.section.identifiers": "Identifiers",
  "resolution.expand.showMore": "Show more",
  "resolution.expand.showLess": "Show less",
  "resolution.payout.aboutMinute": "~1 min after close",
  "resolution.payout.minutes": "{n} min after close",
  "resolution.payout.minute": "{n} min after close",
  "resolution.payout.hours": "{n} hrs after close",
  "resolution.payout.hour": "{n} hr after close",
  "resolution.exclusive.text": "At most one market in this event can resolve to \"Yes\". {collateral}When buying/selling \"No\" across markets you may receive or owe funds.",
  "resolution.exclusive.collateral": "Collateral return type: {type}. ",

  // EventHero — top card on every event page (badges + tooltips)
  "hero.bookmark.add": "Add to watchlist",
  "hero.bookmark.remove": "Remove from watchlist",
  "hero.viewOn": "View on {source}",
  "hero.badge.closed": "CLOSED",
  "hero.badge.live": "LIVE",
  "hero.badge.hot": "HOT",
  "hero.badge.new": "NEW",
  "hero.badge.disputed": "DISPUTED",
  "hero.badge.whale": "WHALE",
  "hero.badge.smart": "SMART",
  "hero.tooltip.hot": "Competitive score: {pct}/100\nCloser to 50% = more uncertain = more active trading",
  "hero.tooltip.new": "Listed {n}h ago",
  "hero.tooltip.disputed": "View UMA dispute",
  "hero.tooltip.whale": "Whale market: {wallets} smart wallets · ${vol}M smart volume",
  "hero.tooltip.smart": "Smart-heavy market: {wallets} smart wallets tracking this",
  "hero.tooltip.kalshi.exact": "Also trading on Kalshi (exact match)",
  "hero.tooltip.kalshi.related": "Also trading on Kalshi (related)",
  "hero.tooltip.kalshi.more": "+{n} more",

  // OutcomeDetailPanel — sidebar tabs on each market detail
  "outcomeTabs.chart": "Chart",
  "outcomeTabs.orderbook": "Orderbook",
  "outcomeTabs.trades": "Trades",
  "outcomeTabs.holders": "Top Holders",
  "outcomeTabs.whos": "Who's Trading",
  "outcomeTabs.smart": "Smart Money",
  "outcomeTabs.oi": "Open Interest",
  "outcomeTabs.volume": "Volume",

  // EventMarketsGrid table headers (desktop view of the markets list)
  "marketsGrid.market": "Market",
  "marketsGrid.outcome": "Outcome",
  "eventGrid.volAbbr": "Vol.",
  "kalshiSort.all": "All",
  "kalshiSort.hot": "Hot",
  "kalshiSort.movers": "Movers",
  "kalshiSort.new": "New",
  "kalshiSort.volume": "Volume",
  "kalshiSort.volAbbr": "Vol",
  "marketsGrid.chance": "Chance",
  "marketsGrid.chart24h": "24h Chart",
  "marketsGrid.volume": "Volume",
  "marketsGrid.volume24h": "24h",
  "marketsGrid.buy": "Buy",
  "marketsGrid.result": "Result",
  "marketsGrid.spread": "Spread",
  "marketsGrid.rewards.tooltip": "Earn rewards by placing limit orders near the midpoint.",
  "marketsGrid.rewards.daily": "Rewards",
  "marketsGrid.rewards.dailySuffix": "/day",
  "marketsGrid.rewards.maxSpread": "Max Spread",
  "marketsGrid.rewards.minShares": "Min Shares",
  "marketsGrid.rewards.aria": "Liquidity rewards",

  // Recent Trades panel (Operaciones tab)
  "recentTrades.filter.all": "All",
  "recentTrades.sort.latest": "Latest",
  "recentTrades.sort.biggest": "Biggest",
  "recentTrades.whales": "Whales",
  "recentTrades.whalesTooltip": "Show only trades ≥ $10K",
  "recentTrades.empty": "No recent trades",
  "recentTrades.anonymous": "Anonymous",

  // Section headings shared across event sidebar / detail
  "section.marketTalks": "Market Talks",
  "resolutionDetails.title": "Resolution Details",
  "section.frenflowAnalysis": "FrenFlow Analysis",
  "section.faq": "FAQ",
  "section.faqSubtitle": "Tap a question to read the answer.",

  // MarketTalks comment composer + empty state
  "marketTalks.placeholder": "Share your take on this market...",
  "marketTalks.on": "On",
  "marketTalks.cancel": "Cancel",
  "marketTalks.post": "Post",
  "marketTalks.posting": "Posting…",
  "marketTalks.reply": "Reply",
  "marketTalks.replyingTo": "Replying to",
  "marketTalks.cancelReply": "Cancel reply",
  "marketTalks.empty.title": "No talks about this market yet",
  "marketTalks.empty.subtitle": "Be the first to share your thoughts!",
  "marketTalks.anonymous": "Anonymous",
  "marketTalks.bought": "Bought",
  "marketTalks.sold": "Sold",
  "marketTalks.viewAll": "View all activity",

  // MarketCardPoly multi-outcome footer
  "marketCard.moreOutcomes": "+{n} more outcomes",

  // Limit-order controls inside PlaceTrade (Limit tab)
  "limitControls.price": "Limit Price",
  "limitControls.marketLabel": "Market: {price}",
  "limitControls.marketShortcut": "Market",
  "limitControls.expires": "Expires",
  "limitControls.postOnly": "Post only",

  // OrderSummary collapsible inside PlaceTrade
  "orderSummary.sharesAt": "shares at",
  "orderSummary.sharesAvg": "shares · avg",
  "orderSummary.sellPrice": "Sell price",
  "orderSummary.avgPrice": "Avg price",
  "orderSummary.shares": "Shares",
  "orderSummary.estFees": "Est. fees",
  "orderSummary.youReceive": "You'll receive",
  "orderSummary.realizedPnl": "Realized P&L",
  "orderSummary.maxPayout": "Max payout",
  "orderSummary.potentialProfit": "Potential profit",
  "orderSummary.slippage": "Slippage",
  "orderSummary.partialFill": "Partial fill",
  "orderSummary.thinBook": "Book too thin",

  // Event-level Liquidity Rewards card
  "rewardsBadge.title": "Liquidity rewards",
  "rewardsBadge.summary.one": "{n} of {total} market with a pool",
  "rewardsBadge.summary.many": "{n} of {total} markets with a pool",
  "rewardsBadge.minShares": "min {n} shares",
  "rewardsBadge.tightestSpread": "tightest spread {spread}",
  "rewardsBadge.pool": "Pool",
  "rewardsBadge.perDay": "/day",

  // Sports event TradePanel — order-type toggle, split/merge, market fallback
  "sportsTrade.market": "Market",
  "sportsTrade.limit": "Limit",
  "sportsTrade.fallback": "Market",
  "sportsTrade.hide": "Hide",
  "sportsTrade.splitMerge": "Split/Merge",
  "sportsTrade.spread": "Spread",
  "sportsTrade.mapHandicap": "Map Handicap",
  "sportsTrade.gameHandicap": "Game Handicap",

  // Sports event tabs (marketsByType groups) + More-games sidebar
  "sportsTabs.moneyline": "Moneyline",
  "sportsTabs.gameLines": "Game Lines",
  "sportsTabs.totalGames": "Total Games",
  "sportsTabs.totalSets": "Total Sets",
  "sportsTabs.seriesLines": "Series Lines",
  "sportsTabs.firstSet": "1st Set",
  "sportsTabs.firstSetWinner": "1st Set Winner",
  "sportsTabs.firstSetTotalGames": "1st Set Total Games",
  "sportsTabs.matchTotal": "Match Total",
  "sportsTabs.totalCorners": "Total Corners",
  "sportsTabs.bothTeamsToScore": "Both Teams to Score",
  "sportsTabs.handicap": "Handicap",
  "sportsTabs.spreads": "Spreads",
  "sportsTabs.totals": "Totals",
  "sportsTabs.firstHalf": "1st Half",
  "sportsTabs.secondHalf": "2nd Half",
  "sportsTabs.map": "Map {n}",
  "sportsTabs.set": "Set {n}",
  "sportsTabs.period": "Period {n}",
  "sportsTabs.quarter": "Quarter {n}",
  "sportsTabs.inning": "Inning {n}",
  "sportsTabs.fallback": "{type}",
  "sportsTabs.moreMarkets": "More Markets",
  "sportsTabs.exactScore": "Exact Score",
  "sportsTabs.halftimeResult": "Halftime Result",
  "sportsTabs.corners": "Corners",
  "sportsTabs.cards": "Cards",
  "sportsTabs.btts": "BTTS",
  "sportsTabs.firstGoal": "First Goal",
  "sportsTabs.goalscorer": "Goalscorer",
  "sportsTabs.teamTopBatter": "Team Top Batter",
  "sportsTabs.mostSixes": "Most Sixes",
  "sportsTabs.tossMatchDouble": "Toss Match Double",

  "moreGames.title": "More {league}",
  "marketLabel.totalLine": "Total {n}",
  "marketLabel.totalLineUnit": "Total {n} {unit}",
  "marketLabel.unit.games": "games",
  "marketLabel.unit.maps": "maps",
  "marketLabel.unit.sets": "sets",
  "marketLabel.unit.gamesCap": "Games",
  "marketLabel.unit.mapsCap": "Maps",

  // Chart meta-footer (debajo del chart en /event/<slug>)
  "chartMeta.totalVolume": "Total volume traded on this event",
  "chartMeta.volSuffix": "Vol.",
  "chartMeta.resolutionDate": "Resolution date",
  "chartMeta.earn": "Earn 4%",
  "chartMeta.earnTooltip": "Earn rewards by holding eligible YES/NO shares. Paid automatically based on time held.",
  "chartMeta.learnMore": "Learn more",
  "chartLegend.showMore": "+{n} more",
  "chartLegend.showLess": "Show less",
  "eventMarkets.moreMarkets": "More markets ({n})",
  "eventMarkets.showLess": "Show less",

  // EventSidebar — pills + AI context drawer
  "eventSidebar.setPriceAlert": "Set price alert",
  "eventSidebar.alert": "Alert",
  "eventSidebar.viewAIContext": "View AI Context",
  "eventSidebar.context": "Context",
  "eventSidebar.relatedMarkets": "Related markets",
  "eventSidebar.aiLoading.politics": "Analyzing election context...",
  "eventSidebar.aiLoading.finance": "Analyzing market data...",
  "eventSidebar.aiLoading.geopolitics": "Analyzing geopolitical context...",
  "eventSidebar.aiLoading.crypto": "Analyzing crypto market...",
  "eventSidebar.aiLoading.awards": "Analyzing awards ceremony...",
  "eventSidebar.aiLoading.weather": "Analyzing weather data...",
  "eventSidebar.aiLoading.generic": "AI analyzing event...",

  // MarketCohortBreakdown ("Who's Trading" tab)
  "cohort.title": "Who's Trading",
  "cohort.summary": "Top {n} holders breakdown vs. platform (7d)",
  "cohort.tooltip.holders": "{cohort}: {count} of {total} top holders ({pct}%)",
  "cohort.tooltip.platformAvg": "Platform avg: {pct}%",
  "cohort.tooltip.over": "→ {ratio}× overrepresented",
  "cohort.signal.qualityPrefix": "→ Quality signal:",
  "cohort.signal.whaleHeavy": "WHALE HEAVY",
  "cohort.signal.aboveAvgWhales": "ABOVE AVG WHALES",
  "cohort.label.WHALE": "Whales",
  "cohort.label.ACTIVE_TRADER": "Active",
  "cohort.label.MARKET_MAKER": "Market Maker",
  "cohort.label.BUY_AND_HOLD": "Hodler",
  "cohort.label.HIGH_CONVICTION": "Conviction",
  "cohort.label.CONTRARIAN": "Contrarian",
  "cohort.label.VALUE_HUNTER": "Value Hunter",
  "cohort.label.DEGEN": "Degen",
  "cohort.label.MIXED": "Mixed",
  "cohort.label.UNKNOWN": "Unknown",

  // SmartMoneyIndicator (Smart Money tab)
  "smartMoney.summary": "{n} wallets · {vol}",
  "smartMoney.yes": "YES",
  "smartMoney.no": "NO",
  "smartMoney.shares": "shares",
  "smartMoney.hideWallets": "Hide wallets",
  "smartMoney.showWallets": "Show {n} smart wallets",

  // Sports match view (cabecera deportiva en /event/<slug>)
  "sportsMatch.leagueFallback": "Match",
  "sportsMatch.vs": "VS",
  "sportsMatch.live": "LIVE",
  "sportsMatch.draw": "Draw",
  "sportsMatch.statsVol": "Vol",
  "sportsMatch.statsLiq": "Liq",
  "sportsMatch.statsOi": "OI",
  "trading.price": "Price",
  "trading.amount": "Amount",
  "trading.size": "Size",
  "trading.outcome": "Outcome",
  "trading.yes": "Yes",
  "trading.no": "No",
  "trading.balance": "Balance",
  "trading.available": "Available",
  "trading.fee": "Fee",
  "trading.fees": "Fees",
  "trading.total": "Total",
  "trading.spent": "Spent",
  "trading.value": "Value",

  // ── Portfolio page (/portfolio) ────────────────────────────────────
  "portfolio.tab.positions": "Positions",
  "portfolio.tab.activity": "Activity",
  "portfolio.tab.copytrading": "Copy",
  "portfolio.tab.alerts": "Alerts",
  "portfolio.signInTitle": "Sign in to view your portfolio",
  "portfolio.signInSubtitle": "Connect your wallet to see your positions and copytrading stats",
  "portfolio.trader": "Trader",

  // ProfileBar
  "portfolio.profile.tradingOn": "Trading On",
  "portfolio.profile.enableTrading": "Enable Trading",
  "portfolio.profile.refresh": "Refresh",
  "portfolio.profile.share": "Share",
  "portfolio.profile.toClaim": "to claim",
  "portfolio.profile.percentPos": "{p}% pos",
  "portfolio.profile.winRate": "Win Rate",
  "portfolio.profile.bestTrade": "Best Trade",
  "portfolio.profile.trades": "Trades",
  "portfolio.profile.volume": "Volume",
  "portfolio.profile.openClosed": "{open} open · {closed} closed",
  "portfolio.profile.fetchError": "Couldn't load the latest portfolio data. Tap refresh to retry.",
  "portfolio.profile.download": "Download",
  "portfolio.profile.copyAddress": "Copy address",

  // PnLChart
  "portfolio.chart.totalPnl": "Total P&L",
  "portfolio.chart.empty": "Complete trades to see your P&L chart",
  "portfolio.chart.error": "Failed to load P&L data",
  "portfolio.chart.day": "Day",
  "portfolio.chart.tradesSuffix": "trade",
  "portfolio.chart.tradesSuffixPlural": "trades",
  "portfolio.chart.range.7d": "7D",
  "portfolio.chart.range.30d": "30D",
  "portfolio.chart.range.90d": "90D",
  "portfolio.chart.range.all": "All",

  // CategoryBreakdown
  "portfolio.categories.title": "Trading Focus",
  "portfolio.categories.tradesSuffix": "trades",
  "portfolio.categories.empty": "Start trading to see your focus areas",
  "portfolio.categories.error": "Failed to load categories",
  "portfolio.categories.tooltipTrades": "{n} trades · {pct}%",

  // RedeemablePositionsCard
  "portfolio.redeem.wonOne": "You won {n} market!",
  "portfolio.redeem.wonMany": "You won {n} markets!",
  "portfolio.redeem.subtitle": "Claim your winnings",
  "portfolio.redeem.subtitleNow": "Claim your winnings now",
  "portfolio.redeem.total": "Total",
  "portfolio.redeem.claimNow": "Claim Now",
  "portfolio.redeem.claim": "Claim",

  // Polymarket Rewards
  "portfolio.rewards.title": "Polymarket rewards",
  "portfolio.rewards.subtitle": "Liquidity rewards + maker rebates paid by Polymarket",
  "portfolio.rewards.range.today": "Today",
  "portfolio.rewards.range.7d": "7d",
  "portfolio.rewards.range.30d": "30d",
  "portfolio.rewards.range.lifetime": "All",
  "portfolio.rewards.total": "Total",
  "portfolio.rewards.liquidity": "Liquidity",
  "portfolio.rewards.rebates": "Rebates",
  "portfolio.rewards.earningOnOne": "Earning on {n} market",
  "portfolio.rewards.earningOnMany": "Earning on {n} markets",
  "portfolio.rewards.empty": "Place resting limit orders (maker) to start earning. Rewards are distributed daily at 00:00 UTC.",

  // PositionsSection
  "portfolio.positions.tab.active": "Active",
  "portfolio.positions.tab.closed": "Closed",
  "portfolio.positions.col.market": "Market",
  "portfolio.positions.col.outcome": "Outcome",
  "portfolio.positions.col.bought": "Bought",
  "portfolio.positions.col.shares": "Shares",
  "portfolio.positions.col.avg": "Avg",
  "portfolio.positions.col.now": "Now",
  "portfolio.positions.col.value": "Value",
  "portfolio.positions.col.pnl": "PnL",
  "portfolio.activity.col.market": "Market",
  "portfolio.activity.col.side": "Side",
  "portfolio.activity.col.shares": "Shares",
  "portfolio.activity.col.notional": "Amount",
  "portfolio.activity.payout": "Payout",
  "portfolio.activity.col.status": "Status",
  "portfolio.activity.col.time": "Time",
  "portfolio.positions.source.all": "All",
  "portfolio.positions.source.polymarket": "Polymarket",
  "portfolio.positions.source.kalshi": "Kalshi",
  "portfolio.positions.source.predict": "Predict",
  "portfolio.positions.source.hyperliquid": "Hyperliquid",
  "portfolio.positions.searchPlaceholder": "Search positions...",
  "portfolio.positions.sort.value": "Value",
  "portfolio.positions.sort.pnl": "P/L $",
  "portfolio.positions.sort.percent": "P/L %",
  "portfolio.positions.sort.avgPrice": "Average Price",
  "portfolio.positions.sort.currentPrice": "Current Price",
  "portfolio.positions.sort.dateClosed": "Date Closed",
  "portfolio.positions.sort.dateOpened": "Date Opened",
  "portfolio.positions.sort.alphabetical": "Alphabetically",
  "portfolio.positions.sort.traded": "Traded",
  "portfolio.wallets.sectionTitle": "Wallets",
  "portfolio.wallets.unit": "wallet",
  "portfolio.wallets.unitPlural": "wallets",
  "portfolio.wallets.depositWallet": "Deposit Wallet",
  "portfolio.wallets.safeWallet": "Safe Wallet",
  "portfolio.wallets.solanaWallet": "Solana Wallet",
  "portfolio.wallets.hyperliquidWallet": "Hyperliquid Wallet",
  "portfolio.wallets.evmWallet": "EVM Wallet",
  "portfolio.wallets.signingOnly": "Login / signing only",
  "portfolio.wallets.active": "Active",
  "portfolio.wallets.legacy": "Legacy",
  "portfolio.wallets.auth": "Auth",
  "portfolio.wallets.usdcTradable": "USDC tradable",
  "portfolio.wallets.usdcAvailable": "USDC available",
  "portfolio.wallets.residualBalance": "Residual balance",
  "portfolio.wallets.positionsLabel": "{platform} positions",
  "portfolio.wallets.usdcHlArb": "USDC (HL + Arbitrum)",
  "portfolio.wallets.usdcOnHl": "USDC on HL",
  "portfolio.wallets.usdcOnArb": "USDC on Arbitrum",
  "portfolio.wallets.warnFunds": "This pre-migration Safe still holds funds. Withdraw to your active wallet from settings.",
  "portfolio.wallets.warnPositions": "Open V2 positions still in this Safe. Resolve / sell them from this wallet.",
  "portfolio.positions.value": "Value:",
  "portfolio.positions.pnl": "P&L:",
  "portfolio.positions.sellAll": "Sell All",
  "portfolio.positions.refresh": "Refresh",
  "portfolio.positions.closedHint": "Your finished positions: sold, won or lost.",
  "portfolio.positions.sell": "Sell",
  "portfolio.positions.share": "Share",
  "portfolio.positions.view": "View",
  "portfolio.positions.merge": "Merge",
  "portfolio.positions.mergeYesNo": "Merge YES+NO",
  "portfolio.positions.copy": "COPY",
  "portfolio.positions.copiedFrom": "Copied from {name}",
  "portfolio.positions.won": "WON",
  "portfolio.positions.lost": "LOST",
  "portfolio.positions.sold": "SOLD",
  "portfolio.positions.closed": "Closed",
  "portfolio.positions.empty.openTitle": "No open positions",
  "portfolio.positions.empty.closedTitle": "No closed positions",
  "portfolio.positions.empty.closedSubtitle": "Your resolved positions will appear here",
  "portfolio.positions.empty.openSubtitle": "Start trading to see your positions here",
  "portfolio.positions.empty.claimableOne": "You have {amount} to claim from {n} resolved market",
  "portfolio.positions.empty.claimableMany": "You have {amount} to claim from {n} resolved markets",
  "portfolio.positions.exploreMarkets": "Explore Markets",
  "portfolio.positions.noMatch": "No positions match \"{q}\"",
  "portfolio.positions.error": "Failed to load positions",
  "portfolio.positions.sellAllTitle": "Sell All Positions",
  "portfolio.positions.sellAllSubtitle": "{n} positions at market price",
  "portfolio.positions.totalValue": "Total Value",
  "portfolio.positions.unrealizedPnl": "Unrealized P&L",
  "portfolio.positions.sellAllWarning": "This will sell across Polymarket, Kalshi, and Predict.fun. Cannot be undone.",
  "portfolio.positions.cancel": "Cancel",
  "portfolio.positions.selling": "Selling...",
  "portfolio.positions.progressOf": "{cur} of {total}",
  "portfolio.positions.soldCount": "{n} sold",
  "portfolio.positions.failedCount": "{n} failed",
  "portfolio.positions.done": "Done",
  "portfolio.positions.successful": "Successful",
  "portfolio.positions.failed": "Failed",
  "portfolio.positions.summarySold": "{success} sold",
  "portfolio.positions.summarySoldFailed": "{success} sold, {failed} failed",

  // RecentActivitySection
  "portfolio.activity.loading": "Loading activity...",
  "portfolio.activity.error": "Failed to load recent activity",
  "portfolio.activity.empty": "No recent trades",
  "portfolio.activity.emptySubtitle": "Your trade history will appear here",
  "portfolio.activity.startTrading": "Start Trading",
  "portfolio.activity.justNow": "Just now",
  "portfolio.activity.minutesAgo": "{n}m ago",
  "portfolio.activity.hoursAgo": "{n}h ago",
  "portfolio.activity.daysAgo": "{n}d ago",
  "portfolio.activity.swap": "SWAP",
  "portfolio.activity.redeem": "REDEEM",
  "portfolio.activity.buy": "BUY",
  "portfolio.activity.sell": "SELL",
  "portfolio.activity.yes": "YES",
  "portfolio.activity.no": "NO",
  "portfolio.activity.copy": "COPY",
  "portfolio.activity.won": "WON",
  "portfolio.activity.lost": "LOST",
  "portfolio.activity.resolving": "RESOLVING",
  "portfolio.activity.active": "ACTIVE",
  "portfolio.activity.fee": "Fee",
  "portfolio.activity.pnl": "P&L",
  "portfolio.activity.unknownMarket": "Unknown Market",
  "portfolio.activity.positionResolved": "Position Resolved",
  "portfolio.activity.shares": "shares",

  // CopyTradingSection
  "portfolio.copy.loading": "Loading traders...",
  "portfolio.copy.error": "Failed to load copytrading data",
  "portfolio.copy.errorBoundary": "Something went wrong loading Copy Trading",
  "portfolio.copy.errorBoundaryHint": "Please try refreshing the page",
  "portfolio.copy.emptyTitle": "Not copying anyone yet",
  "portfolio.copy.emptySubtitle": "Find top traders on the leaderboard and start copying their trades",
  "portfolio.copy.exploreTraders": "Explore Traders",
  "portfolio.copy.activeOf": "{active} active of {total}",
  "portfolio.copy.realtimeOn": "Real-time sync active",
  "portfolio.copy.realtimeOff": "Connecting...",
  "portfolio.copy.fullView": "Manage all",
  "portfolio.copy.copying": "Copying",
  "portfolio.copy.ofTraders": "of {n} traders",
  "portfolio.copy.totalTrades": "Total Trades",
  "portfolio.copy.copied": "copied",
  "portfolio.copy.volume": "Volume",
  "portfolio.copy.totalLabel": "total",
  "portfolio.copy.never": "Never",
  "portfolio.copy.unknown": "Unknown",
  "portfolio.copy.active": "Active",
  "portfolio.copy.paused": "Paused",
  "portfolio.copy.noAddress": "No address",
  "portfolio.copy.vol": "Vol",
  "portfolio.copy.wr": "WR",
  "portfolio.copy.last": "Last",
  "portfolio.copy.editSettings": "Edit copy settings",
  "portfolio.copy.manage": "Manage",
  "portfolio.copy.manageTitle": "Manage on Copy Trading page",
  "portfolio.copy.viewProfile": "View trader profile",
  "portfolio.copy.unfollow": "Unfollow",
  "portfolio.copy.mode": "Mode",
  "portfolio.copy.stake": "Stake",
  "portfolio.copy.max": "Max",
  "portfolio.copy.pnlLabel": "Copy PnL",
  "portfolio.copy.pnlTooltip": "Realized + unrealized − fees from your copy trades only. Manual trades aren't included.",
  "portfolio.copy.openLabel": "Open",
  "portfolio.copy.openPositions": "Open Positions",
  "portfolio.copy.netLabel": "net",
  "portfolio.copy.liveLabel": "live",
  "portfolio.copy.modeName.fixed": "Fixed $",
  "portfolio.copy.modeName.percentage": "% of stake",
  "portfolio.copy.modeName.balance_percentage": "% of balance",
  "portfolio.copy.modeName.mirror_percentage": "Mirror %",
  "portfolio.copy.copiedLabel": "Copied:",
  "portfolio.copy.volumeLabel": "Volume:",
  "portfolio.copy.tradesSuffix": "trades",
  "portfolio.copy.pause": "Pause",
  "portfolio.copy.resume": "Resume",
  "portfolio.copy.confirm": "Confirm",
  "portfolio.copy.cancel": "Cancel",
  "portfolio.copy.toggleFailed": "Failed to update copy status",
  "portfolio.copy.unfollowFailed": "Failed to unfollow leader",

  // PriceAlertsSection
  "portfolio.alerts.emptyTitle": "No active alerts",
  "portfolio.alerts.emptySubtitle": "Visit a market page and click the bell icon to create a price alert",
  "portfolio.alerts.browseMarkets": "Browse Markets",
  "portfolio.alerts.activeOf": "{count} of {max} alerts active",

  // ── Feedback page (/feedback) ──────────────────────────────────────
  "feedback.loading": "Loading...",
  // ── Support chat widget (floating bubble) ───────────────────────────
  "support.title": "FrenFlow Support",
  "support.tooltip": "Need help? Chat with us",
  "support.subtitle": "We reply as soon as possible",
  "support.empty": "Start a conversation — we're here to help.",
  "support.placeholder": "Type a message…",
  "support.you": "You",
  "support.team": "FrenFlow Team",
  "support.attach": "Attach image",
  "support.uploading": "Uploading…",
  "support.sending": "Sending…",
  "support.signIn": "Sign in to chat with support.",
  "support.open": "Open support chat",
  "support.close": "Close",
  "support.imageTooBig": "Image too large (max 5MB)",
  "support.sendError": "Couldn't send. Please try again.",
  "support.resolvedNote": "FrenFlow marked this resolved",
  "support.closedNote": "This conversation is closed",
  "support.convResolved": "Conversation resolved",
  "support.convClosed": "Conversation closed",
  "support.reopen": "Reopen",
  "support.newTicket": "New ticket",
  "support.reopenMin": "Add a bit more detail to reopen (min 5 chars)",
  "support.modeReopen": "Reopening conversation",
  "support.modeNew": "New ticket",
  "support.chooseAction": "Choose an option above",
  "feedback.signInPrompt": "Sign in to send feedback and track responses.",
  "feedback.signIn": "Sign In",
  "feedback.tab.new": "New Feedback",
  "feedback.tab.history": "My Feedback",
  "feedback.intro": "Your feedback helps us improve FrenFlow",
  "feedback.thankYou": "Thank you!",
  "feedback.submittedDesc": "Your feedback has been submitted. You can track its status in \"My Feedback\".",
  "feedback.viewMine": "View My Feedback",
  "feedback.sendMore": "Send More",
  "feedback.message": "Your message",
  "feedback.prefill.onboardingError.heading": "I hit an error while setting up trading.",
  "feedback.prefill.onboardingError.errorLabel": "Error",
  "feedback.prefill.onboardingError.noError": "No error message was captured.",
  "feedback.prefill.onboardingError.contextHeading": "Context (auto-attached):",
  "feedback.placeholder.bug": "Describe the bug: what happened, what you expected, and steps to reproduce...",
  "feedback.placeholder.feature": "Describe the feature you'd like and how it would help...",
  "feedback.placeholder.question": "What would you like to know about FrenFlow?",
  "feedback.placeholder.general": "Share your thoughts, suggestions, or anything else...",
  "feedback.minChars": "Minimum 10 characters",
  "feedback.remove": "Remove",
  "feedback.attachScreenshot": "Attach screenshot",
  "feedback.dropHere": "Drop screenshot here",
  "feedback.dropZoneHint": "or click · PNG, JPG, WebP up to 5MB",
  "feedback.uploading": "Uploading image...",
  "feedback.submitting": "Submitting...",
  "feedback.submit": "Send",
  "feedback.selectTypePrompt": "Please select a feedback type above",
  "feedback.chooseType": "What's this about?",
  "feedback.required": "Required",
  "feedback.historyTitle": "My Feedback",
  "feedback.historySubtitle": "Track your submissions and see responses",
  "feedback.noHistory": "No feedback yet",
  "feedback.sendFirst": "Send your first feedback",
  "feedback.loadMore": "Load more",
  "feedback.loadingDots": "Loading...",
  // Categories
  "feedback.cat.bug.label": "Bug Report",
  "feedback.cat.bug.desc": "Something isn't working correctly",
  "feedback.cat.feature.label": "Feature Request",
  "feedback.cat.feature.desc": "Suggest a new feature or improvement",
  "feedback.cat.question.label": "Question",
  "feedback.cat.question.desc": "Ask us anything about FrenFlow",
  "feedback.cat.general.label": "General Feedback",
  "feedback.cat.general.desc": "Share your thoughts or suggestions",
  // Status badges
  "feedback.status.open": "Open",
  "feedback.status.in_progress": "In Progress",
  "feedback.status.resolved": "Resolved",
  "feedback.status.declined": "Declined",
  "feedback.status.closed": "Closed",
  "feedback.status.deleted": "Deleted",
  "feedback.statusTooltip.open": "We've received this and it's in our queue.",
  "feedback.statusTooltip.in_progress": "We're actively working on this.",
  "feedback.statusTooltip.resolved": "We shipped a fix or response. Reopen if you still have issues.",
  "feedback.statusTooltip.declined": "We've decided not to pursue this. See the team's reply for context.",
  "feedback.statusTooltip.closed": "You closed this ticket. It's archived.",
  "feedback.statusTooltip.deleted": "This ticket was removed.",
  "feedback.historyFilter.all": "All",
  "feedback.historyFilter.open": "Open",
  "feedback.historyFilter.resolved": "Resolved",
  "feedback.historyFilter.closed": "Closed",
  "feedback.historyFilter.showAll": "Show all",
  "feedback.noHistoryInBucket": "Nothing here yet.",
  // Time-ago
  "feedback.time.justNow": "Just now",
  "feedback.time.minutesAgo": "{n}m ago",
  "feedback.time.hoursAgo": "{n}h ago",
  "feedback.time.daysAgo": "{n}d ago",
  // History card
  "feedback.card.hideImage": "Hide image",
  "feedback.card.showImage": "Show image",
  "feedback.card.hideScreenshotAria": "Hide screenshot",
  "feedback.card.showScreenshotAria": "Show screenshot",
  "feedback.card.adminReplyTitle": "Response from FrenFlow",
  "feedback.card.userReplyTitle": "Your Reply",
  "feedback.card.close": "Close feedback",
  "feedback.card.confirmClose": "Close this ticket?",
  "feedback.card.cancel": "Cancel",
  "feedback.card.yesClose": "Yes, close",
  "feedback.card.replyCta": "Reply to this response",
  "feedback.card.replyPlaceholder": "Write your reply...",
  "feedback.card.replyOnceNote": "⚠️ Note: You can only reply once per feedback.",
  "feedback.card.sendReply": "Send Reply",
  "feedback.card.reopen": "Still an issue — reopen",
  "feedback.card.teamBadge": "Team",
  "feedback.reopenModal.title": "Reopen this ticket",
  "feedback.reopenModal.subtitle": "Tell us what's still broken so we can fix it.",
  "feedback.reopenModal.placeholder": "Describe what's still happening — steps to reproduce, what you expected vs what you got…",
  "feedback.reopenModal.minChars": "Minimum 5 characters",
  "feedback.reopenModal.submit": "Reopen",
  // Errors
  "feedback.error.replyMin": "Reply must be at least 5 characters",
  "feedback.error.failedClose": "Failed to close ticket",
  "feedback.error.failedReopen": "Failed to reopen ticket",
  "feedback.error.somethingWrong": "Something went wrong",
  "feedback.error.failedReply": "Failed to send reply",
  "feedback.error.invalidImage": "Only JPEG, PNG, WebP, and GIF images are allowed",
  "feedback.error.imageTooBig": "Image must be less than 5MB",
  "feedback.error.uploadFailed": "Failed to upload image",
  "feedback.error.submitFailed": "Failed to submit feedback",
  "feedback.error.tryAgain": "Something went wrong. Please try again.",

  // ── Error pages (500 / global) ─────────────────────────────────────
  "error.title": "Something went wrong",
  "error.description": "An unexpected error occurred. Please try again.",
  "error.tryAgain": "Try again",
  "error.retrying": "Retrying…",
  "error.headline": "We dropped the order.",
  "error.subtitle": "Something tripped on our side. Give it another shot — usually it just works.",
  "error.offlineHeadline": "You're offline, fren.",
  "error.offlineSubtitle": "Check your connection and we'll be back.",
  "error.offlineChip": "Offline",
  "error.chip": "Error · 500 · {section}",
  "error.portfolio.title": "Portfolio unavailable",
  "error.portfolio.desc": "We couldn't load your portfolio. Your positions are safe.",
  "error.copytrading.title": "Copy Trading unavailable",
  "error.copytrading.desc": "We couldn't load your copy trading settings. Your active copies are still running.",
  "error.activity.title": "Activity unavailable",
  "error.activity.desc": "We couldn't load the activity feed. Please try again.",
  "error.jumpTo": "Or jump to",
  "error.shortcut.markets": "Markets",
  "error.shortcut.traders": "Traders",
  "error.shortcut.activity": "Activity",
  "error.shortcut.home": "Home",
  "error.reportBug": "Report bug",

  // ── Affiliate page (/affiliate) ────────────────────────────────────
  // Hero
  "aff.hero.badge": "Earn 10% Commission on Every Trade",
  "aff.hero.titlePart1": "The Best",
  "aff.hero.titlePart2": "Polymarket Referral",
  "aff.hero.titlePart3": "Program",
  "aff.hero.subtitle": "Join FrenFlow's affiliate program and earn passive income from prediction markets. Get <b>10% of all trading fees</b> from users you refer, trades from your posts, and when others copy your trades. No limits, lifetime commissions.",
  "aff.hero.ctaStart": "Start Earning Now",
  "aff.hero.ctaLearn": "Learn More",
  "aff.hero.statCommission": "Commission",
  "aff.hero.statMinPayout": "Min. Payout",
  "aff.hero.statLifetimeLabel": "Lifetime",
  "aff.hero.statLifetime": "Commissions",
  "aff.hero.calcTitle": "Calculate Your Earnings",
  "aff.hero.calcSubtitle": "Drag the sliders to see your potential",
  "aff.hero.calcReferrals": "Active Referrals",
  "aff.hero.calcVolume": "Avg. Volume/User/Day",
  "aff.hero.calcDaily": "Daily",
  "aff.hero.calcMonthly": "Monthly",
  "aff.hero.calcYearly": "Yearly Projection",
  "aff.calc.badge": "Earnings Calculator",
  "aff.calc.titlePart1": "How Much Can You",
  "aff.calc.titlePart2": "Earn?",
  "aff.calc.subtitle": "Drag the sliders to see your potential earnings from the FrenFlow Polymarket affiliate program.",
  "aff.calc.referrals": "Active Referrals",
  "aff.calc.users": "users",
  "aff.calc.ariaReferrals": "Active referrals",
  "aff.calc.volume": "Avg. Daily Volume per User",
  "aff.calc.perDay": "/day",
  "aff.calc.ariaVolume": "Average daily volume per user",
  "aff.calc.dailyVolume": "Daily Volume",
  "aff.calc.dailyEarnings": "Daily Earnings",
  "aff.calc.monthlyEarnings": "Monthly Earnings",
  "aff.calc.yearlyProjection": "Yearly Projection",
  "aff.calc.formulaReferrals": "{n} referrals",
  "aff.calc.fee": "1% fee",
  "aff.calc.commission": "10% commission",
  "aff.kit.color.lime.name": "Lime",
  "aff.kit.color.obsidian.name": "Obsidian",
  "aff.kit.color.iron.name": "Iron",
  "aff.kit.color.coal.name": "Coal",
  "aff.kit.color.snow.name": "Snow",
  "aff.kit.color.emerald.name": "Emerald",
  "aff.kit.color.crimson.name": "Crimson",
  "aff.kit.color.violet.name": "Violet",
  "aff.dash.source.web": "Web",
  "aff.dash.source.telegram": "Telegram",

  // How it works
  "aff.how.eyebrow": "Simple Process",
  "aff.how.titlePart1": "How the Referral Program",
  "aff.how.titlePart2": "Works",
  "aff.how.subtitle": "Start earning from Polymarket in 4 simple steps. No experience required - just share your link and earn.",
  "aff.how.step1.title": "Sign Up for Free",
  "aff.how.step1.desc": "Create your FrenFlow account in seconds. Use email, Google, or connect your wallet. Your unique referral link is generated instantly.",
  "aff.how.step2.title": "Share Your Link",
  "aff.how.step2.desc": "Share your referral link on social media, YouTube, Discord, or directly with friends. Every click is tracked in your dashboard.",
  "aff.how.step3.title": "Friends Sign Up & Trade",
  "aff.how.step3.desc": "When someone uses your link to sign up and starts trading on Polymarket through FrenFlow, they become your referral.",
  "aff.how.step4.title": "Earn 10% Forever",
  "aff.how.step4.desc": "You earn 10% of all trading fees from your referrals. Commissions are tracked in real-time and paid out on request.",
  "aff.how.cta": "Get Your Referral Link",

  // Benefits
  "aff.benefits.eyebrow": "Why Join Our Program",
  "aff.benefits.titlePart1": "Benefits of FrenFlow",
  "aff.benefits.titlePart2": "Affiliate Program",
  "aff.benefits.subtitle": "The most rewarding Polymarket referral program. Earn passive income while helping others discover prediction markets.",
  "aff.benefits.commission.title": "10% Commission",
  "aff.benefits.commission.desc": "Earn 10% of all trading fees generated by your referrals. Higher than most crypto affiliate programs.",
  "aff.benefits.posts.title": "Earn from Your Posts",
  "aff.benefits.posts.desc": "When someone trades directly from one of your posts or market analysis, you earn commission on that trade. Better content = more engagement = more earnings.",
  "aff.benefits.copy.title": "Copytrading Commissions",
  "aff.benefits.copy.desc": "When other users copy your trades, you earn a commission on every copied trade. Build your reputation and let your followers grow your income.",
  "aff.benefits.lifetime.title": "Lifetime Earnings",
  "aff.benefits.lifetime.desc": "Your referrals are linked to you forever. Earn commissions as long as they keep trading on Polymarket.",
  "aff.benefits.tracking.title": "Instant Tracking",
  "aff.benefits.tracking.desc": "Real-time dashboard shows clicks, signups, and earnings. Track your referral performance instantly.",
  "aff.benefits.fp.title": "Bonus FrenPoints",
  "aff.benefits.fp.desc": "Get +10 FrenPoints for each referral that becomes active ($100+ volume). Use FP for rewards and perks.",
  "aff.benefits.market.title": "Growing Market",
  "aff.benefits.market.desc": "Polymarket volume is exploding. More users = more trades = more commissions for you.",
  "aff.benefits.nolimits.title": "No Limits",
  "aff.benefits.nolimits.desc": "No cap on referrals or earnings. The more you share, the more you earn. Scale without restrictions.",

  // Creator section
  "aff.creator.eyebrow": "For Content Creators & Traders",
  "aff.creator.titlePart1": "Better Content =",
  "aff.creator.titlePart2": "More Commissions",
  "aff.creator.subtitle": "FrenFlow rewards quality. When you share market analysis, post trade ideas, or build a following, <b>every trade that comes from your content earns you commission</b>. The more engagement you create, the more you earn.",
  "aff.creator.step1.title": "Post Market Analysis",
  "aff.creator.step1.desc": "Share your predictions, charts, and insights. Users can trade directly from your posts.",
  "aff.creator.step2.title": "Get Impressions",
  "aff.creator.step2.desc": "Quality content gets seen. More views = more potential trades = more commissions.",
  "aff.creator.step3.title": "Get Copied",
  "aff.creator.step3.desc": "Build a track record and users will copy your trades. Every copied trade earns you commission.",
  "aff.creator.step4.title": "Earn Automatically",
  "aff.creator.step4.desc": "Commissions are tracked and credited to your account automatically. No manual claims needed.",
  "aff.creator.cta": "Start Creating & Earning",
  "aff.creator.mockPost": "Bitcoin will hit $150K before March. Here's why I'm loading up on this market...",
  "aff.creator.mockMarket": "BTC above $150K by March?",
  "aff.creator.mockBuyYes": "Buy YES 42¢",
  "aff.creator.mockBuyNo": "Buy NO 58¢",
  "aff.creator.mockCopies": "{n} copies",
  "aff.creator.mockViews": "{n} views",
  "aff.creator.mockTimeAgo": "2h ago",
  "aff.creator.mockEarned": "+$24.50 earned today",
  "aff.creator.mockEarnedSub": "From 89 copied trades on this post",
  "aff.creator.mockMonth": "This month",

  // Partners section
  "aff.partners.badge": "Exclusive Program",
  "aff.partners.titlePart1": "KOLs & Partners",
  "aff.partners.titlePart2": "Program",
  "aff.partners.subtitle": "Are you a content creator, influencer, or community leader? Get <b>custom deals and higher commissions</b> tailored to your audience.",
  "aff.partners.perk1.title": "Custom Commission Rates",
  "aff.partners.perk1.desc": "Negotiate higher commission rates based on your audience size and engagement.",
  "aff.partners.perk2.title": "Dedicated Support",
  "aff.partners.perk2.desc": "Get a dedicated account manager to help you maximize your earnings.",
  "aff.partners.perk3.title": "Early Access",
  "aff.partners.perk3.desc": "Be the first to try new features and share exclusive content with your audience.",
  "aff.partners.perk4.title": "Co-Marketing",
  "aff.partners.perk4.desc": "Joint campaigns, AMAs, and promotional support to boost your reach.",
  "aff.partners.cta": "Contact Us: partners@frenflow.com",
  "aff.partners.ctaSub": "Tell us about your audience and we'll create a custom deal for you",

  // CTA section
  "aff.cta.title": "Ready to Start Earning?",
  "aff.cta.subtitle": "Join the best Polymarket affiliate program today. Get your referral link in seconds and start earning 10% commission on every trade.",
  "aff.cta.primary": "Get Your Referral Link",
  "aff.cta.telegram": "Try Telegram Bot",
  "aff.cta.trust1": "No deposit required",
  "aff.cta.trust2": "Free to join",
  "aff.cta.trust3": "Instant setup",

  // FAQ
  "aff.faq.eyebrow": "FAQ",
  "aff.faq.titlePart1": "FrenFlow Affiliate Program",
  "aff.faq.titlePart2": "FAQ",
  "aff.faq.subtitle": "Everything you need to know about earning with the FrenFlow prediction markets affiliate program.",
  "aff.faq.contact.prefix": "Have more questions? Contact us at",
  "aff.faq.cat.basics": "Program Basics",
  "aff.faq.cat.earnings": "Earnings & Commissions",
  "aff.faq.cat.payouts": "Payouts",
  "aff.faq.cat.creators": "For Content Creators",
  "aff.faq.cat.tracking": "Referral Tracking",
  "aff.faq.cat.security": "Security & Trust",
  // FAQ items (concatenated by category-index)
  "aff.faq.basics.0.q": "What is the FrenFlow Affiliate Program?",
  "aff.faq.basics.0.a": "The FrenFlow Affiliate Program lets you earn 10% commission on all trading fees generated by users you refer. When someone signs up using your unique referral link and trades on any prediction market through FrenFlow, you earn money automatically - forever.",
  "aff.faq.basics.1.q": "How is this different from other referral programs?",
  "aff.faq.basics.1.a": "FrenFlow offers one of the highest commission rates in prediction markets (10% vs typical 5%). Plus, you earn from ALL prediction markets - not just Polymarket. As we add new markets like Kalshi, PredictIt, and others, your referrals' trades on those platforms will also generate commissions for you.",
  "aff.faq.basics.2.q": "Do I need to trade myself to earn commissions?",
  "aff.faq.basics.2.a": "No! You don't need to deposit funds or place any trades to earn affiliate commissions. Simply sign up, get your referral link, and start sharing. However, trading yourself can help you create better content and attract more referrals.",
  "aff.faq.earnings.0.q": "How much can I earn as a FrenFlow affiliate?",
  "aff.faq.earnings.0.a": "You earn 10% of all trading fees from your referrals. FrenFlow charges 1% per trade, so you get 0.1% of their total volume. Example: If your referrals trade $100,000 total, you earn $100. There's no cap - the more active referrals you have, the more you earn.",
  "aff.faq.earnings.1.q": "Do I earn from all prediction markets?",
  "aff.faq.earnings.1.a": "Yes! You earn commissions from ALL prediction markets integrated with FrenFlow. Currently this includes Polymarket, and as we add more markets (Kalshi, PredictIt, sports betting markets, etc.), your referrals' activity on those platforms will also generate commissions for you.",
  "aff.faq.earnings.2.q": "How long do I earn commissions from a referral?",
  "aff.faq.earnings.2.a": "Lifetime! Once someone signs up using your link, they're permanently linked to your account. You'll earn 10% of their trading fees for as long as they use FrenFlow - there's no expiration date.",
  "aff.faq.payouts.0.q": "What is the minimum payout amount?",
  "aff.faq.payouts.0.a": "The minimum payout threshold is $10. Once your earned commissions reach $10, you can request a withdrawal at any time from your affiliate dashboard.",
  "aff.faq.payouts.1.q": "How and when do I get paid?",
  "aff.faq.payouts.1.a": "Your earnings are tracked in real-time on your affiliate dashboard. Request a payout anytime after reaching the $10 minimum. Payouts are processed within 48 hours to your connected wallet or preferred payment method in USDC.",
  "aff.faq.payouts.2.q": "Is there a limit to how much I can earn?",
  "aff.faq.payouts.2.a": "No limits at all! Whether you have 10 referrals or 10,000, you'll earn 10% commission on all their trading fees. Top affiliates earn thousands of dollars monthly. We encourage you to grow your network as large as possible.",
  "aff.faq.creators.0.q": "Can I earn from my posts and content?",
  "aff.faq.creators.0.a": "Yes! When you share market analysis or predictions on FrenFlow and someone trades directly from your post, you earn commission on that trade. Better content = more impressions = more trades = more earnings.",
  "aff.faq.creators.1.q": "How do I earn from copy trading?",
  "aff.faq.creators.1.a": "When users follow and copy your trades, you earn commission on every copied trade. Build a strong track record, share your wins, and grow your follower base. The more copiers you have, the more passive income you generate.",
  "aff.faq.creators.2.q": "Do my referrals get any benefits?",
  "aff.faq.creators.2.a": "Yes! Users who sign up through your link get full access to FrenFlow features: copy trading, whale tracking, Telegram bot, AI analytics, and more. They benefit from our low 1% fees and secure non-custodial trading.",
  "aff.faq.tracking.0.q": "How do I track my referral performance?",
  "aff.faq.tracking.0.a": "Your affiliate dashboard shows real-time stats: link clicks, signups, active referrals, total trading volume, commissions earned, and pending payouts. You can see which referrals are most active and track your growth over time.",
  "aff.faq.tracking.1.q": "Is there a limit to how many people I can refer?",
  "aff.faq.tracking.1.a": "No limits! Refer as many people as you want. Whether you're sharing with friends or building a large community, every signup through your link earns you lifetime commissions on their trades.",
  "aff.faq.tracking.2.q": "What happens if someone clears their cookies?",
  "aff.faq.tracking.2.a": "Our tracking uses multiple methods beyond cookies. Once someone clicks your link, they're tracked for 30 days. Once they sign up, they're permanently linked to your account regardless of cookies or devices.",
  "aff.faq.security.0.q": "Is FrenFlow safe to promote?",
  "aff.faq.security.0.a": "Absolutely. FrenFlow uses non-custodial architecture - users' funds stay in their own wallets powered by Privy. We can only execute authorized trades, never withdraw funds. Your referrals' money is always secure.",
  "aff.faq.security.1.q": "What is FrenFlow and why promote it?",
  "aff.faq.security.1.a": "FrenFlow is the leading platform for prediction market trading, offering copy trading, whale tracking, and AI analytics. It makes markets like Polymarket accessible to everyone. With billions in trading volume, it's one of the fastest-growing platforms in crypto.",
  "aff.faq.security.2.q": "Do I need to disclose my affiliate relationship?",
  "aff.faq.security.2.a": "Yes, we recommend always disclosing that you're using an affiliate link. It builds trust with your audience and is required by most advertising regulations. Transparent affiliates typically have higher conversion rates.",

  // Promo Ideas
  "aff.promo.eyebrow": "Promotion Playbook",
  "aff.promo.titlePart1": "How to Promote &",
  "aff.promo.titlePart2": "Earn More",
  "aff.promo.subtitle": "Proven strategies to grow your referral network. Pick what fits your style and start earning.",
  "aff.promo.tipsTitle": "Pro Tips for Success",
  "aff.promo.bottomPrefix": "Need more ideas? Check out our",
  "aff.promo.bottomLink": "blog",
  "aff.promo.bottomSuffix": "for marketing guides and content inspiration.",

  // Affiliate Dashboard
  "aff.dash.loadingSetup": "Setting up your affiliate account...",
  "aff.dash.tryAgain": "Try Again",
  "aff.dash.title": "Affiliate Program",
  "aff.dash.subtitle": "Earn {pct}% of fees + 10 FP for each active referral ($100+ volume)",
  "aff.dash.tab.dashboard": "Dashboard",
  "aff.dash.tab.brandKit": "Brand Kit",
  "aff.dash.stat.available": "Available",
  "aff.dash.stat.earned": "Earned",
  "aff.dash.stat.fp": "FP",
  "aff.dash.stat.referrals": "Referrals",
  "aff.dash.stat.clicks": "Clicks",
  "aff.dash.invites.title": "Invite Codes",
  "aff.dash.invites.subtitle": "Single-use codes to invite friends",
  "aff.dash.invites.remaining": "Remaining",
  "aff.dash.invites.unlimited": "unlimited",
  "aff.dash.invites.new": "+ New",
  "aff.dash.invites.empty": "No invite codes yet",
  "aff.dash.invites.emptyHint": "Click \"+ New\" to generate your first code",
  "aff.dash.invites.used": "Used",
  "aff.dash.invites.available": "Available",
  "aff.dash.invites.usedBy": "Used by {who}",
  "aff.dash.invites.usedBySomeone": "Used by someone",
  "aff.dash.invites.copyLink": "Copy Link",
  "aff.dash.invites.copied": "Copied!",
  "aff.dash.invites.shareCard": "Share Card",
  "aff.dash.invites.created": "Created {date}",
  "aff.dash.invites.confirmGenerate": "Generate a new invite code? {msg}",
  "aff.dash.invites.unlimitedMsg": "You have unlimited invites.",
  "aff.dash.invites.remainingMsg": "You have {n} remaining.",
  "aff.dash.tabs.referrals": "Referrals ({n})",
  "aff.dash.tabs.commissions": "Commissions ({n})",
  "aff.dash.tabs.payouts": "Payouts ({n})",
  "aff.dash.referrals.empty": "No referrals yet. Share your link to start earning!",
  "aff.dash.commissions.empty": "No commissions yet. You'll earn when your referrals trade!",
  "aff.dash.payouts.empty": "No payouts yet. Request your first payout from the sidebar.",
  "aff.dash.col.wallet": "Wallet",
  "aff.dash.col.level": "Level",
  "aff.dash.col.origin": "Origin",
  "aff.dash.col.source": "Source",
  "aff.dash.col.date": "Date",
  "aff.dash.col.status": "Status",
  "aff.dash.col.volume": "Volume",
  "aff.dash.col.earned": "Earned",
  "aff.dash.col.from": "From",
  "aff.dash.col.commission": "Commission",
  "aff.dash.col.amount": "Amount",
  "aff.dash.col.destination": "Destination",
  "aff.dash.col.tx": "TX",
  "aff.dash.status.active": "Active",
  "aff.dash.status.pending": "Pending",
  "aff.dash.anonymous": "Anonymous",
  "aff.dash.parentTooltip": "Your L1 referral who brought this user",
  "aff.dash.post": "Post",
  "aff.dash.viewPost": "View post",
  "aff.dash.payoutStatus.requested": "Pending",
  "aff.dash.payoutStatus.approved": "Approved",
  "aff.dash.payoutStatus.processing": "Processing",
  "aff.dash.payoutStatus.completed": "Completed",
  "aff.dash.payoutStatus.rejected": "Rejected",
  "aff.dash.payoutStatus.failed": "Failed",
  "aff.dash.rejected": "${amt} rejected: {reason}",
  "aff.dash.linkCard.yourLink": "Your Code",
  "aff.dash.linkCard.copy": "Copy",
  "aff.dash.linkCard.copied": "Copied",
  "aff.dash.linkCard.webLabel": "Web link",
  "aff.dash.linkCard.telegramLabel": "Telegram bot link",
  "aff.dash.linkCard.shareX": "Share on X",
  "aff.dash.linkCard.shareTg": "Share on Telegram",
  "aff.dash.rewards.title": "Your Rewards",
  "aff.dash.rewards.fees": "Referral Fees",
  "aff.dash.rewards.fp": "FrenPoints Bonus",
  "aff.dash.rewards.l2": "L2 Fees",
  "aff.dash.rewards.l3": "L3 Fees",
  "aff.dash.rewards.note": "Earn {pct}% of trading fees + 10 FP bonus for each referral that reaches $100+ in trading volume.",
  "aff.dash.last30": "Last 30 Days",
  "aff.dash.metrics.clicks": "Clicks",
  "aff.dash.metrics.signups": "Signups",
  "aff.dash.metrics.earned": "Earned",
  "aff.dash.payout.title": "Payout",
  "aff.dash.payout.request": "Request Payout",
  "aff.dash.payout.noBalance": "No balance available",
  "aff.dash.payout.processedIn": "Payouts processed within 48h",
  "aff.dash.error.timeout": "Request timed out. Please try again.",
  "aff.dash.error.loading": "Error loading affiliate data",
  "aff.dash.error.creatingCode": "Error creating referral code",
  "aff.dash.tweetCopy": "Trade prediction markets like a pro with FrenFlow! Join me and get started:",
  "aff.dash.telegramCopy": "Trade prediction markets like a pro with FrenFlow! Join me:",

  // Brand Kit (toolkit)
  "aff.kit.title": "Brand Assets",
  "aff.kit.subtitle": "Logos, colors, banners, and ready-to-post captions. All materials include your personal referral link.",
  "aff.kit.downloadAll": "Download All Assets",
  "aff.kit.downloading": "Downloading assets...",
  "aff.kit.copied": "{hex} copied!",
  "aff.kit.logo.title": "Logo",
  "aff.kit.logo.desc": "Use the wordmark consistently. Prefer monochrome usage on colored backgrounds.",
  "aff.kit.logo.dark": "Logo Dark",
  "aff.kit.logo.light": "Logo Light",
  "aff.kit.logo.accent": "Logo Accent",
  "aff.kit.logo.download": "Download",
  "aff.kit.colors.title": "Color Palette",
  "aff.kit.colors.desc": "The actual hex values shipped in the app. Click any swatch to copy.",
  "aff.kit.color.lime.role": "Brand accent",
  "aff.kit.color.obsidian.role": "Page background (dark)",
  "aff.kit.color.iron.role": "Elevated card",
  "aff.kit.color.coal.role": "Flush card",
  "aff.kit.color.snow.role": "Page background (light)",
  "aff.kit.color.emerald.role": "YES · positive",
  "aff.kit.color.crimson.role": "NO · negative",
  "aff.kit.color.violet.role": "Copytrade marker",
  "aff.kit.type.title": "Typography",
  "aff.kit.type.desc": "Two typefaces: Space Grotesk for headings, Inter for everything else.",
  "aff.kit.type.headings": "Headings & Display",
  "aff.kit.type.ui": "UI · Body · Stats",
  "aff.kit.type.bold": "Bold",
  "aff.kit.type.semibold": "Semibold",
  "aff.kit.type.medium": "Medium",
  "aff.kit.type.regular": "Regular",
  "aff.kit.type.sampleHeading": "Trade Smarter",
  "aff.kit.type.sampleUi": "Copy Trader",

  // Request Payout Modal
  "aff.payout.title": "Request Payout",
  "aff.payout.requested": "Payout Requested!",
  "aff.payout.pendingApproval": "Your payout is pending admin approval",
  "aff.payout.amount": "Amount",
  "aff.payout.commissions": "Commissions",
  "aff.payout.status": "Status",
  "aff.payout.statusPending": "Pending Approval",
  "aff.payout.processedIn": "Your payout will be processed within 48 hours",
  "aff.payout.close": "Close",
  "aff.payout.availableBalance": "Available Balance",
  "aff.payout.allPendingNote": "All pending commissions will be included",
  "aff.payout.destinationLabel": "Destination Address (Polygon)",
  "aff.payout.invalidAddress": "Invalid Polygon address format",
  "aff.payout.invalidAddressError": "Invalid Polygon wallet address",
  "aff.payout.failed": "Failed to request payout",
  "aff.payout.note1": "Payout will be sent as USDC.e on Polygon network.",
  "aff.payout.note2": "Make sure the address supports USDC.e (ERC-20).",
  "aff.payout.requesting": "Requesting...",
  "aff.payout.cta": "Request Payout — ${amount}",

  // Share Invite Modal
  "aff.invite.title": "Share Invite Card",
  "aff.invite.generating": "Generating invite card...",
  "aff.invite.generationFailed": "Failed to generate card",
  "aff.invite.retry": "Retry",
  "aff.invite.dragHint": "Drag to rotate · Double-click for 360°",
  "aff.invite.tagline": "Trade Smarter · Win Together",
  "aff.invite.copy": "Copy",
  "aff.invite.copied": "Copied!",
  "aff.invite.copyError": "Could not copy to clipboard:",
  "aff.invite.shareTextPrefix": "Join FrenFlow with my invite code! Trade prediction markets and earn rewards:",
  "aff.invite.twitterCta": "Trade on @frenflow_",
  "aff.invite.pasteHintTwitter": "Image copied! Paste it (Ctrl+V / Cmd+V) in your tweet to include the invite card.",
  "aff.invite.pasteHintTelegram": "Image copied! Paste it (Ctrl+V / Cmd+V) in Telegram to include the invite card.",

  // ── FrenLinks page (/frenlinks) ────────────────────────────────────
  // Landing — Hero
  "fren.hero.badge": "Prediction Market Gift Links",
  "fren.hero.titlePart1": "Send shares to anyone",
  "fren.hero.titlePart2": "with a single link",
  "fren.hero.subtitle": "Create gift links for Polymarket positions. Share on X, Telegram, or Discord. Recipients claim instantly — no wallet needed upfront.",
  "fren.hero.benefit.escrow": "On-chain escrow",
  "fren.hero.benefit.instant": "Instant claims",
  "fren.hero.benefit.refund": "Auto-refund",
  "fren.hero.cta": "Create Your First FrenLink",
  "fren.hero.trust.free": "Free to use",
  "fren.hero.trust.noWallet": "No wallet needed to claim",
  "fren.hero.demoBadge": "Interactive Demo",
  // How it works
  "fren.how.eyebrow": "How it works",
  "fren.how.title": "From position to viral link in 60 seconds",
  "fren.how.step1.title": "Choose a position",
  "fren.how.step1.desc": "Select any YES or NO shares you hold on Polymarket. Any market, any outcome.",
  "fren.how.step2.title": "Set claim rules",
  "fren.how.step2.desc": "Pick your mode: gift one fren, split among many, randomize amounts, or run a lottery.",
  "fren.how.step3.title": "Add social quests",
  "fren.how.step3.desc": "Gate claims behind X follows, reposts, Discord joins, or Telegram groups. Built-in growth.",
  "fren.how.step4.title": "Share & earn",
  "fren.how.step4.desc": "Every claim auto-registers as your referral. Grow your network while sharing alpha.",
  // Claim modes
  "fren.modes.title": "Four claim modes",
  "fren.modes.subtitle": "Each mode creates a different dynamic. Choose your strategy.",
  "fren.modes.gift.tag": "Direct",
  "fren.modes.gift.label": "Gift",
  "fren.modes.gift.desc": "One link, one recipient. Perfect for rewarding a specific fren or closing a deal.",
  "fren.modes.split.tag": "Community",
  "fren.modes.split.label": "Split",
  "fren.modes.split.desc": "First N claimers split the shares equally. Great for Discord drops and Telegram groups.",
  "fren.modes.random.tag": "Surprise",
  "fren.modes.random.label": "Random",
  "fren.modes.random.desc": "Each claimer gets a random amount from the pool. The surprise factor drives engagement.",
  "fren.modes.lottery.tag": "Viral",
  "fren.modes.lottery.label": "Lottery",
  "fren.modes.lottery.desc": "One lucky winner takes the entire pool. Maximum virality — everyone wants to try their luck.",
  // Use cases
  "fren.useCases.title": "Built for every use case",
  "fren.useCases.kol.title": "KOL Giveaways",
  "fren.useCases.kol.desc": "Run prediction market giveaways on X. Gate claims behind follows and reposts to grow your audience while distributing alpha.",
  "fren.useCases.community.title": "Community Rewards",
  "fren.useCases.community.desc": "Reward your Discord or Telegram community with free Polymarket shares. Split mode ensures fair distribution.",
  "fren.useCases.referrals.title": "Referral Campaigns",
  "fren.useCases.referrals.desc": "Every FrenLink claim auto-registers the claimer as your referral. Scale your network with zero friction.",
  "fren.useCases.competitions.title": "Trading Competitions",
  "fren.useCases.competitions.desc": "Use lottery mode to create high-stakes prediction market raffles. Maximum engagement, maximum virality.",
  // Trust
  "fren.trust.title": "Zero risk by design",
  "fren.trust.subtitle": "FrenLinks use on-chain escrow. Your shares are locked in a smart contract — not held by us. If nobody claims, you get everything back automatically.",
  "fren.trust.escrow.title": "Smart contract escrow",
  "fren.trust.escrow.desc": "Shares are held trustlessly on Polygon. Verifiable on-chain.",
  "fren.trust.claims.title": "One-click claims",
  "fren.trust.claims.desc": "No wallet setup needed. We create one for the claimer if they don't have one.",
  "fren.trust.refunds.title": "Automatic refunds",
  "fren.trust.refunds.desc": "Expired or cancelled links return shares to your wallet instantly.",
  "fren.trust.stat.fee": "Platform fee",
  "fren.trust.stat.refund": "Refund on expiry",
  "fren.trust.stat.claimTime": "Claim time",
  "fren.trust.stat.tasks": "Social tasks per link",
  // FAQ
  "fren.faq.title": "Frequently asked questions",
  "fren.faq.0.q": "What are FrenLinks?",
  "fren.faq.0.a": "FrenLinks let you send Polymarket prediction market shares to anyone via a simple link. Recipients can claim without having a wallet — we handle everything.",
  "fren.faq.1.q": "Do I need to pay any fees?",
  "fren.faq.1.a": "No. Creating and claiming FrenLinks is completely free. The only cost is the shares you choose to gift.",
  "fren.faq.2.q": "What happens if nobody claims my link?",
  "fren.faq.2.a": "Your shares are returned automatically when the link expires. You can also cancel a link at any time to get your shares back instantly.",
  "fren.faq.3.q": "Can I require social actions before claiming?",
  "fren.faq.3.a": "Yes. You can gate claims behind X follows, reposts, likes, Discord server joins, or Telegram group joins. Up to 8 tasks per link.",
  "fren.faq.4.q": "How does the lottery mode work?",
  "fren.faq.4.a": "In lottery mode, all claimers enter a raffle. When the link expires or is full, one random winner receives all the shares. Others get nothing — that's what makes it viral.",
  // Final CTA
  "fren.finalCta.title": "Ready to share the alpha?",
  "fren.finalCta.subtitle": "Create your first FrenLink in seconds. Gift prediction market shares to anyone, anywhere.",
  "fren.finalCta.button": "Get started free",
  "fren.finalCta.note": "No credit card required. No fees. Ever.",

  // Dashboard
  "fren.dash.title": "FrenLinks",
  "fren.dash.subtitle": "Send Polymarket shares to anyone with a link.",
  "fren.dash.everyClaim.bold": "Every claim = a new referral.",
  "fren.dash.everyClaim.rest": "Share on X, Telegram or Discord to grow your network.",
  "fren.dash.linkReady": "Link ready to share",
  "fren.dash.shareOnX": "Share on X",
  "fren.dash.yourLinks": "Your links",
  "fren.dash.shareText.single": "I just sent you free Polymarket shares on \"{market}\"\n\nClaim them before they expire\n{url}",
  "fren.dash.shareText.multi": "Free Polymarket shares on \"{market}\" for the first {n} people\n\nClaim yours\n{url}",
  "fren.dash.shareText.random": "Free Polymarket shares on \"{market}\" — open your surprise prize\n\n{n} spots available\n{url}",
  "fren.dash.shareText.lottery": "{shares} Polymarket shares on \"{market}\" up for grabs\n\n1 in {odds} chance to win it all — try your luck\n{url}",

  // UserLinksTable
  "fren.table.empty": "No links yet",
  "fren.table.market": "Market",
  "fren.table.cancelConfirm": "Cancel this link and recover shares?",
  "fren.table.expired": "Expired",
  "fren.table.status.pending": "Pending",
  "fren.table.status.active": "Active",
  "fren.table.status.completed": "Done",
  "fren.table.status.cancelled": "Cancelled",
  "fren.table.status.expired": "Expired",

  // CreatePolyLinkCard
  "fren.create.tab.gift": "Gift",
  "fren.create.tab.split": "Split",
  "fren.create.tab.lottery": "Lottery",
  "fren.create.header": "Send Polymarket shares",
  "fren.create.expiresIn": "Expires in",
  "fren.create.requireSocial": "Require social actions",
  "fren.create.addTask": "Add task",
  "fren.create.action.follow": "Follow",
  "fren.create.action.repost": "Repost",
  "fren.create.action.like": "Like",
  "fren.create.action.joinServer": "Join server",
  "fren.create.action.joinGroup": "Join group",
  "fren.create.tasksRequiredOne": "{n} task required to claim",
  "fren.create.tasksRequiredMany": "{n} tasks required to claim",
  "fren.create.placeholder.handle": "@username",
  "fren.create.placeholder.tweet": "https://x.com/.../status/...",
  "fren.create.placeholder.discord": "https://discord.gg/...",
  "fren.create.placeholder.telegram": "https://t.me/groupname",
  "fren.create.preview.members": "{n} members",
  "fren.create.position": "Position",
  "fren.create.noPositions": "No open positions",
  "fren.create.tradeFirst": "Trade first →",
  "fren.create.selectPosition": "Select a position",
  "fren.create.shares": "shares",
  "fren.create.amount": "Amount",
  "fren.create.of": "of {n}",
  "fren.create.max": "Max",
  "fren.create.randomNote": "Each recipient opens a surprise reveal animation before seeing their shares",
  "fren.create.recipients": "Recipients",
  "fren.create.eachGets": "Each gets",
  "fren.create.winnerGets": "Winner gets",
  "fren.create.winProb": "Win probability",
  "fren.create.oneIn": "1 in",
  "fren.create.wins": "wins",
  "fren.create.summaryWinner": "Winner gets",
  "fren.create.summaryEach": "Each recipient gets",
  "fren.create.summarySharesSuffix": "shares",
  "fren.create.chance": "{pct}% chance",
  "fren.create.creating": "Creating...",
  "fren.create.create": "Create link",
  "fren.create.error.notEnoughShares": "Not enough shares",
  "fren.create.error.minShares": "Minimum {n} shares",
  "fren.create.error.minRecipients": "Minimum 2 recipients",
  "fren.create.error.maxRecipients": "Maximum {n} recipients",
  "fren.create.error.shareTooSmall": "Each recipient gets {per} shares — minimum is {min}. Max {max} recipients for {total} shares.",
  "fren.create.error.minOdds": "Minimum odds: 1 in 2",
  "fren.create.error.oddsTooHigh": "Odds (1 in {odds}) can't exceed recipients ({recipients}). Set at least {odds} recipients.",
  "fren.create.error.failed": "Failed to create link",
  "fren.create.error.network": "Network error — please try again",

  // ── BubbleFlow page (/bubbleflow) ──────────────────────────────────
  "bub.count.whales": "Whale Holders",
  "bub.count.winners": "Winners",
  "bub.count.builders": "Top Builders",
  "bub.count.leaderboard": "Top Traders",
  "bub.count.markets": "Markets",
  "bub.count.search": "Search",
  "bub.controls.title": "Controls",
  "bub.controls.click": "Click bubble → Market details",
  "bub.controls.drag": "Drag bubble → Fling it",
  "bub.controls.hold": "Hold empty space → Gravity well",
  "bub.controls.tilt": "Tilt phone → Move bubbles",
  "bub.controls.scroll": "Scroll → Zoom in/out",
  "bub.sort.label": "Sort:",
  "bub.sort.today": "Today",
  "bub.sort.week": "Week",
  "bub.sort.month": "Month",
  "bub.sort.allTime": "All Time",
  "bub.sort.totalVol": "Total Vol",
  "bub.sort.vol24h": "24h Vol",
  "bub.sort.liquidity": "Liquidity",
  "bub.rank.byPnl": "By PnL",
  "bub.rank.byVolume": "By Volume",
  "bub.cat.all": "All Categories",
  "bub.cat.allShort": "All",
  "bub.toolbar.change": "Change",
  "bub.toolbar.allOutcomes": "All Outcomes",
  "bub.toolbar.resetZoom": "Reset zoom",
  "bub.toolbar.screenshot": "Screenshot",
  "bub.whales.title": "Whale Tracker",
  "bub.whales.subtitle": "Paste a Polymarket or FrenFlow URL to see its biggest holders",
  "bub.whales.placeholder": "Paste Polymarket or FrenFlow URL...",
  "bub.whales.loading": "Loading whales for this market...",
  "bub.holder": "Holder",
  "bub.rank": "Rank",
  "bub.tradingStyleTooltip": "Trading style classified by Predexon",
  "bub.label.shares": "Shares",
  "bub.label.profit": "Profit",
  "bub.label.finalValue": "Final Value",
  "bub.label.volume": "Volume",
  "bub.label.activeUsers": "Active Users",
  "bub.label.verified": "Verified",
  "bub.label.pnl": "PnL",
  "bub.label.posValue": "Pos. Value",
  "bub.label.biggestWin": "Biggest Win",
  "bub.label.markets": "Markets",
  "bub.label.winRate": "Win Rate",
  "bub.label.totalVolume": "Total Volume",
  "bub.label.liquidity": "Liquidity",
  "bub.label.age": "Age",
  "bub.label.resolves": "Resolves",
  "bub.label.yes": "Yes",
  "bub.label.no": "No",
  "bub.label.vol24h": "24h Volume",
  "bub.action.viewProfile": "View Profile",
  "bub.action.viewTrader": "View Trader",
  "bub.action.trade": "Trade",
  "bub.action.close": "Close",
  "bub.holder.boughtYes": "Bought Yes",
  "bub.holder.boughtNo": "Bought No",
  "bub.winner.rank": "#{n} Biggest Winner",
  "bub.screenshot.share": "Share",
  "bub.screenshot.download": "Download",
  "bub.screenshot.close": "Close",
  "bub.screenshot.shareText": "Check out these prediction markets on FrenFlow!",
  "bub.screenshot.copied": "Image copied to clipboard!",

  // ── Degenbox page (/degenbox) ──────────────────────────────────────
  // Dashboard
  "degen.dash.title": "Degenbox",
  "degen.dash.help": "How it works",
  "degen.dash.helpAria": "Show help",
  "degen.dash.addMarket": "Add Market",
  "degen.dash.empty.title": "Your Degenbox is empty",
  "degen.dash.empty.subtitle": "Add up to 8 markets to create your personalized trading dashboard. Track prices, view order books, and trade — all in one place.",
  "degen.dash.empty.cta": "Add Your First Market",
  // Welcome modal
  "degen.welcome.title": "Welcome to Degenbox",
  "degen.welcome.subtitle": "Track and trade up to eight markets in one screen — your personal trading hub.",
  "degen.welcome.howItWorks": "How it works",
  "degen.welcome.feature.add.title": "Add Market",
  "degen.welcome.feature.add.desc": "Hit the plus icon to search for any market to fill a tile. You can add up to 8 markets.",
  "degen.welcome.feature.click.title": "Click to Trade",
  "degen.welcome.feature.click.desc": "Click a tile's YES or NO buttons to open a trading interface. View order book or recent trades.",
  "degen.welcome.feature.drag.title": "Drag to Reorder",
  "degen.welcome.feature.drag.desc": "Drag the grip icon in a tile header to rearrange. Tiles stay inside the grid.",
  "degen.welcome.feature.toggle.title": "Toggle View",
  "degen.welcome.feature.toggle.desc": "Switch between Order Book, Recent Trades, and Chart in each tile.",
  "degen.welcome.feature.saved.title": "Saved Layout",
  "degen.welcome.feature.saved.desc": "Your markets and order of tiles are automatically saved and restored when you return.",
  "degen.welcome.proTip": "Pro Tip:",
  "degen.welcome.proTipText": "You can remove a market anytime by clicking the X in the tile header.",
  "degen.welcome.dontShow": "Don't show this again",
  "degen.welcome.start": "Start Trading",
  // Search modal
  "degen.search.title": "Add Market",
  "degen.search.tab.markets": "Individual Markets",
  "degen.search.tab.events": "Events ({n})",
  "degen.search.placeholder.markets": "Search markets...",
  "degen.search.placeholder.events": "Search events...",
  "degen.search.noResults": "No {kind} found",
  "degen.search.noAvailable": "No available {kind}",
  "degen.search.kind.markets": "markets",
  "degen.search.kind.events": "events",
  "degen.search.marketsCount": "{n} markets",
  "degen.search.vol": "vol",
  // Tile
  "degen.tile.dragReorder": "Drag to reorder",
  "degen.tile.pin": "Pin market to footer",
  "degen.tile.unpin": "Unpin market",
  "degen.tile.pinEvent": "Pin event to footer",
  "degen.tile.unpinEvent": "Unpin event",
  "degen.tile.viewMarket": "View market details",
  "degen.tile.openEvent": "Open event page",
  "degen.tile.removeMarket": "Remove market",
  "degen.tile.removeEvent": "Remove event",
  "degen.tile.tab.book": "Book",
  "degen.tile.tab.trades": "Trades",
  "degen.tile.tab.chart": "Chart",
  "degen.tile.vol24h": "24h Vol",
  "degen.tile.asks": "ASKS",
  "degen.tile.bids": "BIDS",
  "degen.tile.spread": "Spread",
  "degen.tile.recentTrades": "RECENT TRADES",
  "degen.tile.chartUnavailable": "Chart not available for this market",
  "degen.tile.noTradingData": "No trading data yet",
  "degen.tile.timeAgo.minutes": "{n}m ago",
  "degen.tile.moreMarkets": "+{n} more markets",
  // Hero (landing)
  "degen.hero.badge": "Polymarket Trading Dashboard",
  "degen.hero.titlePart1": "Your Personal",
  "degen.hero.titlePart2": "Prediction Market",
  "degen.hero.titlePart3": "Command Center",
  "degen.hero.subtitle": "Degenbox lets you customize which Polymarket events matter to you. Track real-time prices, orderbooks, and trade history for your favorite markets — visible across every page on FrenFlow.",
  "degen.hero.benefit.instant": "Instant access",
  "degen.hero.benefit.realtime": "Real-time data",
  "degen.hero.benefit.drag": "Drag & drop",
  "degen.hero.cta.create": "Create Your Degenbox",
  "degen.hero.cta.explore": "Explore Markets",
  "degen.hero.trust.free": "Free to use",
  "degen.hero.trust.noSignup": "No sign-up required to browse",
  "degen.hero.demoBadge": "Interactive Demo",
  "degen.demo.yourDegenbox": "Your Degenbox",
  "degen.demo.addAny": "Add any market",
  "degen.demo.pin": "Pin market",
  "degen.demo.unpin": "Unpin market",
  "degen.demo.searchPlaceholder": "Search markets...",
  "degen.demo.marketsCount": "{n} markets",
  "degen.demo.marketCount": "{n} market",
  "degen.demo.pinned": "Pinned! Now visible on all FrenFlow pages",
  // What is section
  "degen.what.title": "What is Degenbox?",
  "degen.what.subtitle": "Degenbox is a customizable trading dashboard for Polymarket prediction markets. Instead of constantly switching between different market pages, you configure the events that matter most to you and access them instantly from anywhere.",
  "degen.what.f1.title": "Focus on What Matters",
  "degen.what.f1.desc": "Select the prediction markets you care about most — politics, crypto, sports, or any trending topic. Your personalized watchlist, always at your fingertips.",
  "degen.what.f2.title": "Visible Everywhere",
  "degen.what.f2.desc": "Pin markets to your Degenbox and see them across all FrenFlow pages. Never miss a price movement on your favorite events.",
  "degen.what.f3.title": "Multiple Views",
  "degen.what.f3.desc": "Switch between orderbook, recent trades, or price chart for each market. Get the data you need, the way you want it.",
  "degen.what.f4.title": "Drag & Drop Layout",
  "degen.what.f4.desc": "Arrange your markets in any order. Put the most important predictions front and center with simple drag-and-drop.",
  "degen.what.f5.title": "Real-Time Updates",
  "degen.what.f5.desc": "Watch prices move in real-time. No refresh needed. Stay informed as prediction markets react to news and events.",
  "degen.what.f6.title": "Quick Trading",
  "degen.what.f6.desc": "Trade directly from your Degenbox. Buy or sell shares instantly without leaving your dashboard.",
  // How section
  "degen.how.title": "How to Use Degenbox",
  "degen.how.subtitle": "Get started in three simple steps",
  "degen.how.s1.title": "Search & Add Markets",
  "degen.how.s1.desc": "Browse trending markets or search for specific events. Click to add any Polymarket prediction to your Degenbox.",
  "degen.how.s2.title": "Customize Your View",
  "degen.how.s2.desc": "Choose orderbook, trades, or chart view for each market. Drag and drop to arrange markets in your preferred order.",
  "degen.how.s3.title": "Trade Instantly",
  "degen.how.s3.desc": "Monitor prices in real-time and execute trades directly from your dashboard. Your markets, your way.",
  // Use cases
  "degen.uc.title": "Perfect for Every Trader",
  "degen.uc.subtitle": "Whether you're tracking elections, crypto prices, or sports outcomes, Degenbox adapts to your prediction market strategy.",
  "degen.uc.political.title": "Political Prediction Traders",
  "degen.uc.political.desc": "Track all major election markets in one place. Monitor presidential races, congressional seats, and policy outcomes. React quickly when polls shift or news breaks.",
  "degen.uc.crypto.title": "Crypto Market Forecasters",
  "degen.uc.crypto.desc": "Follow Bitcoin price predictions, ETF approvals, and crypto regulatory markets. Stay ahead of the curve with real-time data on all your crypto prediction bets.",
  "degen.uc.sports.title": "Sports & Entertainment",
  "degen.uc.sports.desc": "Track game outcomes, championship predictions, and entertainment markets. Perfect for sports bettors exploring prediction markets.",
  "degen.uc.news.title": "News & Current Events",
  "degen.uc.news.desc": "Follow breaking news markets and trending topics. From tech announcements to weather events, stay informed on markets that move fast.",
  // Why Polymarket
  "degen.why.eyebrow": "About Prediction Markets",
  "degen.why.titlePart1": "Why Trade on",
  "degen.why.titlePart2": "Polymarket",
  "degen.why.subtitle": "The world's largest prediction market platform with billions in trading volume",
  "degen.why.f1.title": "Trade Real-World Events",
  "degen.why.f1.desc": "Buy and sell shares based on elections, crypto prices, sports outcomes, tech announcements, and breaking news. If you can predict it, you can trade it.",
  "degen.why.f2.title": "Profit From Your Knowledge",
  "degen.why.f2.desc": "Unlike betting, prediction markets let you trade in and out of positions. Spot mispriced markets, take a position, and exit when the price moves in your favor.",
  "degen.why.f3.title": "Wisdom of the Crowds",
  "degen.why.f3.desc": "Market prices aggregate information from thousands of traders, creating highly accurate probability forecasts that often outperform expert predictions.",
  "degen.why.stat.monthlyVolume": "Monthly Volume",
  "degen.why.stat.activeMarkets": "Active Markets",
  "degen.why.stat.globalTrading": "Global Trading",
  "degen.why.stat.lowFees": "Low Fees",
  "degen.why.popularCats": "Popular Market Categories",
  "degen.why.advantageTitle": "FrenFlow Makes Polymarket Better",
  "degen.why.advantageDesc": "Stop managing dozens of browser tabs. With Degenbox, you organize your most-watched Polymarket events in a single dashboard with real-time prices, instant trading, and social features. Copy top traders, track whale movements, and never miss a market opportunity.",
  "degen.why.tryFree": "Try Free",
  // Final CTA
  "degen.finalCta.title": "Ready to Build Your Degenbox?",
  "degen.finalCta.subtitle": "Join thousands of traders using FrenFlow to trade Polymarket smarter. Create your personalized prediction market dashboard today.",
  "degen.finalCta.start": "Get Started Free",
  "degen.finalCta.browse": "Browse Markets First",
  // FAQ
  "degen.faq.title": "Frequently Asked Questions",
  "degen.faq.0.q": "What is a prediction market?",
  "degen.faq.0.a": "A prediction market is a financial exchange where participants trade shares based on the outcome of future events. Prices reflect the market's collective estimate of an event's probability. For example, if a market trades at 70¢, it suggests a 70% probability of that outcome occurring.",
  "degen.faq.1.q": "Is Degenbox free to use?",
  "degen.faq.1.a": "Yes! Degenbox is completely free. You can add any Polymarket event to your dashboard, customize views, and monitor prices without any subscription fees. Trading on Polymarket may involve transaction fees.",
  "degen.faq.2.q": "How many markets can I add to my Degenbox?",
  "degen.faq.2.a": "You can add multiple markets to your Degenbox to create a focused trading dashboard. We recommend starting with your most-watched events and expanding from there.",
  "degen.faq.3.q": "Can I trade directly from Degenbox?",
  "degen.faq.3.a": "Yes! Once you're signed in and have enabled trading, you can buy and sell shares directly from any market tile in your Degenbox without navigating away from your dashboard.",
  "degen.faq.4.q": "What is FrenFlow?",
  "degen.faq.4.a": "FrenFlow is a social trading platform built for Polymarket. In addition to Degenbox, FrenFlow offers copy trading, social feeds, portfolio tracking, and community features for prediction market traders.",

  // ── Copy Trading dashboard (/copytrading) ──────────────────────────
  "ct.title": "Copy Trading",
  "ct.subtitle.empty": "Copy trading portfolio",
  "ct.subtitle.stats": "{n} traders · {trades} trades executed",
  "ct.refresh": "Refresh data",
  "ct.findTraders": "Find Traders",
  "ct.tab.overview": "Overview",
  "ct.tab.traders": "Traders",
  "ct.tab.history": "History",
  "ct.stat.netPnl": "Net PnL",
  "ct.stat.netPnlTooltip": "Net PnL = Trades PnL - Fees",
  "ct.stat.netPnlBreakdown": "Realized: ${r} | Unrealized: ${u} | Fees: ${f}",
  "ct.stat.volume": "Volume",
  "ct.stat.trades": "Trades",
  "ct.stat.fillRate": "{pct}% fill rate",
  "ct.stat.following": "Copying",
  "ct.stat.fees": "Fees",
  "ct.stat.roi": "ROI",
  "ct.stat.roiSub": "net of fees",
  "ct.stat.openPositions": "Open",
  "ct.stat.activePositions": "Active positions",
  "ct.stat.openPositionsSub": "active positions",
  "ct.stat.openPositionsSubOne": "active position",
  "ct.section.activeTraders": "Active Traders",
  "ct.section.recentTrades": "Recent Trades",
  "ct.viewAll": "View all",
  "ct.label.edit": "Set a custom name",
  "ct.label.placeholder": "Custom name\u2026",
  "ct.label.save": "Save name",
  "ct.label.cancel": "Cancel",
  "ct.label.add": "+ Label",
  "ct.label.remove": "Remove name",
  "ct.label.hint": "Private name, only you can see it. Leave empty to remove.",
  "ct.tradesAndVol": "{n} trades · ${vol} vol",
  "ct.action.pause": "Pause copying",
  "ct.action.resume": "Resume copying",
  "ct.action.settings": "Settings",
  "ct.action.editSettings": "Edit copy settings",
  "ct.action.viewTrader": "View trader",
  "ct.action.unfollow": "Unfollow",
  "ct.action.confirm": "Confirm",
  "ct.action.cancel": "Cancel",
  "ct.action.yes": "Yes",
  "ct.action.no": "No",
  "ct.repauseGuard.title": "Heads up — this will re-pause",
  "ct.repauseGuard.bodyPct": "At your current balance (${balance}) a {pct}% stake means each copy would land at ~${copy} — below Polymarket's $1 minimum. We'd pause this follow again after 5 skipped trades. Fund up to ${minBalance} or switch to Fixed mode to avoid the loop.",
  "ct.repauseGuard.bodyFixed": "Your balance (${balance}) is below the configured fixed stake (${stake}). The first leader BUY will fail with insufficient funds and we'd pause this follow again. Top up or lower the stake first.",
  "ct.repauseGuard.bodyGeneric": "Your current settings won't clear Polymarket's $1 minimum at this balance. We'd pause this follow again after a few skipped trades.",
  "ct.repauseGuard.adjust": "Adjust settings",
  "ct.repauseGuard.proceed": "Resume anyway",
  "ct.empty.activeTraders": "No active traders",
  "ct.empty.activeTraders.title": "No active traders",
  "ct.empty.activeTraders.desc": "Follow top traders and their trades will appear here in real time.",
  "ct.empty.trades": "No trades yet",
  "ct.empty.trades.title": "No trades yet",
  "ct.empty.trades.desc": "Your copied trades will show up here as soon as a leader you follow makes a move.",
  "ct.empty.history": "No trade history yet",
  "ct.empty.history.title": "No history yet",
  "ct.empty.history.desc": "Filled, skipped and failed copies will all be logged here.",
  "ct.history.loadMore": "Load more",
  "ct.pagination.prev": "Previous",
  "ct.pagination.next": "Next",
  "ct.pagination.pageOf": "Page {page} of {total}",
  "ct.history.loading": "Loading…",
  "ct.history.allLoaded": "All {n} trades loaded",
  // ── Copy banners (CopyPausedBanner + CopytradingSkipBanner) ──
  "copyBanner.skip.one": "Skipping some {leader} copies — {reason}",
  "copyBanner.skip.few": "Skipping copies from {count} leaders: {names} — tap to fix",
  "copyBanner.skip.many": "Skipping copies from {count} leaders — tap to fix",
  "copyBanner.paused.one": "Paused {leader} — {reason} · {time}",
  "copyBanner.paused.few": "{count} leaders paused: {names} — tap to review",
  "copyBanner.paused.many": "{count} leaders paused — tap to review",
  "copyBanner.warn.sizeTooSmall": "your copy size is under the $1 minimum",
  "copyBanner.warn.highSlippage": "your execution style is too strict",
  "copyBanner.warn.default": "a setting needs a quick tweak",
  "copyBanner.reason.insufficientBalance": "insufficient balance",
  "copyBanner.reason.lossThreshold": "loss threshold",
  "copyBanner.reason.profitTarget": "profit target",
  "copyBanner.reason.belowMin": "trade size below minimum",
  "copyBanner.reason.aboveMax": "trade size above max cap",
  "copyBanner.reason.maxPositions": "open-positions cap reached",
  "copyBanner.reason.system": "system pause",
  "copyBanner.headline.loss": "down {pct}% (your cap: {cap}%)",
  "copyBanner.headline.lossNoCap": "down {pct}%",
  "copyBanner.headline.balance": "low balance: ${bal} (needs ${need})",
  "copyBanner.headline.balanceNoNeed": "low balance: ${bal}",
  "copyBanner.headline.positions": "position limit reached ({open}/{cap})",
  "copyBanner.headline.attemptedMin": "trade too small: ${attempted} (min ${min})",
  "copyBanner.headline.attemptedCap": "trade too big: ${attempted} (cap ${cap})",
  "copyBanner.headline.attempted": "trade size ${attempted}",
  "copyBanner.time.justNow": "just now",
  "copyBanner.time.minutes": "{n}m ago",
  "copyBanner.time.hours": "{n}h ago",
  "copyBanner.time.days": "{n}d ago",
  "copyBanner.cta.fix": "Fix settings →",
  "copyBanner.cta.manage": "Manage →",
  "copyBanner.dismiss": "Dismiss",
  // "What to do" one-liners shown at the top of the settings modal when it
  // is opened from the banner CTA — tells the user which knob to turn.
  "copyBanner.fix.highSlippage": "Copies aren't filling — set your Execution Style to Normal or Aggressive so more of them go through.",
  "copyBanner.fix.sizeTooSmall": "Your copy size is under the $1 minimum — raise your per-trade stake.",
  "copyBanner.fix.insufficientBalance": "Not enough balance to copy — deposit USDC or lower your stake, then resume.",
  "copyBanner.fix.lossThreshold": "Auto-paused at your loss limit — raise the limit (or switch preset) to resume.",
  "copyBanner.fix.profitTarget": "Profit target hit — raise the target or resume manually.",
  "copyBanner.fix.belowMin": "Your copies keep landing below the minimum — raise your stake.",
  "copyBanner.fix.aboveMax": "The leader's trades are above your max — raise your \"Max per trade\".",
  "copyBanner.fix.maxPositions": "You hit your open-positions limit — close some or raise the cap.",
  "copyBanner.fix.default": "Tweak the settings below to get your copies flowing again.",
  "skipBanner.title": "Your copies aren't landing",
  "skipBanner.body": "Only {pct} of {total} copy attempts filled in the last 24h.",
  "skipBanner.topReason": "Top reason: {reason} ({count} skips).",
  "skipBanner.cta": "Open copy settings",
  "skipBanner.dismissNote": "Dismiss for 24h — will return if copies keep skipping",
  "skipBanner.dismissAria": "Dismiss for 24 hours",
  "skipBanner.reason.slippage": "execution style too strict",
  "skipBanner.reason.sizeBelowMin": "copy size below $1 minimum",
  "skipBanner.reason.belowMinTrade": "trade size below CLOB minimum",
  "skipBanner.reason.sizeAboveMax": "leader trade larger than your \"Max Trade $\"",
  "skipBanner.reason.insufficientBalance": "insufficient pUSD balance",
  "skipBanner.reason.oddsHigh": "price above your Max Odds cap",
  "skipBanner.reason.oddsLow": "price below your Min Odds cap",
  "skipBanner.reason.phantom": "phantom-fill (legacy rollback)",
  "skipBanner.reason.neverFilled": "order submitted but never filled (market too fast)",
  "skipBanner.reason.noMatch": "order reached the book but matched nothing",
  "skipBanner.reason.maxDailyVolume": "daily volume cap reached",
  "skipBanner.reason.maxOpenPositions": "max open positions reached",
  "skipBanner.reason.maxEntries": "max entries per market reached",
  "ct.empty.traders.title": "No traders yet",
  "ct.empty.traders.subtitle": "Start following traders to automatically copy their moves",
  "ct.exec.via": "via {name}",
  "ct.exec.viaLabel": "via",
  "ct.exec.colMarket": "Market",
  "ct.exec.colLeader": "Leader",
  "ct.exec.colYours": "BOUGHT",
  "ct.exec.colStatus": "Status",
  "ct.exec.colTime": "Time",
  "ct.exec.showSkipped": "Show skipped",
  "ct.exec.hideSkipped": "Hide skipped",
  "ct.exec.fee": "Fee: ${amt}",
  "ct.exec.leaderTradeLine": "Leader: ${amt}",
  "ct.exec.yoursTip": "Your copy size",
  "ct.exec.leaderTip": "Leader's trade size",
  "ct.exec.feeTip": "Platform fee for this copy",
  "ct.exec.unknownMarket": "Unknown Market",
  "ct.exec.status.filled": "Filled",
  "ct.exec.status.failed": "Failed",
  "ct.exec.status.skipped": "Skipped",
  "ct.exec.status.illiquid": "Illiquid",
  "ct.exec.status.pending": "Pending",
  // Recent Trades group collapse — runs of 3+ self-filter skips with the
  // same error_code collapse into one row keyed by the filter name.
  "ct.exec.group.summary": "{count} trades skipped by your {filter} filter",
  "ct.exec.group.systemSummary": "{count} technical events · auto-handled by the system",
  "ct.exec.group.show": "Show all",
  "ct.exec.group.hide": "Hide",
  "ct.exec.deposit": "Deposit",
  "ct.exec.group.filter.MARKET_ODDS_TOO_HIGH": "Max Odds",
  "ct.exec.group.filter.MARKET_ODDS_TOO_LOW": "Min Odds",
  "ct.exec.group.filter.MARKET_LIQUIDITY_LOW": "Min Liquidity",
  "ct.exec.group.filter.MARKET_VOLUME_LOW": "Min 24h Volume",
  "ct.exec.group.filter.MARKET_EXPIRY_TOO_FAR": "Max Days to Expiry",
  "ct.exec.group.filter.BLOCKED_MARKET": "Blocked Markets",
  "ct.exec.group.filter.BLOCKED_CATEGORY": "Blocked Categories",
  "ct.exec.group.filter.COPY_SIZE_BELOW_MIN": "Min Trade $",
  "ct.exec.group.filter.COPY_SIZE_ABOVE_MAX": "Max Trade $",
  "ct.exec.group.filter.MAX_OPEN_POSITIONS": "Max Open Positions",
  "ct.exec.group.filter.MAX_DAILY_VOLUME": "Daily Volume cap",
  "ct.exec.group.filter.DAILY_VOLUME_EXCEEDED": "Daily Volume cap",
  "ct.exec.group.filter.MAX_TOTAL_DEPLOYED": "Budget cap",
  "ct.exec.group.filter.MAX_PER_EVENT": "Per-Event cap",
  "ct.exec.group.filter.MAX_EVENTS_OPEN": "Max Events cap",
  "ct.exec.group.filter.MAX_ENTRIES_PER_MARKET": "Max entries per market",
  "ct.exec.group.filter.FIRST_ONLY_ENTRY_BLOCKED": "First-only entry mode",
  "ct.exec.group.filter.SELL_MODE_MANUAL": "Manual sell mode",
  "ct.leader.active": "Active",
  "ct.leader.paused": "Paused",
  "ct.slippageHint": "{count} copies skipped · slip {current}% → try {recommended}%",
  "ct.leader.col.trades": "Trades",
  "ct.leader.col.volume": "Volume",
  "ct.leader.col.pnl": "PnL",
  "ct.leader.col.mode": "Mode",
  "ct.leader.col.max": "Max",
  "ct.leader.tradesShort": "{n} trades",
  "ct.leader.volShort": "${v} vol",
  "ct.tableCol.market": "Market",
  "ct.tableCol.trader": "Trader",
  "ct.tableCol.side": "Side",
  "ct.tableCol.size": "Size",
  "ct.tableCol.price": "Price",
  "ct.tableCol.value": "Value",
  "ct.tableCol.fee": "Fee",
  "ct.tableCol.status": "Status",
  "ct.tableCol.time": "Time",
  "ct.time.justNow": "just now",
  "ct.time.minutesAgo": "{n}m ago",
  "ct.time.hoursAgo": "{n}h ago",
  "ct.time.daysAgo": "{n}d ago",
  "ct.trader": "Trader",


  // Share Stats Modal
  "share.stats.title": "Share your stats",
  "share.stats.period.today": "Today",
  "share.stats.period.week": "Week",
  "share.stats.period.month": "Month",
  "share.stats.period.all": "All",
  "share.stats.generating": "Generating stats card...",
  "share.stats.generationFailed": "Failed to generate card",
  "share.stats.fetchFailed": "Failed to fetch period stats",
  "share.stats.retry": "Retry",
  "share.stats.tagline": "Trade Smarter · Win Together",
  "share.stats.dragHint": "Drag to rotate • Double-click for 360°",
  "share.stats.copy": "Copy",
  "share.stats.copied": "Copied!",
  "share.stats.copyError": "Could not copy to clipboard:",
  "share.stats.pasteHintTwitter": "Image copied! Paste it (Ctrl+V / Cmd+V) in your tweet to include the stats card.",
  "share.stats.pasteHintTelegram": "Image copied! Paste it (Ctrl+V / Cmd+V) in Telegram to include the stats card.",
  "share.stats.shareTextHeader": "My trading stats on @frenflow_",
  "share.stats.shareTextPortfolio": "Portfolio:",
  "share.stats.shareTextPnl": "P&L:",
  "share.stats.shareTextVolume": "Volume:",
  "share.stats.shareTextTrades": "Trades:",
  "share.stats.shareTextTwitterCta": "Trade prediction markets",
  "share.stats.shareTextTelegramCta": "Trade prediction markets on FrenFlow",

  // ── Wallet / deposit ───────────────────────────────────────────────
  "wallet.depositAddress": "Deposit address",
  "wallet.depositTitle": "Deposit",
  "wallet.withdrawTitle": "Withdraw",
  "wallet.recipient": "Recipient",
  "wallet.network": "Network",
  "wallet.minDeposit": "Minimum deposit",
  "wallet.copyAddress": "Copy address",
  "wallet.scanQr": "Scan the QR or copy the address below",

  // ── Watchlist / social ─────────────────────────────────────────────
  "social.watchlist": "Watchlist",
  "social.followers": "Followers",
  "social.following": "Following",
  "social.feed": "Feed",
  "social.comments": "Comments",
  "social.likes": "Likes",
  "social.posts": "Posts",

  // ── Global search (header bar) ─────────────────────────────────────
  "search.placeholder": "Search markets, traders, events…",
  "search.viewAll": "View all results for",
  "search.resultsFor": "Results for",
  "search.title": "Search",
  "search.empty.title": "No matches",
  "search.empty.desc": "Try a different keyword or check your spelling.",
  "search.loadMore": "Load more",
  "search.fallback": "No matches in your selected source. Showing results from other venues.",
  "search.section.categories": "Categories",
  "search.section.markets": "Markets",
  "search.section.events": "Events",
  "search.section.traders": "Traders",
  "search.section.users": "Users",
  "search.section.posts": "Posts",
  "search.section.blog": "Blog",
  "search.section.recent": "Recent",
  "search.section.clear": "Clear",
  "search.empty": "No results",
  "search.noResultsFor": "No results for",

  // ── Notifications dropdown (bell icon) ─────────────────────────────
  "notifications.title": "Notifications",
  "notifications.empty": "No notifications yet",
  "notifications.markAllRead": "Mark all read",
  "notifications.settingsLink": "Notification settings",

  // ── Mobile drawer menu (hamburger panel on phones) ─────────────────
  "menu.title": "Menu",
  "menu.close": "Close menu",
  "menu.balance": "Balance",
  "menu.portfolio": "Portfolio",
  "menu.positionsToClaim": "Positions to claim",
  "menu.points": "pts",

  // ── Toasts (transient feedback) ────────────────────────────────────
  "toast.copied": "Copied to clipboard",
  "toast.linkCopied": "Link copied",
  "toast.addressCopied": "Address copied",
  "toast.success": "Success",
  "toast.error": "Something went wrong",
  "toast.tryAgain": "Try again",
  "toast.tradeExecuted": "Trade executed",
  "toast.orderCancelled": "Order cancelled",
  "toast.orderPlaced": "Order placed",
  "toast.depositReceived": "Deposit received",
  "toast.withdrawalSent": "Withdrawal sent",
  "toast.followed": "Now following",
  "toast.unfollowed": "Unfollowed",
  "toast.savedToWatchlist": "Saved to watchlist",
  "toast.removedFromWatchlist": "Removed from watchlist",
  "toast.networkError": "Network error — check your connection",
  "toast.signInRequired": "Please sign in to continue",
  "dextools.banner.title": "You came from DexTools",
  "dextools.banner.body": "Sign in to place your trade on FrenFlow.",
  "dextools.banner.howItWorks": "How it works",
  "dextools.banner.signup": "Sign up to trade",
  // Branded signup interstitial shown on the DexTools landing (DextoolsSignupModal).
  "dextools.signup.partner": "Official DexTools Partner",
  "dextools.signup.profit": "Turn {stake} into {payout} if you're right",
  "dextools.signup.nonCustodial": "Non-custodial — your keys never leave your wallet",
  "dextools.signup.fast": "Your first prediction in 1 minute — no experience needed",
  "dextools.signup.buy": "Sign up to buy",
  "dextools.signup.pickSide": "Sign up to pick your side",
  "dextools.signup.smartMoney": "Track what smart money is buying",
  "dextools.signup.copy": "Copy the top traders in 1 tap",
  "dextools.signup.noKyc": "No KYC — sign up with email in 30 seconds",
  "dextools.signup.volume": "{volume} traded — deep, liquid market",
  "dextools.signup.payout": "Get paid the instant the market resolves",
  "dextools.signup.explore": "Explore first",
  "dextools.signup.fallbackTitle": "Trade prediction markets",
  // Non-blocking co-branding bar above the event hero / trader profile on a
  // DexTools widget landing. The logo IS the wordmark, so the text doesn't
  // repeat "DexTools": renders as `via [DexTools logo] · Official Partner`.
  "dextools.landing.via": "via",
  "dextools.landing.officialPartner": "Official Partner",
  // Copytrading interstitial (utm_campaign=copytrading → trader profile).
  "dextools.copy.statPnl": "Profit",
  "dextools.copy.statWin": "Win rate",
  "dextools.copy.statCopiers": "Copying",
  "dextools.copy.cta": "Sign up to copy {name}",
  "dextools.copy.ctaGeneric": "Sign up to copy",
  "dextools.copy.autoCopy": "Auto-copy every trade — set it once",
  "dextools.copy.volume": "{volume} traded — proven track record",
  "dextools.copy.selfCustody": "Self-custodial — withdraw anytime",
  // Privy login/signup modal header + subheader (localized; read by
  // ProvidersWithPrivy via the frenflow_locale cookie). Signup-leaning to
  // match the dextools banner above. Privy: header <= 35, message <= 100.
  "privy.landingHeader": "Sign up to start trading",
  "privy.loginMessage": "Real-money prediction markets: crypto, politics, sports & more",
  "dextools.banner.dismiss": "Dismiss",
  "dextools.modal.eyebrow": "Welcome",
  "dextools.modal.headline": "How it works",
  "dextools.modal.step1Title": "Real prediction markets",
  "dextools.modal.step1Body": "Back YES or NO on real events — crypto, politics, sports. The price is the market's odds.",
  "dextools.modal.step2Title": "Trade in seconds",
  "dextools.modal.step2Body": "Pick a side, set your amount, you're in. No complicated setup.",
  "dextools.modal.step3Title": "Your account, your funds",
  "dextools.modal.step3Body": "Sign up with email or Google — we create a self-custodial wallet for you. We never hold your funds.",
  "dextools.modal.cta": "Sign up and trade",
  "dextools.modal.explore": "Skip",

  // ── Deposit modal ──────────────────────────────────────────────────
  "deposit.addFunds": "Add Funds",
  "deposit.addFundsSubtitle": "Choose how to add money",
  "deposit.header.polygonOnly": "Polygon only",
  "menu.viewProfile": "View Profile",

  // ── UMA page (/uma) ─────────────────────────────────────────────────
  "uma.title": "UMA Voting",
  "uma.subtitle": "Track UMA oracle disputes, DVM votes, and voter leaderboards for Polymarket prediction markets",
  "uma.fasterUpdates": "Faster updates",
  "uma.fasterUpdates.tooltip": "Signed-in traders get live vote updates every 45 seconds. Anonymous visitors receive updates every 5 minutes.",
  "uma.nav.disputes": "Disputes",
  "uma.nav.voters": "Voters",
  "uma.nav.proposers": "Proposers & Disputers",
  // Stats header
  "uma.stats.commitEnds": "Commit Ends",
  "uma.stats.revealEnds": "Reveal Ends",
  "uma.stats.committed": "Committed",
  "uma.stats.revealedVotes": "Revealed Votes",
  "uma.stats.voters": "Voters",
  "uma.stats.consensus": "Consensus",
  "uma.stats.participation": "Participation",
  "uma.stats.bondAtRisk": "Bond at Risk",
  "uma.stats.vol24h": "24h Volume",
  "uma.stats.totalVolume": "Total Volume",
  // Stats tooltips
  "uma.tip.commitEnds": "Time remaining for UMA voters to submit encrypted votes. Once this ends, the Reveal phase begins (~24h each). No new votes after this countdown.",
  "uma.tip.revealEnds": "Time remaining for voters to reveal their encrypted votes. Unrevealed votes are slashed — voters lose a portion of their staked UMA tokens.",
  "uma.tip.committed": "Total UMA tokens committed (encrypted) so far vs total staked. Higher participation = more secure outcome. Votes are hidden during this phase.",
  "uma.tip.revealed": "UMA tokens that have been revealed vs total staked. Must meet quorum (GAT) for the vote to be valid. Unrevealed commits get slashed.",
  "uma.tip.committers": "Unique wallet addresses that committed votes this round. More voters = higher decentralization and more trustworthy outcome.",
  "uma.tip.revealers": "Unique wallet addresses that revealed their votes. Committed voters who don't reveal get slashed.",
  "uma.tip.consensus": "Consensus requirement (SPAT): the leading outcome needs ~65% of voted tokens to pass. This prevents small groups from controlling outcomes.",
  "uma.tip.participation": "Percentage of total staked UMA tokens revealed. Must exceed Global Access Threshold (GAT) for the vote to be valid. Below GAT = vote rolls to next round.",
  "uma.tip.bondAtRisk": "Total USDC.e bond posted by proposers and disputers across all active disputes. Winner keeps their bond + receives half of the loser's bond.",
  "uma.tip.vol24h": "Combined Polymarket trading volume in the last 24 hours for all markets being resolved through UMA DVM voting. Higher volume = higher-stakes disputes.",
  "uma.tip.totalVolume": "Total all-time Polymarket trading volume for markets in UMA DVM voting. Shows the combined value at risk from these resolution disputes.",
  // Toggles + filters
  "uma.toggle.simple": "Simple",
  "uma.toggle.advanced": "Advanced",
  "uma.signInForAccess": "Sign in for full access",
  "uma.tab.active": "Active",
  "uma.tab.upcoming": "Upcoming",
  "uma.empty.disputes": "No disputes found.",
  "uma.empty.active": "No active disputes right now.",
  "uma.empty.upcoming": "No upcoming disputes right now.",
  // Dispute card / sidebar chrome
  "uma.card.proposed": "Proposed",
  "uma.card.commitStage": "Commit Stage",
  "uma.card.disputed": "Disputed",
  "uma.card.quorum": "Quorum",
  "uma.card.reveals": "Reveals",
  "uma.card.proposer": "Proposer",
  "uma.card.disputer": "Disputer",
  "uma.card.result": "Result",
  "uma.card.tip.quorumCommit": "Global Access Threshold — minimum UMA tokens that must vote for the result to be valid. Committed tokens count toward quorum even before reveal.",
  "uma.card.tip.quorumReveal": "Global Access Threshold — minimum UMA tokens that must vote for the result to be valid. If not met, the vote rolls to the next round.",
  "uma.card.tip.reveals": "How many committed votes have been revealed. Unrevealed votes get slashed.",
  "uma.card.tip.upcoming": "This dispute is waiting for the next DVM voting round to begin. Voting has not started yet.",
  "uma.sidebar.timestamp": "Timestamp",
  "uma.sidebar.unix": "UNIX",
  "uma.sidebar.totalAtStake": "Total at Stake",
  // Leaderboards (voter / proposer)
  "uma.lb.rank": "Rank",
  "uma.lb.address": "Address",
  "uma.lb.voter": "Voter",
  "uma.lb.proposer": "Proposer",
  "uma.lb.disputer": "Disputer",
  "uma.lb.votes": "Votes",
  "uma.lb.proposals": "Proposals",
  "uma.lb.disputes": "Disputes",
  "uma.lb.winRate": "Win Rate",
  "uma.lb.successRate": "Success Rate",
  "uma.lb.bondedVolume": "Bonded Volume",
  "uma.lb.earnings": "Earnings",
  "uma.lb.slashed": "Slashed",
  "uma.lb.netPnL": "Net P&L",
  "uma.lb.lastActive": "Last Active",
  "uma.common.noResults": "No results found",
  "uma.common.loading": "Loading...",
  "uma.common.search": "Search",
  "uma.common.viewProfile": "View Profile",
  "uma.common.copyAddress": "Copy address",
  "uma.common.copied": "Copied",
  // Phase indicator
  "uma.phase.commit": "Commit",
  "uma.phase.reveal": "Reveal",
  "uma.phase.upcoming": "Upcoming",
  "uma.phase.resolved": "Resolved",
  "uma.phase.expired": "Expired",
  // List view (table headers + meta)
  "uma.list.market": "Market",
  "uma.list.state": "State",
  "uma.list.proposed": "Proposed",
  "uma.list.volume": "Volume",
  "uma.list.bond": "Bond",
  "uma.list.voters": "Voters",
  "uma.list.time": "Time",
  "uma.list.by": "by",
  "uma.list.disputedBy": "disputed by",
  "uma.pagination.previous": "Previous",
  "uma.pagination.next": "Next",
  "uma.pagination.of": "of",
  // DisputeCard extras
  "uma.card.showMore": "Show more",
  "uma.card.showLess": "Show less",
  "uma.card.vote": "vote",
  "uma.card.votes": "votes",
  "uma.card.voter": "voter",
  "uma.card.voters": "voters",
  "uma.card.voteBreakdown": "Vote Breakdown",
  "uma.card.consensus": "Consensus",
  "uma.card.expiresIn": "Expires in",
  "uma.card.tip.consensus": "Stake Participation Agreement Threshold — the leading outcome must have at least this many UMA tokens. If not met, the vote rolls to the next round.",
  "uma.card.tip.commitProgress": "Voting in progress — Commit phase. Click for details.",
  "uma.card.tip.revealProgress": "Voting in progress — Reveal phase. Click for details.",
  "uma.card.voteRound": "Vote round {n} of 4. Previous round(s) failed to reach quorum or consensus. If all 4 rounds fail, the request is deleted and the original proposal stands.",
  "uma.card.commitPhase": "Commit",
  "uma.card.revealPhase": "Reveal",
  "uma.card.pendingVote": "Pending Vote",
  "uma.card.round": "Round {n}/4",
  "uma.card.tip.commitPhase": "Commit Phase: UMA voters submit encrypted votes. Votes are hidden to prevent copying. Lasts ~24 hours.",
  "uma.card.tip.revealPhase": "Reveal Phase: Voters decrypt and reveal their votes. Unrevealed votes get slashed (lose staked tokens). Lasts ~24 hours.",
  // Voter leaderboard
  "uma.voter.empty": "No voter data available.",
  "uma.voter.signInFull": "Sign in to view full leaderboard",
  "uma.voter.prev": "Prev",
  "uma.voter.next": "Next",
  "uma.voter.col.address": "Address",
  "uma.voter.col.staked": "Staked",
  "uma.voter.col.votes": "Votes",
  "uma.voter.col.accuracy": "Accuracy",
  "uma.voter.col.correct": "Correct",
  "uma.voter.col.wrong": "Wrong",
  "uma.voter.col.noVote": "No Vote",
  "uma.voter.col.slashPct": "Slash %",
  "uma.voter.col.slashed": "Slashed",
  "uma.voter.col.rewards": "Rewards",
  "uma.voter.col.netPnl": "Net P&L",
  "uma.voter.tip.staked": "UMA tokens staked in the voting system",
  "uma.voter.tip.votes": "Total revealed votes across all rounds",
  "uma.voter.tip.accuracy": "Correct votes / total votes",
  "uma.voter.tip.noVote": "Committed but did not reveal",
  "uma.voter.tip.slashPct": "Percentage of stake lost to slashing",
  "uma.voter.tip.slashed": "Total UMA lost from wrong/no votes",
  "uma.voter.tip.rewards": "UMA earned from correct voting",
  // Proposer leaderboard
  "uma.proposer.empty": "No proposer data available yet. The indexer may still be running its initial backfill.",
  "uma.proposer.col.address": "Address",
  "uma.proposer.col.proposals": "Proposals",
  "uma.proposer.col.propWl": "Prop W/L",
  "uma.proposer.col.propNet": "Prop Net",
  "uma.proposer.col.disputes": "Disputes",
  "uma.proposer.col.dispWl": "Disp W/L",
  "uma.proposer.col.dispNet": "Disp Net",
  "uma.proposer.col.totalNet": "Total Net",
  "uma.proposer.col.lastActive": "Last Active",
  "uma.proposer.tip.propWl": "Proposals won vs lost",
  "uma.proposer.tip.propNet": "Net USD from proposal bonds",
  "uma.proposer.tip.dispWl": "Disputes won vs lost",
  "uma.proposer.tip.dispNet": "Net USD from dispute bonds",
  // Voter sidebar (modal on click)
  "uma.voterSidebar.viewOnEtherscan": "View on Etherscan",
  "uma.voterSidebar.notFound": "Voter not found.",
  "uma.voterSidebar.accuracy": "Accuracy",
  "uma.voterSidebar.correctVotes": "correct votes",
  "uma.voterSidebar.totalVotes": "Total Votes",
  "uma.voterSidebar.voteBreakdown": "Vote Breakdown",
  "uma.voterSidebar.correct": "correct",
  "uma.voterSidebar.wrong": "wrong",
  "uma.voterSidebar.noVote": "no vote",
  "uma.voterSidebar.disputesInvolved": "Disputes Involved",
  // Proposer sidebar
  "uma.proposerSidebar.viewOnPolygonscan": "View on Polygonscan",
  "uma.proposerSidebar.active": "Active {when}",
  "uma.proposerSidebar.totalNet": "Total Net P&L",
  "uma.proposerSidebar.proposals": "Proposals",
  "uma.proposerSidebar.disputes": "Disputes",
  "uma.proposerSidebar.net": "net",
  "uma.proposerSidebar.won": "won",
  "uma.proposerSidebar.lost": "lost",
  "uma.proposerSidebar.proposalOutcomes": "Proposal Outcomes",
  "uma.proposerSidebar.disputeOutcomes": "Dispute Outcomes",
  "uma.proposerSidebar.noDisputes": "No disputes filed",
  // Dispute sidebar (modal)
  "uma.dsb.commitDetail": "Commit Detail",
  "uma.dsb.disputeDetail": "Dispute Detail",
  "uma.dsb.unknownDispute": "Unknown Dispute",
  "uma.dsb.tab.result": "Result",
  "uma.dsb.tab.details": "Details",
  "uma.dsb.tab.lifecycle": "Lifecycle",
  "uma.dsb.tab.summary": "AI Summary",
  "uma.dsb.tab.comments": "Discord Comments",
  "uma.dsb.readMore": "Read more",
  "uma.dsb.showLess": "Show less",
  // Lifecycle stages
  "uma.dsb.lc.proposal": "Proposal",
  "uma.dsb.lc.dispute": "Dispute",
  "uma.dsb.lc.dvmVote": "DVM Vote",
  "uma.dsb.lc.resolution": "Resolution",
  "uma.dsb.lc.settlement": "Settlement",
  "uma.dsb.lc.commitPhase": "Commit Phase — encrypted votes submitted",
  "uma.dsb.lc.revealPhase": "Reveal Phase — votes decrypted & counted",
  // Outcome explanations
  "uma.dsb.outcome.yes": "Yes",
  "uma.dsb.outcome.no": "No",
  "uma.dsb.outcome.tooEarly": "Too Early",
  "uma.dsb.outcome.unknown": "Unknown / 50-50",
  "uma.dsb.outcome.earlyRequest": "Early Request / Too Early",
  // Vote stats
  "uma.dsb.stat.commitAddrs": "Unique commit addresses",
  "uma.dsb.stat.revealAddrs": "Unique reveal addresses",
  "uma.dsb.stat.tokensCommitted": "Total tokens committed",
  "uma.dsb.stat.tokensRevealed": "Total tokens revealed",
  "uma.dsb.stat.voteRolls": "Vote rolls",
  "uma.dsb.stat.totalSlashed": "Total slashed",
  "uma.dsb.stat.totalCorrectVotes": "Total correct votes",
  "uma.dsb.tip.tokensCommitted": "Total UMA tokens staked during the commit phase.",
  "uma.dsb.tip.tokensRevealed": "Total UMA tokens revealed. This count determines if quorum (GAT) is met.",
  "uma.dsb.tip.totalSlashed": "Total UMA tokens removed from incorrect and absent voters. Redistributed to correct voters as rewards.",
  "uma.dsb.tip.totalCorrect": "UMA tokens that voted for the winning outcome. These voters receive slashing rewards.",
  "uma.dsb.tip.commitAddrs": "Number of wallets that submitted encrypted votes in the commit phase.",
  "uma.dsb.tip.revealAddrs": "Number of wallets that revealed their votes. Unrevealed votes are slashed.",
  "uma.dsb.tip.voteRolls": "Number of times this vote failed quorum or consensus and rolled to the next round. Max 4 rounds.",
  "uma.dsb.tip.participation": "Token-weighted voting metrics. Quorum (GAT) = minimum tokens that must vote for a valid result. Consensus (SPAT) = leading outcome needs ~65% of voted tokens. If either fails, the vote rolls to the next round (max 4).",
  "uma.dsb.tip.quorum": "Global Access Threshold (GAT) — minimum UMA tokens that must vote for the result to be valid.",
  "uma.dsb.tip.consensus": "Stake Participation Agreement Threshold (SPAT) — the leading outcome must reach this token threshold for the result to pass.",
  "uma.dsb.tip.slashing": "UMA slashes voters who vote incorrectly or fail to reveal. Slashed tokens are redistributed to correct voters as rewards.",
  "uma.dsb.tip.wrongVoteSlash": "Percentage of staked tokens lost per UMA for voting on the wrong outcome.",
  "uma.dsb.tip.noVoteSlash": "Percentage lost per UMA for committing but not revealing. Usually higher than wrong vote slash — always reveal your votes.",
  // Result tab
  "uma.dsb.signIn": "Sign in for full access",
  "uma.dsb.participation": "Participation",
  "uma.dsb.quorum": "Quorum ({pct}%)",
  "uma.dsb.consensus": "Consensus ({pct}%)",
  "uma.dsb.leading": "Leading: {outcome}",
  "uma.dsb.slashing": "Slashing Impact",
  "uma.dsb.stat.wrongVoteSlash": "Wrong vote slash/token",
  "uma.dsb.stat.noVoteSlash": "No vote slash/token",
  "uma.dsb.roll": "{count} roll",
  "uma.dsb.rolls": "{count} rolls",
  "uma.dsb.noVoteData.disputed": "Voting data will appear once voters start committing.",
  "uma.dsb.noVoteData": "No vote data available for this dispute.",
  "uma.dsb.notFound": "Dispute not found.",
  // Details tab
  "uma.dsb.viewOnOracle": "View on Oracle UI ↗",
  "uma.dsb.description": "Description",
  "uma.dsb.seeOnPolymarket": "See on Polymarket",
  "uma.dsb.additionalContext": "Additional Context",
  "uma.dsb.votingOptions": "Voting Options",
  "uma.dsb.timestamp": "Timestamp",
  "uma.dsb.links": "Links",
  "uma.dsb.seeOnFrenflow": "See on FrenFlow",
  "uma.dsb.oracleRequest": "Optimistic Oracle Request",
  // Lifecycle tab
  "uma.dsb.lc.title": "Resolution Timeline",
  "uma.dsb.lc.current": "Current",
  "uma.dsb.lc.proposalWindow": "2-hour window",
  "uma.dsb.lc.dvmTime": "~48 hours",
  "uma.dsb.lc.proposalDesc": "Answer proposed with a USDC.e bond. A 2-hour challenge window opens for anyone to dispute.",
  "uma.dsb.lc.disputeDesc": "Someone challenged the proposal by posting an equal bond. Escalated to UMA's DVM (Data Verification Mechanism).",
  "uma.dsb.lc.dvmDesc": "UMA token holders vote on the correct answer through a two-phase commit-reveal process.",
  "uma.dsb.lc.resolutionDesc": "Winning outcome determined based on vote results. Bonds distributed to the winner.",
  "uma.dsb.lc.settlementDesc": "Market settles on-chain. Winning tokens redeem for $1.00, losing tokens for $0.00.",
  // Bond block
  "uma.dsb.bond.title": "Bond & Economics",
  "uma.dsb.bond.proposer": "Proposer Bond",
  "uma.dsb.bond.disputer": "Disputer Bond",
  "uma.dsb.bond.totalAtStake": "Total at Stake",
  "uma.dsb.bond.winner": "Winner",
  "uma.dsb.bond.winnerDesc": "receives their bond back + half of the loser's bond as reward.",
  "uma.dsb.bond.loser": "Loser",
  "uma.dsb.bond.loserDesc": "forfeits their entire bond. Half goes to the winner, half to the UMA Store.",
  // Outcomes block
  "uma.dsb.outcomes.title": "What Outcomes Mean for Traders",
  "uma.dsb.outcomes.yesDesc": "“Yes” tokens redeem at $1.00. “No” tokens worth $0.00.",
  "uma.dsb.outcomes.noDesc": "“No” tokens redeem at $1.00. “Yes” tokens worth $0.00.",
  "uma.dsb.outcomes.tooEarlyDesc": "Question asked too soon. Market returns to active trading — no immediate settlement.",
  "uma.dsb.outcomes.unknownDesc": "Can't be determined. Both “Yes” and “No” tokens redeem at $0.50.",
  // DVM Voting block
  "uma.dsb.dvm.title": "How DVM Voting Works",
  "uma.dsb.dvm.row1Title": "Token-weighted voting",
  "uma.dsb.dvm.row1Desc": "UMA holders stake tokens to vote. More tokens staked = more voting power.",
  "uma.dsb.dvm.row2Title": "Two-phase commit-reveal",
  "uma.dsb.dvm.row2Desc": "Voters first submit encrypted votes (Commit), then decrypt them (Reveal). Prevents vote copying.",
  "uma.dsb.dvm.row3Title": "Quorum & Consensus",
  "uma.dsb.dvm.row3Desc": "Minimum tokens must vote (GAT) and leading outcome needs ≥65% agreement (SPAT).",
  "uma.dsb.dvm.row4Title": "Slashing",
  "uma.dsb.dvm.row4Desc": "Wrong or absent voters lose a portion of their staked UMA. These tokens go to correct voters as rewards.",
  "uma.dsb.dvm.row5Title": "Rolls",
  "uma.dsb.dvm.row5Desc": "If quorum or consensus isn't met, the vote rolls to the next round. Maximum 4 rounds before the request is deleted.",
  // Learn more
  "uma.dsb.learnMore": "Always read the resolution rules before trading on disputed markets. The outcome depends on the specific question wording and criteria.",
  "uma.dsb.learnMore.polymarket": "Polymarket Resolution Docs →",
  "uma.dsb.learnMore.uma": "UMA Protocol Docs →",
  "uma.dsb.learnMore.umip": "UMIP-107 →",
  // AI Summary tab
  "uma.dsb.aiWarning": "Warning: LLM-generated summary of Discord comments, without fact-checking. May not represent UMA voters or Risk Labs. Commenters may attempt to influence votes for profit.",
  "uma.dsb.ai.p1": "No (p1: 0)",
  "uma.dsb.ai.p2": "Yes (p2: 1)",
  "uma.dsb.ai.p3": "Early Request (p3: 0.5)",
  "uma.dsb.ai.p4": "Too Early / Custom (p4)",
  "uma.dsb.ai.uncategorized": "General Discussion",
  // Comments tab
  "uma.dsb.commentsWarning": "Warning: These comments are from the public UMA Protocol Discord server and may not reflect the views of the UMA team.",
  "uma.dsb.noComments": "No comments yet for this dispute.",
  "uma.dsb.joinDiscord": "Join discussion on Discord",
  // Time formatters
  "uma.dsb.time.justNow": "just now",
  "uma.dsb.time.mAgo": "{n}m ago",
  "uma.dsb.time.hAgo": "{n}h ago",
  "uma.dsb.time.dAgo": "{n}d ago",
  "deposit.transferCrypto": "Transfer Crypto",
  "deposit.frenflowBalance": "FrenFlow Balance:",
  "deposit.tab.buy": "Buy",
  "deposit.tab.polymarket": "Polymarket",
  "deposit.tab.bridge": "Bridge",
  "deposit.tab.buyDesc": "Card, wallet or exchange",
  "deposit.tab.tipDesc": "Send a tip",
  "deposit.tab.bridgeDesc": "From another network",
  "deposit.method.card.title": "Buy with card",
  "deposit.method.card.desc": "Visa, Mastercard, Apple Pay, Google Pay",
  "deposit.method.connectWallet.title": "Connect wallet",
  "deposit.method.connectWallet.desc": "MetaMask, Phantom, Coinbase, and more",
  "deposit.method.transferWallet.title": "Transfer from wallet",
  "deposit.method.transferWallet.desc": "Send from your connected wallet",
  "deposit.method.receive.title": "Receive on Polygon",
  "deposit.method.receive.desc": "Copy your address or QR — send USDC on Polygon",
  "deposit.note.directToWallet": "Funds go directly to your trading wallet on Polygon (pUSD)",
  "deposit.note.startTrading": "Start trading immediately after purchase",
  "deposit.selectChain": "Select a chain…",
  "deposit.tip.step1": "Open your FrenFlow wallet on Polymarket",
  "deposit.tip.step2": "Click \"Send Tip\" and enter the amount",
  "deposit.tip.step3": "pUSD arrives in your trading wallet",
  "deposit.tip.hint": "Hint",
  "deposit.tip.yourWallet": "Your wallet on Polymarket",
  "deposit.tip.imageAlt": "Your Polymarket profile with Send Tip",
  "deposit.tip.openOnPolymarket": "Open on Polymarket",
  "deposit.tip.walletLabel": "Wallet",
  "deposit.bridge.tokens": "Tokens",
  "deposit.bridge.chains": "Chains",
  "deposit.bridge.minPrefix": "Min",
  "deposit.bridge.depositAddress": "Your deposit address",
  "deposit.bridge.termsApply": "Terms apply",
  "deposit.bridge.copyAddress": "Copy address",
  "deposit.bridge.copied": "Copied",
  "deposit.bridge.arrivesIn": "Arrives in under 5 min · Auto-converts to pUSD on Polygon",
  "deposit.bridge.notYourWallet": "This is the bridge address for this transfer — not your FrenFlow wallet address.",
  "deposit.bridge.empty": "Select a chain and token to generate your deposit address",

  // ── Pin your prices modal (footer cog) ─────────────────────────────
  "tickerModal.title": "Pin your prices",
  "tickerModal.subtitle": "Pick which crypto prices show in the footer. Drag the handle to reorder.",
  "tickerModal.placeholder.add": "Add a ticker",
  "tickerModal.placeholder.full": "Remove one to add another",
  "tickerModal.add": "Add",
  "tickerModal.reset": "Reset",
  "tickerModal.done": "Done",
  "tickerModal.helpIntro": "Type any Binance ticker — e.g.",
  "tickerModal.helpOutro": "is a tradable spot pair on Binance before pinning it.",
  "tickerModal.helpCheck": ". We check that",
  "tickerModal.limitReached": "You've hit the {max}-ticker limit. Remove one with the trash icon above to free a slot.",
  "tickerModal.limit.prefix": "You've hit the {max}-ticker limit. Remove one with",
  "tickerModal.limit.suffix": "above to free a slot.",
  "tickerModal.error.keepOne": "Keep at least one coin in the strip.",
  "tickerModal.error.tooLong": "Symbol too long.",
  "tickerModal.error.alreadyAdded": "{symbol} is already on the strip.",
  "tickerModal.error.limit": "Limit {max} coins. Remove one first.",
  "tickerModal.error.noPair": "Binance has no {symbol}/USDT pair.",
  "tickerModal.error.noPrice": "Binance returned no price for {symbol}.",
  "tickerModal.error.validation": "Validation request failed. Try again.",
  "tickerModal.dragLabel": "Drag {symbol}",
  "tickerModal.removeLabel": "Remove {symbol}",
  "tickerModal.closeLabel": "Close",

  // ── Filters / sort bar ─────────────────────────────────────────────
  "filterBar.status": "Status",
  "filterBar.statusActive": "Active",
  "filterBar.statusClosed": "Closed",
  "filterBar.sort": "Sort",
  "filterBar.filters": "Filters",
  "filterBar.advancedFilters": "Advanced Filters",
  "filterBar.advancedFiltersDesc": "Refine your market search",
  "filterBar.searchCategories": "Search categories…",
  "filterBar.applyFilters": "Apply filters",
  "filterBar.clearAll": "Clear all",
  "filterBar.savedFilters": "Saved filters",
  "filterBar.reverseSort": "Reverse Sort",
  "filterBar.sort.volume24hr": "24hr Volume",
  "filterBar.sort.volume": "Total Volume",
  "filterBar.sort.liquidity": "Liquidity",
  "filterBar.sort.newest": "Newest",
  "filterBar.sort.endingSoon": "Ending Soon",
  "filterBar.sort.competitive": "Competitive",
  "filterBar.sort.trending": "Trending",
  "filterBar.sort.volatile": "Volatile",
  "filterBar.sort.new": "New",
  "filterBar.sort.closingSoon": "Closing Soon",
  "filterBar.sort.eventVolume": "Volume",
  "filterBar.sort.priceBalance": "50-50",
  "filterBar.sort.relevance": "Relevance",
  "filterBar.sort.hot": "Hot",
  "filterBar.sort.all": "All",
  "filters.categoriesCount": "Categories ({count})",
  "filters.mode.and": "And",
  "filters.mode.or": "Or",
  "filters.mode.include": "Include",
  "filters.mode.exclude": "Exclude",
  "filters.section.endDate": "End Date",
  "filters.section.priceRange": "Price Range (YES)",
  "filters.section.liquidity": "Liquidity",
  "filters.section.volume24h": "24h Volume",
  "filters.section.maxSpread": "Max Spread",
  "filters.section.totalVolume": "Total Volume",
  "filters.section.totalVolumeNote": "Use sort options above to order by total volume",
  "filters.section.rewardsOnly": "Rewards Only",
  "filters.section.rewardsOnlyDesc": "Show only markets with liquidity rewards",
  "filters.range.min": "min",
  "filters.range.max": "max",
  "filters.range.to": "to",
  "filters.range.minimum": "minimum",
  "filters.range.maximum": "maximum",
  "filters.range.daysShort": "days",
  "filters.range.zeroDays": "0 days",
  "filters.range.maxDays": "365 days",
  "filters.preset.lessOneDay": "< 1 day",
  "filters.preset.lessSevenDays": "< 7 days",
  "filters.preset.lessThirtyDays": "< 30 days",
  "filters.footer.reset": "Reset",
  "filters.footer.save": "Save",
  "filters.footer.cancel": "Cancel",
  "filters.footer.apply": "Apply Filters",
  "filters.footer.filterNamePlaceholder": "Filter name…",
  "viewMode.grid": "Grid view",
  "viewMode.list": "List view",
  "table.market": "Market",
  "table.price": "Price",
  "table.change1h": "1h %",
  "table.change24h": "24h %",
  "table.change7d": "7d %",
  "table.chart7d": "Chart (7d)",
  "table.smartMoney": "Smart $",
  "table.roi": "ROI",
  "table.volume24h": "24h Vol",
  "table.volume": "Volume",
  "table.liquidity": "Liquidity",
  "table.total": "Total",
  "table.hot": "Hot",
  "table.hotTooltip": "Hot debate — leading outcome is 45-55%",
  "table.live": "Live",
  "table.rewards": "Rewards",
  "table.rewardsTooltip": "${rate}/day liquidity rewards",
  "table.today": "Today",
  "table.markets": "{n} markets",
  "table.expand": "Expand",
  "table.collapse": "Collapse",
  "table.daysLeft": "{n}d left",
  "table.timeLeft": "{value} left",
  "elon.tweetCounter": "Tweet Counter",
  "elon.live": "LIVE",
  "elon.tweetCount": "Tweet Count",
  "elon.endsIn": "Ends in",
  "elon.view": "View",
  "elon.volume": "Volume",
  "elon.pickBracket": "Pick Your Bracket",
  "elon.leading": "🔥 Leading",
  "elon.passed": "✓ Passed",
  "elon.tweets": "tweets",
  "elon.empty": "Tweet counter markets will appear here when available",
  "elon.subcat.all": "All Markets",
  "elon.subcat.tweets": "Tweet Counter",
  "elon.subcat.tesla": "Tesla",
  "elon.subcat.spacex": "SpaceX",
  "elon.subcat.xai": "xAI / Grok",
  "elon.subcat.doge": "DOGE",
  "elon.tracker.retry": "Retry",
  "elon.tracker.newTweet": "NEW TWEET!",
  "elon.tracker.tweetCount": "Tweet count",
  "elon.tracker.timeLeft": "Time left",
  "elon.tracker.progress": "Progress",
  "elon.tracker.progressPace": "Progress: {progress}%   Pace: {pace} Tweets",
  "elon.tracker.speedTitle": "Speed to reach the target",
  "elon.tracker.pass": "Pass",
  "elon.tracker.current": "Current",
  "elon.tracker.na": "N/A",
  "elon.tracker.timePerTweet": "{mins} min {secs} sec / tweet",
  "elon.tracker.liveSpeed": "Live speed",
  "elon.tracker.timeSinceLast": "Time since the last activity",
  "elon.tracker.activityCount": "Activity count in the last period",
  "elon.tracker.activities": "{count} Activities",
  "elon.tracker.lastOne": "Last one, {tz}",
  "elon.tracker.postingSpeed": "Posting speed in the last period",
  "elon.tracker.perActivity": "{time} / Activity",
  "elon.tracker.screenTime": "Screen addiction time",
  "elon.tracker.firstOne": "First one, {tz}",
  "elon.tracker.heatmapTitle": "Tweets (split into hours)",
  "elon.tracker.repliesIncluded": "Replies included",
  "elon.tracker.focusDay": "Focus Day",
  "elon.tracker.legendET": "Eastern Time",
  "elon.tracker.legendLT": "Your local time based on your location",
  "elon.tracker.legendStart": "Market period starts from here",
  "elon.tracker.legendNow": "Current time",
  "elon.tracker.colDate": "Date",
  "elon.tracker.colTotal": "Total",
  "elon.tracker.refreshNote": "Data refresh interval: ~30 seconds",
  "elon.tracker.recentPosts": "Recent posts",
  "elon.tracker.colCount": "Tweet Count",
  "elon.tracker.colTimestamp": "Timestamp",
  "elon.tracker.colType": "Type",
  "elon.tracker.colId": "Basic ID / X Tracker ID",
  "elon.tracker.colContent": "Post Contents",
  "elon.tracker.typeReply": "Reply",
  "elon.tracker.typeTweet": "Tweet / Reposted / Quoted",
  "elon.tracker.none": "None",
  "elon.tracker.perPage": "{n} / page",
  "elon.tracker.lastUpdated": "Last updated: {time}",
  "elon.tracker.ended": "Ended",
  "elon.tracker.day": "Day",
  "elon.tracker.days": "Days",
  "common.loadMore": "Load more",
  "common.loading": "Loading…",
  "common.tryAgain": "Try Again",
  "common.fetchEventsError": "Failed to fetch events",
  "common.all": "All",

  // ── SEO / browser tab titles & descriptions ───────────────────────
  // Only `title` and `description` are translated. OpenGraph, Twitter
  // and keywords stay in EN — they're SEO-facing and we index the EN
  // canonical. The user-visible browser tab + meta description flip
  // to ES based on the `frenflow_locale` cookie.
  "seo.home.title": "FrenFlow — Copy Trade Polymarket, Kalshi & Hyperliquid",
  "seo.home.description": "Copy top Polymarket and Kalshi traders in under a second. Verified on-chain leaderboards, whale tracking, self-custody wallets. Powered by DexTools.",
  "seo.markets.title": "Prediction Markets - Browse & Trade Polymarket Events",
  "seo.markets.description": "Explore thousands of prediction markets on Polymarket. Filter by category, track trending events, and trade on politics, sports, crypto, and more with real-time prices and analytics.",
  "seo.elon.title": "Elon Musk Markets",
  "seo.elon.description": "Trade Elon Musk prediction markets on FrenFlow. Tweet counters, Tesla, SpaceX, xAI and more.",
  "seo.crypto.title": "Crypto Markets",
  "seo.crypto.description": "Trade crypto prediction markets on FrenFlow. Bitcoin, Ethereum, Solana and more.",
  "seo.elections.title": "Election Markets — Trade US & Global Elections",
  "seo.elections.description": "Trade world elections prediction markets on FrenFlow. US presidential elections, European politics, Latin America and more. Real-time odds and analytics.",
  "seo.forYou.title": "For You — Personalized Prediction Markets",
  "seo.forYou.description": "Discover prediction markets tailored to your interests. AI-powered recommendations on Polymarket for politics, sports, crypto and more.",
  "seo.trending.title": "Trending Prediction Markets — What's Hot Now",
  "seo.trending.description": "Discover the hottest prediction markets right now. See what's trending on Polymarket with real-time volume and price movements.",
  "seo.trending.descriptionN": "Discover {n} hot prediction markets right now. See what's trending on Polymarket with real-time volume, price movements and trading activity.",
  "seo.breakingPage.title": "Breaking News Prediction Markets — Live Events",
  "seo.breakingPage.description": "Trade on breaking news and live events. Real-time prediction markets for the latest developments on Polymarket.",
  "seo.breakingPage.descriptionN": "Trade on {n} breaking news prediction markets. Real-time odds for live events and latest developments on Polymarket.",
  "seo.new.title": "New Prediction Markets — Recently Added",
  "seo.new.description": "Discover the newest prediction markets on Polymarket. Be first to trade on fresh markets with early opportunities.",
  "seo.new.descriptionN": "Discover {n} recently added prediction markets on Polymarket. Be first to trade on fresh markets with early odds and opportunities.",
  "seo.endingSoon.title": "Ending Soon — Prediction Markets Resolving Today",
  "seo.endingSoon.description": "Trade on prediction markets ending soon. Bitcoin, crypto, sports, and more — resolving today on Polymarket.",
  "seo.endingSoon.descriptionN": "Trade on {n} prediction markets ending soon. Bitcoin, Ethereum, sports, and more — resolving today on Polymarket.",
  "seo.categoryFallback.title": "{label} Prediction Markets",
  "seo.categoryFallback.description": "Trade {label} prediction markets on FrenFlow. Real-time odds and deep liquidity powered by Polymarket.",
  // Per-category SEO (CATEGORY_SEO in categories-config.ts). Format
  // mirrors that file: title is the headline, description is appended
  // after a "Browse N active …" prefix when total > 0.
  "seo.cat.titleSuffix": " — Trade with Real Money",
  "seo.cat.dynamicPrefix": "Browse {n} active {label} prediction markets. ",
  "seo.cat.politics.title": "Politics Prediction Markets",
  "seo.cat.politics.description": "Trade on political prediction markets with real money. US elections, congressional legislation, executive orders, Supreme Court decisions and global policy. Real-time odds powered by Polymarket.",
  "seo.cat.sports.title": "Sports Prediction Markets",
  "seo.cat.sports.description": "Sports prediction markets — trade on NFL, NBA, Premier League, La Liga, Champions League, UFC, F1 and more. Real-time odds, deep liquidity. Trade outcomes with real money on FrenFlow.",
  "seo.cat.esports.title": "Esports Prediction Markets",
  "seo.cat.esports.description": "Esports prediction markets — trade on CS2, League of Legends, Dota 2, Valorant, Overwatch and more. Real-time odds, deep liquidity. Trade outcomes with real money on FrenFlow.",
  "seo.cat.crypto.title": "Crypto Prediction Markets",
  "seo.cat.crypto.description": "Crypto prediction markets — Bitcoin price targets, Ethereum milestones, altcoin events, DeFi outcomes and regulatory decisions. Live odds powered by Polymarket on FrenFlow.",
  "seo.cat.finance.title": "Finance Prediction Markets",
  "seo.cat.finance.description": "Financial prediction markets — Federal Reserve rate decisions, stock market milestones, IPO outcomes, earnings surprises and macroeconomic indicators. Trade with real money on FrenFlow.",
  "seo.cat.elections.title": "Elections Prediction Markets",
  "seo.cat.elections.description": "Election prediction markets — US presidential, congressional, gubernatorial races and global elections. Real-time polling odds, voter sentiment and outcome probabilities on FrenFlow.",
  "seo.cat.geopolitics.title": "Geopolitics Prediction Markets",
  "seo.cat.geopolitics.description": "Geopolitical prediction markets — international conflicts, trade agreements, sanctions, NATO decisions, UN resolutions and diplomatic negotiations. Real-time odds on FrenFlow.",
  "seo.cat.economy.title": "Economy Prediction Markets",
  "seo.cat.economy.description": "Economic prediction markets — GDP growth, inflation rates, unemployment data, consumer confidence, housing market trends and central bank policy. Trade real money on FrenFlow.",
  "seo.cat.tech.title": "Tech Prediction Markets",
  "seo.cat.tech.description": "Technology prediction markets — AI breakthroughs, product launches, startup acquisitions, app store rankings, chip wars and Big Tech earnings. Live odds on FrenFlow.",
  "seo.cat.culture.title": "Culture & Entertainment Prediction Markets",
  "seo.cat.culture.description": "Pop culture prediction markets — Oscars, Grammys, box office results, TV show renewals, celebrity events, viral moments and entertainment industry outcomes on FrenFlow.",
  "seo.cat.world.title": "World News Prediction Markets",
  "seo.cat.world.description": "World news prediction markets — global events, humanitarian crises, climate summits, international treaties, space exploration milestones and cross-border developments on FrenFlow.",
  "seo.cat.elon.title": "Elon Musk Prediction Markets",
  "seo.cat.elon.description": "Elon Musk prediction markets — Tesla stock targets, SpaceX launches, X platform changes, Neuralink milestones, DOGE department decisions and Musk tweet outcomes on FrenFlow.",
  "seo.cat.climate-science.title": "Weather & Science Prediction Markets",
  "seo.cat.climate-science.description": "Weather and science prediction markets — daily temperature forecasts, earthquakes, hurricanes, volcanoes, pandemics, space events and climate records on FrenFlow.",
  "seo.cat.weather-science.title": "Weather & Science Prediction Markets",
  "seo.cat.weather-science.description": "Weather and science prediction markets — daily temperature forecasts, earthquakes, hurricanes, volcanoes, pandemics, space events and climate records on FrenFlow.",
  "seo.cat.mentions.title": "Mentions Prediction Markets",
  "seo.cat.mentions.description": "Prediction markets about social media mentions, viral content and trending topics. Track what the internet is talking about and trade on outcomes with real money on FrenFlow.",
  "seo.cat.macro.title": "Macro Prediction Markets",
  "seo.cat.macro.description": "Macroeconomic prediction markets — GDP forecasts, trade balances, consumer spending, manufacturing indices and global economic outlook. Trade with real money on FrenFlow.",
  "seo.cat.fed-rates.title": "Fed Rates Prediction Markets",
  "seo.cat.fed-rates.description": "Federal Reserve interest rate prediction markets — FOMC meeting outcomes, rate cuts, rate hikes, dot plot projections and monetary policy decisions. Live odds on FrenFlow.",
  "seo.cat.trump.title": "Trump Prediction Markets",
  "seo.cat.trump.description": "Trump presidency prediction markets — executive orders, cabinet appointments, policy decisions, legal proceedings and political events. Real-time odds on FrenFlow.",
  "seo.cat.iran.title": "Iran Prediction Markets",
  "seo.cat.iran.description": "Iran prediction markets — nuclear negotiations, sanctions, geopolitical tensions, regime developments, oil production targets and diplomatic outcomes. Real-time odds on FrenFlow.",

  // Page-level SEO (layouts + standalone pages). Only title +
  // description are translated. OG/Twitter/keywords stay in EN.
  "seo.activity.title": "Activity — Social Layer for Prediction Markets",
  "seo.activity.description": "Trade calls, whale alerts, and market insights. The social engine driving prediction market decisions.",
  "seo.bubbleflow.title": "BubbleFlow - Live Prediction Markets Visualization | FrenFlow",
  "seo.bubbleflow.description": "Explore prediction markets as interactive bubbles. See trending markets, volumes, and prices in real-time. Drag, fling, and discover markets on Polymarket.",
  "seo.copytrading.title": "Polymarket Copy Trading Bot 2026 — Auto-Copy",
  "seo.copytrading.description": "Polymarket copy trading bot with Block 0 execution. Auto-copy top prediction-market traders in real time, track whale wallets, trade via Telegram. Non-custodial, 1% taker / 0.5% maker, no monthly fee.",
  "seo.degenbox.title": "Degenbox - Track 8 Polymarket Markets at Once",
  "seo.degenbox.description": "Your personal Polymarket trading dashboard. Track up to 8 markets simultaneously with real-time orderbooks, recent trades, and price charts. Drag & drop to customize your view.",
  "seo.elonTracker.title": "Elon Musk Tweet Tracker",
  "seo.elonTracker.description": "Real-time tracking of Elon Musk tweets. Live notifications, hourly heatmaps, and cumulative charts.",
  "seo.eventLayout.title": "Event",
  "seo.eventLayout.description": "View and trade on prediction markets for this event on Polymarket.",
  "seo.frenlinks.title": "FrenLinks — Send Shares to Anyone | FrenFlow",
  "seo.frenlinks.description": "Send prediction market shares to anyone with a simple link. Gift positions to frens instantly with FrenLinks.",
  "seo.geopoliticsLayout.title": "Geopolitical Intelligence - Real-Time Conflict & Tension Map",
  "seo.geopoliticsLayout.description": "Track global conflicts, diplomatic tensions, and bilateral flashpoints in real-time. Powered by GDELT data with interactive maps, pulsing hotspots, and tension arcs.",
  "seo.hyperliquid.title": "Hyperliquid Prediction Markets · FrenFlow",
  "seo.hyperliquid.description": "Trade live Hyperliquid HIP-4 prediction markets on FrenFlow — the first non-native interface for the BTC daily binary on HyperCore. Self-custody throughout.",
  "seo.portfolio.title": "Portfolio — Track Your Polymarket Positions",
  "seo.portfolio.description": "Manage your Polymarket positions and copy trading. Track P&L, view open positions, and monitor your prediction market portfolio in real-time.",
  "seo.post.title": "Post - FRENFLOW",
  "seo.post.description": "View post on FRENFLOW.",
  "seo.openInterest.title": "Prediction Markets Open Interest | FrenFlow",
  "seo.openInterest.description": "Real-time open interest and volume data across prediction markets. Track Kalshi and Polymarket combined metrics with interactive treemaps, charts, and raw data.",
  "seo.predictor.title": "AI Predictor — Market Analysis for Polymarket",
  "seo.predictor.description": "AI-powered market analysis and predictions for Polymarket events. Get data-driven insights on prediction market outcomes.",
  "seo.profileLayout.title": "Profile",
  "seo.profileLayout.description": "View user profile on FrenFlow.",
  "seo.rewards.title": "Rewards",
  "seo.rewards.description": "Earn Fren Points and open Mystery Chests. Provably fair rewards system with USDC prizes.",
  "seo.settings.title": "Account Settings",
  "seo.settings.description": "Manage your FrenFlow account settings. Configure trading preferences, notifications, and profile settings.",
  "seo.sportsLayout.title": "Sports Prediction Markets",
  "seo.sportsLayout.description": "Trade on live sports events. Real-time scores, odds, and trading tools for sports prediction markets.",
  "seo.swipe.title": "Swipe Markets — Discover Prediction Markets",
  "seo.swipe.description": "Swipe through prediction markets to discover new trading opportunities. Quick way to find markets that match your interests on Polymarket.",
  "seo.uma.title": "UMA Checker — Dispute Dashboard",
  "seo.uma.description": "Track UMA oracle disputes, DVM votes, and voter leaderboards for Polymarket prediction markets.",
  "seo.traders.title": "Top Traders Leaderboard",
  "seo.traders.description": "Discover the top Polymarket traders. Track P&L, win rates, volume and follow the best to copy their trades.",
  "seo.feedback.title": "Feedback",
  "seo.feedback.description": "Get help, report bugs, and suggest features for FrenFlow.",
  "seo.contact.title": "Contact — FrenFlow",
  "seo.contact.description": "Get in touch with the FrenFlow team. Support, partnerships, and general inquiries.",
  "seo.theFrens.title": "The Frens — Community",
  "seo.theFrens.description": "Meet the FrenFlow community. Top traders, influencers, and partners.",
  "seo.affiliate.title": "Affiliate Program — FrenFlow",
  "seo.affiliate.description": "Join the FrenFlow affiliate program. Earn commissions by referring new traders to the platform.",
  "seo.arbitrage.title": "Arbitrage — Find Mispriced Markets",
  "seo.arbitrage.description": "Discover arbitrage opportunities across Polymarket markets. Find mispriced outcomes and trade with confidence.",
  "seo.blogIndex.title": "Blog — FrenFlow",
  "seo.blogIndex.description": "FrenFlow blog: market analyses, product updates, trader interviews, and prediction market insights.",
  "sports.live": "LIVE",
  "sports.final": "FINAL",
  "sports.tbd": "TBD",
  "sports.volShort": "Vol.",
  "sports.draw": "Draw",
  "sports.noProps": "No props or futures available",
  "sports.fetchError": "Failed to fetch sports data",
  "sports.retry": "Retry",
  "sports.watchStream": "Watch Stream",
  "sports.gameXofY": "Game {current} / {total}",
  "sports.startingSoon": "Starting Soon ({n})",
  "crypto.up": "Up",
  "crypto.down": "Down",
  "crypto.resolving": "Resolving",
  "crypto.priceToBeat": "Price to Beat",
  "crypto.target": "Target",
  "crypto.currentPrice": "Current Price",
  "crypto.current": "Current",
  "crypto.finalPrice": "Final Price",
  "crypto.resolvesIn": "Resolves in",
  "crypto.resolves": "Resolves",
  "crypto.resolvedAt": "Resolved at",
  "crypto.loadingPriceData": "Loading price data...",
  "crypto.filtersHeader": "Crypto Filters",
  "crypto.commoditiesFiltersHeader": "Commodities Filters",
  "crypto.tf.all": "All",
  "crypto.tf.5m": "5 Min",
  "crypto.tf.15m": "15 Min",
  "crypto.tf.1h": "Hourly",
  "crypto.tf.4h": "4 Hour",
  "crypto.tf.daily": "Daily",
  "crypto.tf.weekly": "Weekly",
  "crypto.tf.monthly": "Monthly",
  "crypto.tf.yearly": "Yearly",
  "crypto.tf.pre-market": "Pre-Market",
  "crypto.tf.etf": "ETF",
  "crypto.mt.all": "All",
  "crypto.mt.above-below": "Above/Below",
  "crypto.mt.up-down": "Up/Down",
  "crypto.etf.all": "All",
  "crypto.etf.ibit": "IBIT",
  "crypto.etf.fbtc": "FBTC",
  "crypto.etf.gbtc": "GBTC",
  "crypto.etf.ethe": "ETHE",
  "crypto.etf.inflows": "Inflows",
  "crypto.etf.outflows": "Outflows",
  "crypto.marketOverview": "Market Overview",
  "crypto.toggleOverview": "Toggle Overview",
  "crypto.fearGreedIndex": "Fear & Greed Index",
  "crypto.now": "Now",
  "crypto.nowColon": "Now: ",
  "crypto.yesterday": "Yesterday",
  "crypto.lastWeek": "Last Week",
  "crypto.lastMonth": "Last Month",
  "crypto.weekShort": "Week",
  "crypto.monthShort": "Month",
  "crypto.historical": "Historical",
  "crypto.overTime": "Over Time",
  "crypto.exploreTop100": "Explore the Top 100 coins on",
  "crypto.fg.extremeFear": "Extreme Fear",
  "crypto.fg.fear": "Fear",
  "crypto.fg.neutral": "Neutral",
  "crypto.fg.greed": "Greed",
  "crypto.fg.extremeGreed": "Extreme Greed",
  "finance.upcomingEarnings": "Upcoming Earnings",
  "finance.sectorPerformance": "Sector Performance",
  "finance.commodities": "Commodities",
  "finance.eps": "EPS",
  "finance.rev": "Rev",
  "geopol.title": "Geopolitical Monitor",
  "geopol.subtitle": "Real-time conflict & diplomatic tension data from GDELT",
  "geopol.live": "Live",
  "geopol.eventsTracked": "{n} events tracked",
  "geopol.fullMap": "Full map",
  "geopol.conflictZones": "Conflict Zones",
  "geopol.topRegion": "Top Region",
  "geopol.conflictMentions": "Conflict Mentions",
  "geopol.diplomaticTensions": "Diplomatic Tensions",
  "geopol.last24h": "24h",
  "geopol.mostActive": "most active",
  "geopol.inNews": "in news",
  "geopol.hotspots": "Hotspots",
  "geopol.activeCount": "{n} active",
  "geopol.noHotspots": "No active hotspots",

  // ── Settings page ────────────────────────────────────────────────
  "settings.title": "Settings",
  "settings.subtitle": "Manage your account, trading and notifications.",
  "settings.nav.profile": "Profile",
  "settings.nav.trading": "Trading",
  "settings.nav.wallet": "Wallets",
  "settings.nav.notifications": "Notifications",
  "settings.nav.connections": "Connections",
  "settings.nav.invites": "Invites",
  "settings.profile.editCover": "Edit Cover",
  "settings.profile.changeAvatar": "Change Avatar",
  "settings.profile.pickFren": "Pick a Fren or upload your own photo",
  "settings.profile.upload": "Upload",
  "settings.profile.displayName": "Display Name",
  "settings.profile.displayNamePh": "Your name",
  "settings.profile.username": "Username",
  "settings.profile.usernamePh": "username",
  "settings.profile.bio": "Bio",
  "settings.profile.bioPh": "Tell us about yourself...",
  "settings.profile.save": "Save Profile",
  "settings.profile.saving": "Saving...",
  "settings.trading.tradingStatus": "Trading Status",
  "settings.trading.platformConnections": "Platform connections",
  "settings.trading.enable": "Enable",
  "settings.trading.enabling": "Enabling...",
  "settings.trading.active": "Active",
  "settings.trading.inactive": "Inactive",
  "settings.trading.revoke": "Revoke",
  "settings.trading.cancel": "Cancel",
  "settings.trading.deletePolyCreds": "Delete Polymarket credentials?",
  "settings.trading.revokePredict": "Revoke Predict.fun trading access?",
  "settings.trading.revokeKalshi": "Revoke Kalshi trading access?",
  "settings.trading.verifyKyc": "Verify KYC",
  "settings.trading.signing": "Signing...",
  "settings.trading.reenable": "Re-enable",
  "settings.trading.autoRedeem": "Auto-Redeem",
  "settings.trading.autoRedeemDesc": "Convert winnings to pUSD when markets resolve",
  "settings.trading.privateTrading": "Private Trading",
  "settings.trading.privateTradingDesc": "Hide your manual and copy trades from the public activity feed",
  "settings.trading.slippage": "Slippage Tolerance",
  "settings.trading.slippageDesc": "Max price deviation for market orders",
  "settings.trading.painFloor": "Slippage Forgiveness",
  "settings.trading.painFloorDesc": "Let copies pass when the absolute loss is below this $ amount, even if slippage exceeds your cap. Applies to all copytrading leaders.",
  "settings.trading.marketOrderType": "Market Order Type",
  "settings.trading.marketOrderTypeDesc": "How your market orders are executed",
  "settings.trading.fakTitle": "Fill and Kill (FAK)",
  "settings.trading.fakDesc": "Fills as much as possible at the best available prices and cancels any remaining unfilled portion",
  "settings.trading.fokTitle": "Fill or Kill (FOK)",
  "settings.trading.fokDesc": "Executes the entire order immediately at the specified price or cancels it completely",
  "settings.trading.quickBuy": "Quick Buy Amounts",
  "settings.trading.quickBuyDesc": "Custom quick-access buttons in the trade flow",
  "settings.trading.quickBuySave": "Save",
  "settings.trading.quickBuyHint": "Set up to 3 quick amounts ($1-$10,000). Leave empty to use defaults.",
  "settings.trading.updating": "Updating...",
  "settings.trading.onboardingIncomplete": "Onboarding incomplete",
  "settings.trading.revokeHyperliquid": "Disable Hyperliquid trading? Your funds stay on HyperCore — only the agent wallet permission is revoked.",
  "settings.wallet.withdraw": "Withdraw",
  "settings.wallet.withdrawArbitrum": "Withdraw to Arbitrum",
  "settings.wallet.enablePolymarket": "Enable Polymarket trading →",
  "settings.wallet.enableHyperliquid": "Enable Hyperliquid trading →",
  "settings.wallet.usdcHypercore": "USDC on HyperCore (Arbitrum master)",
  "settings.wallet.enableFirst": "Enable trading first",
  "settings.wallet.nothingToWithdraw": "Nothing to withdraw",
  "settings.toast.updateFailed": "Failed to update setting",
  "settings.toast.quickBuySaved": "Quick buy amounts saved",
  "settings.toast.slippageSet": "Slippage set to {value}%",
  "settings.toast.slippageSetSource": "{label} slippage set to {value}%",
  "settings.toast.slippageRange": "Enter a value between 0.1 and 100",
  "settings.toast.orderTypeSet": "Market orders now {type}",
  "settings.toast.orderTypeFok": "Fill-Or-Kill",
  "settings.toast.orderTypeFak": "Fill-And-Kill",
  "settings.toast.autoRedeemOn": "Auto-redeem enabled",
  "settings.toast.autoRedeemOff": "Auto-redeem disabled",
  "settings.toast.kycFailed": "Failed to start KYC verification",
  "settings.toast.kalshiReenabled": "Kalshi trading re-enabled",
  "settings.toast.reenableFailed": "Failed to re-enable",
  "settings.toast.revokeHlFailed": "Failed to revoke Hyperliquid",
  "settings.toast.resetDefault": "Reset to default",
  "settings.toast.resetFailed": "Failed to reset",
  "settings.toast.connected": "{label} connected",
  "settings.toast.connectFailed": "{label} connection failed: {error}",
  "settings.aria.toggleAutoRedeem": "Toggle auto-redeem",
  "settings.aria.togglePrivateTrading": "Toggle private trading",
  "settings.aria.toggleWebAlerts": "Toggle web alerts",
  "settings.aria.toggleLiveTicker": "Toggle live ticker",
  "settings.aria.toggleNewsFeed": "Toggle news feed",
  "settings.aria.togglePriceAlerts": "Toggle price alerts",
  "settings.aria.togglePush": "Toggle push notifications",
  "settings.aria.toggleCopytrading": "Toggle copytrading alerts",
  "settings.aria.toggleWalletTracker": "Toggle wallet tracker alerts",
  "settings.aria.toggleItem": "Toggle {label} notifications",
  "settings.notif.webAlerts": "Web Alerts",
  "settings.notif.webAlertsDesc": "Real-time browser alerts",
  "settings.notif.liveTicker": "Live Ticker",
  "settings.notif.liveTickerDesc": "Market prices at bottom",
  "settings.notif.newsFeed": "News Feed",
  "settings.notif.newsFeedDesc": "Crypto news popups",
  "settings.notif.priceAlerts": "Price Alerts",
  "settings.notif.priceAlertsDesc": "Market price notifications",
  "settings.notif.push": "Push Notifications",
  "settings.notif.pushSubscribed": "Receiving push alerts",
  "settings.notif.pushDenied": "Tap to retry — check browser/OS permissions",
  "settings.notif.pushNative": "Native browser alerts ({state})",
  "settings.notif.bravePushHint": "Brave blocks push by default. Enable it in brave://settings/privacy → \"Use Google services for push messaging\", restart Brave, then retry. Or use Telegram notifications — they work cross-platform without any browser setup.",
  "settings.notif.webApp": "Web App",
  "settings.notif.webAppDesc": "Choose which notifications appear in the header bell",
  "settings.notif.copytrading": "Copytrading",
  "settings.notif.copytradingDesc": "Trades, fills, resolved",
  "settings.notif.walletTracker": "Wallet Tracker",
  "settings.notif.walletTrackerDesc": "Tracked wallet activity",
  "settings.notif.telegram": "Telegram",
  "settings.notif.telegramDesc": "Choose which notifications to receive via Telegram",
  "settings.notif.copyTrade": "Copy Trade",
  "settings.notif.copyTradeDesc": "Trade copied from leader",
  "settings.notif.copySkips": "Copy Skip Alerts",
  "settings.notif.copySkipsDesc": "Why a copy was skipped (too small, slippage, auto-paused)",
  "settings.notif.limitFilled": "Limit Filled",
  "settings.notif.limitFilledDesc": "Order gets filled",
  "settings.notif.limitExpired": "Limit Expired",
  "settings.notif.limitExpiredDesc": "Order expires unfilled",
  "settings.notif.resolved": "Resolved",
  "settings.notif.resolvedDesc": "Market resolves",
  "settings.notif.lowBalance": "Low Balance",
  "settings.notif.lowBalanceDesc": "Balance too low",
  "settings.notif.slippage": "Slippage",
  "settings.notif.slippageDesc": "High slippage warning",
  "settings.wallet.pUsdPolygon": "pUSD on Polygon",
  "settings.wallet.usdcSolana": "USDC on Solana",
  "settings.wallet.eoaSweep.title": "${amount} in your login wallet",
  "settings.wallet.eoaSweep.body": "{tokens} are on your signing wallet, outside your trading wallet. Move them to start trading.",
  "settings.wallet.eoaSweep.button": "Send to trading wallet →",
  "settings.wallet.eoaSweep.sending": "Sending...",
  "settings.wallet.loadingSolana": "Loading Solana wallet...",
  "settings.wallet.exportKeys": "Export Private Keys",
  "settings.wallet.exportKeysDesc": "Your Privy embedded wallets. The EVM wallet owns your Polymarket Safe and is shared across EVM chains.",
  "settings.wallet.evmWallet": "EVM Wallet",
  "settings.wallet.solanaWallet": "Solana Wallet",
  "settings.wallet.export": "Export",
  "settings.wallet.warnPrivKey": "Anyone with access to your private key can control your funds. Never share it with anyone.",
  "settings.wallet.warnNoStore": "FrenFlow does not store or have access to your private keys. No one from FrenFlow will ever ask you for them.",
  "settings.connections.title": "Connected Accounts",
  "settings.connections.connect": "Connect",
  "settings.connections.notConnected": "Not connected",
  "settings.connections.googleBenefit": "Sign in faster with Google SSO",
  "settings.connections.twitterBenefit": "Share your trades and grow your following",
  "settings.connections.telegramBenefit": "Trade and get alerts from our Telegram bot",
  "settings.connections.emailBenefit": "Receive trade confirmations and account recovery",
  "settings.connections.recommended": "Recommended",
  "settings.connections.email": "Email",
  "settings.connections.externalWallet": "External Wallet",
  "settings.connections.externalWalletDesc": "Connect MetaMask, Coinbase, etc.",
  "settings.invites.title": "Your Invites",
  "settings.invites.subtitle": "Invite friends to FrenFlow",
  "settings.invites.unlimited": "∞ unlimited",
  "settings.invites.left": "{n} left",
  "settings.invites.generate": "Generate Invite Code",
  "settings.invites.usedBy": "Used by {who}",
  "settings.invites.someone": "someone",
  "settings.invites.notUsed": "Not used yet",
  "settings.invites.empty": "No invites generated yet. Click the button above to create one.",
  "settings.invites.failed": "Failed to load invites",
  "bridge.balance.available": "Available Balance",
  "bridge.balance.moveFunds": "Move Funds",
  "bridge.balance.converting": "Converting...",
  "bridge.balance.convertUsdce": "Convert {amount} USDC.e to trading balance",
  "bridge.balance.convertUsdc": "Convert {amount} USDC to trading balance",
  "bridge.balance.convertPusdBack": "Convert {amount} pUSD back to USDC.e",
  "bridge.balance.wrapFailed": "Wrap failed: {error}",
  "bridge.balance.unwrapFailed": "Unwrap failed: {error}",
  "bridge.balance.toastConverted": "${amount} {asset} converted to pUSD — ready to trade",
  "bridge.modal.title": "Move Funds",
  "bridge.modal.titleProgress": "Moving Funds...",
  "bridge.modal.titleComplete": "Transfer Complete!",
  "bridge.modal.from": "From",
  "bridge.modal.to": "To",
  "bridge.modal.amount": "Amount",
  "bridge.modal.max": "MAX",
  "bridge.modal.minAmount": "Minimum $5.00",
  "bridge.modal.insufficient": "Insufficient balance",
  "bridge.modal.gettingRoute": "Getting best route...",
  "bridge.modal.youReceive": "You'll receive",
  "bridge.modal.fee": "Fee",
  "bridge.modal.estimatedTime": "Estimated time",
  "bridge.modal.minutes": "~{n} min",
  "bridge.modal.selectDestination": "Select destination",
  "bridge.modal.selectPlaceholder": "Select",
  "bridge.modal.signing": "Signing & approving...",
  "bridge.modal.approved": "Approved — transferring funds...",
  "bridge.modal.bridging": "Bridging cross-chain — almost there...",
  "bridge.modal.canClose": "You can close this — we'll notify you when done.",
  "bridge.modal.addedTo": "added to {market}",
  "bridge.modal.done": "Done",
  "bridge.modal.transferFailed": "Transfer failed",
  "bridge.modal.somethingWrong": "Something went wrong. Your funds are safe.",
  "bridge.modal.tryAgain": "Try Again",
  "weather.tempHigh": "High Temp",
  "weather.tempLow": "Low Temp",
  "news.tab": "News",
  "news.cryptoTitle": "Crypto News",
  "news.live": "Live",
  "news.closeTitle": "Close news",
  "news.openTitle": "Open news",
  "sports.liveLabel": "Live",
  "sports.liveSportMarkets": "Live Sport Markets ({n})",
  "sports.liveEsportsMarkets": "Live Esports Markets ({n})",
  "sport.soccer": "Soccer",
  "sport.basketball": "Basketball",
  "sport.football": "American Football",
  "sport.baseball": "Baseball",
  "sport.hockey": "Hockey",
  "sport.tennis": "Tennis",
  "sport.golf": "Golf",
  "sport.boxing": "Boxing",
  "sport.mma": "MMA",
  "sport.ufc": "UFC",
  "sport.motorsports": "Motorsports",
  "sport.chess": "Chess",
  "sport.pickleball": "Pickleball",
  "sport.table-tennis": "Table Tennis",
  "sport.cricket": "Cricket",
  "sport.rugby": "Rugby",
  "sport.esports": "Esports",
  "sport.winter-olympics": "Winter Olympics",

  // ── Tooltips (event / market cards, badges) ────────────────────────
  "tooltip.addToWatchlist": "Add to watchlist",
  "tooltip.removeFromWatchlist": "Remove from watchlist",
  "tooltip.notInterested": "Not interested",
  "tooltip.boosted": "Boosted — earn extra rewards trading this market",
  "tooltip.liquidity": "Liquidity",
  "tooltip.liquidityHelp": "Available depth in the orderbook",
  "tooltip.openInterest": "Open Interest",
  "tooltip.openInterestHelp": "Total USDC locked in open positions",
  "tooltip.tradersCount": "{count} traders",
  "tooltip.smartMoneyEarly": "Fewer than 5 smart wallets have traded this market yet",
  "smartMoney.label": "Smart Money",
  "smartMoney.shortLabel": "Smart $",
  "smartMoney.outcome": "Outcome: {label}",
  "smartMoney.wallets": "Smart wallets: {count}",
  "smartMoney.netPositioning": "Net positioning: {pct}% {side}",
  "smartMoney.volume": "Smart volume: {volume}",
  "smartMoney.avgRoi": "Avg ROI: {roi}",
  "smartMoney.avgWinRate": "Avg win rate: {rate}",
  "smartMoney.summaryYesNo": "Smart money: {yes} YES · {no} NO · {volume} volume",
  "smartMoney.boxSubtitle": "What top-performing wallets are doing on this market",
  "smartMoney.boxSubtitleOutcome": "Top-performing wallets — strongest signal on “{label}”",
  "smartMoney.statWallets": "Smart wallets",
  "smartMoney.statPositioning": "Positioning",
  "smartMoney.statVolume": "Smart volume",
  "smartMoney.statRoi": "Avg ROI",
  "smartMoney.statWinRate": "Win rate",
  "smartMoney.topHolders": "Top smart wallets",
  "smartMoney.tipWhat": "What top-performing wallets (“smart money”) are doing on this market — positioning, size and historical accuracy.",
  "smartMoney.tipPositioning": "Share of smart-money wallets that are net buyers of each side on the leading outcome. The bar splits YES vs NO.",
  "smartMoney.tipWallets": "Number of top-performing (“smart”) wallets active on this outcome.",
  "smartMoney.tipVolume": "Total USD volume traded by these smart wallets on this outcome.",
  "smartMoney.tipRoi": "Average return on investment of these smart wallets across their resolved positions.",
  "smartMoney.tipWinRate": "Share of these smart wallets’ resolved positions that ended up winning.",
  "smartMoney.tipHolders": "The smart wallets with the largest positions on the leading outcome. Tap to view a wallet’s profile.",
  "tooltip.deadline.next": "Next: {label}",
  "tooltip.deadline.resolves": "Resolves {date}",
  "tooltip.deadline.daysLeftPct": "{days} left · {pct}% elapsed",
  "tooltip.deadline.more": "+{n} more",
  "eventCard.moreOutcomes": "+{n} more outcomes",
  "eventCard.moreMarkets": "+{n} more markets",
  "eventCard.viewAllMarkets": "View all {n} markets",

  // ── Category labels (gamma-api returns slugs only — we own the
  // display labels). Keys are the lowercased slug (`politics`,
  // `pop-culture`, `weather-science`); the resolver in `tCategory`
  // does the lookup so callers pass the slug straight from the data.
  "category.trending": "Trending",
  "category.new": "New",
  "category.breaking": "Breaking",
  "category.for-you": "For You",
  "category.ending-soon": "Ending Soon",
  "category.politics": "Politics",
  "category.sports": "Sports",
  "category.crypto": "Crypto",
  "category.crypto-prices": "Crypto Prices",
  "category.finance": "Finance",
  "category.fed-rates": "Fed Rates",
  "category.geopolitics": "Geopolitics",
  "category.world": "World",
  "category.tech": "Tech",
  "category.business": "Business",
  "category.climate": "Climate",
  "category.climate-science": "Weather & Science",
  "category.weather-science": "Weather & Science",
  "category.pop-culture": "Pop Culture",
  "category.movies": "Movies",
  "category.music": "Music",
  "category.health": "Health",
  "category.science": "Science",
  "category.gaming": "Gaming",
  "category.esports": "Esports",
  "category.elections": "Elections",
  "category.mentions": "Mentions",
  "category.elon": "Elon",
  "category.trump": "Trump",
  "category.biden": "Biden",
  "category.nfl": "NFL",
  "category.nba": "NBA",
  "category.mlb": "MLB",
  "category.nhl": "NHL",
  "category.soccer": "Soccer",
  "category.mma": "MMA",
  "category.ufc": "UFC",
  "category.olympics": "Olympics",
  "category.tennis": "Tennis",
  "category.boxing": "Boxing",
  "category.f1": "F1",
  "category.golf": "Golf",
  "category.cricket": "Cricket",
  "category.ai": "AI",
  "category.bookmarks": "Bookmarks",
  "category.culture": "Culture",
  "category.entertainment": "Culture",
  "category.economy": "Economy",
  "category.economics": "Economics",
  "category.commodities": "Commodities",
  "category.companies": "Companies",
  "category.financials": "Financials",
  "category.live": "Live",
  "category.all": "All",
  "category.hourly": "Hourly",
  "category.daily": "Daily",
  "category.weekly": "Weekly",
  "category.monthly": "Monthly",
  "category.elon-musk": "Elon Musk",
  "category.tech-science": "Tech & Science",
  "category.middle-east": "Middle East",
  "category.china": "China",
  "category.russia": "Russia",
  "category.ukraine": "Ukraine",
  "category.spain": "Spain",
  "category.recession": "Recession",
  "category.inflation": "Inflation",
  "category.stocks": "Stocks",
  "category.tesla": "Tesla",
  "category.apple": "Apple",
  "category.spacex": "SpaceX",
  "category.nasa": "NASA",
  "category.nobel": "Nobel",
  "category.oscars": "Oscars",
  "category.grammys": "Grammys",
  "category.la-liga": "La Liga",
  "category.premier-league": "Premier League",
  "category.champions-league": "Champions League",
  "category.world-cup": "World Cup",
  "category.euro": "Euro",
  "category.copa-america": "Copa America",
  "category.lol": "League of Legends",
  "category.cs": "CS",
  "category.dota": "Dota",
  "category.valorant": "Valorant",
  "category.hurricanes": "Hurricanes",
  "category.wildfires": "Wildfires",
  "category.earthquakes": "Earthquakes",
  "category.tv-shows": "TV Shows",

  // ── Subcategories — politics ─────────────────────────────────────
  "category.election-2024": "2024 Election",
  "category.election-2026": "2026 Election",
  "category.election-2028": "2028 Election",
  "category.elections-2024": "2024 Elections",
  "category.elections-2026": "2026 Elections",
  "category.swing-states": "Swing States",
  "category.polls": "Polls",
  "category.president": "President",
  "category.presidents": "Presidents",
  "category.congress": "Congress",
  "category.senate": "Senate",
  "category.house": "House",
  "category.supreme-court": "Supreme Court",
  "category.fed": "Fed",
  "category.harris": "Harris",
  "category.vance": "Vance",
  "category.musk": "Musk",
  "category.putin": "Putin",
  "category.modi": "Modi",
  "category.xi": "Xi Jinping",
  "category.zelensky": "Zelensky",
  "category.netanyahu": "Netanyahu",
  "category.israel": "Israel",
  "category.iran": "Iran",
  "category.gaza": "Gaza",
  "category.palestine": "Palestine",
  "category.taiwan": "Taiwan",
  "category.korea": "Korea",
  "category.north-korea": "North Korea",

  // ── Subcategories — crypto ───────────────────────────────────────
  "category.bitcoin": "Bitcoin",
  "category.ethereum": "Ethereum",
  "category.solana": "Solana",
  "category.dogecoin": "Dogecoin",
  "category.ripple": "Ripple",
  "category.btc": "BTC",
  "category.eth": "ETH",
  "category.sol": "SOL",
  "category.halving": "Halving",
  "category.etf": "ETF",
  "category.spot-etf": "Spot ETF",
  "category.bull-market": "Bull Market",
  "category.bear-market": "Bear Market",
  "category.altcoins": "Altcoins",
  "category.stablecoins": "Stablecoins",
  "category.memecoins": "Memecoins",
  "category.defi": "DeFi",
  "category.nfts": "NFTs",
  "category.btc-price": "BTC Price",
  "category.eth-price": "ETH Price",
  "category.sol-price": "SOL Price",

  // ── Subcategories — sports leagues ────────────────────────────────
  "category.super-bowl": "Super Bowl",
  "category.world-series": "World Series",
  "category.nba-finals": "NBA Finals",
  "category.nba-mvp": "NBA MVP",
  "category.nfl-mvp": "NFL MVP",
  "category.nfl-week": "NFL Week",
  "category.stanley-cup": "Stanley Cup",
  "category.serie-a": "Serie A",
  "category.bundesliga": "Bundesliga",
  "category.ligue-1": "Ligue 1",
  "category.mls": "MLS",
  "category.europa-league": "Europa League",
  "category.wimbledon": "Wimbledon",
  "category.us-open": "US Open",
  "category.french-open": "Roland Garros",
  "category.australian-open": "Australian Open",
  "category.atp": "ATP",
  "category.wta": "WTA",
  "category.grand-slam": "Grand Slam",
  "category.formula-1": "Formula 1",
  "category.moto-gp": "MotoGP",
  "category.nascar": "NASCAR",
  "category.cricket-world-cup": "Cricket World Cup",
  "category.ipl": "IPL",

  // ── Subcategories — econ / finance ────────────────────────────────
  "category.fed-meeting": "Fed Meeting",
  "category.cpi": "CPI",
  "category.gdp": "GDP",
  "category.jobs-report": "Jobs Report",
  "category.unemployment": "Unemployment",
  "category.interest-rates": "Interest Rates",
  "category.recession-2026": "2026 Recession",
  "category.bonds": "Bonds",
  "category.sp500": "S&P 500",
  "category.nasdaq": "Nasdaq",
  "category.dow": "Dow Jones",

  // ── Finance sidebar / subcat tabs ────────────────────────────────
  "category.earnings": "Earnings",
  "category.indicies": "Indices",
  "category.forex": "Forex",
  "category.collectibles": "Collectibles",
  "category.acquisitions": "Acquisitions",
  "category.earnings-calls": "Earnings Calls",
  "category.ipo": "IPOs",
  "category.prediction-markets": "Prediction Markets",
  "category.treasuries": "Treasuries",
  "category.up-or-down": "Up/Down",
  "category.updown": "Up/Down",
  "category.above": "Above/Below",
  "category.below": "Above/Below",
  "category.above-below": "Above/Below",
  "category.price-range": "Price Range",
  "category.hit-price": "Hit Price",
  "category.daily-close": "Daily Close",
  "category.sp-500": "S&P 500",
  "category.gold": "Gold",
  "category.silver": "Silver",
  "category.oil": "Oil",
  "category.uranium": "Uranium",
  "category.dow-jones": "Dow Jones",
  "category.russell": "Russell 2000",
  "category.nya": "NYA",
  "category.ftse": "FTSE 100",
  "category.hangseng": "Hang Seng",
  "category.kospi": "KOSPI",
  "category.tsla": "Tesla",
  "category.nvda": "NVIDIA",
  "category.app-store": "App Store",
  "category.ipos": "IPOs",
  "category.fda": "FDA",
  "category.microstrategy": "MicroStrategy",
  "category.big-tech": "Big Tech",
  "category.tiktok": "TikTok",
  "category.trade-war": "Trade War",
  "category.macro": "Macro",
  "category.macro-indicators": "Macro Indicators",
  "category.global-rates": "Global Rates",
  "category.taxes": "Taxes",
  "category.tsa": "TSA",
  "category.housing": "Housing",
  "category.temperature": "Temperature",
  "category.high-temperature": "High Temperature",
  "category.low-temperature": "Low Temperature",
  "category.precipitation": "Precipitation",
  "category.tornadoes": "Tornadoes",
  "category.volcanoes": "Volcanoes",
  "category.pandemics": "Pandemics",
  "category.global": "Global",

  // ── Subcategories — culture / entertainment ──────────────────────
  "category.box-office": "Box Office",
  "category.streaming": "Streaming",
  "category.netflix": "Netflix",
  "category.spotify": "Spotify",
  "category.taylor-swift": "Taylor Swift",
  "category.celebrity": "Celebrity",
  "category.awards": "Awards",
  "category.emmys": "Emmys",
  "category.golden-globes": "Golden Globes",

  // ── Subcategories — tech ─────────────────────────────────────────
  "category.openai": "OpenAI",
  "category.gpt": "GPT",
  "category.gpt-5": "GPT-5",
  "category.gpt-6": "GPT-6",
  "category.meta": "Meta",
  "category.google": "Google",
  "category.microsoft": "Microsoft",
  "category.nvidia": "Nvidia",
  "category.amazon": "Amazon",

  // ── Subcategories — weather / climate ────────────────────────────
  "category.weather": "Weather",
  "category.hurricane": "Hurricane",
  "category.wildfire": "Wildfire",
  "category.snowfall": "Snowfall",
  "category.heatwave": "Heatwave",
  "category.tornado": "Tornado",
  "category.flood": "Flood",
  "category.drought": "Drought",
  "category.rainfall": "Rainfall",
  "category.sea-level": "Sea Level",
  "category.global-warming": "Global Warming",
  "category.climate-change": "Climate Change",
  "category.air-quality": "Air Quality",
  "category.storm": "Storm",
  "category.atlantic-hurricane": "Atlantic Hurricane",
  "category.pacific-hurricane": "Pacific Hurricane",
  "category.snow": "Snow",
  "category.rain": "Rain",
  "category.weather-alerts": "Weather Alerts",
  "tooltip.totalVolume": "Total volume: {volume}",
  "tooltip.volume24h": "24h volume: {volume}",
  "tooltip.readAnalysis": "Read the analysis",
  "tooltip.boostedLabel": "Boosted",
  "tooltip.liquidityRewards": "Liquidity rewards",
  "tooltip.lpRewardsBlurb": "Earn rewards by placing limit orders near the midpoint.",
  "tooltip.lpRewards": "Rewards",
  "tooltip.lpMaxSpread": "Max Spread",
  "tooltip.lpMinShares": "Min Shares",
  "tooltip.editTickerCoins": "Edit ticker coins",
  "tooltip.selectLanguage": "Select language",
  "tooltip.theme": "Theme",
  "tooltip.darkMode": "Dark mode",
  "tooltip.lightMode": "Light mode",

  // ── Home / landing ─────────────────────────────────────────────────
  // Hero
  "home.hero.statusPill": "Open beta · Live",
  "home.hero.headingLine1": "The social +",
  "home.hero.headingLine2": "execution layer",
  "home.hero.headingLine3": "for prediction markets.",
  "home.hero.subhead": "FrenFlow is the copy-trading platform for Polymarket: mirror the best traders' entries and exits to your own non-custodial wallet in under a second. Same one-tap copy trading on Kalshi, Predict.fun and Hyperliquid.",
  "home.hero.cta.start": "Start trading",
  "home.hero.cta.explore": "Explore markets",
  "home.hero.tradeAcross": "Trade across",
  "home.hero.backedBy": "Powered by",
  // Counters
  "home.counters.volumeTraded": "Volume traded",
  "home.counters.activeTraders": "Active traders",
  // Stats row
  "home.stats.addressableLiquidity": "Addressable liquidity",
  "home.stats.addressableHint": "Across Polymarket · Kalshi · Hyperliquid",
  "home.stats.acrossVenues": "Across 2 venues",
  "home.stats.venuesValue": "1 UI",
  "home.stats.venuesHint": "Polymarket · Kalshi",
  "home.stats.avgExecution": "Avg execution",
  "home.stats.avgExecutionHint": "Copies land in under a second",
  "home.stats.totalVolume": "Total volume",
  "home.stats.totalVolumeHint": "Traded through FrenFlow",
  // Bottom strip
  "home.strip.liveTrades": "Live trades",
  // Showcase cards
  "home.card.topMover": "Top mover · 24h",
  "home.card.topMoverFallback": "Top Mover",
  "home.card.topTrader": "Top trader · 30d",
  "home.card.topTraderFallback": "Top Trader",
  "home.card.copiers": "{count} copiers",
  "home.card.verifiedTrader": "Verified trader",
  "home.card.pnl30d": "30d PnL",
  "home.card.live": "Live",
  "home.card.liveNow": "Live now",
  "home.card.whaleAlert": "Whale alert",
  "home.card.tier.megaWhale": "Mega whale",
  "home.card.tier.whale": "Whale",
  "home.card.tier.dolphin": "Dolphin",
  "home.card.historicalAccuracy": "{pct}% historical",
  "home.card.bought": "bought {outcome} on",
  "home.card.sold": "sold {outcome} on",
  "home.card.movesNow": "Moves now",
  "home.card.smartMoneyPrefix": "Smart money:",
  "home.card.wallets": "{count} wallets",
  "home.card.traderFallback": "Trader",
  // Copy-centric showcase cards
  "home.card.sameBlock": "Copy · sub-second",
  "home.card.sameBlock.leader": "Leader trades",
  "home.card.sameBlock.you": "You mirror it",
  "home.card.sameBlock.latency": "<1s",
  "home.card.sameBlock.sub": "to your non-custodial wallet",
  "home.card.mostCopied": "Most copied",
  "home.card.copyCta": "Copy this trader",
  "home.card.topToCopy": "Top traders to copy",
  "home.card.seeAll": "See leaderboard",
  "home.card.roi": "ROI",
  // MarketPulse
  "home.pulse.eyebrow": "Market Pulse",
  "home.pulse.title": "The markets moving right now.",
  "home.pulse.body": "The biggest trades, the fastest climbers, and the questions the smart money is quietly loading up on.",
  "home.pulse.cta": "Browse all markets",
  // WhosWinning
  "home.winning.eyebrow": "Who's Winning",
  "home.winning.title": "Copy the traders actually making money.",
  "home.winning.body": "Real wallets, real PnL, real-time. Click any trader to mirror their positions the moment they open a new one.",
  "home.winning.cta": "Full leaderboard",
  "home.winning.copiers": "{count} copiers",
  "home.winning.trades": "{count} trades",
  "home.winning.winPct": "{pct}% win",
  "home.winning.viewProfile": "View profile →",
  // ProductFeatures
  "home.features.eyebrow": "What we built",
  "home.features.title": "Copy trading, and everything around it.",
  "home.features.body": "Copy trading is the core. Social proof, self-custody execution and wallet tracking are everything that makes copying the best traders actually work.",
  "home.features.copy.eyebrow": "Copytrading",
  "home.features.copy.title": "Mirror top traders the second they open a position.",
  "home.features.copy.body": "Pick a leader, set your stake, and every move they make gets replicated onto your wallet in real time — with the same risk-sizing rules you'd set for yourself.",
  "home.features.copy.bullet1": "Fixed or % of balance stake modes",
  "home.features.copy.bullet2": "Per-trade caps, slippage limits, follow-sells",
  "home.features.copy.bullet3": "Sub-second copy execution on Polymarket CLOB",
  "home.features.copy.cta": "Start copytrading",
  "home.features.social.eyebrow": "Social",
  "home.features.social.title": "A feed built for prediction markets, not retail memes.",
  "home.features.social.body": "Verified trades turn into shareable insights. Follow traders, post opinions with a market attached, and see who's calling the biggest moves in the feed.",
  "home.features.social.bullet1": "Verified trade posts with live odds",
  "home.features.social.bullet2": "Whale alerts + market moves in the same timeline",
  "home.features.social.bullet3": "Reward authors whose posts drive volume",
  "home.features.social.cta": "See the feed",
  "home.features.exec.eyebrow": "Execution",
  "home.features.exec.title": "Self-custody routing across Polymarket, Kalshi and Hyperliquid.",
  "home.features.exec.body": "One UI, three venues, zero middlemen holding your funds. We route each trade to the right CLOB with the right wallet — your keys, our speed.",
  "home.features.exec.bullet1": "Privy-backed Safe wallets on Polygon (Polymarket)",
  "home.features.exec.bullet2": "Solana via DFlow (Kalshi) · HyperCore wallets (Hyperliquid)",
  "home.features.exec.bullet3": "CLOB builder fee — 1% taker / 0.5% maker (verified on DeFiLlama)",
  "home.features.exec.cta": "Trade now",
  "home.features.track.eyebrow": "Tracking",
  "home.features.track.title": "Never miss what smart money is doing.",
  "home.features.track.body": "Set price alerts, track any wallet, watch whale flows in real time, and get Telegram pings the moment your picks move. Built for the traders who take this seriously.",
  "home.features.track.bullet1": "Wallet tracker — any Polymarket wallet, live",
  "home.features.track.bullet2": "Smart money signals — who's consistently right",
  "home.features.track.bullet3": "Price alerts + PnL analytics in Telegram",
  "home.features.track.cta": "Open the tracker",
  // Visual mocks
  "home.visual.copy.topLeader": "Top leader · 187 copiers",
  "home.visual.copy.justOpened": "Just opened",
  "home.visual.copy.bought": "Bought",
  "home.visual.copy.boughtSuffix": "on \"Will Scream 7 be top-grossing 2026?\" at 0.7¢",
  "home.visual.copy.mirroring": "Mirroring now on 187 wallets",
  "home.visual.copy.executed": "Copy executed on 187/187 wallets",
  "home.visual.social.followers": "1.2M followers",
  "home.visual.social.verifiedTrade": "Verified trade",
  "home.visual.social.topLeader": "Top leader",
  "home.visual.social.elonBody": "Loading up YES on \"Fed cuts rates March\"",
  "home.visual.social.whaleBody": "Just bought $50K of NO on \"Trump 2028\"",
  "home.visual.social.threadBody": "Thread: 3 markets closing Friday worth watching",
  "home.visual.social.insight": "Insight",
  "home.visual.exec.tagline": "One wallet · Three venues · Zero custodian",
  "home.visual.exec.connected": "Connected",
  "home.visual.exec.endToEnd": "average end-to-end execution",
  "home.visual.track.whaleAlert": "Whale alert",
  "home.visual.track.whaleBody": "0xa1b2… opened $250K YES — 2m ago",
  "home.visual.track.smartMoney": "Smart money",
  "home.visual.track.smartBody": "7 top performers long 'Bitcoin $150K'",
  "home.visual.track.priceAlert": "Price alert",
  "home.visual.track.priceBody": "YES 'Fed cuts' just crossed 62¢",
  "home.visual.track.pnl": "PnL",
  "home.visual.track.pnlBody": "Your copied positions are up +$1,284 today",
  // Why FrenFlow
  "home.why.eyebrow": "Why FrenFlow",
  "home.why.title": "Built for traders who take this seriously.",
  "home.why.r1.title": "Fastest execution on Polymarket",
  "home.why.r1.body": "Sub-second copy routing. We sign on our side with KMS so your trades land the same block as the whale's.",
  "home.why.r1.stat": "~620ms avg",
  "home.why.r2.title": "Self-custody, verified on-chain",
  "home.why.r2.body": "Your funds, your keys. Our FeeCollector contract is open-source and listed on DeFiLlama — no promises, just code.",
  "home.why.r2.stat": "DefiLlama verified",
  "home.why.r3.title": "Multi-venue from day one",
  "home.why.r3.body": "Polymarket (Polygon) and Kalshi (Solana via DFlow). One UI, one Telegram bot, two liquidity pools.",
  "home.why.r3.stat": "2 venues",
  // FAQ
  "home.faq.eyebrow": "Frequently asked",
  "home.faq.title": "Everything you'd ask before signing up.",
  "home.faq.q1": "What is FrenFlow?",
  "home.faq.a1": "FrenFlow is a multi-venue prediction-market platform. It combines a consumer trading UI, a Telegram bot, and self-custody wallets with a native social feed so you can trade Polymarket, Kalshi, Predict.fun and Hyperliquid HIP-4 prediction markets, copy top traders, and share verified positions — all from one account. FrenFlow is powered by DexTools.",
  "home.faq.q2": "Is FrenFlow safe to use?",
  "home.faq.a2": "Yes. FrenFlow is non-custodial — we never hold user funds. Trading wallets are Privy-backed Safes on Polygon (Polymarket), embedded Solana wallets (Kalshi via DFlow), EOAs on BSC (Predict.fun), and Hyperliquid HyperCore accounts. The FeeCollector smart contract that charges our service fee is open-source, audited, and verified on DeFiLlama. All trades settle on-chain on the underlying exchange — FrenFlow only routes orders.",
  "home.faq.q3": "How does copy trading on Polymarket work?",
  "home.faq.a3": "Pick a leader from the FrenFlow leaderboard, choose a stake mode (fixed USD amount or % of your balance), and set per-trade caps. When the leader opens or closes a position, FrenFlow signs and executes the same trade on your self-custody wallet in under a second — with optional slippage limits, size caps, and follow-sells. Average copy latency is around 620ms.",
  "home.faq.q4": "Which prediction markets does FrenFlow support?",
  "home.faq.a4": "FrenFlow supports four venues today: Polymarket (Polygon, USDC), Kalshi via DFlow (Solana, USDC), Predict.fun (BSC, USDT), and Hyperliquid HIP-4 (HyperCore). All four are accessible from the same unified trading modal so you can route capital wherever liquidity is best without juggling separate accounts or wallets.",
  "home.faq.q5": "Is FrenFlow a Polymarket alternative?",
  "home.faq.a5": "FrenFlow is an aggregator layer on top of Polymarket and the other major prediction-market venues — not a competing exchange. You keep trading the same Polymarket order book, with the same liquidity and the same on-chain settlement, but you gain copy trading, whale tracking, smart-money signals, a Telegram bot, and unified access to Kalshi, Predict.fun and Hyperliquid HIP-4 from the same UI.",
  "home.faq.q6": "Does FrenFlow have a Telegram bot?",
  "home.faq.a6": "Yes. The FrenFlow Telegram bot supports full trading, copy trading, wallet tracking, and price alerts on every supported venue. It shares the same backend and self-custody wallets as the web app, so your positions and settings sync across both.",
  "home.faq.q7": "What fees does FrenFlow charge?",
  "home.faq.a7": "1% taker and 0.5% maker on Polymarket trades, collected as a Polymarket builder fee directly at the CLOB level — no service fees on top, no deposit, withdrawal, subscription, or membership fees. Same model for copy trades. Net revenue is published publicly on DeFiLlama.",
  "home.faq.q8": "Who is FrenFlow built for?",
  "home.faq.a8": "FrenFlow is built for active prediction-market traders who want sub-second execution, smart-money tracking, and copy-trading across multiple venues without leaving self-custody. If you trade Polymarket regularly, copy whale wallets, or hunt edge across Kalshi / Predict.fun / Hyperliquid HIP-4, FrenFlow is the unified terminal.",
  "home.faq.q9": "Is copy trading on Polymarket safe?",
  "home.faq.a9": "Copy trading on FrenFlow is non-custodial: your funds stay in a wallet only you control, and FrenFlow only executes the trades you authorize — it can never withdraw your money. The real risk is trading itself: a leader can lose, and past performance never guarantees future results. You stay in control with per-trade size caps, stake limits, and the ability to stop copying at any time. Vet a leader's full track record before you copy.",
  "home.faq.q10": "Can I lose money copy trading?",
  "home.faq.a10": "Yes. Copy trading mirrors real positions on real prediction markets, so you can lose your stake if the copied trades resolve against you. Copying a profitable trader doesn't import a guarantee — even the best wallets have losing streaks. Size your copy stake to what you can afford to lose and use FrenFlow's per-trade caps to bound your risk on every position.",
  // Closing CTA
  "home.closing.title.line1": "Start copy trading where",
  "home.closing.title.line2": "the smart money already is.",
  "home.closing.body": "Join traders copying top-performing Polymarket wallets — set your stake, mirror every move, and keep your funds in a wallet only you control.",
  "home.closing.cta.start": "Start copy trading",
  "home.closing.cta.markets": "Explore markets",
  "home.closing.cta.leaderboard": "Browse the leaderboard",
  "home.blog.eyebrow": "From the blog",
  "home.blog.title": "Learn to copy-trade prediction markets",
  "home.blog.cta": "View all posts",
  "home.blog.readMore": "Read",
  "home.closing.note": "Free to start · Non-custodial · Copy in one tap · No app to install",

  // ── Profile widget (sidebar) ───────────────────────────────────────
  "profile.userFallback": "User",
  "profile.levelInfoAria": "Level info",
  "profile.levelSystem": "Level System",
  "profile.levelInfo.lifetimeFp": "Lifetime FP",
  "profile.levelInfo.determinesLevel": "determines your level",
  "profile.levelInfo.earnFp": "Earn FP by trading ($1 = 0.1 FP) and posting",
  "profile.levelInfo.higherAuraPrefix": "Higher",
  "profile.levelInfo.higherAuraSuffix": "= better FP multiplier",
  "profile.levelInfo.toNextLevel": "{fp} FP to Level {level}",
  "profile.levelInfo.maxLevel": "Max level reached!",
  "profile.followers": "Followers",
  "profile.following": "Following",
  "profile.aura": "Aura",
  "profile.fp": "FP",
  "profile.tooltip.followers": "Followers: users who follow you to see your activity and shared trades.",
  "profile.tooltip.following": "Following: users you follow. See their activity in your social feed.",
  "profile.tooltip.aura": "Aura: your reputation in the community. Earn Aura with likes on posts and correct predictions. Higher Aura = better FP multiplier.",
  "profile.tooltip.fp": "Fren Points (FP): spendable points for Mystery Chests and rewards. Earn FP by trading ($1 = 0.1 FP) and posting content.",
  "profile.addFunds": "Add Funds",
  "profile.levelUp.title": "LEVEL UP!",
  "profile.levelUp.subtitle": "You have reached",
  "profile.levelUp.tapToClose": "Tap anywhere to close",

  // ── For You page ────────────────────────────────────────────────────
  "forYou.title": "For You",
  "forYou.subtitle": "Personalized based on your activity",
  "forYou.howItWorks": "How For You works",
  "forYou.trendingEvents": "Trending Events",
  "forYou.recommended": "Recommended For You",
  "forYou.bookmarks": "Your Bookmarks",
  "forYou.frensTrading": "Your Frens Are Trading",
  "forYou.viewAll": "View all →",
  "forYou.refreshing": "Refreshing recommendations...",
  "forYou.refreshed": "Recommendations updated",
  "forYou.frenBadgeCopy": "COPY",
  "forYou.frenBadgeTracked": "TRACKED",
  "forYou.frenBadgeFren": "FREN",
  "forYou.bought": "Bought {outcome}",
  "forYou.sold": "Sold {outcome}",
  "forYou.unknownMarket": "Unknown market",
  "forYou.unknownOutcome": "Unknown",
  "forYou.timeNow": "now",
  "forYou.timeMinutes": "{n}m",
  "forYou.timeHours": "{n}h",
  "forYou.timeDays": "{n}d",

  // ── Sort pills (markets carousels / lists) ──────────────────────────
  "sort.volume24hr": "24hr Volume",
  "sort.volume": "Total Volume",
  "sort.liquidity": "Liquidity",
  "sort.newest": "Newest",
  "sort.endingSoon": "Ending Soon",
  "sort.competitive": "Competitive",

  // ── Breaking page ───────────────────────────────────────────────────
  "breaking.live": "Live",
  "breaking.short": "Breaking",
  "breaking.title": "Breaking News",
  "breaking.subtitle": "Markets that moved the most in the last 24 hours",
  "breaking.empty": "No breaking markets found",
  "breaking.subcat.all": "All",
  "breaking.subcat.politics": "Politics",
  "breaking.subcat.world": "World",
  "breaking.subcat.sports": "Sports",
  "breaking.subcat.crypto": "Crypto",
  "breaking.subcat.finance": "Finance",
  "breaking.subcat.tech": "Tech",
  "breaking.subcat.culture": "Culture",

  // ── Telegram bot modal ──────────────────────────────────────────────
  "tgBot.title": "Trade from Telegram",
  "tgBot.titleLink": "Link Telegram",
  "tgBot.intro": "FrenFlow on the go without leaving Telegram. Same balance, same markets — chat-native so you can buy, copy and manage positions in seconds.",
  "tgBot.introLink": "Connect your Telegram once and the bot will recognise your FrenFlow account automatically — same balance, same markets, ready to trade in seconds.",
  "tgBot.feature.trade.title": "Trade any market",
  "tgBot.feature.trade.body": "Buy & sell on Polymarket — same routing, same prices.",
  "tgBot.feature.copy.title": "Copy traders",
  "tgBot.feature.copy.body": "Auto-mirror leaders and get a ping on every move.",
  "tgBot.feature.manage.title": "Manage positions",
  "tgBot.feature.manage.body": "Price alerts, redeem winnings, withdraw — in chat.",
  "tgBot.feature.wallet.title": "No new wallet",
  "tgBot.feature.wallet.body": "Same account as the web. Link once and you’re in.",
  "tgBot.cta.open": "Open in Telegram",
  "tgBot.cta.linkAndOpen": "Link Telegram & open",
  "tgBot.cta.linking": "Linking Telegram...",
  "tgBot.linkHint": "We’ll link your Telegram once so the bot recognises your account — no access code needed.",
  "tgBot.linkError": "Couldn’t link Telegram. Try again.",
  "tgBot.openError": "Couldn’t open the link flow. Try again.",
  "tgBot.close": "Close",

  // ── Public profile page (/profile/[username]) ───────────────────────
  "profile.tabs.wall": "Wall",
  "profile.tabs.achievements": "Achievements",
  "profile.tabs.pits": "Pits",
  "profile.pits.emptyTitle": "Not in any Pit yet",
  "profile.pits.emptyDesc": "Pits are public micro-communities for traders. Public pits this user joins will show up here.",
  "profile.pits.explore": "Explore Pits →",
  "profile.edit.invalidImage": "Please select an image file",
  "profile.edit.imageTooLarge": "Image must be less than 5MB",
  "profile.edit.coverTooLarge": "Cover must be less than 10MB",
  "profile.edit.notAuthed": "Not authenticated",
  "profile.edit.uploadImageFailed": "Failed to upload image",
  "profile.edit.setAvatarFailed": "Failed to set avatar",
  "profile.edit.uploadCoverFailed": "Failed to upload cover",
  "profile.edit.socialLinks": "Social links",
  "profile.edit.viewMore": "View more",
  "profile.edit.viewLess": "View less",
  "profile.edit.position": "Position",
  "profile.stats.followers": "Followers",
  "profile.stats.following": "Following",
  "profile.stats.copying": "Copying",
  "profile.stats.frenCopiers": "Fren Copiers",
  "profile.stats.points": "Points",
  "profile.stats.aura": "Aura",
  "profile.stats.totalVolume": "Total Volume",
  "profile.stats.achievements": "Achievements",
  "profile.joined": "Joined {date}",
  "profile.actions.follow": "Follow",
  "profile.actions.following": "Following",
  "profile.actions.copyTrader": "Copy Trader",
  "profile.actions.copy": "Copy",
  "profile.actions.copying": "Copying",
  "profile.actions.stopping": "Stopping...",
  "profile.actions.signInToFollow": "Please sign in to follow users",
  "profile.modal.followers": "Followers",
  "profile.modal.following": "Following",
  "profile.modal.noFollowers": "No followers yet",
  "profile.modal.noFollowing": "No following yet",
  "profile.edit.button": "Edit Profile",
  "profile.edit.title": "Edit Profile",
  "profile.edit.displayName": "Display Name",
  "profile.edit.displayNamePh": "Your display name",
  "profile.edit.username": "Username",
  "profile.edit.usernamePh": "username",
  "profile.edit.usernameHint": "3-30 characters, letters, numbers, and underscores only",
  "profile.edit.bio": "Bio",
  "profile.edit.bioPh": "Tell us about yourself...",
  "profile.edit.save": "Save Changes",
  "profile.edit.saving": "Saving...",
  "profile.edit.cancel": "Cancel",
  "profile.edit.failed": "Failed to update profile",

  // ── Achievements section ────────────────────────────────────────────
  "achievements.title": "Achievements",
  "achievements.unlockedOf": "{n} of {total} unlocked",
  "achievements.closest": "Closest to unlock",
  "achievements.almostThere": "Almost there",
  "achievements.nothingHere": "Nothing here yet",
  "achievements.unlocked": "✓ Unlocked",
  "achievements.complete": "{p}% complete",
  "achievements.tab.all": "All",
  "achievements.tab.unlocked": "Unlocked",
  "achievements.tab.progress": "In Progress",
  "achievements.tab.locked": "Locked",
  // Wall (UserTimeline)
  "wall.placeholder": "What's on your mind? Paste a market or event link to show a preview!",
  "wall.post": "Post",
  "wall.posting": "Posting...",
  "wall.loadingMore": "Loading more…",
  "wall.bought": "Bought",
  "wall.sold": "Sold",
  "wall.leader": "Leader",
  "wall.copy": "Copy",
  "wall.anonymous": "Anonymous",
  "wall.deletePost": "Delete post",
  "wall.sharePost": "Share post",
  "wall.removePreview": "Remove preview",
  "wall.marketPreview": "Market preview",
  "wall.eventMarkets": "Event · {n} markets",
  "wall.moreMarkets": "+{n} more",
  "wall.vol": "Vol: {v}",
  "wall.inPit": "in {name}",
  "wall.empty": "No posts yet.",
  "wall.emptyOwner": "No posts yet. Share your first thought!",
  "wall.yourAvatar": "Your avatar",
  "common.scrollLeft": "Scroll left",
  "common.scrollRight": "Scroll right",

  // Volume badge tooltip (header pills under avatar)
  "volumeBadge.moreToUnlock": "${amount} more to unlock",
  "volumeBadge.desc.og": "Genesis member",
  "volumeBadge.desc.trader": "$1K+ volume",
  "volumeBadge.desc.whale": "$10K+ volume",
  "volumeBadge.desc.shark": "$50K+ volume",
  "volumeBadge.desc.legend": "$100K+ volume",

  // Profile sidebar widgets
  "sidebar.trending.title": "Trending Markets",
  "sidebar.trending.volume": "24h Volume",
  "sidebar.trending.empty": "No trending markets available",
  "sidebar.trending.vol": "{v} vol",
  "sidebar.suggested.title": "Suggested Follows",
  "sidebar.suggested.empty": "No suggestions available",
  "sidebar.suggested.followers": "{n} followers",
  "sidebar.suggested.follower": "{n} follower",
  "sidebar.suggested.follow": "Follow",
  "sidebar.suggested.following": "Following",
  "sidebar.mostCopied.title": "Most Copied",
  "sidebar.mostCopied.window": "24h",
  "sidebar.mostCopied.empty": "No traders found",
  "sidebar.mostCopied.copy": "Copy",

  // Profile sidebar collapse
  "profile.sidebar.hide": "Hide sidebar",
  "profile.sidebar.show": "Show sidebar",

  "achievements.category.Special": "Special",
  "achievements.category.Mastery": "Mastery",
  "achievements.category.Onboarding": "Onboarding",
  "achievements.category.Markets": "Markets",
  "achievements.category.Volume": "Volume",
  "achievements.category.Trade Size": "Trade Size",
  "achievements.category.Milestones": "Milestones",
  "achievements.category.Social": "Social",
  "achievements.category.Copy Trading": "Copy Trading",
  "achievements.category.Watchlist": "Watchlist",
  "achievements.category.Influence": "Influence",
  "achievements.category.Pits": "Pits",
  "achievements.category.Referrals": "Referrals",

  // ── Achievement catalog (name / description / requirement) ──────────
  // 100 logros. Keys: ach.<id>.{name|desc|req}.
  "ach.og.name": "OG FREN",
  "ach.og.desc": "Genesis member - Early supporter of FrenFlow",
  "ach.og.req": "Own an OG Pass NFT",
  "ach.hat_trick.name": "Hat Trick",
  "ach.hat_trick.desc": "Linked Polymarket, Solana and Hyperliquid wallets",
  "ach.hat_trick.req": "All 3 wallets connected",
  "ach.welcome_aboard.name": "Welcome Aboard",
  "ach.welcome_aboard.desc": "Set an avatar and a bio",
  "ach.welcome_aboard.req": "Add avatar + bio",
  "ach.verified_telegram.name": "Linked",
  "ach.verified_telegram.desc": "Linked your Telegram account",
  "ach.verified_telegram.req": "Link Telegram",
  "ach.funded.name": "Funded",
  "ach.funded.desc": "Made your first trade",
  "ach.funded.req": "Trade once",
  "ach.profile_showoff.name": "Profile Showoff",
  "ach.profile_showoff.desc": "Set a cover image on your profile",
  "ach.profile_showoff.req": "Add a cover image",
  "ach.tastemaker_like.name": "Tastemaker",
  "ach.tastemaker_like.desc": "Liked your first post",
  "ach.tastemaker_like.req": "Like 1 post",
  "ach.conversation_starter.name": "Conversation Starter",
  "ach.conversation_starter.desc": "Wrote your first comment",
  "ach.conversation_starter.req": "Comment once",
  "ach.followed_someone.name": "Followed Someone",
  "ach.followed_someone.desc": "Followed your first user",
  "ach.followed_someone.req": "Follow 1 user",
  "ach.first_watch.name": "Window Shopper",
  "ach.first_watch.desc": "Added a market to your watchlist",
  "ach.first_watch.req": "Watch 1 market",
  "ach.first_deposit.name": "First Deposit",
  "ach.first_deposit.desc": "Made your first cross-chain bridge transfer",
  "ach.first_deposit.req": "Bridge funds 1 time",
  "ach.pit_joiner.name": "Pit Joiner",
  "ach.pit_joiner.desc": "Joined your first Pit",
  "ach.pit_joiner.req": "Join 1 Pit",
  "ach.shared_frenlink.name": "Shared FrenLink",
  "ach.shared_frenlink.desc": "Created your first FrenLink",
  "ach.shared_frenlink.req": "Create 1 FrenLink",
  "ach.beneficiary.name": "Beneficiary",
  "ach.beneficiary.desc": "Claimed someone else's FrenLink",
  "ach.beneficiary.req": "Claim 1 FrenLink",
  "ach.watcher_25.name": "Watcher",
  "ach.watcher_25.desc": "Tracking 25 markets",
  "ach.watcher_25.req": "Watch 25 markets",
  "ach.watcher_100.name": "Curator",
  "ach.watcher_100.desc": "Tracking 100 markets",
  "ach.watcher_100.req": "Watch 100 markets",
  "ach.trend_setter_1.name": "Trend Setter",
  "ach.trend_setter_1.desc": "Someone is copying your trades",
  "ach.trend_setter_1.req": "1 copier",
  "ach.trend_setter_5.name": "Copy Magnet",
  "ach.trend_setter_5.desc": "5 people copying your trades",
  "ach.trend_setter_5.req": "5 copiers",
  "ach.trend_setter_25.name": "Whale Alpha",
  "ach.trend_setter_25.desc": "25 people copying your trades",
  "ach.trend_setter_25.req": "25 copiers",
  "ach.trend_setter_100.name": "Copy Legend",
  "ach.trend_setter_100.desc": "100 people copying your trades",
  "ach.trend_setter_100.req": "100 copiers",
  "ach.followers_5.name": "Magnetic",
  "ach.followers_5.desc": "5 people follow your profile",
  "ach.followers_5.req": "5 followers",
  "ach.followers_25.name": "Crowd Favorite",
  "ach.followers_25.desc": "25 people follow your profile",
  "ach.followers_25.req": "25 followers",
  "ach.followers_100.name": "Star",
  "ach.followers_100.desc": "100 people follow your profile",
  "ach.followers_100.req": "100 followers",
  "ach.karma_100.name": "Karma Builder",
  "ach.karma_100.desc": "Earned 100 karma",
  "ach.karma_100.req": "100 karma",
  "ach.karma_500.name": "Karma Boss",
  "ach.karma_500.desc": "Earned 500 karma",
  "ach.karma_500.req": "500 karma",
  "ach.karma_1000.name": "Aura Lord",
  "ach.karma_1000.desc": "Earned 1,000 karma",
  "ach.karma_1000.req": "1,000 karma",
  "ach.karma_5000.name": "God Tier",
  "ach.karma_5000.desc": "Earned 5,000 karma",
  "ach.karma_5000.req": "5,000 karma",
  "ach.pit_master_10.name": "Pit Master",
  "ach.pit_master_10.desc": "Your Pit grew to 10 members",
  "ach.pit_master_10.req": "10 members in your Pit",
  "ach.pit_master_50.name": "Pit King",
  "ach.pit_master_50.desc": "Your Pit grew to 50 members",
  "ach.pit_master_50.req": "50 members in your Pit",
  "ach.pit_master_100.name": "Pit Emperor",
  "ach.pit_master_100.desc": "Your Pit grew to 100 members",
  "ach.pit_master_100.req": "100 members in your Pit",
  "ach.liked_10.name": "Liked",
  "ach.liked_10.desc": "Received 10 likes on your posts",
  "ach.liked_10.req": "10 likes received",
  "ach.liked_100.name": "Loved",
  "ach.liked_100.desc": "Received 100 likes on your posts",
  "ach.liked_100.req": "100 likes received",
  "ach.liked_500.name": "Adored",
  "ach.liked_500.desc": "Received 500 likes on your posts",
  "ach.liked_500.req": "500 likes received",
  "ach.hot_post.name": "Hot Post",
  "ach.hot_post.desc": "One of your posts hit 25 likes",
  "ach.hot_post.req": "25 likes on a single post",
  "ach.viral_post.name": "Viral",
  "ach.viral_post.desc": "One of your posts hit 100 likes",
  "ach.viral_post.req": "100 likes on a single post",
  "ach.discussed_10.name": "Discussed",
  "ach.discussed_10.desc": "10 comments received on your posts",
  "ach.discussed_10.req": "10 comments received",
  "ach.discussed_50.name": "Debated",
  "ach.discussed_50.desc": "50 comments received on your posts",
  "ach.discussed_50.req": "50 comments received",
  "ach.ach_hunter_15.name": "Achievement Hunter",
  "ach.ach_hunter_15.desc": "Unlocked 15 achievements",
  "ach.ach_hunter_15.req": "Unlock 15 achievements",
  "ach.ach_hunter_40.name": "Collector",
  "ach.ach_hunter_40.desc": "Unlocked 40 achievements",
  "ach.ach_hunter_40.req": "Unlock 40 achievements",
  "ach.ach_hunter_75.name": "Completionist",
  "ach.ach_hunter_75.desc": "Unlocked 75 achievements",
  "ach.ach_hunter_75.req": "Unlock 75 achievements",
  "ach.true_completionist.name": "True Completionist",
  "ach.true_completionist.desc": "Unlocked every achievement in the catalog",
  "ach.true_completionist.req": "Unlock 100% of achievements",
  "ach.market_polymarket.name": "Polymarket",
  "ach.market_polymarket.desc": "Made a trade on Polymarket",
  "ach.market_polymarket.req": "1 trade on Polymarket",
  "ach.market_kalshi.name": "Kalshi",
  "ach.market_kalshi.desc": "Made a trade on Kalshi",
  "ach.market_kalshi.req": "1 trade on Kalshi",
  "ach.market_predictfun.name": "Predict Fun",
  "ach.market_predictfun.desc": "Made a trade on Predict Fun",
  "ach.market_predictfun.req": "1 trade on Predict Fun",
  "ach.market_hyperliquid.name": "Hyperliquid",
  "ach.market_hyperliquid.desc": "Made a trade on Hyperliquid",
  "ach.market_hyperliquid.req": "1 trade on Hyperliquid",
  "ach.omni_trader.name": "Omni Trader",
  "ach.omni_trader.desc": "Traded on every supported platform",
  "ach.omni_trader.req": "1 trade on each platform",
  "ach.active_trader_500.name": "Active Trader",
  "ach.active_trader_500.desc": "Reached $500 in trading volume",
  "ach.active_trader_500.req": "$500+ total volume",
  "ach.trader_1k.name": "Trader",
  "ach.trader_1k.desc": "Reached $1,000 in trading volume",
  "ach.trader_1k.req": "$1,000+ total volume",
  "ach.whale_10k.name": "Whale",
  "ach.whale_10k.desc": "Reached $10,000 in trading volume",
  "ach.whale_10k.req": "$10,000+ total volume",
  "ach.shark_50k.name": "Shark",
  "ach.shark_50k.desc": "Reached $50,000 in trading volume",
  "ach.shark_50k.req": "$50,000+ total volume",
  "ach.legend_100k.name": "Legend",
  "ach.legend_100k.desc": "Reached $100,000 in trading volume",
  "ach.legend_100k.req": "$100,000+ total volume",
  "ach.mega_shark_250k.name": "Mega Shark",
  "ach.mega_shark_250k.desc": "Reached $250,000 in trading volume",
  "ach.mega_shark_250k.req": "$250,000+ total volume",
  "ach.mogul_1m.name": "Mogul",
  "ach.mogul_1m.desc": "Reached $1,000,000 in trading volume",
  "ach.mogul_1m.req": "$1M+ total volume",
  "ach.magnate_5m.name": "Magnate",
  "ach.magnate_5m.desc": "Reached $5,000,000 in trading volume",
  "ach.magnate_5m.req": "$5M+ total volume",
  "ach.size_50.name": "$50 Trade",
  "ach.size_50.desc": "Made a single trade of $50+",
  "ach.size_50.req": "Single trade ≥ $50",
  "ach.size_100.name": "$100 Trade",
  "ach.size_100.desc": "Made a single trade of $100+",
  "ach.size_100.req": "Single trade ≥ $100",
  "ach.size_200.name": "$200 Trade",
  "ach.size_200.desc": "Made a single trade of $200+",
  "ach.size_200.req": "Single trade ≥ $200",
  "ach.size_500.name": "$500 Trade",
  "ach.size_500.desc": "Made a single trade of $500+",
  "ach.size_500.req": "Single trade ≥ $500",
  "ach.size_1k.name": "$1K Trade",
  "ach.size_1k.desc": "Made a single trade of $1,000+",
  "ach.size_1k.req": "Single trade ≥ $1,000",
  "ach.size_5k.name": "$5K Trade",
  "ach.size_5k.desc": "Made a single trade of $5,000+",
  "ach.size_5k.req": "Single trade ≥ $5,000",
  "ach.size_10k.name": "$10K Trade",
  "ach.size_10k.desc": "Made a single trade of $10,000+",
  "ach.size_10k.req": "Single trade ≥ $10,000",
  "ach.size_25k.name": "$25K Trade",
  "ach.size_25k.desc": "Made a single trade of $25,000+",
  "ach.size_25k.req": "Single trade ≥ $25,000",
  "ach.first_trade.name": "First Steps",
  "ach.first_trade.desc": "Completed your first trade",
  "ach.first_trade.req": "Make 1 trade",
  "ach.10_trades.name": "Getting Started",
  "ach.10_trades.desc": "Completed 10 trades",
  "ach.10_trades.req": "Make 10 trades",
  "ach.50_trades.name": "Active Trader",
  "ach.50_trades.desc": "Completed 50 trades",
  "ach.50_trades.req": "Make 50 trades",
  "ach.100_trades.name": "Pro Trader",
  "ach.100_trades.desc": "Completed 100 trades",
  "ach.100_trades.req": "Make 100 trades",
  "ach.250_trades.name": "Veteran",
  "ach.250_trades.desc": "Completed 250 trades",
  "ach.250_trades.req": "Make 250 trades",
  "ach.500_trades.name": "Daily Grinder",
  "ach.500_trades.desc": "Completed 500 trades",
  "ach.500_trades.req": "Make 500 trades",
  "ach.1000_trades.name": "Trade Machine",
  "ach.1000_trades.desc": "Completed 1,000 trades",
  "ach.1000_trades.req": "Make 1,000 trades",
  "ach.first_post.name": "Voice Heard",
  "ach.first_post.desc": "Published your first post",
  "ach.first_post.req": "Publish 1 post",
  "ach.10_posts.name": "Contributor",
  "ach.10_posts.desc": "Published 10 posts",
  "ach.10_posts.req": "Publish 10 posts",
  "ach.50_posts.name": "Influencer",
  "ach.50_posts.desc": "Published 50 posts",
  "ach.50_posts.req": "Publish 50 posts",
  "ach.100_posts.name": "Thought Leader",
  "ach.100_posts.desc": "Published 100 posts",
  "ach.100_posts.req": "Publish 100 posts",
  "ach.comments_10.name": "Talker",
  "ach.comments_10.desc": "Wrote 10 comments",
  "ach.comments_10.req": "10 comments",
  "ach.comments_50.name": "Debater",
  "ach.comments_50.desc": "Wrote 50 comments",
  "ach.comments_50.req": "50 comments",
  "ach.comments_100.name": "Town Crier",
  "ach.comments_100.desc": "Wrote 100 comments",
  "ach.comments_100.req": "100 comments",
  "ach.likes_given_10.name": "Tastemaker Pro",
  "ach.likes_given_10.desc": "Liked 10 posts",
  "ach.likes_given_10.req": "10 likes given",
  "ach.likes_given_50.name": "Hype Man",
  "ach.likes_given_50.desc": "Liked 50 posts",
  "ach.likes_given_50.req": "50 likes given",
  "ach.follows_5.name": "Networker",
  "ach.follows_5.desc": "Following 5 users",
  "ach.follows_5.req": "Follow 5 users",
  "ach.follows_25.name": "Curator",
  "ach.follows_25.desc": "Following 25 users",
  "ach.follows_25.req": "Follow 25 users",
  "ach.pit_founder.name": "Pit Founder",
  "ach.pit_founder.desc": "Created your own Pit",
  "ach.pit_founder.req": "Create 1 Pit",
  "ach.pit_hopper.name": "Pit Hopper",
  "ach.pit_hopper.desc": "Member of 5 different Pits",
  "ach.pit_hopper.req": "Join 5 Pits",
  "ach.first_copy.name": "Copycat",
  "ach.first_copy.desc": "Started copying your first trader",
  "ach.first_copy.req": "Copy 1 trader",
  "ach.5_copies.name": "Diversifier",
  "ach.5_copies.desc": "Copying 5 different traders",
  "ach.5_copies.req": "Copy 5 traders",
  "ach.10_copies.name": "Portfolio Builder",
  "ach.10_copies.desc": "Copying 10 different traders",
  "ach.10_copies.req": "Copy 10 traders",
  "ach.25_copies.name": "Whale Watcher",
  "ach.25_copies.desc": "Copying 25 different traders",
  "ach.25_copies.req": "Copy 25 traders",
  "ach.copytrades_10.name": "Copy Cat",
  "ach.copytrades_10.desc": "10 copy trades executed for you",
  "ach.copytrades_10.req": "10 copy trades",
  "ach.copytrades_100.name": "Mirror Pro",
  "ach.copytrades_100.desc": "100 copy trades executed for you",
  "ach.copytrades_100.req": "100 copy trades",
  "ach.copytrades_1000.name": "Auto Pilot",
  "ach.copytrades_1000.desc": "1,000 copy trades executed for you",
  "ach.copytrades_1000.req": "1,000 copy trades",
  "ach.loyal_copier_30.name": "Loyal Copier",
  "ach.loyal_copier_30.desc": "Copying the same trader for 30 days",
  "ach.loyal_copier_30.req": "30 days copying",
  "ach.devoted_copier_90.name": "Devoted Copier",
  "ach.devoted_copier_90.desc": "Copying the same trader for 90 days",
  "ach.devoted_copier_90.req": "90 days copying",
  "ach.referral_1.name": "First Invite",
  "ach.referral_1.desc": "Invited your first friend to FrenFlow",
  "ach.referral_1.req": "Invite 1 friend",
  "ach.referral_5.name": "Connector",
  "ach.referral_5.desc": "Invited 5 friends to FrenFlow",
  "ach.referral_5.req": "Invite 5 friends",
  "ach.referral_10.name": "Networker",
  "ach.referral_10.desc": "Invited 10 friends to FrenFlow",
  "ach.referral_10.req": "Invite 10 friends",
  "ach.referral_25.name": "Recruiter",
  "ach.referral_25.desc": "Invited 25 friends to FrenFlow",
  "ach.referral_25.req": "Invite 25 friends",
  "ach.referral_50.name": "Ambassador",
  "ach.referral_50.desc": "Invited 50 friends to FrenFlow",
  "ach.referral_50.req": "Invite 50 friends",
  "ach.referral_100.name": "Legend",
  "ach.referral_100.desc": "Invited 100 friends to FrenFlow",
  "ach.referral_100.req": "Invite 100 friends",
  "ach.referral_250.name": "Kingmaker",
  "ach.referral_250.desc": "Invited 250 friends to FrenFlow",
  "ach.referral_250.req": "Invite 250 friends",
  "ach.referral_500.name": "Mythic",
  "ach.referral_500.desc": "Invited 500 friends to FrenFlow",
  "ach.referral_500.req": "Invite 500 friends",
  "ach.referral_1000.name": "Movement",
  "ach.referral_1000.desc": "Invited 1,000 friends to FrenFlow",
  "ach.referral_1000.req": "Invite 1,000 friends",

  // ───────── /traders — list, filters, grid, modals ─────────
  // Skeleton + pagination
  "traders.col.trader": "Trader",
  "traders.col.pnl": "PnL",
  "traders.col.volume": "Volume",
  "traders.col.balance": "Balance",
  "traders.col.followers": "Followers",
  "traders.col.profit": "Profit",
  "traders.col.predictions": "Predictions",
  "traders.col.style": "Style",
  "traders.col.chart": "Chart",
  "traders.col.roi": "ROI",
  "traders.col.win": "Win %",
  "traders.tip.win": "Share of resolved trades that closed in profit",
  "traders.col.edge": "Edge",
  "traders.col.bigWin": "Big Win",
  "traders.col.pf": "PF",
  "traders.col.avgBuy": "Avg Buy",
  "traders.col.maxDD": "Max DD",
  "traders.col.posValue": "Pos. Value",
  "traders.col.watch": "Watch",
  "traders.col.copy": "Copy",
  "traders.col.x": "X",
  "traders.pager.prev": "Prev",
  "traders.pager.next": "Next",
  "traders.pager.showing": "Showing {range}",
  "traders.pager.showingOfTraders": "Showing {range} of {total} traders",

  // Filters (UsersFilters)
  "traders.filter.all": "All",
  "traders.filter.watchlist": "Watchlist",
  "traders.filter.favorites": "Favorites",
  "traders.filter.whales": "Whales",
  "traders.filter.degens": "Degens",
  "traders.filter.contrarian": "Contrarian",
  "traders.filter.value": "Value",
  "traders.filter.conviction": "Conviction",
  "traders.filter.marketMakers": "Market Makers",
  "traders.filter.profitable": "Profitable",
  "traders.filter.highRoi": "High ROI",
  "traders.filter.sharp": "Sharp",
  "traders.filter.hft": "HFT Bots",
  "traders.filter.showHftBots": "Show HFT bots",
  "traders.filter.hideHftBots": "Hide HFT bots",
  "traders.filter.xOnly": "Only",
  "traders.filter.xOnly.tooltip.on": "Showing only traders with X linked",
  "traders.filter.xOnly.tooltip.off": "Show only traders with X linked",
  "traders.window.allTime": "All",
  "traders.window.1d": "1D",
  "traders.window.7d": "7D",
  "traders.window.30d": "30D",
  "traders.window.kalshi.all": "All",
  "traders.window.kalshi.daily": "Daily",
  "traders.window.kalshi.weekly": "Weekly",
  "traders.window.kalshi.monthly": "Monthly",
  "traders.window.kalshi.yearly": "Yearly",
  "traders.sort.profit": "Profit",
  "traders.sort.label": "Sort",
  "traders.sort.quality": "Quality",
  "traders.sort.volume": "Volume",
  "traders.sort.predictions": "Predictions",
  "traders.sort.rank": "Rank",
  "traders.sort.pnl": "PnL",
  "traders.sort.roi": "ROI",
  "traders.sort.winRate": "Win Rate",
  "traders.sort.profitFactor": "Profit Factor",
  "traders.view.grid": "Grid view",
  "traders.view.list": "List view",
  "traders.results.suffix": "traders",

  // Grid empty states
  "traders.empty.watchlist.title": "No favorites yet",
  "traders.empty.watchlist.desc": "Star traders to add them to your favorites and jump straight back to them next time.",
  "traders.empty.watchlist.cta": "Browse Traders",
  "traders.empty.profitable.title": "No profitable traders found",
  "traders.empty.profitable.desc": "Try adjusting your other filters or check back later.",
  "traders.empty.whales.title": "No whales here",
  "traders.empty.whales.desc": "Whales are traders with $50K+ balance. They might be in other market categories.",
  "traders.empty.highVolume.title": "No high-volume traders",
  "traders.empty.highVolume.desc": "High-volume traders have $100K+ in total volume. Try removing some filters.",
  "traders.empty.default.title": "No traders found",
  "traders.empty.default.desc": "Try adjusting your search or filters to find what you're looking for.",
  "traders.empty.clearFilters": "Clear Filters",

  // Grid tooltips
  "traders.tip.pnl": "Total profit or loss (realized + unrealized)",
  "traders.tip.roi": "Return on investment — profit / volume traded",
  "traders.tip.edge": "Entry edge — negative = buys below market avg (good)",
  "traders.tip.pf": "Profit factor — gross wins / gross losses. >1.05 = profitable, 1.0-1.05 = breakeven (red), <1.0 = losing money (red).",
  "traders.tip.avgBuy": "Average buy price across all positions (0-100¢)",
  "traders.tip.posValue": "Current value of all open positions",
  "traders.tip.watchAdd": "Add to watchlist",
  "traders.tip.watchRemove": "Remove from watchlist",
  "traders.tip.favAdd": "Add to favorites",
  "traders.tip.favRemove": "Remove from favorites",
  "traders.tip.copyTable": "Copy this trader's strategy",
  "traders.tip.copyMobile": "Copy trader",
  "traders.tip.copyWallet": "Copy wallet address",
  "traders.tip.toggleWatchlist": "Toggle watchlist",
  "traders.tip.compareInList": "In comparison",
  "traders.tip.compareAdd": "Add to compare",
  "traders.tip.compareLimit": "Compare limit reached",
  "traders.tip.xOnHandle": "@{handle} on X",

  // Card stats labels
  "traders.card.profit": "Profit",
  "traders.card.rank": "Rank",
  "traders.card.volume": "Volume",
  "traders.card.copyNow": "Copy Now",
  "traders.card.followers": "Followers",
  "traders.card.predictions": "Predictions",
  "traders.card.winRate": "Win rate",
  "traders.card.trades": "Trades",
  "traders.card.roi": "ROI",
  "traders.card.pnl": "PnL",
  "traders.card.edge": "Edge",
  "traders.card.pf": "PF",
  "traders.card.posValue": "Pos. Value",
  "traders.card.maxDrawdown": "Max drawdown",
  "traders.card.beFirst": "Be the first",
  "traders.card.tradesSuffix": "trades",
  "traders.hftBadge": "HFT BOT",
  "traders.hftBadgeTooltip": "High-Frequency Trading bot",
  "traders.hftBadgeTooltipWithRate": "High-Frequency Trading bot — {rate} trades/hour",
  "traders.badge.high": "HIGH",
  "traders.badge.consistent": "CONSISTENT",
  "traders.badge.whale": "Whale",
  "traders.badge.shark": "Shark",
  "traders.badge.dolphin": "Dolphin",
  "traders.badge.fish": "Fish",
  "traders.badge.shrimp": "Shrimp",

  // AdvancedFiltersDrawer
  "traders.adv.title": "Advanced Filters",
  "traders.adv.activeBadge": "{count} active",
  "traders.adv.reset": "Reset",
  "traders.adv.apply": "Apply",
  "traders.adv.style": "Trading Style",
  "traders.adv.window": "Time Window",
  "traders.adv.metrics": "Metric Ranges",
  "traders.adv.range.pnl": "PnL ($)",
  "traders.adv.range.volume": "Volume ($)",
  "traders.adv.range.trades": "Trades",
  "traders.adv.range.roi": "ROI (decimal)",
  "traders.adv.range.pf": "Profit Factor",
  "traders.adv.range.winRate": "Win Rate (0-1)",
  "traders.adv.range.avgTrade": "Avg Trade ($)",
  "traders.adv.range.walletAge": "Wallet Age (days)",
  "traders.adv.range.entryEdge": "Entry Edge",
  "traders.adv.window.allTime": "All Time",
  "traders.adv.window.30d": "30 Days",
  "traders.adv.window.7d": "7 Days",
  "traders.adv.window.24h": "24 Hours",
  "traders.adv.range.min": "Min",
  "traders.adv.range.max": "Max",
  "traders.adv.filters": "Filters",

  // CopyTraderModal — header
  "traders.copy.editTitle": "Edit Settings",
  "traders.copy.copyTitle": "Copy @{username}",
  "traders.copy.subtitle": "Configure your copy strategy",

  // CopyTraderModal — validation
  "traders.copy.err.invalidAmount": "Please enter a valid amount",
  "traders.copy.err.maxAmount": "Max amount is $10,000",
  "traders.copy.err.pctMin": "Percentage must be at least 1%",
  "traders.copy.err.pctMax": "Percentage cannot exceed 100%",
  "traders.copy.err.balanceBelowMin": "With your ${balance} balance, ${copy} per copy is below the $1 minimum. Top up or raise the %.",
  "traders.copy.err.maxStake": "Max is $10,000",
  "traders.copy.err.sellPctMin": "Must be at least 1%",
  "traders.copy.err.sellPctMax": "Cannot exceed 100%",
  "traders.copy.err.amountGtZero": "Amount must be greater than 0",
  "traders.copy.err.entriesMin": "Must be at least 1",
  "traders.copy.err.slippageRange": "Slippage must be between 0.1% and 50%",
  "traders.copy.err.dailyMin": "Daily limit must be at least $1",
  "traders.copy.err.positiveNumber": "Must be a positive number",
  "traders.copy.err.positivePct": "Must be a positive percentage",
  "traders.copy.err.notLoggedIn": "You need to log in first to start copying",

  // CopyTraderModal — loading / success
  "traders.copy.loading.save": "Saving changes...",
  "traders.copy.loading.start": "Starting copy...",
  "traders.copy.success.editTitle": "Settings Updated!",
  "traders.copy.success.startTitle": "You're now copying!",
  "traders.copy.success.editMsg": "Your copy settings have been updated",
  "traders.copy.success.startMsg": "Every trade @{username} makes will be copied",

  // CopyTraderModal — copy mode
  "traders.copy.mode.title": "Copy Mode",
  "traders.copy.mode.fixed": "Fixed",
  "traders.copy.mode.fixedDesc": "Same $ every trade",
  "traders.copy.mode.percentage": "Balance",
  "traders.copy.mode.percentageDesc": "Scales with you",
  "traders.copy.mode.percentageLegacy": "Leader %",
  "traders.copy.mode.percentageLegacyDesc": "% of the leader's trade",
  "traders.copy.mode.mirror": "Mirror",
  "traders.copy.mode.mirrorDesc": "Match leader's %",
  "traders.copy.mode.betaBadge": "Beta",
  "traders.copy.mode.comingSoon": "Soon",
  "traders.copy.mode.tooltip": "How your copy size is calculated. Hover each option for details.",
  "traders.copy.mode.fixedTooltip": "Fixed amount per trade. You set the $ value (e.g. $5) and every copy uses exactly that, regardless of the leader's trade size or your balance.",
  "traders.copy.mode.percentageTooltip": "Percentage of your balance. Copy size scales with how much pUSD you have. Example: 10% with a $50 balance = $5 per copy.",
  "traders.copy.mode.percentageLegacyTooltip": "Legacy: percentage of the leader's trade size. Example: 5% of a $200 leader trade = $10. Switch to Balance to scale with your own balance instead.",
  "traders.copy.mode.mirrorTooltip": "Mirror the leader's relative sizing. If they bet 5% of their balance, you bet 5% of yours. Capped at 10% of your balance per trade by default.",
  "traders.copy.mode.legacyNotice": "You're on the old mode: 5% means 5% of the leader's trade — a $20 leader trade copies just $1. Switch to size copies off your own balance instead.",
  "traders.copy.mode.legacySwitch": "Switch to % of my balance",
  "traders.copy.mode.mirrorBanner.title": "Mirror replicates the leader's relative sizing",
  "traders.copy.mode.mirrorBanner.body": "If the leader puts 5% of their balance into a trade, you put 5% of yours.\nIf they put 30% (high conviction), you put 30%.\n\nAutomatic cap: 10% of your balance per trade (adjustable 5–25%).\nAuto-pause kicks in if you lose 20% cumulative with this leader.\n\nRecommended only for leaders with a proven risk-management track record.",
  "traders.copy.mode.mirrorBanner.checkbox": "I've read and understood the risk. My automatic cap protects me at 10% per trade by default.",
  "traders.copy.mode.mirrorBanner.required": "Acknowledge the Mirror risk disclosure to continue.",
  "traders.copy.mode.mirrorCapLabel": "Cap per trade",
  "traders.copy.mode.mirrorCapTip": "Maximum share of your balance Mirror will deploy per copy. Slider 5–25%, default 10%.",
  "traders.copy.mode.mirrorCapAdvancedMarker": "advanced",
  "traders.copy.mode.mirrorCapAdvancedWarning": "Above 15%, a single losing streak can wipe 40%+ of your balance. Continue?",
  "traders.copy.mode.mirrorCapPreview": "With your balance of ${balance}, max ${cap} per trade. After 3 losses in a row = -{drawdown}% drawdown.",
  "traders.copy.mode.mirrorCapPreviewNoBalance": "Cap will activate once you deposit pUSD into the wallet.",
  "traders.copy.mode.mirrorCapInvalid": "Cap must be between 5% and 25%.",
  "traders.copy.mode.mirrorAutoBumpLabel": "Bump to $1 when the cap shrinks the copy below the Polymarket minimum",
  "traders.copy.mode.mirrorAutoBumpDesc": "Only fires if the computed copy is ≥ $0.50 AND the leader put ≥ 2% of their balance into the trade. Off by default to preserve Mirror's relative sizing.",
  "traders.copy.mode.mirrorPrecheck.title": "Sizing check",
  "traders.copy.mode.mirrorPrecheck.dismiss": "Hide",
  "traders.copy.mode.mirrorPrecheck.row.leaderBalance": "Leader balance",
  "traders.copy.mode.mirrorPrecheck.row.medianTrade": "Their median trade",
  "traders.copy.mode.mirrorPrecheck.row.yourMatch": "Your match",
  "traders.copy.mode.mirrorPrecheck.row.polyMin": "Polymarket minimum",
  "traders.copy.mode.mirrorPrecheck.row.minFire": "Min leader trade to fire",
  "traders.copy.mode.mirrorPrecheck.row.capture": "Estimated capture",
  "traders.copy.mode.mirrorPrecheck.belowThreshold.tag": "below {threshold} threshold",
  "traders.copy.mode.mirrorPrecheck.belowThreshold.verdict": "Mirror will skip every trade from this leader (under {threshold} threshold).",
  "traders.copy.mode.mirrorPrecheck.smallTrades.verdict": "Copies below $1 (Polymarket minimum) will skip.",
  "traders.copy.mode.mirrorPrecheck.smallTradesBump.verdict": "With auto-bump ON, copies in [$0.50, $1) bump to $1 if the leader put ≥ 2% of their balance.",
  "traders.copy.mode.mirrorPrecheck.recommend.short": "Deposit at least {recommended} for ~80% capture.",
  "traders.copy.mode.mirrorPrecheck.alternative": "Use Balance % instead",
  "traders.copy.mode.mirrorForecast.label": "Estimated capture",
  "traders.copy.mode.mirrorForecast.copiesPerHour": "~{copies} copies/h",
  "traders.copy.mode.mirrorForecast.avgCopy": "avg {avg}",
  "traders.copy.mode.mirrorForecast.medianTrade": "Leader's median trade {trade}",
  "traders.copy.mode.mirrorForecast.noData": "Not enough leader history yet",
  "traders.copy.mode.mirrorForecast.belowThresholdShort": "Leader balance under {threshold}. No trades will be copied.",
  "traders.copy.mode.mirrorRisk.short": "Mirror copies the leader's risk %. Your {cap} cap protects you.",
  "traders.copy.mode.mirrorRisk.expand": "What this means",
  "traders.copy.mode.mirrorRisk.showLess": "Show less",
  "traders.copy.mode.mirrorRisk.ack": "I understand the risk",
  "traders.copy.mode.mirrorCapInline": "Max {cap}/trade · -{drawdown}% after 3 losses",
  "traders.copy.mode.mirrorAutoBumpShort": "Bump small copies to $1",
  "traders.copy.amount.fixed": "Amount per trade",
  "traders.copy.amount.percentage": "Stake",
  "traders.copy.amount.percentageLegacy": "Stake",
  "traders.copy.amount.fixedBumpWarn": "⚠ Will execute at ~$1.10 due to Polymarket's $1 minimum order size.",
  "traders.copy.amount.autoBumpLabel": "Auto-fund trades below $1",
  "traders.copy.amount.autoBumpDescOn": "Sub-$1 copies bump up to $1 to execute.",
  "traders.copy.amount.autoBumpDescOff": "⚠️ Sub-$1 copies are skipped.",
  "traders.copy.amount.autoBumpBadgeActive": "Active",
  "traders.copy.amount.autoBumpBadgeOff": "Off",
  "traders.copy.preset.title": "Execution style",
  "traders.copy.preset.tooltip": "How aggressively to chase the leader's price. Hover the info icon on each option for details.",
  "traders.copy.preset.safe": "Safe",
  "traders.copy.preset.safeSub": "strict",
  "traders.copy.preset.safeTooltip": "Cancels copies when the price has moved against you. You'll miss some trades but never pay much more than the leader did.",
  "traders.copy.preset.normal": "Normal",
  "traders.copy.preset.normalSub": "default",
  "traders.copy.preset.normalTooltip": "Most copies execute. Cancels only when the price has moved a lot against you. Recommended for most users.",
  "traders.copy.preset.aggressive": "Aggressive",
  "traders.copy.preset.aggressiveSub": "loose",
  "traders.copy.preset.aggressiveTooltip": "Almost every copy executes, even when the market has moved. You may end up paying noticeably more than the leader on volatile trades.",
  "traders.copy.preset.migratedBanner": "We replaced the old slippage controls with Execution Style. Based on your previous settings, we picked {{preset}} — review and Save to confirm.",
  // ── Copy onboarding wizard (CopyOnboardingWizard.tsx). 3-step flow
  // shown on first follow before the dense modal. Keys nested under
  // `wizard.*` so they're grep-able as a set and the dense modal keys
  // stay clean. Strings as short as readable so the modal doesn't
  // grow beyond 512px.
  "traders.copy.wizard.stepCounter": "Step {n}",
  "traders.copy.wizard.stepOf": "of {total}",
  "traders.copy.wizard.step1.title": "Amount",
  "traders.copy.wizard.step1.subtitle": "How much do you want to put on each copy?",
  "traders.copy.wizard.step2.title": "Style",
  "traders.copy.wizard.step2.subtitle": "How aggressively to chase the leader's price",
  "traders.copy.wizard.step3.title": "Confirm",
  "traders.copy.wizard.step3.subtitle": "Review and start copying",
  "traders.copy.wizard.copyMode": "Copy mode",
  "traders.copy.wizard.executionMode": "Execution mode",
  "traders.copy.wizard.fixed": "Fixed Amount",
  "traders.copy.wizard.balance": "Balance",
  "traders.copy.wizard.fixedDesc": "Same dollar amount on each copy",
  "traders.copy.wizard.balanceDesc": "Scales with your balance",
  "traders.copy.wizard.recommended": "Recommended",
  "traders.copy.wizard.amountLabel": "Amount per copy",
  "traders.copy.wizard.percentLabel": "% of balance per copy",
  "traders.copy.wizard.helperFixed": "Each copy will be exactly ${amount}",
  "traders.copy.wizard.helperBalance": "Each copy uses {amount}% of your current balance",
  "traders.copy.wizard.helperMirror": "Each copy matches the leader's conviction, never above {amount}% of your balance",
  "traders.copy.wizard.exampleBalance": "Example: with a $100 balance at {pct}%, each copy is ${value}.",
  "traders.copy.wizard.leaderBuys": "Leader buys ${amount}",
  "traders.copy.wizard.youCopy": "→ you copy ${amount}",
  "traders.copy.wizard.balancePreview": "Your balance: ${balance} · enough for ~{count} copies",
  "traders.copy.wizard.portfolio": "portfolio",
  "traders.copy.wizard.preset.safe": "Safe",
  "traders.copy.wizard.preset.normal": "Normal",
  "traders.copy.wizard.preset.aggressive": "Aggressive",
  "traders.copy.wizard.preset.safeTip": "Cancels copies when the price moved against you. Fewer fills, never overpay.",
  "traders.copy.wizard.preset.normalTip": "Most copies execute. Skips only when the price moved a lot against you.",
  "traders.copy.wizard.preset.aggressiveTip": "Almost every copy fills. You may pay noticeably more on volatile trades.",
  "traders.copy.wizard.table.preset": "Preset",
  "traders.copy.wizard.table.maxLoss": "Max loss",
  "traders.copy.wizard.table.maxPrice": "Max price",
  "traders.copy.wizard.table.fills": "Fills",
  "traders.copy.wizard.table.noCap": "no cap",
  "traders.copy.wizard.table.leader": "leader",
  "traders.copy.wizard.table.protection": "Price protection",
  "traders.copy.wizard.table.protectStrict": "Strict",
  "traders.copy.wizard.table.protectBalanced": "Balanced",
  "traders.copy.wizard.table.protectOff": "No cap",
  "traders.copy.wizard.summary.copyMode": "Copy mode",
  "traders.copy.wizard.summary.perCopy": "Per copy",
  "traders.copy.wizard.summary.style": "Style",
  "traders.copy.wizard.summary.autoBump": "Auto bump <$1",
  "traders.copy.wizard.on": "On",
  "traders.copy.wizard.off": "Off",
  "traders.copy.wizard.next.title": "What happens next",
  "traders.copy.wizard.next.b1": "We monitor {handle} 24/7",
  "traders.copy.wizard.next.b2": "Copies execute automatically within seconds",
  "traders.copy.wizard.next.b3": "Pause or change settings anytime",
  "traders.copy.wizard.footerLock": "Funds stay in your wallet — we never hold them.",
  "traders.copy.wizard.back": "Back",
  "traders.copy.wizard.continue": "Continue",
  "traders.copy.wizard.startCopying": "Start copying",
  "traders.copy.wizard.starting": "Starting…",
  "traders.copy.wizard.useAdvanced": "Use advanced setup",
  "traders.copy.wizard.thisTrader": "this trader",
  "traders.copy.wizard.traderFallback": "trader",
  "traders.copy.wizard.done.title": "Copy started",
  "traders.copy.wizard.done.desc": "Your follow is live. The next trade this leader makes will be copied automatically.",
  "traders.copy.wizard.failedCopy": "Failed to start copying",
  "traders.copy.wizard.invalidAmount": "Enter a valid amount",
  "traders.copy.wizard.hftTitle": "This trader makes ~{tph} trades/hour",
  "traders.copy.wizard.hftTitleGeneric": "This trader is a high-frequency bot",
  "traders.copy.wizard.hftDesc": "HFT bots fire hundreds of tiny trades — fees and slippage add up fast and most copiers end up losing. Pause anytime from Copy Settings.",
  "traders.copy.wizard.hftAck": "I understand this is a high-frequency bot and that most copiers lose money.",
  "traders.copy.painFloorChipLabel": "Slippage Forgiveness",
  "traders.copy.painFloorChipScope": "all leaders",
  "traders.copy.painFloorChipEdit": "Edit in Settings",
  "traders.copy.amount.autoPennyLabel": "Auto-adjust slippage",
  "traders.copy.amount.autoPennyDescOn": "Penny markets (<$0.15) get a looser cap.",
  "traders.copy.amount.autoPennyDescOff": "⚠️ Same cap everywhere (more skips in penny).",
  "traders.copy.amount.autoPennyTooltip": "In low-price markets (<$0.15), the slippage limit relaxes automatically so a small price move (just a couple of cents) doesn't cancel every copy. Uncheck to keep the same strict limit on every market.",
  "traders.copy.amount.pennyCapLabel": "Adjust up to",
  "traders.copy.amount.pennyCapTooltip": "How much higher than the leader's price your copy may fill in penny markets (<$0.15). 100% = match the leader's price tolerance (default). 500% = allow paying up to 6× the leader's price. Only matters when Auto-adjust above is ON.",
  "traders.copy.amount.pennyCapUnlimited": "Unlimited",
  "traders.copy.amount.pennyCapWarnConfirm": "You're allowing your copy to fill at up to {pct}% above the leader's price in penny markets — roughly {mult}× the leader's price.\n\nIf the outcome resolves to $0 you can lose almost all the trade's capital. This does NOT automatically improve PnL — only use it if you're copying a leader whose edge persists after the price moves (e.g. pre-resolution arbitrage bots).\n\nConfirm?",
  "traders.copy.amount.pennyCapWarnInline": "Pay up to {pct}% above the leader (~{mult}× their price). Only worth it on arbitrage-style leaders — otherwise expect deeper drawdowns.",
  "traders.copy.amount.pennyCapNoCapLabel": "Unlimited — fill at any price (power-user)",
  "traders.copy.amount.pennyCapNoCapDesc": "Your copy fills at any price the market offers in low-price markets — no slippage protection. The most you can lose per copy is your Max/trade ($) amount. Only worth it for arbitrage-style leaders.",
  "traders.copy.amount.pennyCapNoCapConfirm": "Activate Unlimited mode?\n\nYour copy will fill at WHATEVER price the market offers in markets below $0.15. Example: leader buys at $0.015 → your copy could fill at $0.50 or higher.\n\nMax loss per copy is your Max/trade ($) value. This is power-user mode — only use it if you understand the leader's edge persists after the price has moved.\n\nConfirm?",
  "ct.pennySlip.bannerCapTitle": "Aggressive penny slippage active on {n} leader(s)",
  "ct.pennySlip.bannerUnlimitedTitle": "No-cap penny slippage active on {n} leader(s)",
  "ct.pennySlip.entryUnlimited": "no cap",
  "ct.unlimitedBadge.tooltip": "Slippage unlimited\n\nCopies fill at any price in low-price markets (<$0.15).",
  "traders.curated.recommended": "Recommended",
  "traders.curated.trending": "Trending",
  "traders.curated.mostCopied": "Most copied",
  "traders.curated.whales": "Whales",
  "traders.curated.lowRisk": "Consistent",
  "traders.curated.hft": "HFT Bots",
  "traders.curated.hftHint": "Hundreds of trades/hour. Many copies will skip — high capital churn.",
  "traders.curated.new": "New",
  "traders.curated.heading": "Discover traders",
  "traders.copy.amount.pennyCapHintBody": "Last 7d: {n} penny-market copies failed by slippage. Raising the cap to {pct}% would have captured ~75% of them.",
  "traders.copy.amount.pennyCapHintApply": "Apply {pct}%",
  "traders.copy.amount.autoBumpTooltip": "Polymarket rejects trades under $1. When your % would copy a smaller size we lift it to $1 so the trade still goes through. You'll spend slightly more than your % on those small trades in exchange.",
  "traders.copy.amount.fixedTip": "The exact dollar amount you'll invest each time the trader opens a position.",
  "traders.copy.amount.percentageTip": "Percentage of your available balance to invest on every copy — the leader's trade size doesn't matter.",
  "traders.copy.amount.percentageLegacyTip": "Percentage of the leader's trade size — small leader trades produce small copies (a $20 leader trade × 5% = $1).",
  "traders.copy.maxStake": "Max per trade",
  "traders.copy.maxStakeTip": "Safety limit: maximum amount for any single trade, regardless of mode.",

  // DepositWrapBanner — USDC.e/native → pUSD wrap (wave 16/17)
  "wallet.wrapBanner.body": "in your trading wallet isn't ready to trade. Wrap to pUSD so copytrading can use it.",
  "wallet.wrapBanner.autoWrapInfo": "After this, future Polymarket payouts will be auto-wrapped to pUSD silently — no action needed.",
  "wallet.wrapBanner.cta": "Wrap all to pUSD →",
  "wallet.wrapBanner.wrapping": "Wrapping...",
  "wallet.wrapBanner.successWithAutoWrap": "Wrapped to pUSD ✓ From now on, Polymarket payouts auto-wrap. Copytrading resumes on the next signal.",
  "wallet.wrapBanner.partialSuccess": "Wrapped {done} of {total}. Check Recent Trades.",

  // CopyTraderModal — minimum-order advisory. $1 is the Polymarket CLOB
  // hard minimum for marketable BUY orders; copies that compute below it
  // are skipped with the reason surfaced in Recent Trades.
  "traders.copy.minFloor.fixedTooLow.title": "No trade will execute.",
  "traders.copy.minFloor.fixedTooLow.body": "Minimum order size is $1. Raise your stake to $1 or more so the trade goes through.",
  "traders.copy.minFloor.percentage.title": "Minimum order size: $1.",
  "traders.copy.minFloor.percentage.bodyWithCap": "With {pct}%, leader trades smaller than ${threshold} compute under $1 and are skipped (reason shown in Recent Trades). Your cap (${cap}) sets the upper bound.",
  "traders.copy.minFloor.percentage.bodyNoCap": "With {pct}%, leader trades smaller than ${threshold} compute under $1 and are skipped (reason shown in Recent Trades).",
  "traders.copy.minFloor.fixedOk": "Above the $1 minimum order size.",
  "traders.copy.err.belowPolyMin": "Minimum $1 per order.",

  // "Off" placeholder for optional risk-cap inputs — communicates that
  // empty/0 means "no limit" instead of a recommended default. Less
  // misleading than a numeric example which users read as suggested value.
  "traders.copy.placeholder.off": "Off",

  // Balance-percentage live preview — 3 states:
  //   previewBalance   → normal case, computed copy ≥ $1.
  //   previewBelowMin  → computed copy < $1 (skipped in the engine).
  //   previewNoBalance → wallet not configured yet / 0 balance.
  "traders.copy.amount.previewBalance": "With your ${balance} balance, you'd copy ~${copy} per leader trade.",
  "traders.copy.amount.previewBelowMin": "With your ${balance} balance, ${copy} per copy is below the $1 minimum.",
  "traders.copy.amount.previewBelowMinAction": "Top up your wallet or raise the % to copy this trader.",
  "traders.copy.amount.previewBelowMinRaisePct": "Raise the % to at least {minPct}% to copy this trader.",
  "traders.copy.amount.previewBelowMinTopUp": "Top up your wallet to copy this trader (minimum $1 balance).",
  "traders.copy.amount.previewBumpActive": "${balance} × {pct}% = ${copy} → auto-bumped to $1. Trades will execute at the CLOB minimum.",
  "traders.copy.amount.previewNoBalance": "Top up your wallet to see how much each copy will be.",

  // CopyTraderModal — sell mode
  "traders.copy.sell.title": "When trader sells",
  "traders.copy.sell.tooltip": "What happens when the trader you're copying sells their position.",
  "traders.copy.sell.mirror": "Mirror %",
  "traders.copy.sell.mirrorDesc": "Same % as trader",
  "traders.copy.sell.all": "Sell All",
  "traders.copy.sell.allDesc": "Close entire position",
  "traders.copy.sell.fixed": "Fixed $",
  "traders.copy.sell.fixedDesc": "Sell fixed amount",
  "traders.copy.sell.custom": "Custom %",
  "traders.copy.sell.customDesc": "Set your own %",
  "traders.copy.sell.manual": "Manual",
  "traders.copy.sell.manualDesc": "- Never auto-sell, you control exits",
  "traders.copy.sell.amountFixed": "Sell amount ($)",
  "traders.copy.sell.amountPct": "Sell percentage (%)",

  // CopyTraderModal — entry mode
  "traders.copy.entry.title": "Entries per market",
  "traders.copy.entry.tooltip": "How many times to copy entries on the same market. Prevents over-exposure.",
  "traders.copy.entry.all": "All",
  "traders.copy.entry.allDesc": "Every entry",
  "traders.copy.entry.first": "First",
  "traders.copy.entry.firstDesc": "1 per market",
  "traders.copy.entry.limit": "Limit",
  "traders.copy.entry.limitDesc": "Set max",

  // CopyTraderModal — collapsibles
  "traders.copy.advanced": "Advanced Settings",
  "traders.copy.risk": "Risk Settings",
  "traders.copy.customBadge": "Edited",

  // CopyTraderModal — risk: limits
  "traders.copy.risk.limits": "Limits",
  "traders.copy.risk.dailyVolume": "Daily Volume $",
  "traders.copy.risk.dailyVolumeTip": "Max total volume you can copy per day. Set to 0 for unlimited.",
  "traders.copy.risk.maxPositions": "Max Positions",
  "traders.copy.risk.maxPositionsTip": "Maximum number of open positions at once. Set to 0 for unlimited.",
  "traders.copy.risk.budgetCap": "Budget Cap $",
  "traders.copy.risk.budgetCapTip": "Hard cap on total capital you've put in (cost basis) across all open positions. A winning position that's up 4x still counts at its original cost, so this is a money-deployed cap — not a live-exposure cap. New trades are skipped once reached.",

  // CopyTraderModal — risk: trade size
  "traders.copy.risk.tradeSize": "Leader BUY Filter",
  "traders.copy.risk.minTrade": "Min Leader BUY $",
  "traders.copy.risk.minTradeTip": "Skip BUYs when the leader's own notional is below this. Useful to ignore small noise trades. SELLs are always processed.",
  "traders.copy.risk.maxTrade": "Max Leader BUY $",
  "traders.copy.risk.maxTradeTip": "Skip BUYs when the leader's own notional is above this. Useful when the leader goes heavy on a single ticket. SELLs are always processed.",

  // CopyTraderModal — risk: market quality (mig 254)
  "traders.copy.risk.marketQuality": "Market Quality Filter",
  "traders.copy.risk.minOdds": "Min Odds %",
  "traders.copy.risk.minOddsTip": "Skip BUYs when the market's current price is below this percentage. Ex: 5 skips bets <5¢ (deep out-of-the-money). SELLs are always processed.",
  "traders.copy.risk.maxOdds": "Max Odds %",
  "traders.copy.risk.maxOddsTip": "Skip BUYs when the market's current price is above this percentage. Ex: 95 skips near-resolved markets (>95¢). SELLs are always processed.",
  "traders.copy.risk.minLiquidity": "Min Liquidity $",
  "traders.copy.risk.minLiquidityTip": "Skip BUYs in markets with thin orderbooks (Polymarket liquidity below this $ amount). Helps avoid eating spread on micro-cap markets.",
  "traders.copy.risk.minVolume24h": "Min 24h Volume $",
  "traders.copy.risk.minVolume24hTip": "Skip BUYs in markets with low recent activity (Polymarket 24h volume below this $ amount). Filters out abandoned or pre-launch markets.",
  "traders.copy.risk.maxDaysExpiry": "Max Days to Expiry",
  "traders.copy.risk.maxDaysExpiryTip": "Skip BUYs in markets that resolve further out than this many days. Keeps capital off long-duration positions.",
  "traders.copy.risk.daysSuffix": "d",

  // CopyTraderModal — risk: exposure
  "traders.copy.risk.exposure": "Exposure",
  "traders.copy.risk.perEvent": "Per Event $",
  "traders.copy.risk.perEventTip": "Max total cost basis across all markets within the same event. (Cost basis, not live value — a winning position counts at what you paid.)",
  "traders.copy.risk.maxEvents": "Max Events",
  "traders.copy.risk.maxEventsTip": "Cap the number of distinct events you're in at once. Keeps your exposure focused when the leader trades many markets.",

  // CopyTraderModal — risk: auto-pause
  "traders.copy.risk.autoPause": "Auto-Pause",
  "traders.copy.risk.lossPct": "Loss %",
  "traders.copy.risk.lossPctTip": "Pause copying when your total realized loss reaches this % of volume.",
  "traders.copy.risk.profitPct": "Profit %",
  "traders.copy.risk.profitPctTip": "Automatically pause copying once your realized profit hits this % of volume. Helps you secure gains.",
  "traders.copy.risk.dailyUsd": "Daily $",
  "traders.copy.risk.dailyUsdTip": "Pause copying when daily realized losses hit this amount. Resets every day at midnight UTC.",
  "traders.copy.risk.weeklyUsd": "Weekly $",
  "traders.copy.risk.weeklyUsdTip": "Pause copying when losses over the last 7 days exceed this amount. Rolls forward each day.",

  // CopyTraderModal — slippage
  "traders.copy.slip.title": "Slippage Protection",
  "traders.copy.slip.tooltip": "Cancel trades if price moves too much. Limit fallback uses limit orders instead of cancelling.",
  "traders.copy.slip.maxPct": "Max Slippage %",
  "traders.copy.slip.tightWarn.title": "Tight cap warning:",
  "traders.copy.slip.tightWarn.body": "At {pct}% your BUYs will be skipped in volatile or penny markets.",
  "traders.copy.hftWarn.title": "High-frequency trader",
  "traders.copy.hftWarn.body": "~{tradesPerHour} trades/hour. Many copies will be skipped.",
  "traders.copy.hftWarn.tooltip": "This leader makes ~{tradesPerHour} trades/hour ({trades24h} in 24h). Activity at this scale exceeds typical copytrading; many of your copies will be skipped due to signing rate limits. Consider a less active leader.",
  "traders.copy.hftWarn.ack": "I understand and want to follow anyway.",
  "traders.copy.hftWarn.edu.show": "Pros & cons of copying a bot",
  "traders.copy.hftWarn.edu.hide": "Hide pros & cons",
  "traders.copy.hftWarn.edu.prosTitle": "Why it can work",
  "traders.copy.hftWarn.edu.pro1": "Bots often have a real statistical edge and a high win-rate — capturing micro-inefficiencies a human can't track.",
  "traders.copy.hftWarn.edu.consTitle": "Why it often doesn't work for copiers",
  "traders.copy.hftWarn.edu.con1": "Huge trade volume — every copy pays a fee, which can eat the whole margin.",
  "traders.copy.hftWarn.edu.con2": "They trade thin markets with tiny spreads; your fills are worse (latency + slippage), so their edge doesn't transfer 1:1.",
  "traders.copy.hftWarn.edu.con3": "Constant position churn and a high volume of notifications.",
  "traders.copy.hftWarn.edu.con4": "Their P&L doesn't predict yours — it depends on fill speed your copy can't match.",
  "traders.copy.lossWarn.title": "Copiers have lost money on this trader.",
  "traders.copy.lossWarn.body": "Our copy simulation{roi} flags this wallet as not copy-worthy — its edge doesn't survive being copied. Try one from the Recommended tab instead.",
  "traders.copy.lossWarn.ack": "I understand copiers have lost money copying this trader.",
  "traders.tab.recommended": "Recommended",
  "traders.tab.leaderboard": "Leaderboard",
  "traders.curator.empty": "No copyable traders right now — check back after the next curation run.",
  "traders.curator.teaserTitle": "Sign up to see all copyable traders",
  "traders.curator.teaserCta": "Sign up free",
  "traders.copy.slip.tieredTitle": "Tiered Slippage %",
  "traders.copy.slip.tieredTip": "Set custom slippage limits per price range — high-priced tokens tolerate less slippage than low-priced ones. Leave empty to use global slippage.",
  "traders.copy.slip.tier.high": "40¢+ (high)",
  "traders.copy.slip.tier.mid": "15-40¢ (mid)",
  "traders.copy.slip.tier.low": "<15¢ (low)",

  // CopyTraderModal — footer
  "traders.copy.btn.saving": "Saving...",
  "traders.copy.btn.save": "Save Changes",
  "traders.copy.btn.start": "Start copying",
  "traders.copy.btn.copy": "Copy",
  "traders.copy.btn.loginRequired": "Please log in to start copying",
  "traders.copy.btn.signInPrefix": "Sign in to copy",
  "traders.copy.btn.signInAction": "Sign in",
  "traders.copy.balance.available": "Balance",
  "traders.copy.leaderPortfolio": "Portfolio",
  "traders.copy.btn.depositFirst": "Top up your wallet to start copying — minimum $1.",

  // ───── /traders/[identifier] — profile page ─────
  // UserProfileClient (errors)
  "traders.profile.notFound": "Trader not found",
  "traders.profile.fetchError": "Failed to fetch trader data",
  "traders.profile.loadError": "Failed to load trader profile",
  "traders.profile.errorTitle": "Trader not found",
  "traders.profile.errorDesc": "This profile doesn't exist or couldn't be loaded.",
  "traders.profile.browse": "Browse Traders",
  "traders.profile.goHome": "Go home",
  // UserProfile (header + stats)
  "traders.profile.back": "Back",
  "traders.profile.stat.volume": "Volume",
  "traders.profile.stat.points": "Points",
  "traders.profile.stat.positions": "Positions",
  "traders.profile.stat.followers": "Followers",
  "traders.profile.stat.rank": "Rank",
  "traders.profile.stat.biggestWin": "Biggest Win",
  "traders.profile.stat.markets": "Markets",
  "traders.profile.stat.predictions": "Predictions",
  "traders.profile.stat.posValue": "Positions Value",
  "traders.profile.stat.winRate": "Win Rate",
  "traders.profile.aka.tooltip": "View previous usernames",
  "traders.profile.aka": "AKA",
  "traders.profile.previously": "Previously known as:",
  "traders.profile.copyCta": "Copy This Trader",
  "traders.profile.stopping": "Stopping...",
  "traders.profile.copying": "Copying",
  "traders.profile.tab.profile": "Profile",
  "traders.profile.xLink": "@{handle} on X",
  "traders.profile.copiers.one": "trader is",
  "traders.profile.copiers.many": "traders are",
  "traders.profile.copiers.suffix": "already copying",
  "traders.profile.copiers.first": "Be the first to copy",
  "traders.card.copying": "copying",
  "traders.profile.joined": "Joined {date}",
  "traders.profile.views": "{count} views",
  "traders.profile.volByCategory": "Volume by Category",
  "traders.profile.totalVol": "Total Vol",
  "traders.profile.badges": "Badges",
  "traders.profile.marketsByCategory": "Markets by Category",
  "traders.profile.predictionsCount": "Predictions ({count})",
  "traders.profile.totalDonut": "Total",
  "traders.profile.showLess": "Show less",
  "traders.profile.viewAll": "View all {count} categories",
  "traders.profile.performance": "Performance",
  "traders.profile.netPnl": "Net PnL",
  "traders.profile.toast.comingSoon": "Coming Soon",
  "traders.profile.toast.comingSoonDesc": "Copy trading on {platform} is on the way. Stay tuned!",
  "traders.profile.viewBscScan": "View on BscScan",
  "traders.profile.viewPolygonscan": "View on Polygonscan",
  "traders.profile.viewOnPolymarket": "View on Polymarket",
  "traders.profile.viewOnKalshi": "View on Kalshi",
  "traders.profile.viewOnPredict": "View on Predict.fun",
  // TraderAboutCard
  "traders.about.title": "About",
  "traders.about.autoGenerated": "Auto-generated",
  "traders.about.markets": "Markets",
  // TraderPortfolio (tabs + filters + tables)
  "traders.port.tab.positions": "Positions",
  "traders.port.tab.activity": "Activity",
  "traders.port.tab.comments": "Comments",
  "traders.port.tab.volume": "Volume",
  "traders.port.tab.similar": "Similar Wallets",
  "traders.port.tab.new": "New",
  "traders.port.filter.all": "All",
  "traders.port.filter.open": "Open",
  "traders.port.filter.resolved": "Resolved",
  "traders.port.filter.active": "Active",
  "traders.port.filter.closed": "Closed",
  "traders.port.searchPlaceholder": "Search positions...",
  "traders.port.portfolioValue": "Portfolio Value",
  "traders.port.empty.positions": "No positions found",
  "traders.port.col.market": "Market",
  "traders.port.col.avg": "Avg",
  "traders.port.col.current": "Current",
  "traders.port.col.shares": "Shares",
  "traders.port.col.value": "Value",
  "traders.port.col.pnl": "PnL",
  "traders.port.showMore": "Show more ({n} remaining)",
  "traders.port.shares": "shares",
  "traders.port.empty.closed": "No closed positions found",
  "traders.port.col.result": "Result",
  "traders.port.col.totalTraded": "Total Traded",
  "traders.port.col.amountWon": "Amount Won",
  "traders.port.result.won": "Won",
  "traders.port.result.lost": "Lost",
  "traders.port.empty.activity": "No activity found",
  "traders.port.col.type": "Type",
  "traders.port.col.amount": "Amount",
  "traders.port.col.time": "Time",
  "traders.port.activity.rewards": "Rewards",
  "traders.port.activity.rewards4": "4% Rewards",
  "traders.port.activity.referralReward": "Referral reward",
  "traders.port.activity.referralRewardDesc": "Polymarket affiliate payout",
  "traders.port.activity.yield": "Holdings yield",
  "traders.port.activity.yieldDesc": "4% APR on cash balance",
  "traders.port.activity.unknownMarket": "Unknown market",
  "traders.port.activity.sharesSuffix": "{n} shares",
  "traders.port.badge.reward": "Reward",
  "traders.port.badge.referral": "Referral",
  "traders.port.badge.yield": "Yield",
  "traders.port.badge.redeem": "Redeem",
  "traders.port.badge.buy": "Buy",
  "traders.port.badge.sell": "Sell",
  "traders.port.badge.split": "Split",
  "traders.port.badge.merge": "Merge",
  "traders.port.badge.convert": "Convert",
  "traders.port.badge.rebate": "Rebate",
  "traders.port.empty.comments": "No comments on Polymarket",
  // TraderPnLChart
  "traders.chart.title": "Profit/Loss",
  "traders.chart.noData": "No PnL data available",
  "traders.chart.loadFail": "Failed to load P&L history",
  "traders.chart.noTradingHistory": "No trading history available for this period",
  "traders.chart.tooltip.pnl": "P&L",
  "traders.chart.display.day": "Past Day",
  "traders.chart.display.week": "Past Week",
  "traders.chart.display.month": "Past Month",
  "traders.chart.display.year": "Past Year",
  "traders.chart.display.all": "All-Time",
  // WalletPnLEnriched
  "traders.pnlb.title": "P&L Breakdown",
  "traders.pnlb.realized": "Realized",
  "traders.pnlb.unrealized": "Unrealized",
  // WalletVolumeChart
  "traders.vol.gran.hourly": "Hourly",
  "traders.vol.gran.daily": "Daily",
  "traders.vol.gran.weekly": "Weekly",
  "traders.vol.totalVolume": "Total Volume",
  "traders.vol.buyVolume": "Buy Volume",
  "traders.vol.sellVolume": "Sell Volume",
  "traders.vol.totalTrades": "Total Trades",
  "traders.vol.dataPoints": "Data Points",
  "traders.vol.buy": "Buy",
  "traders.vol.sell": "Sell",
  // UserTimeline (extras hardcoded)
  "traders.timeline.justNow": "just now",
  "traders.timeline.mAgo": "{n}m ago",
  "traders.timeline.hAgo": "{n}h ago",
  "traders.timeline.dAgo": "{n}d ago",
  "traders.timeline.postFailed": "Failed to create post",
  "traders.timeline.copied": "copied",
  // MarketPerformance
  "traders.mperf.title": "Market Performance",
  "traders.mperf.tradesSuffix": "trades",
  "traders.mperf.wrSuffix": "% WR",
  "traders.mperf.roiSuffix": "% ROI",
  "traders.mperf.entryEdge": "Entry edge: {pct}%",
  // RecentTrades
  "traders.recent.title": "Recent Activity",
  "traders.recent.now": "now",
  "traders.recent.tradesSuffix": "trades",
  "traders.recent.buy": "BUY",
  "traders.recent.sell": "SELL",
  "traders.recent.yes": "YES",
  "traders.recent.no": "NO",
  // TraderRelatedArticles
  "traders.articles.thisTrader": "this trader",
  "traders.articles.title": "News about {name}",
  // PredexonStatsPanel
  "traders.psp.style.whale": "Whale",
  "traders.psp.style.marketMaker": "Market Maker",
  "traders.psp.style.active": "Active",
  "traders.psp.style.holder": "Holder",
  "traders.psp.style.degen": "Degen",
  "traders.psp.style.conviction": "Conviction",
  "traders.psp.style.contrarian": "Contrarian",
  "traders.psp.style.value": "Value",
  "traders.psp.section.performance": "Performance",
  "traders.psp.section.tradingStyle": "Trading Style",
  "traders.psp.row.volume": "Volume",
  "traders.psp.row.roi": "ROI",
  "traders.psp.row.winRate": "Win Rate",
  "traders.psp.row.wl": "W / L",
  "traders.psp.row.trades": "Trades",
  "traders.psp.row.profitFactor": "Profit Factor",
  "traders.psp.row.entryEdge": "Entry Edge",
  "traders.psp.row.netFees": "Net Fees",
  "traders.psp.row.bestTrade": "Best Trade",
  "traders.psp.row.worstTrade": "Worst Trade",
  "traders.psp.row.streaks": "Streaks",
  "traders.psp.row.avgHold": "Avg Hold",
  "traders.psp.row.walletAge": "Wallet Age",
  "traders.psp.row.positions": "Positions",
  // CompareTraders
  "traders.compare.profit": "Profit",
  "traders.compare.roi": "ROI",
  "traders.compare.volume": "Volume",
  "traders.compare.pnl": "PnL",
  "traders.compare.vs": "VS",
  "traders.compare.selectTrader": "Select a trader to compare...",
  "traders.compare.cancel": "Cancel",
  "traders.compare.tapAnother": "Tap another trader to compare",
  "traders.compare.copyTrader": "Copy trader",
  "traders.compare.analyzing": "Analyzing traders...",
  "traders.compare.results": "Results",
  "traders.compare.winner": "— Winner",
  // PortfolioTracker
  "traders.tracker.loadFail": "Failed to load portfolio",
  "traders.tracker.noData": "No portfolio data available",
  "traders.tracker.tryAgain": "Try Again",
  "traders.tracker.title": "My Portfolio",
  "traders.tracker.lastUpdated": "Last updated: {time}",
  "traders.tracker.refresh": "Refresh",
  "traders.tracker.portfolioValue": "Portfolio Value",
  "traders.tracker.totalPnl": "Total P&L",
  "traders.tracker.realized": "Realized: {amount}",
  "traders.tracker.unrealized": "Unrealized: {amount}",
  "traders.tracker.roi": "ROI",
  "traders.tracker.activePositions": "Active Positions",
  "traders.tracker.pnlHistory": "P&L History",
  "traders.tracker.change": "Change: {currency} ({percent})",
  "traders.tracker.tooltipPnl": "P&L",
  "traders.tracker.byCategory": "By Category",
  // SimilarWallets
  "traders.similar.intro": "Wallets that trade the same markets — ranked by portfolio overlap. Click any row to explore that wallet.",
  "traders.similar.col.wallet": "Wallet",
  "traders.similar.col.shared": "Shared",
  "traders.similar.col.overlap": "Overlap",
  "traders.similar.col.theirMarkets": "Their Markets",
  "traders.similar.col.totalPnl": "Total PnL",
  "traders.similar.col.roi": "ROI",
  "traders.similar.col.winRate": "Win Rate",
  "traders.similar.tip.shared": "Number of markets both wallets have traded",
  "traders.similar.tip.overlap": "Percentage of the trader's markets that this wallet also trades",
  "traders.similar.tip.theirMarkets": "Total number of markets this wallet has traded",
  "traders.similar.tip.totalPnl": "All-time total profit or loss for this wallet",
  "traders.similar.tip.roi": "Return on investment — profit relative to volume traded",
  "traders.similar.tip.winRate": "Percentage of closed positions that were profitable",
  // Error boundaries
  "traders.err.title": "Something went wrong",
  "traders.err.profileDesc": "We couldn't load this trader profile. Please try again.",
  "traders.err.pageDesc": "We couldn't load the traders page. Please try again.",
  "traders.err.tryAgain": "Try Again",
  "traders.err.goHome": "Go Home",

  // ───── /the-frens ─────
  "frens.download": "Download",

  // ───── Misc small pages ─────
  "common.redirecting": "Redirecting...",
  // /telegram/login (status messages + buttons)
  "tgLogin.init": "Initializing...",
  "tgLogin.switching": "Switching accounts...",
  "tgLogin.authTg": "Authenticating with Telegram...",
  "tgLogin.loading": "Loading...",
  "tgLogin.clickToLogin": "Click below to login...",
  "tgLogin.connecting": "Connecting your account...",
  "tgLogin.errNoTg": "Could not get Telegram ID. Please try again from the bot.",
  "tgLogin.errAuth": "Authentication error. Please try again.",
  "tgLogin.errOpenFromTg": "Please open this page from Telegram",
  "tgLogin.success": "Account connected successfully!",
  "tgLogin.errFail": "Failed to connect account",
  "tgLogin.errGeneric": "An error occurred",
  "tgLogin.cta": "Login with Telegram",
  "tgLogin.returning": "Returning to bot...",
  "tgLogin.errorLabel": "Error",
  "tgLogin.tryAgain": "Try Again",
  "tgLogin.poweredBy": "Powered by Privy",
  // /restricted
  "restricted.title": "Content Not Available",
  "restricted.desc": "Kalshi prediction markets are not available in your region due to regulatory restrictions.",
  "restricted.cta": "Browse Polymarket",
  "restricted.footer": "Polymarket and other features remain fully accessible.",
  // /traders/cohorts
  "cohorts.back": "Back to Traders",
  "cohorts.title": "Trading Style Cohorts",
  "cohorts.desc": "Compare performance across 8 trading styles on Polymarket. Data powered by Predexon.",
  "cohorts.empty": "Cohort data not available. Requires Predexon Dev+ plan.",
  "cohorts.tradersCount": "{count} traders",
  "cohorts.stat.avgPnl": "Avg PnL",
  "cohorts.stat.winRate": "Win Rate",
  "cohorts.stat.avgVol": "Avg Vol",
  "cohorts.stat.avgTrades": "Avg Trades",
  "cohorts.avgPnlSmall": "avg PnL",

  // ───── UMA — VoterProfile ─────
  "uma.voter.back": "Back to Leaderboard",
  "uma.voter.notFound": "Voter not found.",
  "uma.voter.staked": "Staked",
  "uma.voter.apy": "APY",
  "uma.voter.accuracy": "Accuracy",
  "uma.voter.slashed": "Slashed",
  "uma.voter.correct": "Correct",
  "uma.voter.wrong": "Wrong",
  "uma.voter.noVote": "No Vote",
  "uma.voter.disputesInvolved": "Disputes Involved",

  // ───── UMA — AnalyticsDashboard ─────
  "uma.analytics.loadFail": "Failed to load analytics: {error}",
  "uma.analytics.participation": "Participation Rate",
  "uma.analytics.turnout": "Voter Turnout",
  "uma.analytics.bondSizes": "Bond Sizes (USD)",
  "uma.analytics.monthly": "Monthly Disputes",
  "uma.analytics.slashing": "Slashing Over Time",

  // ───── UMA — DisputeDetail ─────
  "uma.detail.back": "Back to disputes",
  "uma.detail.notFound": "Dispute not found.",
  "uma.detail.proposed": "Proposed",
  "uma.detail.bond": "Bond",
  "uma.detail.proposer": "Proposer",
  "uma.detail.disputer": "Disputer",
  "uma.detail.voteBreakdown": "Vote Breakdown",
  "uma.detail.voters": "Voters",
  "uma.detail.tokensRevealed": "Tokens Revealed",
  "uma.detail.participation": "Participation",
  "uma.detail.winner": "Winner",
  "uma.detail.voter": "voter",
  "uma.detail.votersPlural": "voters",
  "uma.detail.revealedVotes": "Revealed Votes ({n})",
  "uma.detail.col.address": "Address",
  "uma.detail.col.tokens": "Tokens",
  "uma.detail.col.vote": "Vote",
  "uma.detail.col.status": "Status",
  "uma.detail.status.correct": "Correct",
  "uma.detail.status.wrong": "Wrong",
  "uma.detail.status.pending": "Pending",
  "uma.detail.unrevealed": "Unrevealed Voters ({n})",
  "uma.detail.unrevealedDesc": "Committed but haven't revealed yet",
  "uma.detail.col.stake": "Stake",
  "uma.detail.col.tokensCommitted": "Tokens Committed",

  // ───── /traders/category ─────
  "tradersCat.crumb.home": "Home",
  "tradersCat.crumb.traders": "Traders",
  "tradersCat.crumb.categories": "Categories",
  "tradersCat.title": "Polymarket Trader Categories",
  "tradersCat.desc": "Browse the most profitable traders on Polymarket organized by market category. Each category shows traders ranked by profit who specialize in that type of prediction market.",
  "tradersCat.tradersCount": "{count} traders",
  "tradersCat.empty": "No categories available yet.",
  "tradersCat.browseAll": "Browse All Traders",
  "tradersCat.about": "About Polymarket Trader Categories",
  "tradersCat.aboutDesc": "Polymarket traders often specialize in specific market categories where they have domain expertise. FrenFlow tracks which categories each trader operates in and lets you browse the top performers in each area. Whether you're interested in politics, sports, crypto, or finance — find the traders with the best track record and copy their strategies.",
  // /traders/category/[category]
  "tradersCat.detail.title": "Top {cat} Traders on Polymarket",
  "tradersCat.detail.descHas": "{n} traders specializing in {cat} prediction markets, ranked by profit. Track their positions and copy their trades on FrenFlow.",
  "tradersCat.detail.descEmpty": "No traders found for {cat} markets yet.",
  "tradersCat.detail.empty": "No traders found for this category.",
  "tradersCat.detail.volSuffix": "vol",
  "tradersCat.detail.winSuffix": "% win",
  "tradersCat.detail.moreCats": "More Trader Categories",
  "tradersCat.detail.seoTitle": "{cat} Prediction Market Trading",
  "tradersCat.detail.seoDesc": "{cat} is one of the most active categories on Polymarket, with traders placing bets on outcomes related to {cat2} events.",
  "tradersCat.detail.seoTracks": "FrenFlow tracks {n}+ traders who specialize in {cat} markets.",
  "tradersCat.detail.seoCopy": "You can follow any trader to see their positions in real time, or set up copy trading to automatically mirror their trades.",

  // ───── /pits ─────
  // Discovery
  "pits.title": "Pits",
  "pits.intro": "Find your tribe. Share trades, calls and alpha inside communities built around shared interests.",
  "pits.create": "Create a pit",
  "pits.myPits": "My pits",
  "pits.searchPlaceholder": "Search pits…",
  "pits.sort.trending": "Trending",
  "pits.sort.mostMembers": "Most members",
  "pits.sort.newest": "Newest",
  "pits.section.searchResults": "Search results",
  "pits.section.trending": "Trending",
  "pits.section.mostPopular": "Most popular",
  "pits.section.newest": "Newest",
  "pits.empty.search": "No pits match your search",
  "pits.empty.title": "Nothing here yet",
  "pits.empty.desc": "Be the first to create a pit and start a community.",
  // Card
  "pits.card.private": "Private",
  "pits.card.noDescription": "No description.",
  // Image picker
  "pits.img.errImageOnly": "Image files only",
  "pits.img.errMaxSize": "Max {mb}MB",
  "pits.img.altAvatar": "avatar preview",
  "pits.img.altBanner": "banner preview",
  "pits.img.change": "Change",
  "pits.img.removeAvatar": "Remove avatar",
  "pits.img.removeBanner": "Remove banner",
  "pits.img.dropOrClick": "Drop or click",
  "pits.img.errSaveFirst": "Save the group first to upload images.",
  "pits.img.errOnlyImages": "Only image files are allowed",
  "pits.img.noAvatar": "No avatar yet",
  "pits.img.noBanner": "No banner yet",
  "pits.img.uploadAvatar": "Upload avatar",
  "pits.img.uploadBanner": "Upload banner",
  "pits.img.saveFirstHint": "Save the group first to enable uploads.",
  // Invite Accept
  "pits.invite.loading": "Loading…",
  "pits.invite.title": "You're invited",
  "pits.invite.signInDesc": "Sign in to accept the invite.",
  "pits.invite.signIn": "Sign in",
  "pits.invite.joining": "Joining the pit…",
  "pits.invite.success": "You're in. Redirecting…",
  "pits.invite.errFallback": "Could not accept invite",
  // Header
  "pits.header.errInviteOnly": "This pit is invite-only. Ask an admin for an invite link.",
  "pits.header.errCouldNotJoin": "Could not join",
  "pits.header.confirmLeave": "Leave {name}?",
  "pits.header.errCouldNotLeave": "Could not leave",
  "pits.header.altCover": "{name} cover",
  "pits.header.role.owner": "Owner",
  "pits.header.role.admin": "Admin",
  "pits.header.role.mod": "Mod",
  "pits.header.role.member": "Member",
  "pits.header.private": "Private",
  "pits.header.public": "Public",
  "pits.header.settings": "Settings",
  "pits.header.requestInvite": "Request invite",
  "pits.header.join": "Join pit",
  "pits.header.leave": "Leave",
  "pits.header.created": "Created {date}",
  "pits.header.member": "Member",
  "pits.header.members": "Members",
  "pits.header.post": "Post",
  "pits.header.posts": "Posts",
  "pits.header.access.invite": "Invite-only",
  "pits.header.access.open": "Open",
  "pits.header.access": "Access",
  // Shell tabs
  "pits.tab.posts": "Posts",
  "pits.tab.members": "Members",
  "pits.tab.about": "About",
  // Sidebar
  "pits.sidebar.topMarkets": "Top markets in this pit",
  "pits.sidebar.col.posts": "Posts",
  "pits.sidebar.post": "post",
  "pits.sidebar.posts": "posts",
  "pits.sidebar.vol24h": "{v} 24h vol",
  "pits.sidebar.activeMembers": "Active members",
  "pits.sidebar.noMembers": "No members yet.",
  "pits.sidebar.anonymous": "Anonymous",
  "pits.sidebar.postCount": "{n} {label}",
  "pits.sidebar.seeAll": "See all members",
  // About tab
  "pits.about.title": "About",
  "pits.about.noDesc": "No description provided.",
  "pits.about.stats": "Stats",
  "pits.about.details": "Details",
  "pits.about.visibility": "Visibility",
  "pits.about.visPrivateDesc": "Invite-only — only members can see posts",
  "pits.about.visPublicDesc": "Anyone can view; members can post",
  "pits.about.created": "Created",
  "pits.about.tags": "Tags",
  "pits.about.slug": "Slug",
  "pits.about.links": "Links",
  // Members tab
  "pits.members.empty": "No members yet",
  // Composer
  "pits.composer.placeholder": "Share something with the pit…",
  "pits.composer.posting": "Posting…",
  "pits.composer.post": "Post",
  // Members leaderboard
  "pits.lb.title": "Members",
  "pits.lb.descSortable": "Sortable by Polymarket trading stats",
  "pits.lb.descLoading": "Loading stats…",
  "pits.lb.totalSuffix": "{n} total",
  "pits.lb.col.member": "Member",
  "pits.lb.col.style": "Style",
  "pits.lb.col.volume": "Volume",
  "pits.lb.col.pnl": "PnL",
  "pits.lb.col.win": "Win",
  "pits.lb.col.trades": "Trades",
  "pits.lb.col.posts": "Posts",
  "pits.lb.col.joined": "Joined",
  "pits.lb.empty": "No members yet.",
  "pits.lb.anonymous": "Anonymous",
  // Posts tab
  "pits.posts.justNow": "just now",
  "pits.posts.mAgo": "{m}m ago",
  "pits.posts.hAgo": "{h}h ago",
  "pits.posts.dAgo": "{d}d ago",
  "pits.posts.errOnlyDomains": "Only links to {domains} are allowed",
  "pits.posts.confirmDelete": "Delete this post?",
  "pits.posts.errDeleteStatus": "Could not delete post ({status})",
  "pits.posts.errNetworkDelete": "Network error deleting post",
  "pits.posts.signInToVote": "Sign in to vote on posts",
  "pits.posts.wall": "Wall",
  "pits.posts.altYourAvatar": "Your avatar",
  "pits.posts.placeholder": "What's on your mind? Paste a market or event link to show a preview!",
  "pits.posts.eventMarkets": "Event · {n} markets",
  "pits.posts.marketPreview": "Market preview",
  "pits.posts.removePreview": "Remove preview",
  "pits.posts.moreMarkets": "+{n} more",
  "pits.posts.volPreview": "Vol {v}",
  "pits.posts.charCount": "{n}/500",
  "pits.posts.posting": "Posting…",
  "pits.posts.post": "Post",
  "pits.posts.signInToPost": "Sign in to post in this pit.",
  "pits.posts.joinPrivate": "Join this private pit to post (invite required).",
  "pits.posts.joinPublic": "Join this pit to post.",
  "pits.posts.empty.title": "No posts yet",
  "pits.posts.empty.beFirst": "Be the first to share something",
  "pits.posts.empty.joinToStart": "Join to start the conversation",
  "pits.posts.user": "User",
  "pits.posts.anonymous": "Anonymous",
  "pits.posts.deletePost": "Delete post",
  "pits.posts.sold": "Sold",
  "pits.posts.bought": "Bought",
  "pits.posts.sharePost": "Share post",
  // Create pit
  "pits.create.loading": "Loading…",
  "pits.create.gateTitle": "Create a pit",
  "pits.create.gateDesc": "Sign in to start your own pit on FrenFlow.",
  "pits.create.signIn": "Sign in",
  "pits.create.browseInstead": "Browse pits instead",
  "pits.create.errName": "Name must be at least 3 characters",
  "pits.create.errSlug": "Slug must be 3–64 chars (a–z, 0–9, hyphens) and start with a letter or number",
  "pits.create.progressCreating": "Creating pit…",
  "pits.create.progressUploadAvatar": "Uploading avatar…",
  "pits.create.progressUploadBanner": "Uploading banner…",
  "pits.create.progressUploadImages": "Uploading images…",
  "pits.create.progressDone": "Done. Redirecting…",
  "pits.create.title": "Create a pit",
  "pits.create.subtitle": "A space for your tribe to share trades, calls and ideas. The right side updates as you type.",
  "pits.create.section.identity": "Identity",
  "pits.create.field.name": "Name",
  "pits.create.placeholder.name": "e.g. Polymarket Whales",
  "pits.create.field.slug": "URL handle",
  "pits.create.placeholder.slug": "polymarket-whales",
  "pits.create.hint.slug": "3–64 chars · a–z, 0–9, hyphens",
  "pits.create.section.imagery": "Imagery",
  "pits.create.field.banner": "Banner",
  "pits.create.hint.banner": "3:1 ratio, max 10MB · JPG/PNG/WebP",
  "pits.create.upload": "Upload",
  "pits.create.field.avatar": "Avatar",
  "pits.create.hint.avatar": "Square, max 5MB · JPG/PNG/WebP",
  "pits.create.uploadAvatar": "Upload avatar",
  "pits.create.changeAvatar": "Change avatar",
  "pits.create.removeBtn": "Remove",
  "pits.create.section.description": "Description",
  "pits.create.field.description": "What's this pit about?",
  "pits.create.placeholder.description": "Whales swapping alpha on Polymarket. PnL screenshots welcome.",
  "pits.create.section.visibility": "Visibility",
  "pits.create.vis.public": "Public",
  "pits.create.vis.publicDesc": "Anyone can find and join",
  "pits.create.vis.private": "Private",
  "pits.create.vis.privateDesc": "Invite-only",
  "pits.create.section.tags": "Tags",
  "pits.create.field.tags": "Topics",
  "pits.create.hint.tags": "Comma-separated · max 10",
  "pits.create.placeholder.tags": "crypto, sports, whales",
  "pits.create.section.social": "Social links",
  "pits.create.submit": "Create pit",
  "pits.create.submitDefault": "Creating…",
  "pits.create.cancel": "Cancel",
  "pits.create.preview": "Preview",
  "pits.create.previewName": "Your pit",
  "pits.create.createdToday": "Created today",
  "pits.create.preview.member": "Member",
  "pits.create.preview.posts": "Posts",
  "pits.create.preview.access": "Access",
  // Settings
  "pits.settings.checkPerms": "Checking permissions…",
  "pits.settings.gateSignIn": "Sign in to manage this pit",
  "pits.settings.gateNoPerm": "You don't have permission to manage this pit",
  "pits.settings.nav.profile": "Profile",
  "pits.settings.nav.members": "Members",
  "pits.settings.nav.invites": "Invites",
  "pits.settings.nav.danger": "Danger zone",
  "pits.settings.backTo": "Back to {name}",
  "pits.settings.title": "Pit Settings",
  "pits.settings.subtitle": "Manage profile, members, invites and access for {name}.",
  "pits.settings.back": "Back",
  "pits.settings.uploading": "Uploading…",
  "pits.settings.editCover": "Edit cover",
  "pits.settings.removeCover": "Remove cover",
  "pits.settings.changeAvatar": "Change avatar",
  "pits.settings.imgHint": "Avatar: square, max 5MB · Cover: 3:1 ratio, max 10MB · JPG/PNG/WebP",
  "pits.settings.field.name": "Name",
  "pits.settings.visibility": "Visibility",
  "pits.settings.public": "Public",
  "pits.settings.private": "Private",
  "pits.settings.field.description": "Description",
  "pits.settings.placeholder.description": "What's this pit about?",
  "pits.settings.field.tags": "Tags",
  "pits.settings.placeholder.tags": "crypto, whales, alpha (comma separated, max 10)",
  "pits.settings.social": "Social links",
  "pits.settings.saved": "Saved",
  "pits.settings.saving": "Saving…",
  "pits.settings.save": "Save changes",
  "pits.settings.failed": "Failed",
  "pits.settings.confirmBan": "Ban this user from the pit?",
  "pits.settings.confirmRemove": "Remove this member?",
  "pits.settings.loadingMembers": "Loading members…",
  "pits.settings.makeAdmin": "Make admin",
  "pits.settings.mod": "Mod",
  "pits.settings.demote": "Demote",
  "pits.settings.ban": "Ban",
  "pits.settings.kick": "Kick",
  "pits.settings.invite.create": "Create invite link",
  "pits.settings.invite.maxUses": "Max uses (optional)",
  "pits.settings.invite.expiresHrs": "Expires in hours (optional)",
  "pits.settings.invite.never": "never",
  "pits.settings.invite.creating": "Creating…",
  "pits.settings.invite.createBtn": "Create",
  "pits.settings.invite.usesLabel": "{used}/{max} uses",
  "pits.settings.invite.expires": "expires {date}",
  "pits.settings.invite.copy": "Copy link",
  "pits.settings.invite.revoke": "Revoke",
  "pits.settings.invite.empty": "No invites yet.",
  "pits.settings.invite.confirmRevoke": "Revoke this invite?",
  "pits.settings.danger.title": "Delete this pit",
  "pits.settings.danger.desc": "This permanently removes the pit, all members, posts and invites. This cannot be undone.",
  "pits.settings.danger.typeToConfirm": "Type {slug} to confirm:",
  "pits.settings.danger.deleting": "Deleting…",
  "pits.settings.danger.delete": "Delete pit permanently",

  // ───── /claim/[shortCode] (FlowLinks) ─────
  // Countdown
  "claim.countdown.expired": "Expired",
  "claim.countdown.day": "Day",
  "claim.countdown.days": "Days",
  "claim.countdown.hour": "Hour",
  "claim.countdown.hours": "Hours",
  "claim.countdown.min": "Min",
  "claim.countdown.sec": "Sec",
  "claim.countdown.expiresIn": "Expires in",
  // Steps in claim animation
  "claim.steps.verifyingLink": "Verifying link",
  "claim.steps.transferring": "Transferring shares",
  "claim.steps.done": "Done!",
  // Errors
  "claim.err.verifyTasks": "Error verifying social tasks",
  "claim.err.linkNotFound": "Link not found",
  "claim.err.loading": "Error loading link",
  "claim.err.claiming": "Error claiming",
  "claim.err.processing": "Error processing claim",
  "claim.err.lotteryTimeout": "Lottery result timeout — try refreshing",
  // Phase titles
  "claim.title.tryLuck": "Try your luck",
  "claim.title.openGift": "Open your gift",
  "claim.title.claiming": "Claiming shares...",
  "claim.title.rollingPrize": "Rolling your prize...",
  "claim.title.drawing": "Drawing...",
  "claim.title.congrats": "🎉 Congrats Fren!",
  "claim.title.lost": "Not this time",
  "claim.title.notAvailable": "Link not available",
  "claim.goHome": "Go home",
  // Lottery / random hints
  "claim.lottery.rollingInfo": "Rolling 1-{odds}... Need a 1 to win",
  "claim.random.prizeRange": "Prize range: {min} — {max} shares",
  // Messages with creator name
  "claim.msg.receivedFrom": "You received from @{name}",
  "claim.msg.betterLuck": "Better luck next time",
  "claim.msg.givingAway": "Your fren @{name} is giving away",
  "claim.msg.sentYou": "Your fren @{name} sent you",
  "claim.polymarketShares": "Polymarket shares",
  // Badges
  "claim.lotteryBadge": "1 in {odds} chance",
  "claim.randomBadge": "Everyone wins — amount is random",
  "claim.estValue": "≈ ${amount} value",
  // Social tasks
  "claim.social.unlock": "Complete to unlock",
  "claim.social.counter": "{verified}/{total}",
  "claim.social.requiredVerify": "Required to verify tasks",
  "claim.social.connectPlatform": "Connect {platform}",
  "claim.social.connect": "Connect",
  "claim.social.follow": "Follow @{username}",
  "claim.social.repostUser": "Repost @{username}'s post",
  "claim.social.repost": "Repost this post",
  "claim.social.likeUser": "Like @{username}'s post",
  "claim.social.like": "Like this post",
  "claim.social.joinGuild": "Join {name}",
  "claim.social.joinDiscord": "Join Discord server",
  "claim.social.joinTelegramTitle": "Join {title}",
  "claim.social.joinTelegram": "Join Telegram group",
  "claim.social.doIt": "Do it",
  "claim.social.verifying": "Verifying...",
  "claim.social.verifyAll": "Verify all",
  // Result CTAs
  "claim.cta.viewPortfolio": "View your portfolio",
  // Type cards
  "claim.lottery.label": "Lottery",
  "claim.lottery.chance": "{n}% chance",
  "claim.random.label": "Random prize",
  "claim.random.range": "{min} — {max} shares",
  // Spots progress
  "claim.spots.claimed": "Spots claimed",
  "claim.spots.counter": "{claimed}/{max}",
  "claim.spots.left": "{n} left",
  // Status
  "claim.status.expired": "This link has expired",
  "claim.status.unavailable": "This link is no longer available",
  "claim.status.noShares": "No shares remaining",
  // Bottom button
  "claim.btn.connectToClaim": "Connect to claim",
  "claim.btn.notAvailable": "Not available",
  "claim.btn.enterLottery": "Enter lottery",
  "claim.btn.openPrize": "Open your prize",
  "claim.btn.claimShares": "Claim shares",
  "claim.btn.entering": "Entering...",
  "claim.btn.processing": "Processing...",
  // Lottery animation
  "claim.lottery.drawing": "Drawing...",
  "claim.lottery.revealing": "Revealing...",
  "claim.lottery.oddsInfo": "1 in {odds} chance ({pct}%)",
  "claim.lottery.youWon": "You won!",
  "claim.lottery.sharesYours": "{n} shares are yours",
  "claim.lottery.notThisTime": "Not this time",
  "claim.lottery.betterLuck": "Better luck next time",

  // ───── /coming-soon ─────
  "comingSoon.tagline": "Prediction markets go harder with frens.",
  "comingSoon.label": "Coming Soon",
  "comingSoon.successTitle": "You're in!",
  "comingSoon.formTitle": "Join the Waitlist",
  "comingSoon.formDesc": "Be the first to trade with your frens",
  "comingSoon.emailPlaceholder": "Enter your email",
  "comingSoon.acceptIntro": "I accept the",
  "comingSoon.privacyPolicy": "Privacy Policy",
  "comingSoon.acceptAnd": "and",
  "comingSoon.terms": "Terms",
  "comingSoon.submit": "Get Early Access",
  "comingSoon.joining": "Joining...",
  "comingSoon.noSpam": "No spam, ever. Unsubscribe anytime.",
  "comingSoon.followX": "Follow us on X",
  "comingSoon.joinTg": "Join Telegram announcements",
  "comingSoon.adminAccess": "Admin access",
  "comingSoon.password": "Password",
  "comingSoon.enter": "Enter",
  "comingSoon.errInvalid": "Invalid password",
  "comingSoon.errGeneric": "Something went wrong",

  // ───── /gate ─────
  "gate.title": "Restricted",
  "gate.desc": "Enter the access password to continue.",
  "gate.placeholder": "Password",
  "gate.submit": "Enter",
  "gate.invalid": "Invalid password",
  "gate.stagingTitle": "Staging Access",
  "gate.stagingDesc": "Enter password to continue",
  "gate.verifying": "Verifying...",
  "gate.tooMany": "Too many attempts. Try again later.",
  "gate.wrong": "Wrong password",
  "gate.errGeneric": "Something went wrong",

  // ───── /telegram/miniapp ─────
  "tgmini.tagline": "Trade smarter with your frens",
  "tgmini.initializing": "Initializing...",
  "tgmini.connectingPrivy": "Connecting to Privy...",
  "tgmini.linkingAccount": "Linking your account...",
  "tgmini.statusLine": "Status: {s}",
  "tgmini.connected": "Connected!",
  "tgmini.linkedSuccess": "Account linked successfully",
  "tgmini.returnToBot": "Return to Bot",
  "tgmini.checkingStatus": "Checking Status",
  "tgmini.verifyingSetup": "Verifying your trading setup...",
  "tgmini.tradingEnabled": "Trading Enabled!",
  "tgmini.allSetTrade": "You're all set to start trading.",
  "tgmini.settingUpTrading": "Setting Up Trading",
  "tgmini.pleaseWait": "Please wait...",
  "tgmini.creatingWallet": "Creating wallet",
  "tgmini.registeringSigner": "Registering signer",
  "tgmini.readyToTrade": "Ready to trade",
  "tgmini.setupFailed": "Setup Failed",
  "tgmini.tryAgain": "Try Again",
  "tgmini.completeSetup": "Complete Setup",
  "tgmini.completeSetupDesc": "Your wallet is ready, but we need to finish setting up trading credentials.",
  "tgmini.enableTrading": "Enable Trading",
  "tgmini.enableTradingDesc": "Complete one-time setup to start trading on Polymarket.",
  "tgmini.gaslessSetup": "Gasless setup (we pay fees)",
  "tgmini.securePrivy": "Secure Privy TEE signing",
  "tgmini.takes30s": "Takes ~30 seconds",
  "tgmini.fundCard": "Fund with Card",
  "tgmini.fundCardDesc": "Buy USDC directly with your credit or debit card.",
  "tgmini.instantPurchase": "Instant purchase",
  "tgmini.sentToWallet": "Sent to your wallet on Polygon",
  "tgmini.poweredPrivy": "Powered by Privy",
  "tgmini.buyUsdc": "Buy USDC",
  "tgmini.close": "Close",
  "tgmini.logout": "Logout",
  "tgmini.logoutDesc": "Are you sure you want to log out? You'll need to log in again to use FrenFlow.",
  "tgmini.confirmLogout": "Confirm Logout",
  "tgmini.cancel": "Cancel",
  "tgmini.loggedOut": "Logged Out",
  "tgmini.loggedOutDesc": "You've been signed out successfully.",
  "tgmini.openingWallet": "Opening wallet settings...",
  "tgmini.errorTitle": "Error",
  "tgmini.openFromTg": "Please open this from Telegram",
  "tgmini.debugLog": "Debug Log ({n} entries)",

  // ── Profile widget (logged-out hero card) ──────────────────────────
  "profileWidget.liveBeta": "Live · Open beta",
  "profileWidget.headlineTop": "The new home of",
  "profileWidget.headlineBottom": "prediction markets.",
  "profileWidget.kpi": "Trade Polymarket & Kalshi · Copy top traders in <1s · Self-custody.",
  "profileWidget.cta": "Get started",
  "profileWidget.trust": "Privy-secured",

  // ── Price Alert modal ──────────────────────────────────────────────
  "priceAlert.title": "Price Alert",
  "priceAlert.loadingAlerts": "Loading alerts...",
  "priceAlert.activeAlerts": "Active Alerts ({count})",
  "priceAlert.createNewAlert": "Create New Alert",
  "priceAlert.outcome": "Outcome",
  "priceAlert.yes": "Yes",
  "priceAlert.no": "No",
  "priceAlert.alertWhen": "Alert when price goes",
  "priceAlert.above": "Above",
  "priceAlert.below": "Below",
  "priceAlert.targetPrice": "Target Price",
  "priceAlert.helperAbove": "Current: {current}¢ · Range: {min}¢ - 99¢",
  "priceAlert.helperBelow": "Current: {current}¢ · Range: 1¢ - {max}¢",
  "priceAlert.errorRange": "Price must be between 1¢ and 99¢",
  "priceAlert.errorTargetAbove": "Target must be above current price ({current}¢)",
  "priceAlert.errorTargetBelow": "Target must be below current price ({current}¢)",
  "priceAlert.errorCreate": "Failed to create alert",
  "priceAlert.errorTimeout": "Request timed out. Please try again.",
  "priceAlert.success": "Alert created! You'll be notified when the price reaches your target.",
  "priceAlert.creating": "Creating...",
  "priceAlert.createAlert": "Create Alert",

  // ── Slippage warning (shared, market + sports) ─────────────────────
  "trade.slippageWarn.title": "High slippage tolerance",
  "trade.slippageWarn.body": "Your {pct}% setting lets this order fill at any price up to {maxCents}¢, well above the {shownCents}¢ shown.",
  "trade.slippageWarn.cta": "Reduce in Settings",

  // ── Trading Panel (sidebar) ────────────────────────────────────────
  "tradingPanel.placeTrade": "Place Trade",
  "tradingPanel.resolution": "Resolution",
  "tradingPanel.modeMarket": "Market",
  "tradingPanel.modeLimit": "Limit",
  "tradingPanel.tradeSettings": "Trade settings",
  "tradingPanel.slippageSettings": "Slippage settings",
  "tradingPanel.slippageAutoHint": "Slippage adapts per trade to market liquidity. May reach 50% on low-liquidity markets.",
  "tradingPanel.slippage": "Slippage",
  "tradingPanel.slippageCustom": "Custom",
  "tradingPanel.slippageCustomAria": "Custom slippage percent",
  "tradingPanel.slippageError": "Failed to update slippage",
  "tradingPanel.slippageHint.polymarket": "Max price deviation on market orders before rejecting the fill. Higher = more chance of filling on volatile markets.",
  "tradingPanel.slippageHint.hyperliquid": "Max upward price drift on HIP-4 IOC orders before HL rejects the fill. Higher = more chance of filling on volatile spreads.",
  "tradingPanel.slippageHint.kalshi": "Max price deviation on Kalshi market orders before the swap reverts. Higher = more chance of filling on thin books.",
  "tradingPanel.slippageHint.predict": "Max price deviation accepted on Predict market orders before the order rejects.",
  "tradingPanel.orderType": "Order Type",
  "tradingPanel.orderTypeError": "Failed to update order type",
  "tradingPanel.fak.title": "Fill and Kill",
  "tradingPanel.fak.desc": "Fill what's available now, cancel the rest.",
  "tradingPanel.fok.title": "Fill or Kill",
  "tradingPanel.fok.desc": "Fill the whole order or cancel entirely.",

  // ── Amount Field ───────────────────────────────────────────────────
  "amountField.amount": "Amount",
  "amountField.amountSlider": "Amount slider",
  "amountField.max": "Max",
  "amountField.editQuickAmounts": "Edit quick amounts",
  "amountField.needMoreFunds": "Need more funds — click to see details",
  "amountField.notEnoughShares": "Not enough shares",
  "amountField.notEnoughSharesDesc": "You only own {max} shares. Capping at {target}.",
  "amountField.thisAmount": "this amount",
  "amountField.sharesUnit": "{n} shares",
  "amountField.exceedsPosition": "Exceeds your position · max {max} shares",
  "amountField.insufficientBalance": "Insufficient balance · need ${diff} more",
  "amountField.balanceTooltip": "Balance: ${max}",
  "amountField.sharesToSell": "Shares to sell",
  "amountField.sharesUnitLabel": "Shares",
  "amountField.onlyAvailable": "Only {n} shares available",
  "positionImpact.small": "Small ticket",
  "positionImpact.reasonable": "Reasonable size",
  "positionImpact.large": "Large for your balance",
  "positionImpact.major": "Major position — sure?",

  // ── Profit Fantasy ─────────────────────────────────────────────────
  "profitFantasy.youReceive": "You'll receive",
  "profitFantasy.potentialWin": "Potential win",
  "profitFantasy.moonshot": "Moonshot",

  // ── Smart Money inline pill ────────────────────────────────────────
  // pillLabel/pillWallets are the compact strings used inside the trade
  // panel inline pill — kept distinct from smartMoney.label/.wallets
  // (full "Smart Money" / "Smart wallets: {count}") to avoid duplicate
  // keys when both the badge and the pill render in the same locale.
  "smartMoney.pillLabel": "Smart $",
  "smartMoney.pillWallets": "wallets",
  "smartMoney.flip": "Flip →",
  "smartMoney.match": "Match ${n}",

  // ── Live Trade Ticker ──────────────────────────────────────────────
  "liveTicker.bought": "bought",
  "liveTicker.sold": "sold",
  "liveTicker.anon": "anon",
  "liveTicker.agoSeconds": "{n}s ago",
  "liveTicker.agoMinutes": "{n}m ago",
  "liveTicker.agoHours": "{n}h ago",

  // ── Sell Context ───────────────────────────────────────────────────
  "sellContext.dontOwn": "You don't own any {outcome} shares on this market",
  "sellContext.youOwn": "You own",

  // ── Primary CTA ────────────────────────────────────────────────────
  "primaryCTA.marketClosed": "Market closed",
  "primaryCTA.signIn": "Sign in",
  "primaryCTA.placingOrder": "Placing order…",
  "primaryCTA.retry": "Retry",
  "trade.polymarketIssue": "Polymarket is having issues right now — orders may be slow or fail. Your funds are safe (nothing was charged). Try again in a few minutes.",
  "trade.polymarketStatusLink": "Polymarket status ↗",
  "primaryCTA.enterAmount": "Enter an amount",
  "primaryCTA.setLimitPrice": "Set a limit price",
  "primaryCTA.exceedsPosition": "Exceeds position",
  "primaryCTA.addMore": "Add ${diff} more",
  "primaryCTA.limitPrefix": "Limit ",
  "primaryCTA.sellAction": "Sell",
  "primaryCTA.buyAction": "Buy",

  // ── My Positions ───────────────────────────────────────────────────
  "myPositions.title": "My Positions",
  "myPositions.startTrading": "Start Trading",
  "myPositions.noPositions": "No positions yet",
  "myPositions.invested": "Invested",
  "myPositions.value": "Value",
  "myPositions.resolving": "Resolving",
  "myPositions.waitingSettlement": "Waiting for settlement",
  "myPositions.readyToClaim": "+${n} ready to claim",
  "myPositions.claim": "Claim",
  "myPositions.won": "Won",
  "myPositions.lost": "Lost",
  "myPositions.pending": "Pending",
  "myPositions.sell": "Sell",
  "myPositions.share": "Share",
  "myPositions.net": "Net",
  "myPositions.shares": "Shares",
  "myPositions.pnl": "PnL",
  "myPositions.cost": "Cost ${n}",

  // ── Open Orders ────────────────────────────────────────────────────
  "openOrders.title": "Open Orders",
  "openOrders.cancelling": "Cancelling...",
  "openOrders.cancelAll": "CANCEL ALL",
  "openOrders.noOpen": "No open orders",
  "openOrders.col.market": "Market",
  "openOrders.col.side": "Side",
  "openOrders.col.outcome": "Outcome",
  "openOrders.col.price": "Price",
  "openOrders.col.filled": "Filled",
  "openOrders.col.total": "Total",
  "openOrders.cancelOrder": "Cancel order",
  "openOrders.earningRewards": "This order is currently earning Polymarket liquidity rewards",
  "openOrders.validUntilCancelled": "All orders are valid until cancelled",

  // ── Scenario Bar ───────────────────────────────────────────────────
  "scenarioBar.ifYes": "If YES",
  "scenarioBar.ifNo": "If NO",

  // ── Sports Event page ──────────────────────────────────────────────
  "sportsEvent.loadingChart": "Loading chart...",
  "sportsEvent.live": "Live",
  "sportsEvent.final": "Final",
  "sportsEvent.livestream": "Livestream",
  "sportsEvent.notHeadToHead": "This market isn't a head-to-head match.",
  "sportsEvent.whale": "WHALE",
  "sportsEvent.smart": "SMART",
  "sportsEvent.viewOnPolymarket": "View on Polymarket",
  "sportsEvent.watchStream": "Watch Stream",
  "sportsEvent.killTotals": "Kill Totals",
  "sportsEvent.totalGames": "Total",
  "sportsEvent.setPriceAlert": "Set price alert",
  "sportsEvent.alert": "Alert",

  // ── Sports Order Book ──────────────────────────────────────────────
  "sportsOrderBook.title": "Order Book",
  "sportsOrderBook.mid": "Mid",
  "sportsOrderBook.midLabel": "MID",
  "sportsOrderBook.spread": "Spread",
  "sportsOrderBook.colPrice": "Price",
  "sportsOrderBook.colSize": "Size",
  "sportsOrderBook.colTotal": "Total ($)",
  "sportsOrderBook.noAsks": "No asks",
  "sportsOrderBook.noBids": "No bids",

  // ── Sports Hero ────────────────────────────────────────────────────
  "sportsHero.vol": "Vol",
  "sportsHero.liquidity": "Liquidity",
  "sportsHero.whaleTooltip": "Whale market: {wallets} smart wallets · ${vol}M smart volume",
  "sportsHero.smartTooltip": "Smart-heavy market: {wallets} smart wallets tracking this",
  "sportsHero.kalshiAlsoExact": "Also trading on Kalshi (exact match)",
  "sportsHero.kalshiAlsoRelated": "Also trading on Kalshi (related)",
  "sportsHero.kalshiMore": "+{n} more",
  "sportsHero.addWatchlist": "Add to watchlist",
  "sportsHero.removeWatchlist": "Remove from watchlist",
  "sportsHero.week": "Week {n}",
  "sportsHero.vs": "VS",
  "sportsHero.liveBadge": "LIVE",
  "sportsHero.setLabel": "Set {n}: {score}",
  "sportsHero.setCurrent": "Set {n}",
  "sportsHero.sets": "Sets {home}–{away}",
  "sportsHero.game": "Game {n}",
  "sportsHero.bestOf": "Best of {n}",
  "sportsHero.map": "Map {home}–{away}",
  "sportsHero.series": "Series {home}–{away}",
  "sportsHero.gameWord": "Game",
  "sportsHero.mapWord": "Map",

  // ── Period long names (sports) ────────────────────────────────────
  "period.FT": "Full Time",
  "period.VFT": "Full Time (after ET)",
  "period.HT": "Half Time",
  "period.ET": "Extra Time",
  "period.AET": "After Extra Time",
  "period.PEN": "Penalties",
  "period.Q1": "1st Quarter",
  "period.Q2": "2nd Quarter",
  "period.Q3": "3rd Quarter",
  "period.Q4": "4th Quarter",
  "period.OT": "Overtime",
  "period.1H": "1st Half",
  "period.2H": "2nd Half",
  "period.P1": "1st Period",
  "period.P2": "2nd Period",
  "period.P3": "3rd Period",

  // ── Sports Market Labels (cleanMarketLabel) ───────────────────────
  "marketLabel.moneyline": "Moneyline",
  "marketLabel.totalGames": "Total Games",
  "marketLabel.totalMaps": "Total Maps",
  "marketLabel.totalPrefix": "Total {what}",
  "marketLabel.mapHandicap": "Map Handicap",
  "marketLabel.gameHandicap": "Game Handicap",
  "marketLabel.spread": "Spread",
  "marketLabel.spreadValue": "Spread {value}",
  "marketLabel.totalCorners": "Total Corners",
  "marketLabel.firstBlood": "First Blood",
  "marketLabel.killTotalsLabel": "Kill Totals",
  "marketLabel.killTotalsValue": "Kill Totals {value}",
  "marketLabel.bothTeamsToScore": "Both Teams to Score",
  "marketLabel.bothTeamsBaron": "Both Teams Slay Baron",
  "marketLabel.bothTeamsDragon": "Both Teams Slay Dragon",
  "marketLabel.bothTeamsInhibitors": "Both Teams Destroy Inhibitors",
  "marketLabel.oddEvenKills": "Odd/Even Total Kills",
  "marketLabel.pentaKill": "Any Player Penta Kill",
  "marketLabel.quadraKill": "Any Player Quadra Kill",
  "marketLabel.firstSetWinner": "Set 1 Winner",
  "marketLabel.secondSetWinner": "Set 2 Winner",
  "marketLabel.thirdSetWinner": "Set 3 Winner",
  "marketLabel.setWinner": "Set Winner",
  "marketLabel.matchWinner": "Match Winner",
  "marketLabel.gameWinner": "Game Winner",
  "marketLabel.gameNWinner": "Game {n} Winner",
  "marketLabel.mapNWinner": "Map {n} Winner",
  "marketLabel.nrfi": "No run in 1st inning",
  "marketLabel.yrfi": "Run in 1st inning",
  "marketLabel.oddEvenRounds": "Odd/Even Total Rounds",

  // ── Sidebar OrderBook (market/[slug]/components/OrderBook) ────────
  "orderBook.title": "Order Book",
  "orderBook.loading": "Loading orderbook...",
  "orderBook.trade": "Trade {label}",
  "orderBook.colPrice": "Price",
  "orderBook.colShares": "Shares",
  "orderBook.colTotal": "Total",
  "orderBook.asks": "Asks",
  "orderBook.bids": "Bids",
  "orderBook.last": "Last:",
  "orderBook.spread": "Spread:",
  "orderBook.levels": "{n} levels",
  "orderBook.rewardsBand": "rewards band",
  "orderBook.rewardsBandTooltip": "Rewards band {low}¢ – {high}¢ · ${rate}/day pool",
  "orderBook.liveUpdates": "Live updates",

  // ── Player Profile (sports/players/[league]/[slug]) ───────────────
  "playerProfile.back": "Back",
  "playerProfile.live": "Live",
  "playerProfile.vs": "vs",
  "playerProfile.unknown": "Unknown",
  "playerProfile.hot": "HOT",
  "playerProfile.hotTooltip": "Competitive {pct}/100 — close to 50/50, expect heavy action",
  "playerProfile.playerCountOne": "{n} player",
  "playerProfile.playerCountMany": "{n} players",
  "playerProfile.tile.tradingVolume": "Trading Volume",
  "playerProfile.tile.activeMarkets": "Active Markets",
  "playerProfile.tile.winRate": "Win Rate",
  "playerProfile.tile.record": "Record",
  "playerProfile.tile.nationality": "Nationality",
  "playerProfile.ai.heading": "Matchup analysis",
  "playerProfile.ai.readMore": "Read full breakdown",
  "playerProfile.ai.hide": "Hide full breakdown",
  "playerProfile.ai.disclaimer": "Generated from on-chain trades, recent results and h2h history. Not financial advice.",
  "playerProfile.matchHistory.title": "Match History",
  "playerProfile.matchHistory.colDate": "Date",
  "playerProfile.matchHistory.colMatch": "Match",
  "playerProfile.matchHistory.colScore": "Score",
  "playerProfile.matchHistory.colTournament": "Tournament",
  "playerProfile.upcoming.title": "Upcoming",
  "playerProfile.upcoming.empty": "No upcoming matches scheduled.",
  "playerProfile.more.title": "More {league}",
  "playerProfile.roster.title": "Player Roster",
  "playerProfile.roster.active": "{n} active",
  "playerProfile.roster.colPlayer": "Player",
  "playerProfile.roster.colName": "Name",
  "playerProfile.roster.colRole": "Role",
  "playerProfile.roster.colCountry": "Country",
  "playerProfile.showLess": "Show less",
  "playerProfile.loadMore": "Load more ({n})",
  "playerProfile.time.inMinutes": "In {n}m",
  "playerProfile.time.inHours": "In {n}h",
  "playerProfile.time.today": "Today {time}",
  "playerProfile.time.tomorrow": "Tomorrow {time}",
  "playerProfile.about.title": "About {name}",
  "playerProfile.about.p1": "{name} {ticker} is a professional {league} player tracked on FrenFlow with live odds, head-to-head context, and AI-aided matchup insights pulled from on-chain prediction markets.",
  "playerProfile.about.p2": "Track {name}'s next match, recent results, and crowd-sourced odds on this page. FrenFlow aggregates live data from Polymarket alongside our own AI scoring so the prices reflect what the market actually thinks — not just static bookie lines.",
  "playerProfile.faq.title": "Frequently asked questions",
  "playerProfile.faq.q1": "What {league} markets are available for {name} on FrenFlow?",
  "playerProfile.faq.a1": "Every match {name} plays in opens a moneyline (winner) and a set of derived markets — set winners, total games, exact-score and over/under lines. All of them resolve on the same on-chain Polymarket contract, so the prices stay tight and trades clear instantly.",
  "playerProfile.faq.q2": "How do {league} prediction markets work on FrenFlow?",
  "playerProfile.faq.a2": "Each market is a binary \"Yes/No\" or \"Over/Under\" pair. You buy shares of the side you think will resolve true; if you're right each share pays out $1, and the difference between your buy price and the payout is your profit. Liquidity sits in shared on-chain orderbooks, so the price you see is the price you trade.",
  "playerProfile.faq.q3": "How are {name} markets resolved?",
  "playerProfile.faq.a3": "Markets resolve based on official {league} results. The winning side pays out automatically once the match is final and the resolution source confirms the outcome — no claims, no deadline gymnastics.",
  "playerProfile.faq.q4": "Why are FrenFlow's {league} odds reliable?",
  "playerProfile.faq.a4": "Because real money is on the line. Crowd-sourced traders adjust prices in real time as news breaks — injuries, surface changes, momentum shifts. FrenFlow surfaces those prices alongside an AI matchup analysis so you can sanity-check the market against the underlying signals.",
  "playerProfile.faq.q5": "What factors influence {name}'s market odds?",
  "playerProfile.faq.a5": "Recent form, head-to-head history, surface preference, fitness, draw difficulty and live news (retirements, walkovers, weather). Each new piece of information moves the orderbook within seconds.",
  "playerProfile.faq.q6": "Can I follow {name} odds and results without trading?",
  "playerProfile.faq.a6": "Yes — you can watchlist any market with the bookmark button and we'll keep the prices and results visible without you placing a single trade. Set price alerts from the trading modal if you want to be notified when an outcome hits a target.",
  "playerProfile.faq.q7": "How do I start trading on {name} matches?",
  "playerProfile.faq.a7": "Connect a wallet, deposit USDC into your FrenFlow trading account, and tap any outcome on this page or on the match page. The trading modal handles approvals automatically — you don't need to leave the app.",
  "playerProfile.faq.q8": "What does a price of 65¢ for \"{ticker}\" mean?",
  "playerProfile.faq.a8": "The market is pricing a 65% implied probability that {ticker} wins the resolved outcome. Buy at 65¢, win 35¢ if it resolves your way, lose 65¢ if it doesn't. The market is the consensus probability across every active trader.",
  "playerProfile.faq.q9": "What is FrenFlow?",
  "playerProfile.faq.a9": "FrenFlow is a social prediction-market platform built on top of Polymarket. We bring the deepest on-chain liquidity, copy-trading, AI matchup analysis and a clean trading UX into one place — for sports, crypto, politics and culture.",

  // ── Wallet onboarding + migration + withdraw modals ───────────────
  "onboarding.close": "Close",
  "onboarding.setup.title": "Setting up your wallet",
  "onboarding.setup.waitingForWallet": "Provisioning your secure wallet...",
  "onboarding.setup.fallbackMsg": "Just a moment…",
  "onboarding.setup.step.creatingWallet": "Creating wallet",
  "onboarding.setup.step.authorizing": "Authorizing",
  "onboarding.setup.step.finalTouches": "Final touches",
  "onboarding.fund.title": "Wallet ready",
  "onboarding.fund.subtitle": "Your trading wallet — add funds to start.",
  "onboarding.fund.acceptedTokens": "Polygon network only. Send USDC / USDC.e / pUSD.\nOther tokens or networks will be lost forever.",
  "onboarding.fund.addressLabel": "Your address",
  "onboarding.fund.copy": "Copy address",
  "onboarding.fund.copied": "Copied",
  "onboarding.fund.methodsLabel": "Several ways to fund your wallet — pick one:",
  "onboarding.fund.haveCrypto": "Already have crypto? Receive or bridge",
  "onboarding.fund.haveCryptoTitle": "Already have crypto?",
  "onboarding.fund.haveCryptoSub": "Receive or bridge",
  "onboarding.fund.networkWarn": "Polygon network only.",
  "onboarding.fund.networkWarnRest": "Sending from Ethereum, BSC, Arbitrum or any other network = funds lost permanently.",
  "onboarding.fund.waiting": "Waiting for funds…",
  "onboarding.fund.fundLater": "I'll fund later",
  "onboarding.fund.intentLeader": "You're following {name} — fund your wallet to copy their next trade.",
  "onboarding.fund.intentMarket": "Fund your wallet to take your position on “{name}”.",
  "onboarding.funded.title": "You're funded.",
  "onboarding.funded.amountReady": "{amount} ready to trade.",
  "onboarding.funded.cta1": "Make your first prediction.",
  "onboarding.funded.browse": "Browse markets",
  "onboarding.funded.stay": "Stay here",
  "onboarding.error.title": "Setup failed",
  "onboarding.error.fallback": "Something went wrong. Try again?",
  "onboarding.error.tryAgain": "Try again",
  "onboarding.error.reportIssue": "Report this issue",
  "onboarding.error.persistentHint": "If this keeps failing, reload the page and let us know via feedback.",

  "migration.intro.title": "Wallet upgrade",
  "migration.intro.subtitle": "Polymarket has upgraded the wallet system. We'll set up your new wallet and move your funds in 30 seconds.",
  "migration.intro.note": "Open positions stay in your current wallet — you can sell/redeem them as usual. Only cash gets moved.",
  "migration.intro.cta": "Start migration",
  "migration.intro.helper": "Required to keep trading. Gasless, takes ~30 seconds.",
  "migration.deploying.title": "Setting up your new wallet",
  "migration.deploying.fallbackMsg": "Just a moment…",
  "migration.deploying.dontClose": "Don't close this tab.",
  "migration.confirm.title": "New wallet ready",
  "migration.confirm.subtitleWithCash": "Move your {amount} to your new wallet to continue trading.",
  "migration.confirm.subtitleEmpty": "Your Safe is empty — finish migration to start trading.",
  "migration.confirm.row.pusd": "pUSD",
  "migration.confirm.row.usdce": "USDC.e",
  "migration.confirm.row.total": "Total to move",
  "migration.confirm.newWalletLabel": "Your new wallet",
  "migration.confirm.copy": "Copy address",
  "migration.confirm.copied": "Copied",
  "migration.confirm.warn": "Open positions stay in your current wallet (you can sell/redeem them normally). Only cash moves now.",
  "migration.confirm.ctaMove": "Move {amount} to new wallet",
  "migration.confirm.ctaComplete": "Complete migration",
  "migration.moving.title": "Moving your funds",
  "migration.moving.subtitle": "Transferring on-chain — about 30 seconds.",
  "migration.moving.dontClose": "Don't close this tab.",
  "migration.done.title": "Migration complete",
  "migration.done.amountReady": "{amount} ready in your new wallet.",
  "migration.done.emptyReady": "Your new wallet is ready. Trade as usual.",
  "migration.done.continue": "Continue",
  "migration.error.title": "Migration failed",
  "migration.error.fallback": "Something went wrong. Try again?",
  "migration.error.safeNote": "Your funds are safe — they're still in your original wallet.",
  "migration.error.tryAgain": "Try again",

  "polyWithdraw.title": "Withdraw from Polymarket",
  "polyWithdraw.submitted.title": "Withdrawal submitted",
  "polyWithdraw.submitted.subtitle": "USDC.e is on its way. Confirmation in ~30 seconds on Polygon.",
  "polyWithdraw.viewTx": "View on Polygonscan ↗",
  "polyWithdraw.intro": "Sends USDC.e from your trading wallet to any Polygon address. If you have pUSD it auto-unwraps in the same transaction. Lands in ~30 seconds.",
  "polyWithdraw.feeLine.lead": "FrenFlow takes 0%",
  "polyWithdraw.feeLine.rest": "on withdrawals. Network gas is paid by the relayer (gasless for you).",
  "polyWithdraw.row.usdce": "USDC.e (loose)",
  "polyWithdraw.row.pusd": "pUSD (auto-unwraps)",
  "polyWithdraw.row.available": "Available to withdraw",
  "polyWithdraw.fields.destLabel": "Destination on Polygon",
  "polyWithdraw.fields.destHelp": "Defaults to your Privy EOA. Override to send to a CEX or another wallet.",
  "polyWithdraw.fields.amountLabel": "Amount (USD)",
  "polyWithdraw.fields.max": "Max ({amount})",
  "polyWithdraw.fields.amountHelp": "Min $1 · Max $5,000 per withdrawal · $10,000 daily limit",
  "polyWithdraw.submitting": "Submitting…",
  "polyWithdraw.submittingMsg": "Submitting withdrawal…",
  "polyWithdraw.cta": "Withdraw",
  "polyWithdraw.error.positive": "Enter a positive amount",
  "polyWithdraw.error.min": "Minimum withdrawal is $1",
  "polyWithdraw.error.max": "Maximum withdrawal is $5,000 per transaction",
  "polyWithdraw.error.invalidDest": "Invalid destination address",
  "polyWithdraw.error.balanceNotLoaded": "Balance not loaded yet — try again",
  "polyWithdraw.error.maxWithdrawable": "Max withdrawable is {amount}",
  "polyWithdraw.error.fallback": "Withdrawal failed ({status})",
  "market.chance": "chance",
  "market.outcome": "Outcome",
  "market.volume": "Volume",
  "market.liquidity": "Liquidity",
  "market.buy": "Buy",
  "market.goToLiveMarket": "Go to live market",
  "placeTrade.resolvedWinner": "Resolved · Winner",
  "placeTrade.redeemPayout": "Holders of this side can redeem their shares for $1.00 each.",
  "placeTrade.awaitingResolution": "Awaiting resolution",
  "placeTrade.verifyingOutcome": "Verifying outcome",
  "placeTrade.verifyingOutcomeDesc": "Trading is closed. The resolution source is determining the winner — this usually settles within a minute.",
  "chartError.failedToLoad": "Failed to load {chart}",
  "chartError.failedToLoadGeneric": "Chart failed to load",
  "chartError.retry": "Retry",
  "chartError.pnl": "P&L Chart",
  "chartError.traderPnl": "Trader P&L Chart",
  "common.loadingChart": "Loading chart...",
  "common.loadingTrades": "Loading trades...",
  "common.loadingOrderbook": "Loading orderbook...",
  "common.live": "Live",
  "common.view": "View",
  "common.viewAll": "View all",
  "common.viewAllOutcomes": "View all outcomes",
  "marketHero.resolved": "RESOLVED",
  "marketHero.active": "ACTIVE",
  "marketStatus.resolved": "RESOLVED",
  "marketChart.priceChart": "Price Chart",
  "marketChart.priceHistory": "Price History",
  "marketChart.historical": "Historical",
  "marketResolution.rules": "Resolution Rules",
  "marketResolution.umaResolution": "UMA Resolution",
  "rewardsBadge.youEarnedToday": "You earned today",
  "rewardsBadge.makerRebatePortion": "Maker rebate portion",
  "rewardsBadge.placeLimitOrderHint": "Place a limit order inside the spread band to start earning.",
  "tradingPanel.tradeError": "Trade Error",
  "tradingPanel.oneClickHint": "Trade with one click, no modals",
  "portfolio.balance.available": "Available",
  "portfolio.balance.open": "Open",
  "portfolio.balance.claim": "Claim",
  "portfolio.balance.total": "Total Balance",
  "portfolio.balance.inPositions": "In Positions",
  "portfolio.balance.toClaim": "To Claim",
  "activity.categories": "Categories",
  "activity.loadingMore": "Loading more...",
  "activity.tradeCall.title": "New Trade Call",
  "activity.tradeCall.subtitle": "Share your alpha with the community",
  "activity.shareToEarn.title": "Share to Earn",
  "activity.shareToEarn.newBadge": "New",
  "activity.shareToEarn.earn": "Earn",
  "activity.shareToEarn.ofFees": "of fees",
  "activity.shareToEarn.tagline.before": "When someone trades a market through",
  "activity.shareToEarn.tagline.yourPost": "your post",
  "activity.shareToEarn.tagline.after": ", you collect.",
  "activity.shareToEarn.step1": "Post a trade or thesis on any market",
  "activity.shareToEarn.step2": "Frens discover and trade through your post",
  "activity.shareToEarn.step3": "You collect 10% of every fee they pay",
  "activity.shareToEarn.cta": "Trade and share",
  "activity.composer.placeholder": "What's on your mind? Share an opinion or trade…",
  "activity.composer.cta": "Post",
  "mostCopied.title": "Most Copied",
  "mostCopied.window24h": "24h",
  "mostCopied.noTraders": "No traders found",
  "mostCopied.copyButton": "Copy",
  "activity.filter.following": "Following",
  "activity.viewToggle.expanded": "Expanded view",
  "activity.viewToggle.compact": "Compact view",
  "activity.filter.all": "All",
  "activity.filter.trades": "Trades",
  "activity.filter.copies": "Copies",
  "activity.filter.watchlist": "Watchlist",
  "activity.filter.opinions": "Opinions",
  "activity.filter.whales": "Whales",
  "activity.filter.fresh": "Fresh Wallets",
  "activity.filter.moves": "Moves",
  "activity.filter.new": "New",
  "activity.newMarket.label": "New market",
  "activity.newEvent.label": "New event",
  "activity.move.pump": "Pump",
  "activity.move.dump": "Dump",
  "activity.action.closed": "Closed",
  "activity.action.copied": "copied",
  "activity.tier.mega_whale": "Mega Whale",
  "activity.tier.whale": "Whale",
  "activity.tier.dolphin": "Dolphin",
  "activity.tier.fish": "Big Fish",
  "activity.tier.fresh": "Fresh Wallet",
  "activity.badge.tracked": "Tracked",
  "activity.badge.copy": "Copy",
  "activity.badge.marketClosed": "Closed",
  "activity.empty.following.title": "No traders followed yet",
  "activity.empty.following.desc": "Follow traders to see their activity here and copy their trades automatically.",
  "activity.empty.watchlist.title": "No favorites yet",
  "activity.empty.watchlist.desc": "Star traders from the leaderboard to track their trades here.",
  "activity.empty.trades.title": "No trades yet",
  "activity.empty.trades.desc": "Your executed trades will appear here. Start trading on any market to see your history.",
  "activity.empty.copies.title": "No copy trades yet",
  "activity.empty.copies.desc": "When you follow a trader and they place a trade, your copies will show up here.",
  "activity.empty.opinions.title": "No opinions yet",
  "activity.empty.opinions.desc": "Share your market predictions and analysis with the community.",
  "activity.empty.whales.title": "No whale activity detected",
  "activity.empty.whales.desc": "Large trades from top wallets will appear here when they happen.",
  "activity.empty.moves.title": "No market moves right now",
  "activity.empty.moves.desc": "Significant price movements across markets will show up here.",
  "activity.empty.new.title": "No new markets",
  "activity.empty.new.desc": "Newly created prediction markets will appear here as they launch.",
  "activity.empty.default.title": "No activity yet",
  "activity.empty.default.desc": "Activity will show up here as it happens.",
  "activity.empty.browseTraders": "Browse traders",
  "activity.empty.findTraders": "Find traders to watch",
  "activity.empty.exploreMarkets": "Explore markets",
  "activity.empty.createPost": "Create a post",
  "activity.empty.viewAll": "View all activity",
  "createPost.yourAvatar": "Your avatar",
  "createPost.greeting": "Hey {name}!",
  "createPost.whatsYourPrediction": "What's your prediction?",
  "createPost.shareYourTake": "Share your take",
  "createPost.publicFeed": "Public feed",
  "createPost.placeholder.alpha": "Drop your alpha here...",
  "createPost.placeholder.market": "I think this market will...",
  "createPost.placeholder.event": "This event is gonna be wild because...",
  "createPost.pasteHint": "Paste a market link to attach a preview",
  "createPost.postButton": "Post",
  "activity.action.bought": "bought",
  "activity.action.sold": "sold",
  "activity.group.justNow": "Just now",
  "activity.group.minutesAgo": "Minutes ago",
  "activity.group.oneHourAgo": "1 hour ago",
  "activity.group.hoursAgo": "{n} hours ago",
  "activity.group.yesterday": "Yesterday",
  "activity.group.daysAgo": "{n} days ago",
  "activity.group.lastWeek": "Last week",
  "activity.group.older": "Older",
  "activity.copyButton.copy": "Copy",
  "activity.copyButton.pause": "Pause",
  "activity.copyButton.resume": "Resume",
  "activityDetail.post": "Post",
  "activityDetail.leader": "Leader",
  "activityDetail.copy": "Copy",
  "activityDetail.bought": "Bought",
  "activityDetail.sold": "Sold",
  "activityDetail.postedIn": "Posted in",
  "activityDetail.tradeThisMarket": "Trade this market",
  "activity.eventLabel.megaWhale": "MEGA WHALE",
  "activity.eventLabel.whale": "WHALE",
  "activity.eventLabel.dolphin": "DOLPHIN",
  "activity.eventLabel.bigFish": "BIG FISH",
  "activity.eventLabel.trade": "TRADE",
  "activity.eventLabel.post": "POST",
  "activity.eventLabel.copy": "COPY",
  "activity.eventLabel.win": "WIN",
  "activity.eventLabel.loss": "LOSS",
  "activity.eventLabel.pump": "PUMP",
  "activity.eventLabel.dump": "DUMP",
  "activity.eventLabel.resolved": "RESOLVED",
  "activity.eventLabel.newMarket": "NEW MARKET",
  "activity.eventLabel.follow": "FOLLOW",
  "activity.eventLabel.watchlist": "WATCHLIST",
  "activity.eventLabel.article": "ARTICLE",
  "comments.title": "Comments",
  "comments.sort.featured": "Featured",
  "comments.sort.top": "Top",
  "comments.sort.recent": "Recent",
  "comments.placeholder": "Share your thoughts...",
  "comments.signInPrompt": "Sign in to join the conversation",
  "comments.signIn": "Sign In",
  "comments.empty": "No comments yet. Be the first to share your thoughts!",
  "comments.cancel": "Cancel",
  "comments.reply": "Reply",
  "comments.submitButton": "Comment",
  "comments.kbdHint.before": "Press",
  "comments.kbdHint.after": "to send",
  "marketCard.archived": "Archived",
  "marketCard.closedHeyFren": "Hey fren, market's closed!",
  "activity.reachedEnd": "You've reached the end",
  "home.tape.boughtOn": "bought {outcome} on",
  "home.tape.soldOn": "sold {outcome} on",
  "home.tape.copiedOn": "copied a trade on",
  "home.tape.shares": "shares",
  "install.title": "Install FrenFlow",
  "install.subtitleMobile": "Trade prediction markets anywhere, anytime",
  "install.subtitleDesktop": "The ultimate prediction markets trading experience",
  "install.button": "Install App",
  "install.maybeLater": "Maybe later",
  "install.iosPrompt.before": "Tap",
  "install.iosPrompt.then": "then",
  "install.iosPrompt.addToHome": "\"Add to Home Screen\"",
  "cookies.message": "We use cookies to analyze web usage and improve your experience.",
  "cookies.policyLink": "View our Cookie Policy",
  "cookies.accept": "Accept",
  "uma.signInForFullAccess": "Sign in for full access",
  "copytrading.hero.badge.long": "Block 0 Execution — Copy trades before they hit the blockchain",
  "copytrading.hero.badge.short": "Block 0 — Mempool speed",
  "copytrading.hero.headline.desktop": "Polymarket Copy Trading Bot",
  "copytrading.hero.headline.mobilePrefix": "Polymarket copy bot, like",
  "copytrading.hero.headline.desktopLikePrefix": "Auto-copy like",
  "copytrading.hero.rotating.desktop.1": "top Polymarket traders",
  "copytrading.hero.rotating.desktop.2": "winning strategies",
  "copytrading.hero.rotating.desktop.3": "profitable predictions",
  "copytrading.hero.rotating.mobile.1": "a pro",
  "copytrading.hero.rotating.mobile.2": "the best",
  "copytrading.hero.subtitle": "The only copy trading bot that executes in the same block as the leader. Block 0. Not blocks later.",
  "copytrading.hero.ctaPrimary": "Start Copy Trading",
  "copytrading.hero.ctaSecondary": "Earn FREN Points",
  "copytrading.hero.subcopy": "Non-custodial. Same-block execution. Your funds stay in your wallet.",
  "copytrading.hero.rating.short": "rating",
  "copytrading.hero.rating.long": "from Polymarket traders",
  "stats.headline.before": "Trusted by the",
  "stats.headline.accent": "smartest traders",
  "stats.subtitle": "Join thousands of traders profiting from Polymarket copy trading",
  "stats.footnote": "Numbers updated in real-time. All trades verified on-chain.",
  "stats.totalVolume.label": "Total Volume Copied",
  "stats.totalVolume.desc": "Trades executed through FrenFlow",
  "stats.activeTraders.label": "Active Traders",
  "stats.activeTraders.desc": "Users copying strategies daily",
  "stats.activeMarkets.label": "Active Markets",
  "stats.activeMarkets.desc": "Polymarket events available to trade",
  "stats.tradesExecuted.label": "Trades Executed",
  "stats.tradesExecuted.desc": "Total trades copied to date",
  "how.eyebrow": "How Polymarket Copy Trading Works",
  "how.headline.before": "Start copy trading on Polymarket in",
  "how.headline.accent": "4 easy steps",
  "how.subtitle": "No trading experience needed. Set up your Polymarket copy trading bot in under 5 minutes.",
  "how.bottomNote": "Ready to copy top Polymarket traders? Start your prediction market journey today.",
  "how.step1.title": "Sign Up & Connect",
  "how.step1.desc": "Create your FrenFlow account in seconds via email or Telegram. We set up a secure non-custodial Polymarket wallet for you automatically.",
  "how.step2.title": "Find Top Traders",
  "how.step2.desc": "Browse our Polymarket leaderboard of top prediction market traders ranked by ROI, or paste any whale wallet address to copy.",
  "how.step3.title": "Block 0 Copy",
  "how.step3.desc": "Your trades execute in the same block as the leader — not blocks later. We detect from the mempool before the transaction is even mined.",
  "how.step4.title": "Track & Profit",
  "how.step4.desc": "Monitor your Polymarket positions, track performance analytics, and earn FREN points with every copy trade.",
  "features.eyebrow": "Why FrenFlow",
  "features.headline.before": "The only Polymarket bot with",
  "features.headline.after": "copy trading",
  "features.subtitle": "Other bots copy trades blocks later. We copy in the same block — because we detect trades before they're even mined.",
  "features.f1.title": "Block 0 Execution",
  "features.f1.highlight": "Same block as the leader",
  "features.f1.desc": "Your copy trade lands in the same block as the original. Not 1 block later, not 5 — the same one. That's the FrenFlow difference.",
  "features.f2.title": "Mempool Detection",
  "features.f2.highlight": "Before it hits the chain",
  "features.f2.desc": "We intercept trades from the mempool — before they're mined. Other bots wait for on-chain confirmation. We don't wait.",
  "features.f3.title": "Whale Tracking",
  "features.f3.desc": "Follow and copy any Polymarket whale wallet. Track smart money movements and replicate winning prediction market strategies.",
  "features.f4.title": "Non-Custodial & Secure",
  "features.f4.desc": "Your funds stay in your wallet. Our Polymarket bot executes trades but never holds your crypto. Full transparency.",
  "topTraders.eyebrow": "Polymarket Leaderboard",
  "topTraders.headline.before": "Copy from the",
  "topTraders.headline.accent": "best Polymarket traders",
  "topTraders.subtitle": "Track whale wallets and copy proven prediction market strategies with one click. Real-time Polymarket trader analytics.",
  "topTraders.badge.topVolume": "Top Volume",
  "topTraders.badge.risingStar": "Rising Star",
  "topTraders.badge.consistent": "Consistent",
  "topTraders.totalVolume": "Total Volume",
  "topTraders.pnl": "PnL",
  "topTraders.markets": "Markets",
  "topTraders.viewProfile": "View Profile",
  "topTraders.bottom.title": "Explore More Polymarket Traders",
  "topTraders.bottom.subtitle": "Browse our full Polymarket leaderboard with 100+ whale wallets, prediction market analytics, and copy trading stats",
  "topTraders.bottom.cta": "View Full Leaderboard",
  "mobileShowcase.eyebrow": "Best Polymarket Telegram Bot",
  "mobileShowcase.headline.before": "One account.",
  "mobileShowcase.headline.accent": "Web + Telegram",
  "mobileShowcase.headline.after": "synced.",
  "mobileShowcase.subtitle1": "Sign up with Telegram and get instant access on both platforms. Same wallet, same settings, same followed traders. Configure copy trading on the web, execute and monitor from Telegram - everything stays in sync.",
  "mobileShowcase.subtitle2": "Open @FrenFlowBot, login with your Telegram account, and your entire FrenFlow setup is ready. No extra configuration needed.",
  "mobileShowcase.faq1.q": "Do settings sync between web and Telegram?",
  "mobileShowcase.faq1.a": "Yes! FrenFlow keeps everything in sync. Follow a trader on the web, it appears in Telegram. Change your stake amount in Telegram, it updates on web. Same wallet, same settings, same followed traders - always synchronized in real-time.",
  "mobileShowcase.faq2.q": "How do I connect my Telegram account?",
  "mobileShowcase.faq2.a": "Simply sign up or login with Telegram as your authentication method. Your FrenFlow account will be automatically linked to your Telegram, giving you instant access to @FrenFlowBot with all your settings already configured.",
  "mobileShowcase.faq3.q": "What can I do from the Telegram bot?",
  "mobileShowcase.faq3.a": "Everything! Check your portfolio and PnL, follow/unfollow traders, pause/resume copy trading, adjust stake amounts, deposit and withdraw USDC, get instant trade notifications - full Polymarket copy trading from your phone.",
  "saasShowcase.headline.before": "AI that",
  "saasShowcase.headline.accent": "trades for you",
  "saasShowcase.subtitle": "Forget manual trading chaos. FrenFlow's AI analyzes trader performance like an expert, predicts winning moves, and helps you understand how your portfolio is really performing.",
  "saasShowcase.feature1.title": "Automated trader analysis",
  "saasShowcase.feature1.desc": "AI explains trader performance, win rates, and patterns without spreadsheets.",
  "saasShowcase.close": "Close",
  "pricing.eyebrow": "Rewards",
  "pricing.headline.before": "Multiple ways to",
  "pricing.headline.accent": "earn rewards",
  "pricing.subtitle": "Trade on Polymarket, refer friends, and build your reputation to maximize your earnings.",
  "pricing.frenpoints.name": "Earn FrenPoints",
  "pricing.frenpoints.subtitle": "Trade, share, and level up",
  "pricing.frenpoints.f1": "0.1 FP per $1 traded on Polymarket",
  "pricing.frenpoints.f2": "0.5 FP per post with market links (3/day max)",
  "pricing.frenpoints.f3": "Daily Spin: win 1–100 FP every 24 hours",
  "pricing.frenpoints.f4": "Trade streaks: $100+/day unlocks FP milestones",
  "pricing.frenpoints.cta": "Start Trading",
  "pricing.referral.name": "Referral Program",
  "pricing.referral.subtitle": "Earn from your network",
  "pricing.referral.f1": "10% lifetime commission on referral trading fees",
  "pricing.referral.f2": "+10 FP bonus when referral hits $100 volume",
  "pricing.referral.f3": "Real-time dashboard: clicks, signups & earnings",
  "pricing.referral.f4": "Share via X, Telegram, or custom invite link",
  "pricing.referral.badge": "Earn Passive Income",
  "pricing.referral.cta": "Get Referral Link",
  "pricing.aura.name": "Aura & Levels",
  "pricing.aura.subtitle": "Grow your reputation, multiply your earnings",
  "pricing.aura.f1": "5 Aura tiers with multipliers up to 1.25x",
  "pricing.aura.f2": "31 levels with badges: Bronze → Diamond",
  "pricing.aura.f3": "Daily FP cap scales from 200 to 400 FP/day",
  "pricing.aura.f4": "Earn Aura through trading, posts & referrals",
  "pricing.aura.cta": "View Levels",
  "pricing.auraTiers.label": "Aura Tiers:",
  "pricing.auraTiers.list": "Newbie (1.0x) → Fren (1.1x) → OG (1.15x) → Whale (1.2x) → Legend (1.25x)",
  "pricing.auraTiers.decay": "Aura decays 0.5%/week if inactive for 7+ days — stay active to keep your multiplier",
  "pricing.ogPass.suffix": "111 free genesis passes for early supporters",
  "testimonials.eyebrow": "Testimonials",
  "testimonials.headline.before": "Wall of",
  "testimonials.headline.accent": "Love",
  "testimonials.subtitle": "Polymarket traders who use FrenFlow and are crushing it with automated copy trading.",
  "testimonials.tradersCount": "500+ traders",
  "testimonials.role.cryptoTrader": "Crypto Trader",
  "testimonials.role.defiInvestor": "DeFi Investor",
  "testimonials.role.polymarketPro": "Polymarket Pro",
  "testimonials.role.predictionMarketsTrader": "Prediction Markets Trader",
  "testimonials.role.quantTrader": "Quantitative Trader",
  "testimonials.role.riskManager": "Risk Manager",
  "testimonials.role.formerManualTrader": "Former Manual Trader",
  "testimonials.role.professionalBettor": "Professional Bettor",
  "testimonials.r1.t1": "Following top traders with FrenFlow has 3x my Polymarket returns. The AI picks winners consistently.",
  "testimonials.r1.t2": "I was skeptical about copy trading, but FrenFlow's analytics convinced me. Up 47% in 2 months.",
  "testimonials.r1.t3": "The non-custodial approach sold me. My funds never leave my wallet. Smart and secure.",
  "testimonials.r1.t4": "Best Polymarket tool I've used. Set it and forget it. The bot handles everything perfectly.",
  "testimonials.r2.t1": "Setup took 3 minutes. Started copying trades immediately. ROI was positive day one.",
  "testimonials.r2.t2": "The portfolio diversification feature is genius. Following 5 traders with different strategies.",
  "testimonials.r2.t3": "I tried manual trading for months. FrenFlow's AI does it better. Switching was the best decision.",
  "testimonials.r2.t4": "Real-time copying, zero lag. The execution is flawless. Highly recommend to anyone on Polymarket.",
  "faq.headline.before": "Polymarket Copy Trading",
  "faq.subtitle": "Everything you need to know about copy trading prediction markets with FrenFlow.",
  "faq.section.bot": "Polymarket Trading Bot",
  "faq.section.markets": "Prediction Markets Trading",
  "faq.section.settings": "Copy Trading Settings",
  "faq.section.fees": "Fees & Pricing",
  "faq.section.security": "Security & Wallet",
  "faq.section.start": "Getting Started",
  "faq.bot.q1": "Is there a Polymarket trading bot?",
  "faq.bot.a1": "Yes! FrenFlow is the leading Polymarket trading bot for automated copy trading. Our bot lets you auto-copy top prediction market traders, track whale wallets, and execute trades automatically 24/7 without manual intervention.",
  "faq.bot.q2": "What is Polymarket copy trading?",
  "faq.bot.a2": "Polymarket copy trading allows you to automatically replicate trades from successful prediction market traders. When a top trader buys YES or NO shares on any Polymarket event, FrenFlow's bot instantly copies that trade to your wallet.",
  "faq.bot.q3": "How does the Polymarket auto-trade bot work?",
  "faq.bot.a3": "FrenFlow's Polymarket bot monitors your followed traders in real-time. When they make a trade, our automated system instantly replicates it to your wallet with your chosen stake amount. No manual trading needed.",
  "faq.markets.q1": "What are prediction markets?",
  "faq.markets.a1": "Prediction markets like Polymarket let you trade on real-world event outcomes. Buy YES shares if you think something will happen, NO shares if it won't. Winning shares pay $1 each. FrenFlow makes prediction market trading accessible by letting you copy experienced traders.",
  "faq.markets.q2": "Is prediction market copy trading profitable?",
  "faq.markets.a2": "Copy trading top Polymarket traders can be profitable if you follow consistent winners. FrenFlow shows each trader's ROI, win rate, and volume history so you can make informed decisions. Past performance doesn't guarantee future results.",
  "faq.markets.q3": "What markets can I copy trade?",
  "faq.markets.a3": "FrenFlow supports all Polymarket prediction markets: politics, sports, crypto prices, entertainment, world events, and more. When your followed trader bets on any market, the trade is automatically copied to your account.",
  "faq.settings.q1": "How do I set my copy trading stake amount?",
  "faq.settings.a1": "Choose between Fixed Amount (e.g., $10 per copied trade) or Percentage mode (e.g., 5% of your balance). Set a maximum stake per trade to control risk. Adjust these settings anytime from your copy trading dashboard.",
  "faq.settings.q2": "Does FrenFlow copy sell orders too?",
  "faq.settings.a2": "Yes! Enable 'Follow Sells' to automatically exit positions when your copied trader sells. This helps you lock in profits or cut losses at the same time as experienced Polymarket traders.",
  "faq.settings.q3": "Can I pause or stop copy trading?",
  "faq.settings.a3": "Absolutely. Pause any trader with one click, adjust your copy settings, or unfollow completely. You maintain full control over your Polymarket copy trading strategy at all times.",
  "faq.fees.q1": "How much does Polymarket copy trading cost?",
  "faq.fees.a1": "FrenFlow charges the Polymarket builder fee at the CLOB level: 1% taker / 0.5% maker. Same rate for manual and copy trades — no service fee on top, no monthly subscriptions, no hidden fees, no minimum deposits. We also cover all blockchain gas fees for you.",
  "faq.fees.q2": "Are there Polymarket trading fees?",
  "faq.fees.a2": "Polymarket has its own trading fees (typically 1-2% spread). These are standard for all Polymarket users and separate from FrenFlow's builder fee (1% taker / 0.5% maker).",
  "faq.fees.q3": "Is there a minimum to start copy trading?",
  "faq.fees.a3": "No minimum deposit required. However, we recommend at least $50-100 USDC to properly diversify across multiple Polymarket traders and prediction markets.",
  "faq.security.q1": "Is Polymarket copy trading safe?",
  "faq.security.a1": "FrenFlow uses non-custodial architecture - your funds stay in your own wallet powered by Privy. We can only execute the trades you authorize. We cannot withdraw or transfer your USDC.",
  "faq.security.q2": "How does the crypto wallet work?",
  "faq.security.a2": "When you sign up, FrenFlow creates a secure embedded wallet on Polygon. It's non-custodial (you control the keys) and works without managing seed phrases. Your USDC is always under your control.",
  "faq.security.q3": "Can I withdraw from my copy trading wallet?",
  "faq.security.a3": "Yes, withdraw your USDC anytime to any external Polygon wallet. Open Polymarket positions need to resolve or be sold first. Your funds are never locked by FrenFlow.",
  "faq.start.q1": "How do I start copy trading on Polymarket?",
  "faq.start.a1": "1) Sign up with email or Telegram - we create your wallet automatically. 2) Deposit USDC on Polygon. 3) Enable trading with one click. 4) Choose traders to copy from our leaderboard. Start copying prediction market trades in under 5 minutes.",
  "faq.start.q2": "What do I need to copy trade prediction markets?",
  "faq.start.a2": "Just an email or Telegram account to sign up. FrenFlow handles wallet creation automatically. Then deposit USDC on Polygon and you're ready to copy Polymarket traders.",
  "faq.start.q3": "Can I use FrenFlow on mobile?",
  "faq.start.a3": "Yes! FrenFlow works on any device. Use our Telegram bot (@FrenFlowBot) for mobile copy trading, or access the web app from any browser. Manage your Polymarket copy trades from anywhere.",
  "blogCarousel.eyebrow": "Learn",
  "blogCarousel.headline.before": "Master",
  "blogCarousel.headline.accent": "copy trading",
  "blogCarousel.subtitle": "Expert guides, strategies, and insights to help you succeed on Polymarket.",
  "blogCarousel.viewAll": "View all articles",
  "blogCarousel.readMore": "Read more",
  "blogCarousel.tag.predictionMarkets": "Prediction Markets",
  "blogCarousel.tag.cryptoTrading": "Crypto Trading",
  "blogCarousel.tag.earnMoney": "Earn Money",
  "blogCarousel.tag.trading": "Trading",
  "blogCarousel.tag.tradingStrategies": "Trading Strategies",
  "blogCarousel.tag.arbitrage": "Arbitrage",
  "blogCarousel.post1.title": "What is Polymarket? The Complete Guide to Prediction Markets",
  "blogCarousel.post1.excerpt": "Discover everything about Polymarket, the world's largest decentralized prediction market platform.",
  "blogCarousel.post2.title": "How to Earn Money Sharing Your Polymarket Predictions",
  "blogCarousel.post2.excerpt": "Discover how to monetize your prediction market expertise with FrenFlow's referral system.",
  "blogCarousel.post3.title": "Polymarket Arbitrage Strategies: Complete 2026 Guide",
  "blogCarousel.post3.excerpt": "Learn the most profitable arbitrage strategies: delta-neutral hedging, cross-platform opportunities, and more.",
  "finalCta.headline": "Ready to copy the best Polymarket traders?",
  "finalCta.subtitle.before": "Total control. Automated copy trading with",
  "finalCta.subtitle.bold1": "AI analytics",
  "finalCta.subtitle.middle": "and",
  "finalCta.subtitle.bold2": "portfolio management",
  "finalCta.subtitle.after": "Trade like the pros, without the manual work.",
  "finalCta.cta": "Start Copy Trading Now",
  "finalCta.noCard": "No credit card. No commitment.",
  "finalCta.trusted": "The best Polymarket bot, trusted by 500+ traders",
  "mentions.empty": "No mention events available",
  "mentions.live": "LIVE",
  "mentions.new": "NEW",
  "mentions.volSuffix": "vol",
  "mentions.marketCountOne": "{n} market",
  "mentions.marketCountMany": "{n} markets",
  "mentions.viewFullEvent": "View full event",
  "blog.trending": "Trending",
  "blog.searchPlaceholder": "Search articles...",
  "blog.allTag": "All",
  "blog.readMore": "Read more",
  "blog.empty.search": "No articles matching \"{q}\".",
  "blog.empty.tag": "No articles found for \"{tag}\".",
  "blog.empty.none": "No articles found.",
  "blog.loadMore": "Load more articles",
  "blog.endAll": "You've seen all articles ({n})",
  "blog.endAllFeatured": "You've seen all articles ({n} + 1 featured)",
  "blog.endTag": "You've seen all \"{tag}\" articles ({n})",
  "blog.h1.before": "The",
  "blog.h1.after": "Blog",
  "blog.subtitle": "Polymarket guides, copy trading strategies, and expert insights to help you trade smarter.",
  "blog.cat.traders": "Traders",
  "blog.cat.markets": "Markets",
  "blog.cat.strategies": "Strategies",
  "blog.cat.news": "News",
  "blog.cat.guides": "Guides",
  "blog.cat.education": "Education",
  "blog.cat.tools": "Tools",
  "blog.tag.prediction-markets": "Prediction Markets",
  "blog.tag.polymarket-sports-betting": "Polymarket Sports Betting",
  "blog.tag.polymarket-whale-trader": "Polymarket Whale Trader",
  "blog.tag.sports-prediction-markets": "Sports Prediction Markets",
  "blog.tag.trading": "Trading",
  "blog.tag.on-chain": "On-Chain",
  "blog.tag.copy-trading": "Copy Trading",
  "blog.tag.crypto-trading": "Crypto Trading",
  "blog.tag.earn-money": "Earn Money",
  "blog.tag.trading-strategies": "Trading Strategies",
  "blog.tag.arbitrage": "Arbitrage",
  "blogPost.minRead": "{n} min read",
  "blogPost.relatedArticles": "Related articles",
  "blogPost.authorName": "FrenFlow Team",
  "blogPost.authorRole": "Prediction Markets Experts",
  "blogPost.authorBio": "Building the future of Polymarket copy trading. We help traders discover opportunities and automate their prediction market strategies.",
  "blogTraderCard.profit": "Profit",
  "blogTraderCard.volume": "Volume",
  "blogTraderCard.copy": "Copy",
  "blogTraderCard.profile": "Profile",
  "finalCtaBlog.headline": "Ready to copy trade like a pro?",
  "finalCtaBlog.subtitle": "Follow the best Polymarket wallets. Auto-copy every position. Same block execution.",
  "finalCtaBlog.cta": "Start Copying Now",
  "mobileStickyBlogCta.title": "Copy Top Predictors",
  "mobileStickyBlogCta.subtitle": "Block 0 execution on FrenFlow",
  "mobileStickyBlogCta.cta": "Get Started",
  "blogSidebar.headline": "Copy Top Predictors. Same Block.",
  "blogSidebar.subtitle": "Follow the best Polymarket wallets and auto-copy every position they take — executed in the same block.",
  "blogSidebar.bullet1": "Block 0 execution — same block as the leader",
  "blogSidebar.bullet2": "Zero gas fees — we cover everything",
  "blogSidebar.bullet3": "Non-custodial — your funds, your wallet",
};

const es: Catalog = {
  // ── Footer / live ticker ────────────────────────────────────────────
  "footer.live": "EN VIVO",
  "footer.blog": "Blog",
  "footer.docs": "Docs",
  "footer.help": "Ayuda",
  "footer.legal": "Legal",
  "footer.tradeFromTelegram": "Opera desde Telegram",

  // ── Sidebar primary nav ─────────────────────────────────────────────
  "nav.home": "Inicio",
  "nav.markets": "Mercados",
  "nav.more": "Más",
  "nav.activity": "Actividad",
  "nav.traders": "Traders",
  "nav.copy": "Copy",
  "nav.degenbox": "Degenbox",
  "nav.bubbleflow": "BubbleFlow",
  "nav.frenlinks": "FrenLinks",
  "nav.uma": "UMA",
  "nav.affiliate": "Afiliados",
  "nav.feedback": "Feedback",
  "nav.portfolio": "Cartera",
  "nav.settings": "Ajustes",
  "nav.profile": "Perfil",
  "nav.notifications": "Notificaciones",
  "nav.search": "Buscar",
  "nav.post": "Publicar",

  // ── Page titles ────────────────────────────────────────────────────
  "page.markets": "Mercados",
  "page.activity": "Actividad",
  "page.traders": "Traders",
  "page.copy": "Copy",
  "page.portfolio": "Cartera",
  "page.settings": "Ajustes",
  "page.profile": "Perfil",
  "page.event": "Evento",
  "page.market": "Mercado",
  "page.trader": "Trader",
  "page.feedback": "Feedback",
  "page.affiliate": "Afiliados",

  // ── Common buttons / actions ────────────────────────────────────────
  "action.buy": "Comprar",
  "action.sell": "Vender",
  "action.cancel": "Cancelar",
  "action.confirm": "Confirmar",
  "action.close": "Cerrar",
  "action.save": "Guardar",
  "action.continue": "Continuar",
  "action.back": "Atrás",
  "action.next": "Siguiente",
  "action.submit": "Enviar",
  "action.retry": "Reintentar",
  "action.copy": "Copiar",
  "action.copied": "Copiado",
  "action.share": "Compartir",
  "action.like": "Me gusta",
  "action.dislike": "No me gusta",
  "action.comment": "Comentar",
  "action.shareOnX": "Compartir en X",
  "action.viewOnPolymarket": "Ver en Polymarket",
  "action.delete": "Eliminar",
  "action.edit": "Editar",
  "action.search": "Buscar",
  "action.menu": "Menú",
  "action.closeSearch": "Cerrar búsqueda",
  "action.notInterested": "No me interesa",
  "action.setPriceAlert": "Crear alerta de precio",
  "action.shareAlpha": "Comparte la alpha",
  "action.openEvent": "Abrir página del evento",
  "action.useBalanceAsAmount": "Usar saldo como cantidad",
  "action.maybeLater": "Quizás más tarde",
  "action.snapMidpoint": "Ajustar al punto medio",
  "hlEnable.tooltip.authoriseAgent": "Autorizar agente FrenFlow",
  "hlEnable.tooltip.depositUsdc": "Depositar USDC en Hyperliquid",
  "hlEnable.tooltip.moveToSpot": "Mover USDC a spot para operar",
  "hlEnable.tooltip.approveBuilderFee": "Aprobar builder fee (0,1%)",
  "action.timeUntilSettlement": "Tiempo hasta liquidación",
  "action.livestream": "Retransmisión",
  "sports.serving": "Al saque",
  "climate.seismic.title": "Monitor sísmico",
  "climate.seismic.subtitle": "Datos de terremotos M4.5+ en tiempo real de USGS",
  "climate.hurricane.title": "Seguimiento de huracanes y ciclones",
  "climate.hurricane.subtitle": "Sistemas tropicales activos de NOAA",
  "climate.weather.title": "Monitor de tiempo severo",
  "climate.weather.subtitle": "Alertas de tiempo severo activas de NWS",
  "climate.disaster.title": "Monitor de desastres naturales",
  "climate.disaster.subtitle": "Datos sísmicos y meteorológicos en tiempo real",
  "climate.section.activeCyclones": "Ciclones tropicales activos",
  "climate.section.hurricaneAlerts": "Alertas relacionadas con huracanes",
  "climate.section.weatherAlerts": "Alertas meteorológicas activas",
  "climate.section.severeWeatherAlerts": "Alertas de tiempo severo",
  "climate.count.thisWeek": "esta semana",
  "climate.count.active": "activas",
  "leadForm.errFillAll": "Rellena todos los campos.",
  "leadForm.errAcceptPolicy": "Debes aceptar la política de privacidad.",
  "leadForm.errGeneric": "Ha ocurrido un error.",
  "leadForm.errConnection": "Error de conexión.",
  "leadForm.success": "¡Gracias por apuntarte! Te avisaremos pronto 🚀",
  "leadForm.nameLabel": "Tu nombre",
  "leadForm.namePlaceholder": "Nombre o apodo",
  "leadForm.emailLabel": "Tu mejor email",
  "leadForm.acceptPre": "Acepto la ",
  "leadForm.privacyPolicy": "política de privacidad",
  "leadForm.submitLoading": "Reservando...",
  "leadForm.submit": "Reserva tu plaza",
  "action.signIn": "Iniciar sesión",
  "action.signOut": "Cerrar sesión",
  "action.connectWallet": "Conectar wallet",
  "action.deposit": "Depositar",
  "action.withdraw": "Retirar",
  "action.send": "Enviar",
  "action.receive": "Recibir",
  "action.swap": "Swap",
  "action.viewMore": "Ver más",
  "action.viewAll": "Ver todo",
  "action.seeMore": "Ver más",
  "action.follow": "Seguir",
  "action.unfollow": "Dejar de seguir",
  "action.following": "Siguiendo",

  "time.justNow": "Justo ahora",
  "time.minutesAgo": "hace {n}m",
  "time.hoursAgo": "hace {n}h",
  "time.daysAgo": "hace {n}d",
  "time.inMinutes": "En {n} min",
  "time.inHours": "En {n} h",
  "time.today": "Hoy {time}",
  "time.tomorrow": "Mañana {time}",

  "whale.tier.megaWhale": "Mega Ballena",
  "whale.tier.whale": "Ballena",
  "whale.tier.dolphin": "Delfín",
  "whale.tier.trader": "Trader",
  "whale.empty.title": "No se ha detectado actividad de ballenas recientemente",
  "whale.empty.subtitle": "Vuelve pronto para ver alertas de ballenas",
  "whale.accuracy": "{pct}% prec",
  "whale.copyTrade": "Copiar trade",
  "whale.viewMarket": "Ver mercado",
  "whale.viewAll": "Ver toda la actividad de ballenas",

  "topCallers.title": "Top traders",
  "topCallers.subtitle": "Los mejores resultados",
  "topCallers.empty": "Sin datos disponibles",
  "topCallers.pro": "Pro",
  "topCallers.calls": "{n} llamadas",
  "topCallers.copiers": "{n} copiando",
  "topCallers.accuracy": "precisión",
  "topCallers.following": "Siguiendo",
  "topCallers.copy": "Copiar",
  "topCallers.copiedVolume": "Volumen copiado",
  "topCallers.avgReturn": "Retorno medio",
  "topCallers.bestCall": "Mejor llamada",
  "topCallers.winStreak": "Racha de aciertos",
  "topCallers.viewLeaderboard": "Ver clasificación completa",

  // ── Status / general ───────────────────────────────────────────────
  "status.loading": "Cargando…",
  "status.empty": "Aún no hay nada por aquí",
  "status.error": "Algo ha ido mal",
  "status.new": "Nuevo",
  "status.live": "En directo",
  "status.trending": "Tendencias",
  "status.resolved": "Resuelto",
  "status.closed": "Cerrado",
  "status.open": "Abierto",
  "status.pending": "Pendiente",
  "status.expired": "Expirado",

  // ── Trading ────────────────────────────────────────────────────────
  "trading.position": "Posición",
  "trading.positions": "Posiciones",
  "trading.openPositions": "Posiciones abiertas",
  "trading.closedPositions": "Posiciones cerradas",
  "trading.profit": "Ganancia",
  "trading.loss": "Pérdida",
  "trading.pnl": "P&L",
  "trading.volume": "Volumen",
  "trading.liquidity": "Liquidez",
  "common.openInterest": "Interés abierto",
  "common.oi": "IA",
  "common.24h": "24h",

  "resolution.title.market": "Reglas del mercado",
  "resolution.title.event": "Detalles de resolución",
  "resolution.meta.created": "Creado",
  "resolution.meta.resolver": "Resuelto por",
  "resolution.meta.source": "Fuente",
  "resolution.meta.marketOpen": "Apertura",
  "resolution.meta.marketCloses": "Cierre",
  "resolution.meta.mayCloseEarly": "Puede cerrar antes",
  "resolution.meta.payout": "Pago",
  "resolution.meta.series": "Serie",
  "resolution.meta.event": "Evento",
  "resolution.meta.market": "Mercado",
  "resolution.section.timeline": "Cronograma y pago",
  "resolution.section.earlyClose": "Cierre anticipado",
  "resolution.section.outcomeVerified": "Resultado verificado por",
  "resolution.section.identifiers": "Identificadores",
  "resolution.expand.showMore": "Ver más",
  "resolution.expand.showLess": "Ver menos",
  "resolution.payout.aboutMinute": "~1 min tras el cierre",
  "resolution.payout.minutes": "{n} min tras el cierre",
  "resolution.payout.minute": "{n} min tras el cierre",
  "resolution.payout.hours": "{n} h tras el cierre",
  "resolution.payout.hour": "{n} h tras el cierre",
  "resolution.exclusive.text": "Como máximo un mercado de este evento puede resolverse como \"Sí\". {collateral}Al comprar/vender \"No\" entre mercados puedes recibir o deber fondos.",
  "resolution.exclusive.collateral": "Tipo de retorno de colateral: {type}. ",

  "hero.bookmark.add": "Añadir a la watchlist",
  "hero.bookmark.remove": "Quitar de la watchlist",
  "hero.viewOn": "Ver en {source}",
  "hero.badge.closed": "CERRADO",
  "hero.badge.live": "EN VIVO",
  "hero.badge.hot": "TRENDING",
  "hero.badge.new": "NUEVO",
  "hero.badge.disputed": "DISPUTADO",
  "hero.badge.whale": "BALLENA",
  "hero.badge.smart": "SMART",
  "hero.tooltip.hot": "Score de competitividad: {pct}/100\nMás cerca del 50% = más incertidumbre = más actividad",
  "hero.tooltip.new": "Listado hace {n}h",
  "hero.tooltip.disputed": "Ver disputa UMA",
  "hero.tooltip.whale": "Mercado ballena: {wallets} wallets smart · ${vol}M de volumen smart",
  "hero.tooltip.smart": "Mercado smart: {wallets} wallets smart siguiéndolo",
  "hero.tooltip.kalshi.exact": "También se opera en Kalshi (coincidencia exacta)",
  "hero.tooltip.kalshi.related": "También se opera en Kalshi (relacionado)",
  "hero.tooltip.kalshi.more": "+{n} más",

  "outcomeTabs.chart": "Gráfico",
  "outcomeTabs.orderbook": "Orderbook",
  "outcomeTabs.trades": "Operaciones",
  "outcomeTabs.holders": "Top holders",
  "outcomeTabs.whos": "Quién opera",
  "outcomeTabs.smart": "Smart money",
  "outcomeTabs.oi": "Interés abierto",
  "outcomeTabs.volume": "Volumen",

  "marketsGrid.market": "Mercado",
  "marketsGrid.outcome": "Resultado",
  "eventGrid.volAbbr": "Vol.",
  "kalshiSort.all": "Todos",
  "kalshiSort.hot": "Populares",
  "kalshiSort.movers": "Cambios",
  "kalshiSort.new": "Nuevos",
  "kalshiSort.volume": "Volumen",
  "kalshiSort.volAbbr": "Vol",
  "kalshiSubcat.bitcoin": "Bitcoin",
  "kalshiSubcat.ethereum": "Ethereum",
  "kalshiSubcat.solana": "Solana",
  "kalshiSubcat.crypto": "Cripto",
  "kalshiSubcat.football": "Fútbol americano",
  "kalshiSubcat.soccer": "Fútbol",
  "kalshiSubcat.basketball": "Baloncesto",
  "kalshiSubcat.baseball": "Béisbol",
  "kalshiSubcat.hockey": "Hockey",
  "kalshiSubcat.tennis": "Tenis",
  "kalshiSubcat.golf": "Golf",
  "kalshiSubcat.boxing": "Boxeo",
  "kalshiSubcat.mma": "MMA",
  "kalshiSubcat.racing": "Carreras",
  "kalshiSubcat.esports": "Esports",
  "kalshiSubcat.tech": "Tecnología",
  "kalshiSubcat.ai": "IA",
  "kalshiSubcat.companies": "Empresas",
  "kalshiSubcat.markets": "Mercados",
  "kalshiSubcat.economy": "Economía",
  "kalshiSubcat.inflation": "Inflación",
  "kalshiSubcat.fed": "Fed",
  "kalshiSubcat.gdp": "PIB",
  "kalshiSubcat.unemployment": "Desempleo",
  "kalshiSubcat.elections": "Elecciones",
  "kalshiSubcat.politics": "Política",
  "kalshiSubcat.world": "Mundo",
  "kalshiSubcat.climate": "Clima",
  "kalshiSubcat.weather": "Tiempo",
  "kalshiSubcat.energy": "Energía",
  "kalshiSubcat.oil": "Petróleo",
  "kalshiSubcat.gas": "Gas",
  "kalshiSubcat.commodities": "Materias primas",
  "kalshiSubcat.agriculture": "Agricultura",
  "kalshiSubcat.health": "Salud",
  "kalshiSubcat.science": "Ciencia",
  "kalshiSubcat.education": "Educación",
  "kalshiSubcat.religion": "Religión",
  "kalshiSubcat.entertainment": "Entretenimiento",
  "kalshiSubcat.movies": "Películas",
  "kalshiSubcat.music": "Música",
  "kalshiSubcat.tv": "TV",
  "kalshiSubcat.awards": "Premios",
  "kalshiSubcat-pop-culture": "Cultura pop",
  "kalshiSubcat.popculture": "Cultura pop",
  "kalshiSubcat.popular": "Popular",
  "kalshiSubcat.gold": "Oro",
  "kalshiSubcat.silver": "Plata",
  "kalshiSubcat.stocks": "Acciones",
  "kalshiSubcat.bonds": "Bonos",
  "kalshiSubcat.forex": "Forex",
  "kalshiSubcat.tariffs": "Aranceles",
  "kalshiSubcat.trade": "Comercio",
  "kalshiSubcat.law": "Derecho",
  "kalshiSubcat.crime": "Crimen",
  "kalshiSubcat.space": "Espacio",
  "kalshiSubcat.transport": "Transporte",
  "kalshiSubcat.transportation": "Transporte",
  "kalshiSubcat.travel": "Viajes",
  "kalshiSubcat.holidays": "Festividades",
  "kalshiSubcat.media": "Medios",
  "kalshiSubcat.streaming": "Streaming",
  "kalshiSubcat.gaming": "Videojuegos",
  "marketsGrid.chance": "Probabilidad",
  "marketsGrid.chart24h": "Gráfico 24h",
  "marketsGrid.volume": "Volumen",
  "marketsGrid.volume24h": "24h",
  "marketsGrid.buy": "Comprar",
  "marketsGrid.result": "Resultado",
  "marketsGrid.spread": "Spread",
  "marketsGrid.rewards.tooltip": "Gana recompensas colocando órdenes límite cerca del midpoint.",
  "marketsGrid.rewards.daily": "Recompensas",
  "marketsGrid.rewards.dailySuffix": "/día",
  "marketsGrid.rewards.maxSpread": "Spread máx.",
  "marketsGrid.rewards.minShares": "Shares mín.",
  "marketsGrid.rewards.aria": "Recompensas de liquidez",

  "recentTrades.filter.all": "Todas",
  "recentTrades.sort.latest": "Recientes",
  "recentTrades.sort.biggest": "Mayores",
  "recentTrades.whales": "Ballenas",
  "recentTrades.whalesTooltip": "Mostrar solo operaciones ≥ $10K",
  "recentTrades.empty": "Sin operaciones recientes",
  "recentTrades.anonymous": "Anónimo",

  "section.marketTalks": "Comentarios del mercado",
  "resolutionDetails.title": "Detalles de resolución",
  "section.frenflowAnalysis": "Análisis de FrenFlow",
  "section.faq": "FAQ",
  "section.faqSubtitle": "Pulsa una pregunta para ver la respuesta.",

  "marketTalks.placeholder": "Comparte tu opinión sobre este mercado...",
  "marketTalks.on": "En",
  "marketTalks.cancel": "Cancelar",
  "marketTalks.post": "Publicar",
  "marketTalks.posting": "Publicando…",
  "marketTalks.reply": "Responder",
  "marketTalks.replyingTo": "Respondiendo a",
  "marketTalks.cancelReply": "Cancelar respuesta",
  "marketTalks.empty.title": "Aún no hay comentarios sobre este mercado",
  "marketTalks.empty.subtitle": "¡Sé el primero en compartir tu opinión!",
  "marketTalks.anonymous": "Anónimo",
  "marketTalks.bought": "Compró",
  "marketTalks.sold": "Vendió",
  "marketTalks.viewAll": "Ver toda la actividad",

  "marketCard.moreOutcomes": "+{n} resultados más",

  "limitControls.price": "Precio límite",
  "limitControls.marketLabel": "Mercado: {price}",
  "limitControls.marketShortcut": "Mercado",
  "limitControls.expires": "Caduca",
  "limitControls.postOnly": "Solo limit",

  "orderSummary.sharesAt": "shares a",
  "orderSummary.sharesAvg": "shares · medio",
  "orderSummary.sellPrice": "Precio de venta",
  "orderSummary.avgPrice": "Precio medio",
  "orderSummary.shares": "Shares",
  "orderSummary.estFees": "Fees est.",
  "orderSummary.youReceive": "Recibirás",
  "orderSummary.realizedPnl": "P&L realizado",
  "orderSummary.maxPayout": "Pago máximo",
  "orderSummary.potentialProfit": "Beneficio potencial",
  "orderSummary.slippage": "Slippage",
  "orderSummary.partialFill": "Llenado parcial",
  "orderSummary.thinBook": "Libro insuficiente",

  "rewardsBadge.title": "Recompensas de liquidez",
  "rewardsBadge.summary.one": "{n} de {total} mercado con pool",
  "rewardsBadge.summary.many": "{n} de {total} mercados con pool",
  "rewardsBadge.minShares": "mín. {n} shares",
  "rewardsBadge.tightestSpread": "spread más bajo {spread}",
  "rewardsBadge.pool": "Pool",
  "rewardsBadge.perDay": "/día",

  "sportsTrade.market": "Mercado",
  "sportsTrade.limit": "Límite",
  "sportsTrade.fallback": "Mercado",
  "sportsTrade.hide": "Ocultar",
  "sportsTrade.splitMerge": "Split/Merge",
  "sportsTrade.spread": "Spread",
  "sportsTrade.mapHandicap": "Hándicap por mapa",
  "sportsTrade.gameHandicap": "Hándicap por partido",

  "sportsMatch.leagueFallback": "Partido",
  "sportsMatch.vs": "VS",
  "sportsMatch.live": "EN VIVO",
  "sportsMatch.draw": "Empate",
  "sportsMatch.statsVol": "Vol",
  "sportsMatch.statsLiq": "Liq",
  "sportsMatch.statsOi": "OI",

  "sportsTabs.moneyline": "Moneyline",
  "sportsTabs.gameLines": "Líneas del partido",
  "sportsTabs.totalGames": "Total de juegos",
  "sportsTabs.totalSets": "Total de sets",
  "sportsTabs.seriesLines": "Líneas de la serie",
  "sportsTabs.firstSet": "Set 1",
  "sportsTabs.firstSetWinner": "Ganador del set 1",
  "sportsTabs.firstSetTotalGames": "Juegos totales set 1",
  "sportsTabs.matchTotal": "Total del partido",
  "sportsTabs.totalCorners": "Total de córners",
  "sportsTabs.bothTeamsToScore": "Ambos equipos marcan",
  "sportsTabs.handicap": "Hándicap",
  "sportsTabs.spreads": "Spreads",
  "sportsTabs.totals": "Totales",
  "sportsTabs.firstHalf": "1ª parte",
  "sportsTabs.secondHalf": "2ª parte",
  "sportsTabs.map": "Mapa {n}",
  "sportsTabs.set": "Set {n}",
  "sportsTabs.period": "Periodo {n}",
  "sportsTabs.quarter": "Cuarto {n}",
  "sportsTabs.inning": "Entrada {n}",
  "sportsTabs.fallback": "{type}",
  "sportsTabs.moreMarkets": "Más mercados",
  "sportsTabs.exactScore": "Resultado exacto",
  "sportsTabs.halftimeResult": "Resultado al descanso",
  "sportsTabs.corners": "Córners",
  "sportsTabs.cards": "Tarjetas",
  "sportsTabs.btts": "Ambos marcan",
  "sportsTabs.firstGoal": "Primer gol",
  "sportsTabs.goalscorer": "Goleador",
  "sportsTabs.teamTopBatter": "Mejor bateador del equipo",
  "sportsTabs.mostSixes": "Más sixes",
  "sportsTabs.tossMatchDouble": "Toss Match Double",

  "moreGames.title": "Más de {league}",
  "marketLabel.totalLine": "Total {n}",
  "marketLabel.totalLineUnit": "Total {n} {unit}",
  "marketLabel.unit.games": "juegos",
  "marketLabel.unit.maps": "mapas",
  "marketLabel.unit.sets": "sets",
  "marketLabel.unit.gamesCap": "Juegos",
  "marketLabel.unit.mapsCap": "Mapas",

  "chartMeta.totalVolume": "Volumen total negociado en este evento",
  "chartMeta.volSuffix": "Vol.",
  "chartMeta.resolutionDate": "Fecha de resolución",
  "chartMeta.earn": "Gana 4%",
  "chartMeta.earnTooltip": "Gana recompensas manteniendo shares YES/NO elegibles. Se paga automáticamente según el tiempo que las mantengas.",
  "chartMeta.learnMore": "Saber más",
  "chartLegend.showMore": "+{n} más",
  "chartLegend.showLess": "Ver menos",
  "eventMarkets.moreMarkets": "Más mercados ({n})",
  "eventMarkets.showLess": "Ver menos",

  // EventSidebar — pills + AI context drawer
  "eventSidebar.setPriceAlert": "Configurar alerta de precio",
  "eventSidebar.alert": "Alerta",
  "eventSidebar.viewAIContext": "Ver contexto IA",
  "eventSidebar.context": "Contexto",
  "eventSidebar.relatedMarkets": "Mercados relacionados",
  "eventSidebar.aiLoading.politics": "Analizando contexto electoral...",
  "eventSidebar.aiLoading.finance": "Analizando datos de mercado...",
  "eventSidebar.aiLoading.geopolitics": "Analizando contexto geopolítico...",
  "eventSidebar.aiLoading.crypto": "Analizando mercado cripto...",
  "eventSidebar.aiLoading.awards": "Analizando ceremonia de premios...",
  "eventSidebar.aiLoading.weather": "Analizando datos meteorológicos...",
  "eventSidebar.aiLoading.generic": "IA analizando evento...",

  "cohort.title": "Quién opera",
  "cohort.summary": "Desglose de los {n} mayores holders vs. la plataforma (7d)",
  "cohort.tooltip.holders": "{cohort}: {count} de {total} top holders ({pct}%)",
  "cohort.tooltip.platformAvg": "Media plataforma: {pct}%",
  "cohort.tooltip.over": "→ {ratio}× sobrerrepresentado",
  "cohort.signal.qualityPrefix": "→ Señal de calidad:",
  "cohort.signal.whaleHeavy": "MUCHAS BALLENAS",
  "cohort.signal.aboveAvgWhales": "BALLENAS POR ENCIMA DE LA MEDIA",
  "cohort.label.WHALE": "Ballenas",
  "cohort.label.ACTIVE_TRADER": "Activos",
  "cohort.label.MARKET_MAKER": "Market maker",
  "cohort.label.BUY_AND_HOLD": "Hodler",
  "cohort.label.HIGH_CONVICTION": "Convicción",
  "cohort.label.CONTRARIAN": "Contrario",
  "cohort.label.VALUE_HUNTER": "Cazador de valor",
  "cohort.label.DEGEN": "Degen",
  "cohort.label.MIXED": "Mixto",
  "cohort.label.UNKNOWN": "Desconocido",

  "smartMoney.summary": "{n} wallets · {vol}",
  "smartMoney.yes": "SÍ",
  "smartMoney.no": "NO",
  "smartMoney.shares": "shares",
  "smartMoney.hideWallets": "Ocultar wallets",
  "smartMoney.showWallets": "Ver {n} smart wallets",
  "trading.price": "Precio",
  "trading.amount": "Cantidad",
  "trading.size": "Tamaño",
  "trading.outcome": "Resultado",
  "trading.yes": "Sí",
  "trading.no": "No",
  "trading.balance": "Saldo",
  "trading.available": "Disponible",
  "trading.fee": "Comisión",
  "trading.fees": "Comisiones",
  "trading.total": "Total",
  "trading.spent": "Gastado",
  "trading.value": "Valor",

  // ── Portfolio page (/portfolio) ────────────────────────────────────
  "portfolio.tab.positions": "Posiciones",
  "portfolio.tab.activity": "Actividad",
  "portfolio.tab.copytrading": "Copy",
  "portfolio.tab.alerts": "Alertas",
  "portfolio.signInTitle": "Inicia sesión para ver tu cartera",
  "portfolio.signInSubtitle": "Conecta tu wallet para ver tus posiciones y stats de copytrading",
  "portfolio.trader": "Trader",

  "portfolio.profile.tradingOn": "Trading activo",
  "portfolio.profile.enableTrading": "Activar trading",
  "portfolio.profile.refresh": "Actualizar",
  "portfolio.profile.share": "Compartir",
  "portfolio.profile.toClaim": "por reclamar",
  "portfolio.profile.percentPos": "{p}% pos",
  "portfolio.profile.winRate": "Aciertos",
  "portfolio.profile.bestTrade": "Mejor trade",
  "portfolio.profile.trades": "Trades",
  "portfolio.profile.volume": "Volumen",
  "portfolio.profile.openClosed": "{open} abiertas · {closed} cerradas",
  "portfolio.profile.fetchError": "No se pudieron cargar los datos del portfolio. Pulsa Actualizar para reintentar.",
  "portfolio.profile.download": "Descargar",
  "portfolio.profile.copyAddress": "Copiar dirección",

  "portfolio.chart.totalPnl": "P&L total",
  "portfolio.chart.empty": "Cierra trades para ver tu gráfico de P&L",
  "portfolio.chart.error": "No se pudo cargar el P&L",
  "portfolio.chart.day": "Día",
  "portfolio.chart.tradesSuffix": "trade",
  "portfolio.chart.tradesSuffixPlural": "trades",
  "portfolio.chart.range.7d": "7D",
  "portfolio.chart.range.30d": "30D",
  "portfolio.chart.range.90d": "90D",
  "portfolio.chart.range.all": "Todo",

  "portfolio.categories.title": "Categorías",
  "portfolio.categories.tradesSuffix": "trades",
  "portfolio.categories.empty": "Empieza a operar para ver tus categorías",
  "portfolio.categories.error": "No se pudieron cargar las categorías",
  "portfolio.categories.tooltipTrades": "{n} trades · {pct}%",

  "portfolio.redeem.wonOne": "¡Ganaste {n} mercado!",
  "portfolio.redeem.wonMany": "¡Ganaste {n} mercados!",
  "portfolio.redeem.subtitle": "Reclama tus ganancias",
  "portfolio.redeem.subtitleNow": "Reclama tus ganancias ahora",
  "portfolio.redeem.total": "Total",
  "portfolio.redeem.claimNow": "Reclamar ahora",
  "portfolio.redeem.claim": "Reclamar",

  "portfolio.rewards.title": "Recompensas Polymarket",
  "portfolio.rewards.subtitle": "Recompensas de liquidez + rebates de maker pagados por Polymarket",
  "portfolio.rewards.range.today": "Hoy",
  "portfolio.rewards.range.7d": "7d",
  "portfolio.rewards.range.30d": "30d",
  "portfolio.rewards.range.lifetime": "Todo",
  "portfolio.rewards.total": "Total",
  "portfolio.rewards.liquidity": "Liquidez",
  "portfolio.rewards.rebates": "Rebates",
  "portfolio.rewards.earningOnOne": "Ganando en {n} mercado",
  "portfolio.rewards.earningOnMany": "Ganando en {n} mercados",
  "portfolio.rewards.empty": "Coloca limit orders en reposo (maker) para empezar a ganar. Las recompensas se pagan cada día a las 00:00 UTC.",

  "portfolio.positions.tab.active": "Activas",
  "portfolio.positions.tab.closed": "Cerradas",
  "portfolio.positions.col.market": "Mercado",
  "portfolio.positions.col.outcome": "Resultado",
  "portfolio.positions.col.bought": "Comprado",
  "portfolio.positions.col.shares": "Shares",
  "portfolio.positions.col.avg": "Promedio",
  "portfolio.positions.col.now": "Ahora",
  "portfolio.positions.col.value": "Valor",
  "portfolio.positions.col.pnl": "PnL",
  "portfolio.activity.col.market": "Mercado",
  "portfolio.activity.col.side": "Lado",
  "portfolio.activity.col.shares": "Acciones",
  "portfolio.activity.col.notional": "Importe",
  "portfolio.activity.payout": "Cobrado",
  "portfolio.activity.col.status": "Estado",
  "portfolio.activity.col.time": "Hora",
  "portfolio.positions.source.all": "Todas",
  "portfolio.positions.source.polymarket": "Polymarket",
  "portfolio.positions.source.kalshi": "Kalshi",
  "portfolio.positions.source.predict": "Predict",
  "portfolio.positions.source.hyperliquid": "Hyperliquid",
  "portfolio.positions.searchPlaceholder": "Buscar posiciones…",
  "portfolio.positions.sort.value": "Valor",
  "portfolio.positions.sort.pnl": "P/L $",
  "portfolio.positions.sort.percent": "P/L %",
  "portfolio.positions.sort.avgPrice": "Precio medio",
  "portfolio.positions.sort.currentPrice": "Precio actual",
  "portfolio.positions.sort.dateClosed": "Fecha de cierre",
  "portfolio.positions.sort.dateOpened": "Fecha de apertura",
  "portfolio.positions.sort.alphabetical": "Alfabéticamente",
  "portfolio.positions.sort.traded": "Operado",
  "portfolio.wallets.sectionTitle": "Wallets",
  "portfolio.wallets.unit": "wallet",
  "portfolio.wallets.unitPlural": "wallets",
  "portfolio.wallets.depositWallet": "Wallet de depósito",
  "portfolio.wallets.safeWallet": "Wallet Safe",
  "portfolio.wallets.solanaWallet": "Wallet de Solana",
  "portfolio.wallets.hyperliquidWallet": "Wallet de Hyperliquid",
  "portfolio.wallets.evmWallet": "Wallet EVM",
  "portfolio.wallets.signingOnly": "Solo login / firma",
  "portfolio.wallets.active": "Activa",
  "portfolio.wallets.legacy": "Antigua",
  "portfolio.wallets.auth": "Auth",
  "portfolio.wallets.usdcTradable": "USDC operable",
  "portfolio.wallets.usdcAvailable": "USDC disponible",
  "portfolio.wallets.residualBalance": "Saldo residual",
  "portfolio.wallets.positionsLabel": "Posiciones de {platform}",
  "portfolio.wallets.usdcHlArb": "USDC (HL + Arbitrum)",
  "portfolio.wallets.usdcOnHl": "USDC en HL",
  "portfolio.wallets.usdcOnArb": "USDC en Arbitrum",
  "portfolio.wallets.warnFunds": "Este Safe previo a la migración aún tiene fondos. Retíralos a tu wallet activa desde ajustes.",
  "portfolio.wallets.warnPositions": "Aún hay posiciones V2 abiertas en este Safe. Resuélvelas o véndelas desde esta wallet.",
  "portfolio.positions.sellAll": "Vender todo",
  "portfolio.positions.refresh": "Actualizar",
  "portfolio.positions.closedHint": "Aquí ves tus posiciones terminadas: vendidas, ganadas o perdidas.",
  "portfolio.positions.sell": "Vender",
  "portfolio.positions.share": "Compartir",
  "portfolio.positions.view": "Ver",
  "portfolio.positions.merge": "Merge",
  "portfolio.positions.mergeYesNo": "Merge YES+NO",
  "portfolio.positions.copy": "COPY",
  "portfolio.positions.copiedFrom": "Copiado de {name}",
  "portfolio.positions.won": "GANADA",
  "portfolio.positions.lost": "PERDIDA",
  "portfolio.positions.sold": "VENDIDA",
  "portfolio.positions.empty.openTitle": "No tienes posiciones abiertas",
  "portfolio.positions.empty.closedTitle": "No tienes posiciones cerradas",
  "portfolio.positions.empty.closedSubtitle": "Tus posiciones resueltas aparecerán aquí",
  "portfolio.positions.empty.openSubtitle": "Empieza a operar para ver tus posiciones aquí",
  "portfolio.positions.empty.claimableOne": "Tienes {amount} por reclamar de {n} mercado resuelto",
  "portfolio.positions.empty.claimableMany": "Tienes {amount} por reclamar de {n} mercados resueltos",
  "portfolio.positions.exploreMarkets": "Explorar mercados",
  "portfolio.positions.noMatch": "No hay posiciones que coincidan con \"{q}\"",
  "portfolio.positions.error": "No se pudieron cargar las posiciones",
  "portfolio.positions.sellAllTitle": "Vender todas las posiciones",
  "portfolio.positions.sellAllSubtitle": "{n} posiciones a precio de mercado",
  "portfolio.positions.totalValue": "Valor total",
  "portfolio.positions.unrealizedPnl": "P&L no realizado",
  "portfolio.positions.sellAllWarning": "Esto venderá en Polymarket, Kalshi y Predict.fun. Es irreversible.",
  "portfolio.positions.cancel": "Cancelar",
  "portfolio.positions.selling": "Vendiendo…",
  "portfolio.positions.progressOf": "{cur} de {total}",
  "portfolio.positions.soldCount": "{n} vendidas",
  "portfolio.positions.failedCount": "{n} fallidas",
  "portfolio.positions.done": "Listo",
  "portfolio.positions.successful": "Correctas",
  "portfolio.positions.failed": "Fallidas",
  "portfolio.positions.summarySold": "{success} vendidas",
  "portfolio.positions.summarySoldFailed": "{success} vendidas, {failed} fallidas",

  "portfolio.activity.loading": "Cargando actividad…",
  "portfolio.activity.error": "No se pudo cargar la actividad reciente",
  "portfolio.activity.empty": "Sin trades recientes",
  "portfolio.activity.emptySubtitle": "Tu historial aparecerá aquí",
  "portfolio.activity.startTrading": "Empezar a operar",
  "portfolio.activity.justNow": "Ahora mismo",
  "portfolio.activity.minutesAgo": "hace {n}m",
  "portfolio.activity.hoursAgo": "hace {n}h",
  "portfolio.activity.daysAgo": "hace {n}d",
  "portfolio.activity.swap": "SWAP",
  "portfolio.activity.redeem": "REDEEM",
  "portfolio.activity.buy": "COMPRA",
  "portfolio.activity.sell": "VENTA",
  "portfolio.activity.yes": "YES",
  "portfolio.activity.no": "NO",
  "portfolio.activity.copy": "COPY",
  "portfolio.activity.won": "GANADA",
  "portfolio.activity.lost": "PERDIDA",
  "portfolio.activity.resolving": "RESOLVIENDO",
  "portfolio.activity.active": "ACTIVA",
  "portfolio.activity.fee": "Comisión",
  "portfolio.activity.pnl": "P&L",
  "portfolio.activity.unknownMarket": "Mercado desconocido",
  "portfolio.activity.positionResolved": "Posición resuelta",
  "portfolio.activity.shares": "shares",

  "portfolio.copy.loading": "Cargando traders…",
  "portfolio.copy.error": "No se pudo cargar el copytrading",
  "portfolio.copy.errorBoundary": "Algo falló al cargar Copy Trading",
  "portfolio.copy.errorBoundaryHint": "Intenta refrescar la página",
  "portfolio.copy.emptyTitle": "Aún no copias a nadie",
  "portfolio.copy.emptySubtitle": "Encuentra a los mejores traders en el leaderboard y empieza a copiar sus trades",
  "portfolio.copy.exploreTraders": "Explorar traders",
  "portfolio.copy.activeOf": "{active} activos de {total}",
  "portfolio.copy.realtimeOn": "Sincronización en tiempo real",
  "portfolio.copy.realtimeOff": "Conectando…",
  "portfolio.copy.fullView": "Gestionar todo",
  "portfolio.copy.copying": "Copiando",
  "portfolio.copy.pnlLabel": "Copy PnL",
  "portfolio.copy.pnlTooltip": "Realizado + no realizado − fees, solo de tus copy trades. Los manuales no cuentan aquí.",
  "portfolio.copy.openLabel": "Abiertas",
  "portfolio.copy.openPositions": "Posiciones abiertas",
  "portfolio.copy.netLabel": "neto",
  "portfolio.copy.liveLabel": "vivas",
  "portfolio.copy.modeName.fixed": "Fijo $",
  "portfolio.copy.modeName.percentage": "% del stake",
  "portfolio.copy.modeName.balance_percentage": "% del balance",
  "portfolio.copy.modeName.mirror_percentage": "Espejo %",
  "portfolio.copy.ofTraders": "de {n} traders",
  "portfolio.copy.totalTrades": "Total de trades",
  "portfolio.copy.copied": "copiados",
  "portfolio.copy.volume": "Volumen",
  "portfolio.copy.totalLabel": "total",
  "portfolio.copy.never": "Nunca",
  "portfolio.copy.unknown": "Desconocido",
  "portfolio.copy.active": "Activo",
  "portfolio.copy.paused": "Pausado",
  "portfolio.copy.noAddress": "Sin dirección",
  "portfolio.copy.vol": "Vol",
  "portfolio.copy.wr": "WR",
  "portfolio.copy.last": "Últ.",
  "portfolio.copy.editSettings": "Editar ajustes de copia",
  "portfolio.copy.manage": "Gestionar",
  "portfolio.copy.manageTitle": "Gestionar en Copy Trading",
  "portfolio.copy.viewProfile": "Ver perfil del trader",
  "portfolio.copy.unfollow": "Dejar de seguir",
  "portfolio.copy.mode": "Modo",
  "portfolio.copy.stake": "Stake",
  "portfolio.copy.max": "Máx",
  "portfolio.copy.copiedLabel": "Copiados:",
  "portfolio.copy.volumeLabel": "Volumen:",
  "portfolio.copy.tradesSuffix": "trades",
  "portfolio.copy.pause": "Pausar",
  "portfolio.copy.resume": "Reanudar",
  "portfolio.copy.confirm": "Confirmar",
  "portfolio.copy.cancel": "Cancelar",
  "portfolio.copy.toggleFailed": "No se pudo cambiar el estado de copia",
  "portfolio.copy.unfollowFailed": "No se pudo dejar de seguir al leader",

  "portfolio.alerts.emptyTitle": "Sin alertas activas",
  "portfolio.alerts.emptySubtitle": "Entra en un mercado y pulsa la campana para crear una alerta de precio",
  "portfolio.alerts.browseMarkets": "Ver mercados",
  "portfolio.alerts.activeOf": "{count} de {max} alertas activas",

  // ── Feedback page (/feedback) ──────────────────────────────────────
  "feedback.loading": "Cargando…",
  // ── Support chat widget (floating bubble) ───────────────────────────
  "support.title": "Soporte FrenFlow",
  "support.tooltip": "¿Necesitas ayuda? Escríbenos",
  "support.subtitle": "Respondemos lo antes posible",
  "support.empty": "Empieza una conversación, estamos para ayudarte.",
  "support.placeholder": "Escribe un mensaje…",
  "support.you": "Tú",
  "support.team": "Equipo FrenFlow",
  "support.attach": "Adjuntar imagen",
  "support.uploading": "Subiendo…",
  "support.sending": "Enviando…",
  "support.signIn": "Inicia sesión para hablar con soporte.",
  "support.open": "Abrir chat de soporte",
  "support.close": "Cerrar",
  "support.imageTooBig": "Imagen demasiado grande (máx. 5MB)",
  "support.sendError": "No se pudo enviar. Inténtalo de nuevo.",
  "support.resolvedNote": "FrenFlow marcó esto como resuelto",
  "support.closedNote": "Esta conversación está cerrada",
  "support.convResolved": "Conversación resuelta",
  "support.convClosed": "Conversación cerrada",
  "support.reopen": "Reabrir",
  "support.newTicket": "Nuevo ticket",
  "support.reopenMin": "Añade un poco más de detalle para reabrir (mín. 5 caracteres)",
  "support.modeReopen": "Reabriendo conversación",
  "support.modeNew": "Nuevo ticket",
  "support.chooseAction": "Elige una opción arriba",
  "feedback.signInPrompt": "Inicia sesión para enviar feedback y ver las respuestas.",
  "feedback.signIn": "Iniciar sesión",
  "feedback.tab.new": "Nuevo feedback",
  "feedback.tab.history": "Mi feedback",
  "feedback.intro": "Tu feedback nos ayuda a mejorar FrenFlow",
  "feedback.thankYou": "¡Gracias!",
  "feedback.submittedDesc": "Tu feedback se ha enviado. Puedes ver su estado en \"Mi feedback\".",
  "feedback.viewMine": "Ver mi feedback",
  "feedback.sendMore": "Enviar otro",
  "feedback.message": "Tu mensaje",
  "feedback.prefill.onboardingError.heading": "Me ha dado un error configurando el trading.",
  "feedback.prefill.onboardingError.errorLabel": "Error",
  "feedback.prefill.onboardingError.noError": "No se capturó ningún mensaje de error.",
  "feedback.prefill.onboardingError.contextHeading": "Contexto (auto-adjunto):",
  "feedback.placeholder.bug": "Describe el bug: qué pasó, qué esperabas y los pasos para reproducirlo…",
  "feedback.placeholder.feature": "Describe la feature que te gustaría y para qué te serviría…",
  "feedback.placeholder.question": "¿Qué te gustaría saber sobre FrenFlow?",
  "feedback.placeholder.general": "Cuéntanos tus ideas, sugerencias o lo que quieras…",
  "feedback.minChars": "Mínimo 10 caracteres",
  "feedback.remove": "Quitar",
  "feedback.attachScreenshot": "Adjuntar captura",
  "feedback.dropHere": "Suelta la captura aquí",
  "feedback.dropZoneHint": "o haz clic · PNG, JPG, WebP hasta 5MB",
  "feedback.uploading": "Subiendo imagen…",
  "feedback.submitting": "Enviando…",
  "feedback.submit": "Enviar",
  "feedback.selectTypePrompt": "Selecciona arriba un tipo de feedback",
  "feedback.chooseType": "¿De qué se trata?",
  "feedback.required": "Obligatorio",
  "feedback.historyTitle": "Mi feedback",
  "feedback.historySubtitle": "Sigue tus envíos y mira las respuestas",
  "feedback.noHistory": "Aún no has enviado feedback",
  "feedback.sendFirst": "Envía tu primer feedback",
  "feedback.loadMore": "Cargar más",
  "feedback.loadingDots": "Cargando…",
  "feedback.cat.bug.label": "Reporte de bug",
  "feedback.cat.bug.desc": "Algo no funciona como debería",
  "feedback.cat.feature.label": "Petición de feature",
  "feedback.cat.feature.desc": "Sugiere una nueva feature o mejora",
  "feedback.cat.question.label": "Pregunta",
  "feedback.cat.question.desc": "Pregúntanos cualquier cosa sobre FrenFlow",
  "feedback.cat.general.label": "Feedback general",
  "feedback.cat.general.desc": "Comparte tus ideas o sugerencias",
  "feedback.status.open": "Abierto",
  "feedback.status.in_progress": "En progreso",
  "feedback.status.resolved": "Resuelto",
  "feedback.status.declined": "Rechazado",
  "feedback.status.closed": "Cerrado",
  "feedback.status.deleted": "Eliminado",
  "feedback.statusTooltip.open": "Lo hemos recibido y está en la cola.",
  "feedback.statusTooltip.in_progress": "Estamos trabajando en ello.",
  "feedback.statusTooltip.resolved": "Aplicamos un fix o respuesta. Reabre si sigues con el problema.",
  "feedback.statusTooltip.declined": "Hemos decidido no implementarlo. Mira la respuesta del equipo.",
  "feedback.statusTooltip.closed": "Cerraste el ticket. Está archivado.",
  "feedback.statusTooltip.deleted": "Este ticket fue eliminado.",
  "feedback.historyFilter.all": "Todos",
  "feedback.historyFilter.open": "Abiertos",
  "feedback.historyFilter.resolved": "Resueltos",
  "feedback.historyFilter.closed": "Cerrados",
  "feedback.historyFilter.showAll": "Ver todos",
  "feedback.noHistoryInBucket": "Aquí no hay nada todavía.",
  "feedback.time.justNow": "Ahora mismo",
  "feedback.time.minutesAgo": "hace {n}m",
  "feedback.time.hoursAgo": "hace {n}h",
  "feedback.time.daysAgo": "hace {n}d",
  "feedback.card.hideImage": "Ocultar imagen",
  "feedback.card.showImage": "Ver imagen",
  "feedback.card.hideScreenshotAria": "Ocultar captura",
  "feedback.card.showScreenshotAria": "Ver captura",
  "feedback.card.adminReplyTitle": "Respuesta de FrenFlow",
  "feedback.card.userReplyTitle": "Tu respuesta",
  "feedback.card.close": "Cerrar feedback",
  "feedback.card.confirmClose": "¿Cerrar este ticket?",
  "feedback.card.cancel": "Cancelar",
  "feedback.card.yesClose": "Sí, cerrar",
  "feedback.card.replyCta": "Responder",
  "feedback.card.replyPlaceholder": "Escribe tu respuesta…",
  "feedback.card.replyOnceNote": "⚠️ Solo puedes responder una vez por feedback.",
  "feedback.card.sendReply": "Enviar respuesta",
  "feedback.card.reopen": "Sigue habiendo problema — reabrir",
  "feedback.card.teamBadge": "Equipo",
  "feedback.reopenModal.title": "Reabrir este ticket",
  "feedback.reopenModal.subtitle": "Cuéntanos qué sigue roto para que podamos arreglarlo.",
  "feedback.reopenModal.placeholder": "Describe qué sigue pasando — pasos para reproducir, qué esperabas vs qué obtuviste…",
  "feedback.reopenModal.minChars": "Mínimo 5 caracteres",
  "feedback.reopenModal.submit": "Reabrir",
  "feedback.error.replyMin": "La respuesta debe tener al menos 5 caracteres",
  "feedback.error.failedClose": "No se pudo cerrar el ticket",
  "feedback.error.failedReopen": "No se pudo reabrir el ticket",
  "feedback.error.somethingWrong": "Algo ha ido mal",
  "feedback.error.failedReply": "No se pudo enviar la respuesta",
  "feedback.error.invalidImage": "Solo se permiten imágenes JPEG, PNG, WebP y GIF",
  "feedback.error.imageTooBig": "La imagen debe pesar menos de 5MB",
  "feedback.error.uploadFailed": "No se pudo subir la imagen",
  "feedback.error.submitFailed": "No se pudo enviar el feedback",
  "feedback.error.tryAgain": "Algo ha ido mal. Inténtalo de nuevo.",

  // ── Error pages (500 / global) ─────────────────────────────────────
  "error.title": "Algo ha ido mal",
  "error.description": "Ha ocurrido un error inesperado. Inténtalo de nuevo.",
  "error.tryAgain": "Reintentar",
  "error.retrying": "Reintentando…",
  "error.headline": "¡Algo fue mal!",
  "error.subtitle": "Ha fallado algo de nuestro lado. Inténtalo de nuevo — normalmente se arregla al recargar.",
  "error.offlineHeadline": "Estás offline, fren.",
  "error.offlineSubtitle": "Revisa la conexión y volvemos.",
  "error.offlineChip": "Offline",
  "error.chip": "Error · 500 · {section}",
  "error.portfolio.title": "Portfolio no disponible",
  "error.portfolio.desc": "No pudimos cargar tu portfolio. Tus posiciones están a salvo.",
  "error.copytrading.title": "Copy Trading no disponible",
  "error.copytrading.desc": "No pudimos cargar tu configuración de copy trading. Tus copias activas siguen funcionando.",
  "error.activity.title": "Actividad no disponible",
  "error.activity.desc": "No pudimos cargar el feed de actividad. Inténtalo de nuevo.",
  "error.jumpTo": "O ve a",
  "error.shortcut.markets": "Mercados",
  "error.shortcut.traders": "Traders",
  "error.shortcut.activity": "Actividad",
  "error.shortcut.home": "Inicio",
  "error.reportBug": "Reportar bug",

  // ── Affiliate page (/affiliate) ────────────────────────────────────
  "aff.hero.badge": "Gana 10% de comisión en cada trade",
  "aff.hero.titlePart1": "El mejor",
  "aff.hero.titlePart2": "programa de referidos",
  "aff.hero.titlePart3": "de Polymarket",
  "aff.hero.subtitle": "Únete al programa de afiliados de FrenFlow y gana ingresos pasivos con prediction markets. Llévate <b>el 10% de todas las comisiones</b> de los usuarios que refieras, de los trades que salen de tus posts y cuando alguien copia tus trades. Sin límites, comisiones de por vida.",
  "aff.hero.ctaStart": "Empezar a ganar",
  "aff.hero.ctaLearn": "Saber más",
  "aff.hero.statCommission": "Comisión",
  "aff.hero.statMinPayout": "Pago mínimo",
  "aff.hero.statLifetimeLabel": "De por vida",
  "aff.hero.statLifetime": "Comisiones",
  "aff.hero.calcTitle": "Calcula tus ganancias",
  "aff.hero.calcSubtitle": "Mueve los sliders para ver tu potencial",
  "aff.hero.calcReferrals": "Referidos activos",
  "aff.hero.calcVolume": "Vol. medio/usuario/día",
  "aff.hero.calcDaily": "Diario",
  "aff.hero.calcMonthly": "Mensual",
  "aff.hero.calcYearly": "Proyección anual",
  "aff.calc.badge": "Calculadora de ganancias",
  "aff.calc.titlePart1": "¿Cuánto puedes",
  "aff.calc.titlePart2": "ganar?",
  "aff.calc.subtitle": "Mueve los deslizadores para ver tus ganancias potenciales con el programa de afiliados de FrenFlow para Polymarket.",
  "aff.calc.referrals": "Referidos activos",
  "aff.calc.users": "usuarios",
  "aff.calc.ariaReferrals": "Referidos activos",
  "aff.calc.volume": "Volumen diario medio por usuario",
  "aff.calc.perDay": "/día",
  "aff.calc.ariaVolume": "Volumen diario medio por usuario",
  "aff.calc.dailyVolume": "Volumen diario",
  "aff.calc.dailyEarnings": "Ganancias diarias",
  "aff.calc.monthlyEarnings": "Ganancias mensuales",
  "aff.calc.yearlyProjection": "Proyección anual",
  "aff.calc.formulaReferrals": "{n} referidos",
  "aff.calc.fee": "1% tarifa",
  "aff.calc.commission": "10% comisión",
  "aff.kit.color.lime.name": "Lima",
  "aff.kit.color.obsidian.name": "Obsidiana",
  "aff.kit.color.iron.name": "Hierro",
  "aff.kit.color.coal.name": "Carbón",
  "aff.kit.color.snow.name": "Nieve",
  "aff.kit.color.emerald.name": "Esmeralda",
  "aff.kit.color.crimson.name": "Carmesí",
  "aff.kit.color.violet.name": "Violeta",
  "aff.dash.source.web": "Web",
  "aff.dash.source.telegram": "Telegram",

  "aff.how.eyebrow": "Proceso simple",
  "aff.how.titlePart1": "Cómo funciona el programa",
  "aff.how.titlePart2": "de referidos",
  "aff.how.subtitle": "Empieza a ganar con Polymarket en 4 pasos. Sin experiencia previa: solo comparte tu enlace y gana.",
  "aff.how.step1.title": "Regístrate gratis",
  "aff.how.step1.desc": "Crea tu cuenta de FrenFlow en segundos. Usa email, Google o conecta tu wallet. Tu enlace de referido único se genera al instante.",
  "aff.how.step2.title": "Comparte tu enlace",
  "aff.how.step2.desc": "Comparte tu enlace en redes sociales, YouTube, Discord o directamente con amigos. Cada clic se trackea en tu dashboard.",
  "aff.how.step3.title": "Tus amigos se registran y operan",
  "aff.how.step3.desc": "Cuando alguien usa tu enlace para registrarse y empieza a operar en Polymarket vía FrenFlow, se convierte en tu referido.",
  "aff.how.step4.title": "Gana 10% para siempre",
  "aff.how.step4.desc": "Te llevas el 10% de todas las comisiones de tus referidos. Las comisiones se trackean en tiempo real y se pagan a demanda.",
  "aff.how.cta": "Consigue tu enlace de referido",

  "aff.benefits.eyebrow": "Por qué unirte",
  "aff.benefits.titlePart1": "Beneficios del programa",
  "aff.benefits.titlePart2": "de afiliados de FrenFlow",
  "aff.benefits.subtitle": "El programa de referidos de Polymarket más rentable. Gana ingresos pasivos mientras ayudas a otros a descubrir los prediction markets.",
  "aff.benefits.commission.title": "10% de comisión",
  "aff.benefits.commission.desc": "Gana el 10% de todas las comisiones generadas por tus referidos. Más alto que la mayoría de programas crypto.",
  "aff.benefits.posts.title": "Gana con tus posts",
  "aff.benefits.posts.desc": "Cuando alguien opera directamente desde uno de tus posts o análisis, te llevas comisión de ese trade. Mejor contenido = más engagement = más ganancias.",
  "aff.benefits.copy.title": "Comisiones de copytrading",
  "aff.benefits.copy.desc": "Cuando otros usuarios copian tus trades, ganas comisión por cada copia. Construye tu reputación y deja que tus seguidores hagan crecer tus ingresos.",
  "aff.benefits.lifetime.title": "Ganancias de por vida",
  "aff.benefits.lifetime.desc": "Tus referidos quedan vinculados a ti para siempre. Sigues ganando comisiones mientras sigan operando en Polymarket.",
  "aff.benefits.tracking.title": "Tracking instantáneo",
  "aff.benefits.tracking.desc": "Dashboard en tiempo real con clicks, registros y ganancias. Mide tu rendimiento al instante.",
  "aff.benefits.fp.title": "Bonus FrenPoints",
  "aff.benefits.fp.desc": "Recibe +10 FrenPoints por cada referido que se active ($100+ de volumen). Usa los FP para recompensas y ventajas.",
  "aff.benefits.market.title": "Mercado en crecimiento",
  "aff.benefits.market.desc": "El volumen de Polymarket está disparado. Más usuarios = más trades = más comisiones para ti.",
  "aff.benefits.nolimits.title": "Sin límites",
  "aff.benefits.nolimits.desc": "Sin tope de referidos ni de ganancias. Cuanto más compartas, más ganas. Escala sin restricciones.",

  "aff.creator.eyebrow": "Para creadores y traders",
  "aff.creator.titlePart1": "Mejor contenido =",
  "aff.creator.titlePart2": "más comisiones",
  "aff.creator.subtitle": "FrenFlow premia la calidad. Cuando compartes análisis, ideas de trade o construyes audiencia, <b>cada trade que salga de tu contenido te paga comisión</b>. Cuanto más engagement generes, más ganas.",
  "aff.creator.step1.title": "Publica análisis",
  "aff.creator.step1.desc": "Comparte tus predicciones, charts e insights. Los usuarios pueden operar directamente desde tus posts.",
  "aff.creator.step2.title": "Consigue impresiones",
  "aff.creator.step2.desc": "El buen contenido se ve. Más views = más posibles trades = más comisiones.",
  "aff.creator.step3.title": "Que te copien",
  "aff.creator.step3.desc": "Construye un track record y los usuarios copiarán tus trades. Cada copia te paga comisión.",
  "aff.creator.step4.title": "Cobra automáticamente",
  "aff.creator.step4.desc": "Las comisiones se trackean y se acreditan a tu cuenta solas. Sin reclamos manuales.",
  "aff.creator.cta": "Empezar a crear y ganar",
  "aff.creator.mockPost": "Bitcoin llegará a $150K antes de marzo. Aquí te explico por qué cargo este mercado…",
  "aff.creator.mockMarket": "¿BTC por encima de $150K en marzo?",
  "aff.creator.mockBuyYes": "Comprar SÍ 42¢",
  "aff.creator.mockBuyNo": "Comprar NO 58¢",
  "aff.creator.mockCopies": "{n} copias",
  "aff.creator.mockViews": "{n} views",
  "aff.creator.mockTimeAgo": "hace 2h",
  "aff.creator.mockEarned": "+$24,50 ganados hoy",
  "aff.creator.mockEarnedSub": "De 89 trades copiados de este post",
  "aff.creator.mockMonth": "Este mes",

  "aff.partners.badge": "Programa exclusivo",
  "aff.partners.titlePart1": "Programa de KOLs",
  "aff.partners.titlePart2": "y partners",
  "aff.partners.subtitle": "¿Eres creador de contenido, influencer o líder de comunidad? Consigue <b>acuerdos a medida y comisiones más altas</b> según tu audiencia.",
  "aff.partners.perk1.title": "Comisiones a medida",
  "aff.partners.perk1.desc": "Negocia comisiones más altas según el tamaño y engagement de tu audiencia.",
  "aff.partners.perk2.title": "Soporte dedicado",
  "aff.partners.perk2.desc": "Tendrás un account manager dedicado para maximizar tus ingresos.",
  "aff.partners.perk3.title": "Acceso anticipado",
  "aff.partners.perk3.desc": "Sé el primero en probar nuevas features y comparte contenido exclusivo con tu audiencia.",
  "aff.partners.perk4.title": "Co-marketing",
  "aff.partners.perk4.desc": "Campañas conjuntas, AMAs y soporte promocional para potenciar tu alcance.",
  "aff.partners.cta": "Contáctanos: partners@frenflow.com",
  "aff.partners.ctaSub": "Cuéntanos sobre tu audiencia y te montamos un acuerdo a medida",

  "aff.cta.title": "¿Listo para empezar a ganar?",
  "aff.cta.subtitle": "Únete hoy al mejor programa de afiliados de Polymarket. Consigue tu enlace en segundos y empieza a ganar el 10% en cada trade.",
  "aff.cta.primary": "Consigue tu enlace de referido",
  "aff.cta.telegram": "Probar el bot de Telegram",
  "aff.cta.trust1": "Sin depósito",
  "aff.cta.trust2": "Gratis",
  "aff.cta.trust3": "Setup instantáneo",

  "aff.faq.eyebrow": "FAQ",
  "aff.faq.titlePart1": "FAQ del programa",
  "aff.faq.titlePart2": "de afiliados",
  "aff.faq.subtitle": "Todo lo que necesitas saber para ganar con el programa de afiliados de FrenFlow.",
  "aff.faq.contact.prefix": "¿Más preguntas? Escríbenos a",
  "aff.faq.cat.basics": "Lo básico",
  "aff.faq.cat.earnings": "Ganancias y comisiones",
  "aff.faq.cat.payouts": "Pagos",
  "aff.faq.cat.creators": "Para creadores",
  "aff.faq.cat.tracking": "Tracking de referidos",
  "aff.faq.cat.security": "Seguridad y confianza",
  "aff.faq.basics.0.q": "¿Qué es el programa de afiliados de FrenFlow?",
  "aff.faq.basics.0.a": "Te permite ganar el 10% de las comisiones de trading generadas por los usuarios que refieras. Cuando alguien se registra con tu enlace y opera en cualquier prediction market vía FrenFlow, ganas dinero automáticamente — para siempre.",
  "aff.faq.basics.1.q": "¿En qué se diferencia de otros programas?",
  "aff.faq.basics.1.a": "FrenFlow ofrece una de las comisiones más altas en prediction markets (10% frente al 5% típico). Además, ganas con TODOS los prediction markets, no solo Polymarket. Según vayamos integrando Kalshi, PredictIt y otros, los trades de tus referidos en esas plataformas también te generarán comisión.",
  "aff.faq.basics.2.q": "¿Tengo que operar yo para ganar comisiones?",
  "aff.faq.basics.2.a": "No. No necesitas depositar fondos ni hacer trades para ganar comisiones de afiliado. Regístrate, consigue tu enlace y empieza a compartir. Aun así, operar tú mismo te ayudará a hacer mejor contenido y atraer más referidos.",
  "aff.faq.earnings.0.q": "¿Cuánto puedo ganar como afiliado?",
  "aff.faq.earnings.0.a": "Te llevas el 10% de todas las comisiones de tus referidos. FrenFlow cobra 1% por trade, así que recibes 0,1% de su volumen total. Ejemplo: si tus referidos operan $100.000, ganas $100. Sin tope: cuantos más referidos activos tengas, más ganas.",
  "aff.faq.earnings.1.q": "¿Gano con todos los prediction markets?",
  "aff.faq.earnings.1.a": "Sí. Ganas con TODOS los prediction markets integrados con FrenFlow. Hoy son Polymarket, y según añadamos más (Kalshi, PredictIt, mercados deportivos…), la actividad de tus referidos en ellos también te pagará comisión.",
  "aff.faq.earnings.2.q": "¿Cuánto tiempo gano comisiones de un referido?",
  "aff.faq.earnings.2.a": "De por vida. Una vez alguien se registra con tu enlace, queda vinculado permanentemente a tu cuenta. Ganas el 10% de sus comisiones mientras siga usando FrenFlow — sin caducidad.",
  "aff.faq.payouts.0.q": "¿Cuál es el pago mínimo?",
  "aff.faq.payouts.0.a": "El umbral mínimo es $10. Cuando tus comisiones lleguen a $10, puedes solicitar el pago en cualquier momento desde el dashboard.",
  "aff.faq.payouts.1.q": "¿Cómo y cuándo cobro?",
  "aff.faq.payouts.1.a": "Tus ganancias se trackean en tiempo real. Solicita el pago cuando llegues al mínimo de $10. Procesamos los pagos en menos de 48h en USDC a tu wallet o método de pago preferido.",
  "aff.faq.payouts.2.q": "¿Hay límite a cuánto puedo ganar?",
  "aff.faq.payouts.2.a": "Sin límite. Tengas 10 referidos o 10.000, ganas el 10% de sus comisiones. Los mejores afiliados ganan miles al mes. Te animamos a hacer crecer tu red lo máximo posible.",
  "aff.faq.creators.0.q": "¿Puedo ganar con mis posts y contenido?",
  "aff.faq.creators.0.a": "Sí. Cuando compartes análisis o predicciones en FrenFlow y alguien opera directamente desde tu post, te llevas comisión. Mejor contenido = más impresiones = más trades = más ganancias.",
  "aff.faq.creators.1.q": "¿Cómo gano con copy trading?",
  "aff.faq.creators.1.a": "Cuando los usuarios siguen y copian tus trades, ganas comisión por cada copia. Construye un buen track record, comparte tus aciertos y haz crecer tu base de seguidores.",
  "aff.faq.creators.2.q": "¿Mis referidos reciben algún beneficio?",
  "aff.faq.creators.2.a": "Sí. Quien se registre con tu enlace tiene acceso completo a FrenFlow: copy trading, whale tracking, bot de Telegram, AI analytics y más. También aprovechan nuestras comisiones bajas (1%) y trading non-custodial seguro.",
  "aff.faq.tracking.0.q": "¿Cómo trackeo mi rendimiento?",
  "aff.faq.tracking.0.a": "Tu dashboard de afiliados muestra stats en tiempo real: clicks, registros, referidos activos, volumen total, comisiones ganadas y pagos pendientes.",
  "aff.faq.tracking.1.q": "¿Hay límite a cuánta gente puedo referir?",
  "aff.faq.tracking.1.a": "Sin límite. Refiere a cuanta gente quieras. Cada registro vía tu enlace te da comisiones de por vida sobre sus trades.",
  "aff.faq.tracking.2.q": "¿Y si alguien borra sus cookies?",
  "aff.faq.tracking.2.a": "Nuestro tracking usa varios métodos además de cookies. Cuando alguien hace clic en tu enlace queda trackeado 30 días. Una vez se registra, queda vinculado permanentemente a tu cuenta sin importar cookies o dispositivos.",
  "aff.faq.security.0.q": "¿Es seguro promocionar FrenFlow?",
  "aff.faq.security.0.a": "Totalmente. FrenFlow usa arquitectura non-custodial — los fondos están en las wallets de los usuarios, propulsadas por Privy. Solo podemos ejecutar trades autorizados, nunca retirar fondos. El dinero de tus referidos siempre está seguro.",
  "aff.faq.security.1.q": "¿Qué es FrenFlow y por qué promocionarlo?",
  "aff.faq.security.1.a": "FrenFlow es la plataforma líder para operar prediction markets, con copy trading, whale tracking y AI analytics. Hace mercados como Polymarket accesibles para todos. Con miles de millones de volumen, es una de las plataformas que más rápido crece en crypto.",
  "aff.faq.security.2.q": "¿Tengo que avisar de la relación de afiliado?",
  "aff.faq.security.2.a": "Sí, te recomendamos siempre revelar que es un enlace de afiliado. Genera confianza con tu audiencia y muchas regulaciones publicitarias lo exigen. Los afiliados transparentes suelen tener mejores tasas de conversión.",

  "aff.promo.eyebrow": "Playbook de promoción",
  "aff.promo.titlePart1": "Cómo promocionar y",
  "aff.promo.titlePart2": "ganar más",
  "aff.promo.subtitle": "Estrategias probadas para hacer crecer tu red de referidos. Elige la que encaje con tu estilo y empieza a ganar.",
  "aff.promo.tipsTitle": "Tips para triunfar",
  "aff.promo.bottomPrefix": "¿Más ideas? Visita nuestro",
  "aff.promo.bottomLink": "blog",
  "aff.promo.bottomSuffix": "para guías de marketing e inspiración.",

  "aff.dash.loadingSetup": "Preparando tu cuenta de afiliado…",
  "aff.dash.tryAgain": "Reintentar",
  "aff.dash.title": "Programa de afiliados",
  "aff.dash.subtitle": "Gana el {pct}% de comisiones + 10 FP por cada referido activo ($100+ de volumen)",
  "aff.dash.tab.dashboard": "Panel",
  "aff.dash.tab.brandKit": "Kit de marca",
  "aff.dash.stat.available": "Disponible",
  "aff.dash.stat.earned": "Ganado",
  "aff.dash.stat.fp": "FP",
  "aff.dash.stat.referrals": "Referidos",
  "aff.dash.stat.clicks": "Clicks",
  "aff.dash.invites.title": "Códigos de invitación",
  "aff.dash.invites.subtitle": "Códigos de un solo uso para invitar amigos",
  "aff.dash.invites.remaining": "Restantes",
  "aff.dash.invites.unlimited": "ilimitados",
  "aff.dash.invites.new": "+ Nuevo",
  "aff.dash.invites.empty": "Aún no tienes códigos",
  "aff.dash.invites.emptyHint": "Pulsa \"+ Nuevo\" para generar el primero",
  "aff.dash.invites.used": "Usado",
  "aff.dash.invites.available": "Disponible",
  "aff.dash.invites.usedBy": "Usado por {who}",
  "aff.dash.invites.usedBySomeone": "Usado por alguien",
  "aff.dash.invites.copyLink": "Copiar enlace",
  "aff.dash.invites.copied": "¡Copiado!",
  "aff.dash.invites.shareCard": "Compartir tarjeta",
  "aff.dash.invites.created": "Creado {date}",
  "aff.dash.invites.confirmGenerate": "¿Generar un nuevo código? {msg}",
  "aff.dash.invites.unlimitedMsg": "Tienes invitaciones ilimitadas.",
  "aff.dash.invites.remainingMsg": "Te quedan {n}.",
  "aff.dash.tabs.referrals": "Referidos ({n})",
  "aff.dash.tabs.commissions": "Comisiones ({n})",
  "aff.dash.tabs.payouts": "Pagos ({n})",
  "aff.dash.referrals.empty": "Aún no tienes referidos. ¡Comparte tu enlace para empezar!",
  "aff.dash.commissions.empty": "Sin comisiones todavía. Empezarás a ganar cuando tus referidos operen.",
  "aff.dash.payouts.empty": "Sin pagos aún. Solicita el primero desde la barra lateral.",
  "aff.dash.col.wallet": "Wallet",
  "aff.dash.col.level": "Nivel",
  "aff.dash.col.origin": "Origen",
  "aff.dash.col.source": "Fuente",
  "aff.dash.col.date": "Fecha",
  "aff.dash.col.status": "Estado",
  "aff.dash.col.volume": "Volumen",
  "aff.dash.col.earned": "Ganado",
  "aff.dash.col.from": "De",
  "aff.dash.col.commission": "Comisión",
  "aff.dash.col.amount": "Importe",
  "aff.dash.col.destination": "Destino",
  "aff.dash.col.tx": "TX",
  "aff.dash.status.active": "Activo",
  "aff.dash.status.pending": "Pendiente",
  "aff.dash.anonymous": "Anónimo",
  "aff.dash.parentTooltip": "Tu referido L1 que trajo a este usuario",
  "aff.dash.post": "Post",
  "aff.dash.viewPost": "Ver post",
  "aff.dash.payoutStatus.requested": "Pendiente",
  "aff.dash.payoutStatus.approved": "Aprobado",
  "aff.dash.payoutStatus.processing": "Procesando",
  "aff.dash.payoutStatus.completed": "Completado",
  "aff.dash.payoutStatus.rejected": "Rechazado",
  "aff.dash.payoutStatus.failed": "Fallido",
  "aff.dash.rejected": "${amt} rechazados: {reason}",
  "aff.dash.linkCard.yourLink": "Tu código",
  "aff.dash.linkCard.copy": "Copiar",
  "aff.dash.linkCard.copied": "Copiado",
  "aff.dash.linkCard.webLabel": "Enlace web",
  "aff.dash.linkCard.telegramLabel": "Enlace del bot de Telegram",
  "aff.dash.linkCard.shareX": "Compartir en X",
  "aff.dash.linkCard.shareTg": "Compartir en Telegram",
  "aff.dash.rewards.title": "Tus recompensas",
  "aff.dash.rewards.fees": "Comisiones de referidos",
  "aff.dash.rewards.fp": "Bonus FrenPoints",
  "aff.dash.rewards.l2": "Comisiones L2",
  "aff.dash.rewards.l3": "Comisiones L3",
  "aff.dash.rewards.note": "Gana el {pct}% de las comisiones + bonus de 10 FP por cada referido que llegue a $100+ de volumen.",
  "aff.dash.last30": "Últimos 30 días",
  "aff.dash.metrics.clicks": "Clicks",
  "aff.dash.metrics.signups": "Registros",
  "aff.dash.metrics.earned": "Ganado",
  "aff.dash.payout.title": "Pago",
  "aff.dash.payout.request": "Solicitar pago",
  "aff.dash.payout.noBalance": "Sin saldo disponible",
  "aff.dash.payout.processedIn": "Pagos procesados en menos de 48h",
  "aff.dash.error.timeout": "La petición ha tardado demasiado. Inténtalo otra vez.",
  "aff.dash.error.loading": "Error al cargar los datos del afiliado",
  "aff.dash.error.creatingCode": "Error al crear el código de referido",
  "aff.dash.tweetCopy": "Opera prediction markets como un pro con FrenFlow! Únete:",
  "aff.dash.telegramCopy": "Opera prediction markets como un pro con FrenFlow! Únete:",

  "aff.kit.title": "Recursos de marca",
  "aff.kit.subtitle": "Logos, colores, banners y captions listos para publicar. Todo incluye tu enlace de referido.",
  "aff.kit.downloadAll": "Descargar todo",
  "aff.kit.downloading": "Descargando recursos…",
  "aff.kit.copied": "¡{hex} copiado!",
  "aff.kit.logo.title": "Logo",
  "aff.kit.logo.desc": "Usa el wordmark de forma consistente. Prefiere monocromo sobre fondos de color.",
  "aff.kit.logo.dark": "Logo Dark",
  "aff.kit.logo.light": "Logo Light",
  "aff.kit.logo.accent": "Logo Accent",
  "aff.kit.logo.download": "Descargar",
  "aff.kit.colors.title": "Paleta de color",
  "aff.kit.colors.desc": "Los hex reales que usa la app. Pulsa cualquier muestra para copiar.",
  "aff.kit.color.lime.role": "Acento de marca",
  "aff.kit.color.obsidian.role": "Fondo de página (dark)",
  "aff.kit.color.iron.role": "Card elevada",
  "aff.kit.color.coal.role": "Card a ras",
  "aff.kit.color.snow.role": "Fondo de página (light)",
  "aff.kit.color.emerald.role": "SÍ · positivo",
  "aff.kit.color.crimson.role": "NO · negativo",
  "aff.kit.color.violet.role": "Marcador copytrade",
  "aff.kit.type.title": "Tipografía",
  "aff.kit.type.desc": "Dos tipografías: Space Grotesk para títulos, Inter para todo lo demás.",
  "aff.kit.type.headings": "Títulos y display",
  "aff.kit.type.ui": "UI · cuerpo · stats",
  "aff.kit.type.bold": "Bold",
  "aff.kit.type.semibold": "Semibold",
  "aff.kit.type.medium": "Medium",
  "aff.kit.type.regular": "Regular",
  "aff.kit.type.sampleHeading": "Opera mejor",
  "aff.kit.type.sampleUi": "Copy Trader",

  "aff.payout.title": "Solicitar pago",
  "aff.payout.requested": "¡Pago solicitado!",
  "aff.payout.pendingApproval": "Tu pago está pendiente de aprobación",
  "aff.payout.amount": "Importe",
  "aff.payout.commissions": "Comisiones",
  "aff.payout.status": "Estado",
  "aff.payout.statusPending": "Pendiente de aprobación",
  "aff.payout.processedIn": "Tu pago se procesará en menos de 48 horas",
  "aff.payout.close": "Cerrar",
  "aff.payout.availableBalance": "Saldo disponible",
  "aff.payout.allPendingNote": "Se incluyen todas las comisiones pendientes",
  "aff.payout.destinationLabel": "Dirección de destino (Polygon)",
  "aff.payout.invalidAddress": "Formato de dirección Polygon no válido",
  "aff.payout.invalidAddressError": "Dirección de wallet Polygon no válida",
  "aff.payout.failed": "No se pudo solicitar el pago",
  "aff.payout.note1": "El pago se enviará como USDC.e en la red Polygon.",
  "aff.payout.note2": "Asegúrate de que la dirección soporta USDC.e (ERC-20).",
  "aff.payout.requesting": "Solicitando…",
  "aff.payout.cta": "Solicitar pago — ${amount}",

  "aff.invite.title": "Compartir tarjeta de invitación",
  "aff.invite.generating": "Generando tarjeta…",
  "aff.invite.generationFailed": "No se pudo generar la tarjeta",
  "aff.invite.retry": "Reintentar",
  "aff.invite.dragHint": "Arrastra para rotar · Doble clic para 360°",
  "aff.invite.tagline": "Opera mejor · Ganamos juntos",
  "aff.invite.copy": "Copiar",
  "aff.invite.copied": "¡Copiado!",
  "aff.invite.copyError": "No se pudo copiar al portapapeles:",
  "aff.invite.shareTextPrefix": "¡Únete a FrenFlow con mi código de invitación! Opera prediction markets y gana recompensas:",
  "aff.invite.twitterCta": "Opera en @frenflow_",
  "aff.invite.pasteHintTwitter": "¡Imagen copiada! Pégala (Ctrl+V / Cmd+V) en tu tuit para incluir la tarjeta.",
  "aff.invite.pasteHintTelegram": "¡Imagen copiada! Pégala (Ctrl+V / Cmd+V) en Telegram para incluir la tarjeta.",

  // ── FrenLinks page (/frenlinks) ────────────────────────────────────
  "fren.hero.badge": "Enlaces de regalo de prediction markets",
  "fren.hero.titlePart1": "Envía shares a cualquiera",
  "fren.hero.titlePart2": "con un solo enlace",
  "fren.hero.subtitle": "Crea enlaces de regalo de posiciones de Polymarket. Compártelos en X, Telegram o Discord. Tus frens reclaman al instante — sin necesidad de wallet.",
  "fren.hero.benefit.escrow": "Escrow on-chain",
  "fren.hero.benefit.instant": "Reclamos instantáneos",
  "fren.hero.benefit.refund": "Auto-refund",
  "fren.hero.cta": "Crea tu primer FrenLink",
  "fren.hero.trust.free": "Gratis",
  "fren.hero.trust.noWallet": "Sin wallet para reclamar",
  "fren.hero.demoBadge": "Demo interactiva",
  "fren.how.eyebrow": "Cómo funciona",
  "fren.how.title": "De posición a enlace viral en 60 segundos",
  "fren.how.step1.title": "Elige una posición",
  "fren.how.step1.desc": "Selecciona cualquier share YES o NO que tengas en Polymarket. Cualquier mercado, cualquier outcome.",
  "fren.how.step2.title": "Define las reglas",
  "fren.how.step2.desc": "Elige el modo: regala a un fren, reparte entre varios, distribuye al azar o monta una lotería.",
  "fren.how.step3.title": "Añade quests sociales",
  "fren.how.step3.desc": "Bloquea el reclamo tras follows en X, reposts, joins de Discord o grupos de Telegram. Crecimiento incorporado.",
  "fren.how.step4.title": "Comparte y gana",
  "fren.how.step4.desc": "Cada reclamo se registra como tu referido. Haz crecer tu red mientras compartes alpha.",
  "fren.modes.title": "Cuatro modos de reclamo",
  "fren.modes.subtitle": "Cada modo crea una dinámica distinta. Elige tu estrategia.",
  "fren.modes.gift.tag": "Directo",
  "fren.modes.gift.label": "Regalo",
  "fren.modes.gift.desc": "Un enlace, un destinatario. Perfecto para premiar a un fren concreto o cerrar un trato.",
  "fren.modes.split.tag": "Comunidad",
  "fren.modes.split.label": "Reparto",
  "fren.modes.split.desc": "Los primeros N reclaman se reparten los shares por igual. Ideal para drops en Discord y Telegram.",
  "fren.modes.random.tag": "Sorpresa",
  "fren.modes.random.label": "Aleatorio",
  "fren.modes.random.desc": "Cada reclamante recibe una cantidad aleatoria del pool. La sorpresa engancha.",
  "fren.modes.lottery.tag": "Viral",
  "fren.modes.lottery.label": "Lotería",
  "fren.modes.lottery.desc": "Un único ganador se lleva todo el pool. Máxima viralidad — todos quieren probar suerte.",
  "fren.useCases.title": "Para cualquier caso de uso",
  "fren.useCases.kol.title": "Sorteos de KOLs",
  "fren.useCases.kol.desc": "Monta sorteos de prediction markets en X. Bloquea reclamos tras follows y reposts para hacer crecer tu audiencia mientras repartes alpha.",
  "fren.useCases.community.title": "Recompensas a la comunidad",
  "fren.useCases.community.desc": "Premia a tu Discord o Telegram con shares gratis de Polymarket. El modo Reparto reparte de forma justa.",
  "fren.useCases.referrals.title": "Campañas de referidos",
  "fren.useCases.referrals.desc": "Cada reclamo de FrenLink registra a quien reclama como tu referido. Escala tu red sin fricción.",
  "fren.useCases.competitions.title": "Concursos de trading",
  "fren.useCases.competitions.desc": "Usa el modo lotería para crear sorteos de prediction markets de alto riesgo. Engagement y viralidad al máximo.",
  "fren.trust.title": "Cero riesgo por diseño",
  "fren.trust.subtitle": "Los FrenLinks usan escrow on-chain. Tus shares quedan bloqueadas en un smart contract — no las custodiamos. Si nadie reclama, te las devolvemos automáticamente.",
  "fren.trust.escrow.title": "Escrow en smart contract",
  "fren.trust.escrow.desc": "Los shares quedan custodiados sin confianza en Polygon. Verificable on-chain.",
  "fren.trust.claims.title": "Reclamos en un clic",
  "fren.trust.claims.desc": "Sin setup de wallet. Si quien reclama no tiene wallet, le creamos una.",
  "fren.trust.refunds.title": "Reembolsos automáticos",
  "fren.trust.refunds.desc": "Los enlaces caducados o cancelados devuelven los shares a tu wallet al instante.",
  "fren.trust.stat.fee": "Comisión de plataforma",
  "fren.trust.stat.refund": "Reembolso al caducar",
  "fren.trust.stat.claimTime": "Tiempo de reclamo",
  "fren.trust.stat.tasks": "Tasks sociales por enlace",
  "fren.faq.title": "Preguntas frecuentes",
  "fren.faq.0.q": "¿Qué son los FrenLinks?",
  "fren.faq.0.a": "Los FrenLinks te permiten enviar shares de prediction markets de Polymarket a cualquiera vía un enlace simple. Los receptores pueden reclamar sin tener wallet — nosotros nos encargamos.",
  "fren.faq.1.q": "¿Tengo que pagar comisiones?",
  "fren.faq.1.a": "No. Crear y reclamar FrenLinks es totalmente gratis. El único coste son los shares que decides regalar.",
  "fren.faq.2.q": "¿Qué pasa si nadie reclama mi enlace?",
  "fren.faq.2.a": "Tus shares se devuelven automáticamente cuando el enlace caduca. También puedes cancelar un enlace en cualquier momento para recuperar los shares al instante.",
  "fren.faq.3.q": "¿Puedo exigir acciones sociales antes de reclamar?",
  "fren.faq.3.a": "Sí. Puedes bloquear los reclamos tras follows en X, reposts, likes, joins en servidores de Discord o grupos de Telegram. Hasta 8 tasks por enlace.",
  "fren.faq.4.q": "¿Cómo funciona el modo lotería?",
  "fren.faq.4.a": "En modo lotería, todos los reclamantes entran a un sorteo. Cuando el enlace caduca o se completa, un único ganador aleatorio se lleva todos los shares. Los demás no reciben nada — eso es lo que lo hace viral.",
  "fren.finalCta.title": "¿Listo para compartir el alpha?",
  "fren.finalCta.subtitle": "Crea tu primer FrenLink en segundos. Regala shares de prediction markets a cualquiera, en cualquier sitio.",
  "fren.finalCta.button": "Empieza gratis",
  "fren.finalCta.note": "Sin tarjeta. Sin comisiones. Nunca.",

  "fren.dash.title": "FrenLinks",
  "fren.dash.subtitle": "Envía shares de Polymarket a cualquiera con un enlace.",
  "fren.dash.everyClaim.bold": "Cada reclamo = un nuevo referido.",
  "fren.dash.everyClaim.rest": "Comparte en X, Telegram o Discord para hacer crecer tu red.",
  "fren.dash.linkReady": "Enlace listo para compartir",
  "fren.dash.shareOnX": "Compartir en X",
  "fren.dash.yourLinks": "Tus enlaces",
  "fren.dash.shareText.single": "Te acabo de enviar shares gratis de Polymarket en \"{market}\"\n\nReclámalos antes de que caduquen\n{url}",
  "fren.dash.shareText.multi": "Shares gratis de Polymarket en \"{market}\" para los primeros {n}\n\nReclama el tuyo\n{url}",
  "fren.dash.shareText.random": "Shares gratis de Polymarket en \"{market}\" — abre tu premio sorpresa\n\n{n} plazas disponibles\n{url}",
  "fren.dash.shareText.lottery": "{shares} shares de Polymarket en \"{market}\" en juego\n\n1 entre {odds} de llevártelo todo — prueba suerte\n{url}",

  "fren.table.empty": "Sin enlaces aún",
  "fren.table.market": "Mercado",
  "fren.table.cancelConfirm": "¿Cancelar este enlace y recuperar los shares?",
  "fren.table.expired": "Caducado",
  "fren.table.status.pending": "Pendiente",
  "fren.table.status.active": "Activo",
  "fren.table.status.completed": "Hecho",
  "fren.table.status.cancelled": "Cancelado",
  "fren.table.status.expired": "Caducado",

  "fren.create.tab.gift": "Regalo",
  "fren.create.tab.split": "Reparto",
  "fren.create.tab.lottery": "Lotería",
  "fren.create.header": "Enviar shares de Polymarket",
  "fren.create.expiresIn": "Caduca en",
  "fren.create.requireSocial": "Requerir acciones sociales",
  "fren.create.addTask": "Añadir task",
  "fren.create.action.follow": "Follow",
  "fren.create.action.repost": "Repost",
  "fren.create.action.like": "Like",
  "fren.create.action.joinServer": "Unirse al server",
  "fren.create.action.joinGroup": "Unirse al grupo",
  "fren.create.tasksRequiredOne": "{n} task requerida para reclamar",
  "fren.create.tasksRequiredMany": "{n} tasks requeridas para reclamar",
  "fren.create.placeholder.handle": "@usuario",
  "fren.create.placeholder.tweet": "https://x.com/.../status/...",
  "fren.create.placeholder.discord": "https://discord.gg/...",
  "fren.create.placeholder.telegram": "https://t.me/grupo",
  "fren.create.preview.members": "{n} miembros",
  "fren.create.position": "Posición",
  "fren.create.noPositions": "Sin posiciones abiertas",
  "fren.create.tradeFirst": "Opera primero →",
  "fren.create.selectPosition": "Selecciona una posición",
  "fren.create.shares": "shares",
  "fren.create.amount": "Cantidad",
  "fren.create.of": "de {n}",
  "fren.create.max": "Máx",
  "fren.create.randomNote": "Cada destinatario abre una animación de sorpresa antes de ver sus shares",
  "fren.create.recipients": "Destinatarios",
  "fren.create.eachGets": "Cada uno recibe",
  "fren.create.winnerGets": "El ganador recibe",
  "fren.create.winProb": "Probabilidad de ganar",
  "fren.create.oneIn": "1 entre",
  "fren.create.wins": "gana",
  "fren.create.summaryWinner": "El ganador recibe",
  "fren.create.summaryEach": "Cada destinatario recibe",
  "fren.create.summarySharesSuffix": "shares",
  "fren.create.chance": "{pct}% de probabilidad",
  "fren.create.creating": "Creando…",
  "fren.create.create": "Crear enlace",
  "fren.create.error.notEnoughShares": "Shares insuficientes",
  "fren.create.error.minShares": "Mínimo {n} shares",
  "fren.create.error.minRecipients": "Mínimo 2 destinatarios",
  "fren.create.error.maxRecipients": "Máximo {n} destinatarios",
  "fren.create.error.shareTooSmall": "Cada destinatario recibiría {per} shares — el mínimo es {min}. Máx {max} destinatarios para {total} shares.",
  "fren.create.error.minOdds": "Probabilidad mínima: 1 entre 2",
  "fren.create.error.oddsTooHigh": "La probabilidad (1 entre {odds}) no puede superar a los destinatarios ({recipients}). Pon al menos {odds} destinatarios.",
  "fren.create.error.failed": "No se pudo crear el enlace",
  "fren.create.error.network": "Error de red — inténtalo de nuevo",

  // ── BubbleFlow page (/bubbleflow) ──────────────────────────────────
  "bub.count.whales": "Whales",
  "bub.count.winners": "Ganadores",
  "bub.count.builders": "Top Builders",
  "bub.count.leaderboard": "Top traders",
  "bub.count.markets": "Mercados",
  "bub.count.search": "Búsqueda",
  "bub.controls.title": "Controles",
  "bub.controls.click": "Clic burbuja → Detalles del mercado",
  "bub.controls.drag": "Arrastra burbuja → Lánzala",
  "bub.controls.hold": "Mantén pulsado → Pozo gravitacional",
  "bub.controls.tilt": "Inclina el móvil → Mueve burbujas",
  "bub.controls.scroll": "Scroll → Zoom in/out",
  "bub.sort.label": "Orden:",
  "bub.sort.today": "Hoy",
  "bub.sort.week": "Semana",
  "bub.sort.month": "Mes",
  "bub.sort.allTime": "Total",
  "bub.sort.totalVol": "Vol. total",
  "bub.sort.vol24h": "Vol. 24h",
  "bub.sort.liquidity": "Liquidez",
  "bub.rank.byPnl": "Por PnL",
  "bub.rank.byVolume": "Por Volumen",
  "bub.cat.all": "Todas las categorías",
  "bub.cat.allShort": "Todas",
  "bub.toolbar.change": "Cambiar",
  "bub.toolbar.allOutcomes": "Todos los outcomes",
  "bub.toolbar.resetZoom": "Resetear zoom",
  "bub.toolbar.screenshot": "Captura",
  "bub.whales.title": "Whale Tracker",
  "bub.whales.subtitle": "Pega una URL de Polymarket o FrenFlow para ver a sus mayores holders",
  "bub.whales.placeholder": "Pega URL de Polymarket o FrenFlow…",
  "bub.whales.loading": "Cargando whales del mercado…",
  "bub.holder": "Holder",
  "bub.rank": "Rango",
  "bub.tradingStyleTooltip": "Estilo de trading clasificado por Predexon",
  "bub.label.shares": "Shares",
  "bub.label.profit": "Beneficio",
  "bub.label.finalValue": "Valor final",
  "bub.label.volume": "Volumen",
  "bub.label.activeUsers": "Usuarios activos",
  "bub.label.verified": "Verificado",
  "bub.label.pnl": "PnL",
  "bub.label.posValue": "Valor pos.",
  "bub.label.biggestWin": "Mayor ganancia",
  "bub.label.markets": "Mercados",
  "bub.label.winRate": "Aciertos",
  "bub.label.totalVolume": "Volumen total",
  "bub.label.liquidity": "Liquidez",
  "bub.label.age": "Edad",
  "bub.label.resolves": "Resuelve",
  "bub.label.yes": "Sí",
  "bub.label.no": "No",
  "bub.label.vol24h": "Volumen 24h",
  "bub.action.viewProfile": "Ver perfil",
  "bub.action.viewTrader": "Ver trader",
  "bub.action.trade": "Operar",
  "bub.action.close": "Cerrar",
  "bub.holder.boughtYes": "Compró SÍ",
  "bub.holder.boughtNo": "Compró NO",
  "bub.winner.rank": "#{n} mayor ganador",
  "bub.screenshot.share": "Compartir",
  "bub.screenshot.download": "Descargar",
  "bub.screenshot.close": "Cerrar",
  "bub.screenshot.shareText": "¡Mira estos prediction markets en FrenFlow!",
  "bub.screenshot.copied": "¡Imagen copiada al portapapeles!",

  // ── Degenbox page (/degenbox) ──────────────────────────────────────
  "degen.dash.title": "Degenbox",
  "degen.dash.help": "Cómo funciona",
  "degen.dash.helpAria": "Mostrar ayuda",
  "degen.dash.addMarket": "Añadir mercado",
  "degen.dash.empty.title": "Tu Degenbox está vacío",
  "degen.dash.empty.subtitle": "Añade hasta 8 mercados para crear tu dashboard de trading personalizado. Sigue precios, mira el order book y opera — todo en un sitio.",
  "degen.dash.empty.cta": "Añade tu primer mercado",
  "degen.welcome.title": "Bienvenido a Degenbox",
  "degen.welcome.subtitle": "Sigue y opera hasta ocho mercados en una pantalla — tu hub personal de trading.",
  "degen.welcome.howItWorks": "Cómo funciona",
  "degen.welcome.feature.add.title": "Añadir mercado",
  "degen.welcome.feature.add.desc": "Pulsa el icono + para buscar cualquier mercado y añadirlo a una tile. Hasta 8 mercados.",
  "degen.welcome.feature.click.title": "Clic para operar",
  "degen.welcome.feature.click.desc": "Pulsa los botones SÍ o NO de una tile para abrir el panel de trading. Mira el order book o trades recientes.",
  "degen.welcome.feature.drag.title": "Arrastra para reordenar",
  "degen.welcome.feature.drag.desc": "Arrastra el icono del header de una tile para reordenarlas. Las tiles se quedan dentro del grid.",
  "degen.welcome.feature.toggle.title": "Cambiar vista",
  "degen.welcome.feature.toggle.desc": "Alterna entre Order Book, Trades recientes y Chart en cada tile.",
  "degen.welcome.feature.saved.title": "Layout guardado",
  "degen.welcome.feature.saved.desc": "Tus mercados y el orden de las tiles se guardan automáticamente y se restauran al volver.",
  "degen.welcome.proTip": "Pro Tip:",
  "degen.welcome.proTipText": "Puedes quitar un mercado en cualquier momento con la X del header de la tile.",
  "degen.welcome.dontShow": "No volver a mostrar",
  "degen.welcome.start": "Empezar a operar",
  "degen.search.title": "Añadir mercado",
  "degen.search.tab.markets": "Mercados individuales",
  "degen.search.tab.events": "Eventos ({n})",
  "degen.search.placeholder.markets": "Buscar mercados…",
  "degen.search.placeholder.events": "Buscar eventos…",
  "degen.search.noResults": "Sin {kind}",
  "degen.search.noAvailable": "Sin {kind} disponibles",
  "degen.search.kind.markets": "mercados",
  "degen.search.kind.events": "eventos",
  "degen.search.marketsCount": "{n} mercados",
  "degen.search.vol": "vol",
  "degen.tile.dragReorder": "Arrastra para reordenar",
  "degen.tile.pin": "Fijar mercado al footer",
  "degen.tile.unpin": "Quitar del footer",
  "degen.tile.pinEvent": "Fijar evento al footer",
  "degen.tile.unpinEvent": "Quitar evento del footer",
  "degen.tile.viewMarket": "Ver detalles del mercado",
  "degen.tile.openEvent": "Abrir página del evento",
  "degen.tile.removeMarket": "Quitar mercado",
  "degen.tile.removeEvent": "Quitar evento",
  "degen.tile.tab.book": "Book",
  "degen.tile.tab.trades": "Trades",
  "degen.tile.tab.chart": "Chart",
  "degen.tile.vol24h": "Vol. 24h",
  "degen.tile.asks": "ASKS",
  "degen.tile.bids": "BIDS",
  "degen.tile.spread": "Spread",
  "degen.tile.recentTrades": "TRADES RECIENTES",
  "degen.tile.chartUnavailable": "Chart no disponible para este mercado",
  "degen.tile.noTradingData": "Sin datos de trading aún",
  "degen.tile.timeAgo.minutes": "hace {n}m",
  "degen.tile.moreMarkets": "+{n} mercados más",
  "degen.hero.badge": "Dashboard de trading de Polymarket",
  "degen.hero.titlePart1": "Tu centro de mando personal",
  "degen.hero.titlePart2": "de prediction markets",
  "degen.hero.titlePart3": "",
  "degen.hero.subtitle": "Degenbox te deja personalizar qué eventos de Polymarket te importan. Sigue precios en tiempo real, orderbooks e historial de trades de tus mercados favoritos — visibles en cualquier página de FrenFlow.",
  "degen.hero.benefit.instant": "Acceso instantáneo",
  "degen.hero.benefit.realtime": "Datos en tiempo real",
  "degen.hero.benefit.drag": "Arrastrar y soltar",
  "degen.hero.cta.create": "Crea tu Degenbox",
  "degen.hero.cta.explore": "Explorar mercados",
  "degen.hero.trust.free": "Gratis",
  "degen.hero.trust.noSignup": "Sin registro para navegar",
  "degen.hero.demoBadge": "Demo interactiva",
  "degen.demo.yourDegenbox": "Tu Degenbox",
  "degen.demo.addAny": "Añade cualquier mercado",
  "degen.demo.pin": "Fijar mercado",
  "degen.demo.unpin": "Quitar fijado",
  "degen.demo.searchPlaceholder": "Buscar mercados…",
  "degen.demo.marketsCount": "{n} mercados",
  "degen.demo.marketCount": "{n} mercado",
  "degen.demo.pinned": "¡Fijado! Ahora visible en todas las páginas de FrenFlow",
  "degen.what.title": "¿Qué es Degenbox?",
  "degen.what.subtitle": "Degenbox es un dashboard de trading personalizable para los prediction markets de Polymarket. En lugar de saltar entre páginas de mercado, configuras los eventos que más te importan y los tienes accesibles al instante desde cualquier sitio.",
  "degen.what.f1.title": "Foco en lo que importa",
  "degen.what.f1.desc": "Selecciona los prediction markets que más te interesen — política, cripto, deportes o cualquier tema trending. Tu watchlist personal, siempre a mano.",
  "degen.what.f2.title": "Visible en todas partes",
  "degen.what.f2.desc": "Fija mercados a tu Degenbox y los verás en todas las páginas de FrenFlow. No te pierdes ningún movimiento de precio en tus eventos favoritos.",
  "degen.what.f3.title": "Múltiples vistas",
  "degen.what.f3.desc": "Alterna entre orderbook, trades recientes o chart para cada mercado. Los datos que necesitas, como los necesitas.",
  "degen.what.f4.title": "Layout drag & drop",
  "degen.what.f4.desc": "Ordena tus mercados como quieras. Pon las predicciones más importantes al frente con drag-and-drop.",
  "degen.what.f5.title": "Updates en tiempo real",
  "degen.what.f5.desc": "Mira los precios moverse en tiempo real. Sin refresh. Mantente al tanto mientras los prediction markets reaccionan a noticias y eventos.",
  "degen.what.f6.title": "Trading rápido",
  "degen.what.f6.desc": "Opera directamente desde tu Degenbox. Compra o vende shares al instante sin salir del dashboard.",
  "degen.how.title": "Cómo usar Degenbox",
  "degen.how.subtitle": "Empieza en tres pasos sencillos",
  "degen.how.s1.title": "Busca y añade mercados",
  "degen.how.s1.desc": "Navega los mercados trending o busca eventos concretos. Añade cualquier predicción de Polymarket a tu Degenbox con un clic.",
  "degen.how.s2.title": "Personaliza tu vista",
  "degen.how.s2.desc": "Elige orderbook, trades o chart para cada mercado. Arrastra y suelta para ordenarlos a tu gusto.",
  "degen.how.s3.title": "Opera al instante",
  "degen.how.s3.desc": "Sigue precios en tiempo real y ejecuta trades directamente desde el dashboard. Tus mercados, a tu manera.",
  "degen.uc.title": "Perfecto para cualquier trader",
  "degen.uc.subtitle": "Ya sigas elecciones, precios cripto o resultados deportivos, Degenbox se adapta a tu estrategia de prediction markets.",
  "degen.uc.political.title": "Traders políticos",
  "degen.uc.political.desc": "Sigue todos los grandes mercados electorales en un sitio. Monitoriza carreras presidenciales, escaños del congreso y resultados de políticas. Reacciona rápido cuando cambien las encuestas o salten noticias.",
  "degen.uc.crypto.title": "Forecasters cripto",
  "degen.uc.crypto.desc": "Sigue predicciones de precio de Bitcoin, aprobaciones de ETFs y mercados regulatorios cripto. Adelántate con datos en tiempo real de tus apuestas cripto.",
  "degen.uc.sports.title": "Deportes y entretenimiento",
  "degen.uc.sports.desc": "Sigue resultados de partidos, predicciones de campeonatos y mercados de entretenimiento. Perfecto para apostadores de deportes que exploran prediction markets.",
  "degen.uc.news.title": "Noticias y actualidad",
  "degen.uc.news.desc": "Sigue mercados de breaking news y temas trending. De anuncios tech a eventos meteorológicos, mantente al tanto de mercados que se mueven rápido.",
  "degen.why.eyebrow": "Sobre los prediction markets",
  "degen.why.titlePart1": "¿Por qué operar en",
  "degen.why.titlePart2": "Polymarket",
  "degen.why.subtitle": "La mayor plataforma de prediction markets del mundo con miles de millones en volumen",
  "degen.why.f1.title": "Opera eventos del mundo real",
  "degen.why.f1.desc": "Compra y vende shares basadas en elecciones, precios cripto, resultados deportivos, anuncios tech y breaking news. Si puedes predecirlo, puedes operarlo.",
  "degen.why.f2.title": "Saca partido a lo que sabes",
  "degen.why.f2.desc": "A diferencia de las apuestas, los prediction markets te permiten entrar y salir de posiciones. Detecta mercados mal valorados, toma posición y sal cuando el precio se mueva a tu favor.",
  "degen.why.f3.title": "Sabiduría de la multitud",
  "degen.why.f3.desc": "Los precios de mercado agregan información de miles de traders, creando forecasts de probabilidad muy precisos que a menudo superan a las predicciones de expertos.",
  "degen.why.stat.monthlyVolume": "Volumen mensual",
  "degen.why.stat.activeMarkets": "Mercados activos",
  "degen.why.stat.globalTrading": "Trading global",
  "degen.why.stat.lowFees": "Comisiones bajas",
  "degen.why.popularCats": "Categorías populares",
  "degen.why.advantageTitle": "FrenFlow hace Polymarket mejor",
  "degen.why.advantageDesc": "Deja de gestionar pestañas de navegador. Con Degenbox organizas tus eventos de Polymarket más vigilados en un único dashboard con precios en tiempo real, trading instantáneo y features sociales. Copia a los mejores traders, sigue movimientos whale y no te pierdas ninguna oportunidad.",
  "degen.why.tryFree": "Probar gratis",
  "degen.finalCta.title": "¿Listo para montar tu Degenbox?",
  "degen.finalCta.subtitle": "Únete a miles de traders que usan FrenFlow para operar Polymarket más inteligente. Crea hoy tu dashboard personalizado de prediction markets.",
  "degen.finalCta.start": "Empieza gratis",
  "degen.finalCta.browse": "Explorar mercados primero",
  "degen.faq.title": "Preguntas frecuentes",
  "degen.faq.0.q": "¿Qué es un prediction market?",
  "degen.faq.0.a": "Un prediction market es un mercado financiero donde los participantes operan shares basadas en el resultado de eventos futuros. Los precios reflejan la estimación colectiva de la probabilidad del evento. Por ejemplo, si un mercado cotiza a 70¢, sugiere un 70% de probabilidad de que ese resultado ocurra.",
  "degen.faq.1.q": "¿Es gratis usar Degenbox?",
  "degen.faq.1.a": "¡Sí! Degenbox es totalmente gratis. Puedes añadir cualquier evento de Polymarket a tu dashboard, personalizar vistas y seguir precios sin suscripciones. Operar en Polymarket sí puede tener comisiones de transacción.",
  "degen.faq.2.q": "¿Cuántos mercados puedo añadir a mi Degenbox?",
  "degen.faq.2.a": "Puedes añadir varios mercados a tu Degenbox para crear un dashboard de trading enfocado. Te recomendamos empezar con tus eventos más vigilados e ir ampliando.",
  "degen.faq.3.q": "¿Puedo operar directamente desde Degenbox?",
  "degen.faq.3.a": "¡Sí! Una vez logueado y con trading activado, puedes comprar y vender shares directamente desde cualquier tile de tu Degenbox sin salir del dashboard.",
  "degen.faq.4.q": "¿Qué es FrenFlow?",
  "degen.faq.4.a": "FrenFlow es una plataforma social de trading construida para Polymarket. Además de Degenbox, FrenFlow ofrece copy trading, feeds sociales, tracking de portfolio y features de comunidad para traders de prediction markets.",

  // ── Copy Trading dashboard (/copytrading) ──────────────────────────
  "ct.title": "Copy Trading",
  "ct.subtitle.empty": "Gestiona tu cartera de copy trading",
  "ct.subtitle.stats": "{n} traders · {trades} trades ejecutados",
  "ct.refresh": "Refrescar datos",
  "ct.findTraders": "Buscar traders",
  "ct.tab.overview": "Resumen",
  "ct.tab.traders": "Traders",
  "ct.tab.history": "Historial",
  "ct.stat.netPnl": "PnL neto",
  "ct.stat.netPnlTooltip": "PnL neto = PnL trades − comisiones",
  "ct.stat.netPnlBreakdown": "Realizado: ${r} | No realizado: ${u} | Comisiones: ${f}",
  "ct.stat.volume": "Volumen",
  "ct.stat.trades": "Trades",
  "ct.stat.fillRate": "{pct}% fill rate",
  "ct.stat.following": "Copiando",
  "ct.stat.fees": "Comisiones",
  "ct.stat.roi": "ROI",
  "ct.stat.roiSub": "neto de comisiones",
  "ct.stat.openPositions": "Abiertas",
  "ct.stat.activePositions": "Posiciones activas",
  "ct.stat.openPositionsSub": "posiciones activas",
  "ct.stat.openPositionsSubOne": "posición activa",
  "ct.section.activeTraders": "Traders activos",
  "ct.section.recentTrades": "Trades recientes",
  "ct.viewAll": "Ver todos",
  "ct.label.edit": "Poner un nombre propio",
  "ct.label.placeholder": "Nombre personalizado\u2026",
  "ct.label.save": "Guardar nombre",
  "ct.label.cancel": "Cancelar",
  "ct.label.add": "+ Etiqueta",
  "ct.label.remove": "Quitar nombre",
  "ct.label.hint": "Nombre privado, solo t\u00fa lo ves. D\u00e9jalo vac\u00edo para quitarlo.",
  "ct.tradesAndVol": "{n} trades · ${vol} vol",
  "ct.action.pause": "Pausar copia",
  "ct.action.resume": "Reanudar copia",
  "ct.action.settings": "Ajustes",
  "ct.action.editSettings": "Editar ajustes de copia",
  "ct.action.viewTrader": "Ver trader",
  "ct.action.unfollow": "Dejar de seguir",
  "ct.action.confirm": "Confirmar",
  "ct.action.cancel": "Cancelar",
  "ct.action.yes": "Sí",
  "ct.action.no": "No",
  "ct.repauseGuard.title": "Ojo — esto se va a re-pausar",
  "ct.repauseGuard.bodyPct": "Con tu balance actual (${balance}) un stake de {pct}% genera copias de ~${copy} — por debajo del mínimo de $1 de Polymarket. Te re-pausaríamos este follow tras 5 trades skipeados. Deposita hasta ${minBalance} o cambia a modo Fijo para evitar el loop.",
  "ct.repauseGuard.bodyFixed": "Tu balance (${balance}) es menor que el stake fijo configurado (${stake}). El primer BUY del leader fallaría por fondos insuficientes y te re-pausaríamos. Deposita o baja el stake primero.",
  "ct.repauseGuard.bodyGeneric": "Tu configuración actual no llega al mínimo de $1 de Polymarket con este balance. Te re-pausaríamos tras unos trades skipeados.",
  "ct.repauseGuard.adjust": "Ajustar config",
  "ct.repauseGuard.proceed": "Reanudar igual",
  "ct.empty.activeTraders": "Sin traders activos",
  "ct.empty.activeTraders.title": "Sin traders activos",
  "ct.empty.activeTraders.desc": "Sigue a top traders y verás sus operaciones aquí en tiempo real.",
  "ct.empty.trades": "Sin trades aún",
  "ct.empty.trades.title": "Sin trades aún",
  "ct.empty.trades.desc": "Tus copias aparecerán aquí en cuanto un leader que sigues haga un movimiento.",
  "ct.empty.history": "Sin historial de trades aún",
  "ct.empty.history.title": "Sin historial aún",
  "ct.empty.history.desc": "Aquí se registran todas las copias: filled, skipped y failed.",
  "ct.history.loadMore": "Cargar más",
  "ct.pagination.prev": "Anterior",
  "ct.pagination.next": "Siguiente",
  "ct.pagination.pageOf": "Página {page} de {total}",
  "ct.history.loading": "Cargando…",
  "ct.history.allLoaded": "Todos los {n} trades cargados",
  // ── Copy banners (CopyPausedBanner + CopytradingSkipBanner) ──
  "copyBanner.skip.one": "Saltando algunas copias de {leader} — {reason}",
  "copyBanner.skip.few": "Saltando copias de {count} líderes: {names} — toca para ajustar",
  "copyBanner.skip.many": "Saltando copias de {count} líderes — toca para ajustar",
  "copyBanner.paused.one": "{leader} en pausa — {reason} · {time}",
  "copyBanner.paused.few": "{count} líderes en pausa: {names} — toca para revisar",
  "copyBanner.paused.many": "{count} líderes en pausa — toca para revisar",
  "copyBanner.warn.sizeTooSmall": "tu tamaño de copia está por debajo del mínimo de $1",
  "copyBanner.warn.highSlippage": "tu estilo de ejecución es muy estricto",
  "copyBanner.warn.default": "un ajuste rápido lo soluciona",
  "copyBanner.reason.insufficientBalance": "saldo insuficiente",
  "copyBanner.reason.lossThreshold": "umbral de pérdida",
  "copyBanner.reason.profitTarget": "objetivo de ganancia",
  "copyBanner.reason.belowMin": "tamaño por debajo del mínimo",
  "copyBanner.reason.aboveMax": "tamaño por encima del máximo",
  "copyBanner.reason.maxPositions": "límite de posiciones abiertas alcanzado",
  "copyBanner.reason.system": "pausa del sistema",
  "copyBanner.headline.loss": "cae {pct}% (tu límite: {cap}%)",
  "copyBanner.headline.lossNoCap": "cae {pct}%",
  "copyBanner.headline.balance": "saldo bajo: ${bal} (faltan ${need})",
  "copyBanner.headline.balanceNoNeed": "saldo bajo: ${bal}",
  "copyBanner.headline.positions": "límite de posiciones alcanzado ({open}/{cap})",
  "copyBanner.headline.attemptedMin": "trade muy pequeño: ${attempted} (mín ${min})",
  "copyBanner.headline.attemptedCap": "trade muy grande: ${attempted} (límite ${cap})",
  "copyBanner.headline.attempted": "tamaño del trade ${attempted}",
  "copyBanner.time.justNow": "ahora mismo",
  "copyBanner.time.minutes": "hace {n}m",
  "copyBanner.time.hours": "hace {n}h",
  "copyBanner.time.days": "hace {n}d",
  "copyBanner.cta.fix": "Ajustar config →",
  "copyBanner.cta.manage": "Gestionar →",
  "copyBanner.dismiss": "Descartar",
  "copyBanner.fix.highSlippage": "Las copias no se llenan — pon tu Estilo de ejecución en Normal o Aggressive para que entren más.",
  "copyBanner.fix.sizeTooSmall": "Tu tamaño de copia está por debajo del mínimo de $1 — sube tu stake por trade.",
  "copyBanner.fix.insufficientBalance": "Saldo insuficiente para copiar — deposita USDC o baja tu stake, y reanuda.",
  "copyBanner.fix.lossThreshold": "Pausado en tu límite de pérdida — súbelo (o cambia de preset) para reanudar.",
  "copyBanner.fix.profitTarget": "Objetivo de ganancia alcanzado — súbelo o reanuda manualmente.",
  "copyBanner.fix.belowMin": "Tus copias quedan por debajo del mínimo — sube tu stake.",
  "copyBanner.fix.aboveMax": "Los trades del líder superan tu máximo — sube tu \"Máx por trade\".",
  "copyBanner.fix.maxPositions": "Llegaste a tu límite de posiciones abiertas — cierra algunas o sube el límite.",
  "copyBanner.fix.default": "Ajusta la configuración de abajo para que tus copias vuelvan a fluir.",
  "skipBanner.title": "Tus copias no se están ejecutando",
  "skipBanner.body": "Solo {pct} de {total} intentos de copia se ejecutaron en las últimas 24h.",
  "skipBanner.topReason": "Motivo principal: {reason} ({count} saltadas).",
  "skipBanner.cta": "Abrir configuración de copia",
  "skipBanner.dismissNote": "Descartar 24h — volverá si las copias siguen saltándose",
  "skipBanner.dismissAria": "Descartar durante 24 horas",
  "skipBanner.reason.slippage": "estilo de ejecución muy estricto",
  "skipBanner.reason.sizeBelowMin": "tamaño de copia por debajo del mínimo de $1",
  "skipBanner.reason.belowMinTrade": "tamaño por debajo del mínimo del CLOB",
  "skipBanner.reason.sizeAboveMax": "trade del líder mayor que tu \"Máx por trade $\"",
  "skipBanner.reason.insufficientBalance": "saldo pUSD insuficiente",
  "skipBanner.reason.oddsHigh": "precio por encima de tu límite de cuota máx",
  "skipBanner.reason.oddsLow": "precio por debajo de tu límite de cuota mín",
  "skipBanner.reason.phantom": "phantom-fill (rollback heredado)",
  "skipBanner.reason.neverFilled": "orden enviada pero nunca ejecutada (mercado demasiado rápido)",
  "skipBanner.reason.noMatch": "la orden llegó al libro pero no casó nada",
  "skipBanner.reason.maxDailyVolume": "límite de volumen diario alcanzado",
  "skipBanner.reason.maxOpenPositions": "máximo de posiciones abiertas alcanzado",
  "skipBanner.reason.maxEntries": "máximo de entradas por mercado alcanzado",
  "ct.exec.group.systemSummary": "{count} eventos técnicos · resueltos por el sistema",
  "ct.exec.deposit": "Depositar",
  "ct.empty.traders.title": "Aún no sigues a nadie",
  "ct.empty.traders.subtitle": "Empieza a seguir traders para copiar sus movimientos automáticamente",
  "ct.exec.via": "vía {name}",
  "ct.exec.viaLabel": "vía",
  "ct.exec.colMarket": "Mercado",
  "ct.exec.colLeader": "Leader",
  "ct.exec.colYours": "COMPRADO",
  "ct.exec.colStatus": "Estado",
  "ct.exec.colTime": "Hora",
  "ct.exec.showSkipped": "Ver omitidos",
  "ct.exec.hideSkipped": "Ocultar omitidos",
  "ct.exec.fee": "Comisión: ${amt}",
  "ct.exec.leaderTradeLine": "Leader: ${amt}",
  "ct.exec.yoursTip": "Tamaño de tu copia",
  "ct.exec.leaderTip": "Tamaño del trade del leader",
  "ct.exec.feeTip": "Comisión de la plataforma por esta copia",
  "ct.exec.unknownMarket": "Mercado desconocido",
  "ct.exec.status.filled": "Ejecutado",
  "ct.exec.status.failed": "Falló",
  "ct.exec.status.skipped": "Omitido",
  "ct.exec.status.illiquid": "Sin liquidez",
  "ct.exec.status.pending": "Pendiente",
  "ct.exec.group.summary": "{count} trades omitidos por tu filtro {filter}",
  "ct.exec.group.show": "Ver todo",
  "ct.exec.group.hide": "Ocultar",
  "ct.leader.active": "Activo",
  "ct.leader.paused": "Pausado",
  "ct.slippageHint": "{count} copies skipped · slippage {current}% → prueba {recommended}%",
  "ct.leader.col.trades": "Trades",
  "ct.leader.col.volume": "Volumen",
  "ct.leader.col.pnl": "PnL",
  "ct.leader.col.mode": "Modo",
  "ct.leader.col.max": "Máx",
  "ct.leader.tradesShort": "{n} trades",
  "ct.leader.volShort": "${v} vol",
  "ct.tableCol.market": "Mercado",
  "ct.tableCol.trader": "Trader",
  "ct.tableCol.side": "Lado",
  "ct.tableCol.size": "Tamaño",
  "ct.tableCol.price": "Precio",
  "ct.tableCol.value": "Valor",
  "ct.tableCol.fee": "Comisión",
  "ct.tableCol.status": "Estado",
  "ct.tableCol.time": "Tiempo",
  "ct.time.justNow": "ahora",
  "ct.time.minutesAgo": "hace {n}m",
  "ct.time.hoursAgo": "hace {n}h",
  "ct.time.daysAgo": "hace {n}d",
  "ct.trader": "Trader",


  "share.stats.title": "Comparte tus stats",
  "share.stats.period.today": "Hoy",
  "share.stats.period.week": "Semana",
  "share.stats.period.month": "Mes",
  "share.stats.period.all": "Todo",
  "share.stats.generating": "Generando tu tarjeta…",
  "share.stats.generationFailed": "No se pudo generar la tarjeta",
  "share.stats.fetchFailed": "No se pudieron obtener los stats",
  "share.stats.retry": "Reintentar",
  "share.stats.tagline": "Opera mejor · Ganamos juntos",
  "share.stats.dragHint": "Arrastra para rotar • Doble clic para 360°",
  "share.stats.copy": "Copiar",
  "share.stats.copied": "¡Copiado!",
  "share.stats.copyError": "No se pudo copiar al portapapeles:",
  "share.stats.pasteHintTwitter": "¡Imagen copiada! Pégala (Ctrl+V / Cmd+V) en tu tuit para incluir la tarjeta.",
  "share.stats.pasteHintTelegram": "¡Imagen copiada! Pégala (Ctrl+V / Cmd+V) en Telegram para incluir la tarjeta.",
  "share.stats.shareTextHeader": "Mis stats de trading en @frenflow_",
  "share.stats.shareTextPortfolio": "Cartera:",
  "share.stats.shareTextPnl": "P&L:",
  "share.stats.shareTextVolume": "Volumen:",
  "share.stats.shareTextTrades": "Trades:",
  "share.stats.shareTextTwitterCta": "Opera prediction markets",
  "share.stats.shareTextTelegramCta": "Opera prediction markets en FrenFlow",

  // ── Wallet / deposit ───────────────────────────────────────────────
  "wallet.depositAddress": "Dirección de depósito",
  "wallet.depositTitle": "Depositar",
  "wallet.withdrawTitle": "Retirar",
  "wallet.recipient": "Destinatario",
  "wallet.network": "Red",
  "wallet.minDeposit": "Depósito mínimo",
  "wallet.copyAddress": "Copiar dirección",
  "wallet.scanQr": "Escanea el QR o copia la dirección de abajo",

  // ── Watchlist / social ─────────────────────────────────────────────
  "social.watchlist": "Watchlist",
  "social.followers": "Seguidores",
  "social.following": "Siguiendo",
  "social.feed": "Feed",
  "social.comments": "Comentarios",
  "social.likes": "Me gusta",
  "social.posts": "Publicaciones",

  // ── Global search (header bar) ─────────────────────────────────────
  "search.placeholder": "Busca mercados, traders, eventos…",
  "search.viewAll": "Ver todos los resultados de",
  "search.resultsFor": "Resultados para",
  "search.title": "Buscar",
  "search.empty.title": "Sin resultados",
  "search.empty.desc": "Prueba con otra palabra o revisa la ortografía.",
  "search.loadMore": "Ver más",
  "search.fallback": "No hay coincidencias en tu fuente activa. Mostrando resultados de otras plataformas.",
  "search.section.categories": "Categorías",
  "search.section.markets": "Mercados",
  "search.section.events": "Eventos",
  "search.section.traders": "Traders",
  "search.section.users": "Usuarios",
  "search.section.posts": "Publicaciones",
  "search.section.blog": "Blog",
  "search.section.recent": "Recientes",
  "search.section.clear": "Borrar",
  "search.empty": "Sin resultados",
  "search.noResultsFor": "Sin resultados para",

  // ── Notifications dropdown (bell icon) ─────────────────────────────
  "notifications.title": "Notificaciones",
  "notifications.empty": "Aún no tienes notificaciones",
  "notifications.markAllRead": "Marcar todas como leídas",
  "notifications.settingsLink": "Ajustes de notificaciones",

  // ── Mobile drawer menu (hamburger panel on phones) ─────────────────
  "menu.title": "Menú",
  "menu.close": "Cerrar menú",
  "menu.balance": "Balance",
  "menu.portfolio": "Cartera",
  "menu.positionsToClaim": "Posiciones por reclamar",
  "menu.points": "pts",

  // ── Toasts (transient feedback) ────────────────────────────────────
  "toast.copied": "Copiado al portapapeles",
  "toast.linkCopied": "Enlace copiado",
  "toast.addressCopied": "Dirección copiada",
  "toast.success": "Listo",
  "toast.error": "Algo ha ido mal",
  "toast.tryAgain": "Inténtalo de nuevo",
  "toast.tradeExecuted": "Operación ejecutada",
  "toast.orderCancelled": "Orden cancelada",
  "toast.orderPlaced": "Orden enviada",
  "toast.depositReceived": "Depósito recibido",
  "toast.withdrawalSent": "Retiro enviado",
  "toast.followed": "Ahora sigues a este usuario",
  "toast.unfollowed": "Has dejado de seguir a este usuario",
  "toast.savedToWatchlist": "Añadido a la watchlist",
  "toast.removedFromWatchlist": "Quitado de la watchlist",
  "toast.networkError": "Error de red — revisa tu conexión",
  "toast.signInRequired": "Inicia sesión para continuar",
  "dextools.banner.title": "Vienes de DexTools",
  "dextools.banner.body": "Inicia sesión para operar en FrenFlow.",
  "dextools.banner.howItWorks": "Cómo funciona",
  "dextools.banner.signup": "Regístrate para operar",
  "dextools.signup.partner": "Partner oficial de DexTools",
  "dextools.signup.profit": "Convierte {stake} en {payout} si aciertas",
  "dextools.signup.nonCustodial": "Non-custodial — tus llaves nunca salen de tu wallet",
  "dextools.signup.fast": "Tu primera predicción en 1 minuto — sin experiencia",
  "dextools.signup.buy": "Regístrate para comprar",
  "dextools.signup.pickSide": "Regístrate y elige tu lado",
  "dextools.signup.smartMoney": "Sigue lo que compra el smart money",
  "dextools.signup.copy": "Copia a los mejores traders en 1 toque",
  "dextools.signup.noKyc": "Sin KYC — entra con email en 30 segundos",
  "dextools.signup.volume": "{volume} negociados — mercado con liquidez",
  "dextools.signup.payout": "Cobras al instante cuando el mercado resuelve",
  "dextools.signup.explore": "Explorar primero",
  "dextools.signup.fallbackTitle": "Opera mercados de predicción",
  // Barra de co-branding no bloqueante sobre el hero / perfil en un landing del
  // widget DexTools. El logo ES el wordmark, asi que el texto no repite
  // "DexTools": se renderiza `via [logo DexTools] · Socio oficial`.
  "dextools.landing.via": "vía",
  "dextools.landing.officialPartner": "Socio oficial",
  // Interstitial de copytrading (utm_campaign=copytrading → perfil de trader).
  "dextools.copy.statPnl": "Beneficio",
  "dextools.copy.statWin": "Aciertos",
  "dextools.copy.statCopiers": "Copiando",
  "dextools.copy.cta": "Regístrate para copiar a {name}",
  "dextools.copy.ctaGeneric": "Regístrate para copiar",
  "dextools.copy.autoCopy": "Auto-copia cada operación — configúralo una vez",
  "dextools.copy.volume": "{volume} negociados — historial probado",
  "dextools.copy.selfCustody": "Self-custodial — retira cuando quieras",
  "privy.landingHeader": "Regístrate para operar",
  "privy.loginMessage": "Mercados de predicción con dinero real: cripto, política, deportes y más",
  "dextools.banner.dismiss": "Cerrar",
  "dextools.modal.eyebrow": "Bienvenido",
  "dextools.modal.headline": "Cómo funciona",
  "dextools.modal.step1Title": "Mercados de predicción reales",
  "dextools.modal.step1Body": "Apuesta YES o NO sobre eventos reales: cripto, política, deportes. El precio es la probabilidad.",
  "dextools.modal.step2Title": "Opera en segundos",
  "dextools.modal.step2Body": "Elige un lado, pon tu importe y entras. Sin configuraciones complicadas.",
  "dextools.modal.step3Title": "Tu cuenta, tus fondos",
  "dextools.modal.step3Body": "Regístrate con email o Google — te creamos una wallet self-custodial. Nunca custodiamos tus fondos.",
  "dextools.modal.cta": "Regístrate y opera",
  "dextools.modal.explore": "Saltar",

  // ── Deposit modal ──────────────────────────────────────────────────
  "deposit.addFunds": "Añadir fondos",
  "deposit.addFundsSubtitle": "Elige cómo añadir dinero",
  "deposit.header.polygonOnly": "Solo Polygon",
  "menu.viewProfile": "Ver perfil",

  // ── UMA page (/uma) ─────────────────────────────────────────────────
  "uma.title": "Votación UMA",
  "uma.subtitle": "Sigue las disputas del oráculo UMA, los votos del DVM y la clasificación de votantes para los mercados de Polymarket",
  "uma.fasterUpdates": "Actualizaciones más rápidas",
  "uma.fasterUpdates.tooltip": "Los traders con sesión iniciada reciben actualizaciones cada 45 segundos. Los visitantes anónimos cada 5 minutos.",
  "uma.nav.disputes": "Disputas",
  "uma.nav.voters": "Votantes",
  "uma.nav.proposers": "Proponentes y Disputadores",
  // Stats header
  "uma.stats.commitEnds": "Fin del Commit",
  "uma.stats.revealEnds": "Fin del Reveal",
  "uma.stats.committed": "Comprometidos",
  "uma.stats.revealedVotes": "Votos revelados",
  "uma.stats.voters": "Votantes",
  "uma.stats.consensus": "Consenso",
  "uma.stats.participation": "Participación",
  "uma.stats.bondAtRisk": "Bond en riesgo",
  "uma.stats.vol24h": "Volumen 24h",
  "uma.stats.totalVolume": "Volumen total",
  // Stats tooltips
  "uma.tip.commitEnds": "Tiempo restante para que los votantes UMA envíen votos cifrados. Cuando termine, empieza la fase Reveal (~24h cada una). No se aceptan votos después.",
  "uma.tip.revealEnds": "Tiempo restante para revelar los votos cifrados. Los votos no revelados se cortan — los votantes pierden parte de sus tokens UMA en stake.",
  "uma.tip.committed": "Tokens UMA comprometidos (cifrados) vs total en stake. Más participación = resultado más seguro. Los votos están ocultos durante esta fase.",
  "uma.tip.revealed": "Tokens UMA revelados vs total en stake. Debe cumplirse el quórum (GAT) para que la votación sea válida. Los commits no revelados son penalizados.",
  "uma.tip.committers": "Wallets únicas que comprometieron votos esta ronda. Más votantes = más descentralización y mejor resultado.",
  "uma.tip.revealers": "Wallets únicas que revelaron sus votos. Los que comprometieron pero no revelan son penalizados.",
  "uma.tip.consensus": "Requisito de consenso (SPAT): el resultado líder necesita ~65% de los tokens votados para pasar. Evita que grupos pequeños controlen los resultados.",
  "uma.tip.participation": "Porcentaje del total de UMA en stake que se reveló. Debe superar el Global Access Threshold (GAT) o la votación se traslada a la siguiente ronda.",
  "uma.tip.bondAtRisk": "Bond total en USDC.e depositado por proponentes y disputadores en todas las disputas activas. El ganador conserva el suyo + recibe la mitad del perdedor.",
  "uma.tip.vol24h": "Volumen combinado de Polymarket en las últimas 24h para todos los mercados que se resuelven vía DVM de UMA. Más volumen = disputas con más en juego.",
  "uma.tip.totalVolume": "Volumen histórico total de Polymarket para mercados en votación DVM de UMA. Muestra el valor combinado en riesgo por estas disputas.",
  // Toggles + filters
  "uma.toggle.simple": "Simple",
  "uma.toggle.advanced": "Avanzado",
  "uma.signInForAccess": "Inicia sesión para acceso completo",
  "uma.tab.active": "Activas",
  "uma.tab.upcoming": "Próximas",
  "uma.empty.disputes": "No hay disputas.",
  "uma.empty.active": "No hay disputas activas en este momento.",
  "uma.empty.upcoming": "No hay próximas disputas en este momento.",
  // Dispute card / sidebar chrome
  "uma.card.proposed": "Propuesto",
  "uma.card.commitStage": "Fase Commit",
  "uma.card.disputed": "Disputado",
  "uma.card.quorum": "Quórum",
  "uma.card.reveals": "Reveals",
  "uma.card.proposer": "Proponente",
  "uma.card.disputer": "Disputador",
  "uma.card.result": "Resultado",
  "uma.card.tip.quorumCommit": "Global Access Threshold — mínimo de tokens UMA que deben votar para que el resultado sea válido. Los tokens comprometidos cuentan para el quórum aunque no se hayan revelado.",
  "uma.card.tip.quorumReveal": "Global Access Threshold — mínimo de tokens UMA que deben votar para que el resultado sea válido. Si no se alcanza, la votación pasa a la siguiente ronda.",
  "uma.card.tip.reveals": "Cuántos votos comprometidos han sido revelados. Los no revelados se penalizan.",
  "uma.card.tip.upcoming": "Esta disputa espera a que empiece la próxima ronda de votación del DVM. Aún no ha comenzado.",
  "uma.sidebar.timestamp": "Timestamp",
  "uma.sidebar.unix": "UNIX",
  "uma.sidebar.totalAtStake": "Total en juego",
  // Leaderboards
  "uma.lb.rank": "Pos.",
  "uma.lb.address": "Dirección",
  "uma.lb.voter": "Votante",
  "uma.lb.proposer": "Proponente",
  "uma.lb.disputer": "Disputador",
  "uma.lb.votes": "Votos",
  "uma.lb.proposals": "Propuestas",
  "uma.lb.disputes": "Disputas",
  "uma.lb.winRate": "% Aciertos",
  "uma.lb.successRate": "% Éxito",
  "uma.lb.bondedVolume": "Volumen en bond",
  "uma.lb.earnings": "Ganancias",
  "uma.lb.slashed": "Slashed",
  "uma.lb.netPnL": "P&L neto",
  "uma.lb.lastActive": "Última actividad",
  "uma.common.noResults": "Sin resultados",
  "uma.common.loading": "Cargando...",
  "uma.common.search": "Buscar",
  "uma.common.viewProfile": "Ver perfil",
  "uma.common.copyAddress": "Copiar dirección",
  "uma.common.copied": "Copiado",
  // Phase indicator
  "uma.phase.commit": "Commit",
  "uma.phase.reveal": "Reveal",
  "uma.phase.upcoming": "Próxima",
  "uma.phase.resolved": "Resuelta",
  "uma.phase.expired": "Expirada",
  // Vista lista
  "uma.list.market": "Mercado",
  "uma.list.state": "Estado",
  "uma.list.proposed": "Propuesto",
  "uma.list.volume": "Volumen",
  "uma.list.bond": "Bond",
  "uma.list.voters": "Votantes",
  "uma.list.time": "Hora",
  "uma.list.by": "por",
  "uma.list.disputedBy": "disputado por",
  "uma.pagination.previous": "Anterior",
  "uma.pagination.next": "Siguiente",
  "uma.pagination.of": "de",
  // DisputeCard extras
  "uma.card.showMore": "Ver más",
  "uma.card.showLess": "Ver menos",
  "uma.card.vote": "voto",
  "uma.card.votes": "votos",
  "uma.card.voter": "votante",
  "uma.card.voters": "votantes",
  "uma.card.voteBreakdown": "Desglose de votos",
  "uma.card.consensus": "Consenso",
  "uma.card.expiresIn": "Expira en",
  "uma.card.tip.consensus": "Stake Participation Agreement Threshold — el resultado líder debe tener al menos este número de tokens UMA. Si no se alcanza, la votación pasa a la siguiente ronda.",
  "uma.card.tip.commitProgress": "Votación en marcha — fase Commit. Click para detalles.",
  "uma.card.tip.revealProgress": "Votación en marcha — fase Reveal. Click para detalles.",
  "uma.card.voteRound": "Ronda {n} de 4. Las rondas previas no alcanzaron quórum o consenso. Si fallan las 4, la petición se elimina y queda la propuesta original.",
  "uma.card.commitPhase": "Commit",
  "uma.card.revealPhase": "Reveal",
  "uma.card.pendingVote": "Pendiente de votar",
  "uma.card.round": "Ronda {n}/4",
  "uma.card.tip.commitPhase": "Fase Commit: los votantes UMA envían votos cifrados. Los votos están ocultos para prevenir copia. Dura ~24 horas.",
  "uma.card.tip.revealPhase": "Fase Reveal: los votantes descifran y revelan sus votos. Los no revelados son penalizados (pierden tokens). Dura ~24 horas.",
  // Voter leaderboard
  "uma.voter.empty": "No hay datos de votantes.",
  "uma.voter.signInFull": "Inicia sesión para ver el ranking completo",
  "uma.voter.prev": "Anterior",
  "uma.voter.next": "Siguiente",
  "uma.voter.col.address": "Dirección",
  "uma.voter.col.staked": "En stake",
  "uma.voter.col.votes": "Votos",
  "uma.voter.col.accuracy": "Aciertos",
  "uma.voter.col.correct": "Correctos",
  "uma.voter.col.wrong": "Erróneos",
  "uma.voter.col.noVote": "Sin votar",
  "uma.voter.col.slashPct": "% Slash",
  "uma.voter.col.slashed": "Slashed",
  "uma.voter.col.rewards": "Recompensas",
  "uma.voter.col.netPnl": "P&L neto",
  "uma.voter.tip.staked": "Tokens UMA en stake en el sistema de votación",
  "uma.voter.tip.votes": "Total de votos revelados en todas las rondas",
  "uma.voter.tip.accuracy": "Votos correctos / votos totales",
  "uma.voter.tip.noVote": "Comprometieron pero no revelaron",
  "uma.voter.tip.slashPct": "Porcentaje del stake perdido por slashing",
  "uma.voter.tip.slashed": "Total de UMA perdido por votos erróneos o no revelados",
  "uma.voter.tip.rewards": "UMA ganado por votar correctamente",
  // Proposer leaderboard
  "uma.proposer.empty": "Aún no hay datos de proponentes. El indexador puede estar haciendo el backfill inicial.",
  "uma.proposer.col.address": "Dirección",
  "uma.proposer.col.proposals": "Propuestas",
  "uma.proposer.col.propWl": "Prop G/P",
  "uma.proposer.col.propNet": "Prop Neto",
  "uma.proposer.col.disputes": "Disputas",
  "uma.proposer.col.dispWl": "Disp G/P",
  "uma.proposer.col.dispNet": "Disp Neto",
  "uma.proposer.col.totalNet": "Total Neto",
  "uma.proposer.col.lastActive": "Última actividad",
  "uma.proposer.tip.propWl": "Propuestas ganadas vs perdidas",
  "uma.proposer.tip.propNet": "USD neto de los bonds de propuestas",
  "uma.proposer.tip.dispWl": "Disputas ganadas vs perdidas",
  "uma.proposer.tip.dispNet": "USD neto de los bonds de disputas",
  // Voter sidebar (modal al hacer click)
  "uma.voterSidebar.viewOnEtherscan": "Ver en Etherscan",
  "uma.voterSidebar.notFound": "Votante no encontrado.",
  "uma.voterSidebar.accuracy": "Aciertos",
  "uma.voterSidebar.correctVotes": "votos correctos",
  "uma.voterSidebar.totalVotes": "Votos totales",
  "uma.voterSidebar.voteBreakdown": "Desglose de votos",
  "uma.voterSidebar.correct": "correctos",
  "uma.voterSidebar.wrong": "erróneos",
  "uma.voterSidebar.noVote": "sin votar",
  "uma.voterSidebar.disputesInvolved": "Disputas en las que ha participado",
  // Proposer sidebar
  "uma.proposerSidebar.viewOnPolygonscan": "Ver en Polygonscan",
  "uma.proposerSidebar.active": "Activo {when}",
  "uma.proposerSidebar.totalNet": "P&L neto total",
  "uma.proposerSidebar.proposals": "Propuestas",
  "uma.proposerSidebar.disputes": "Disputas",
  "uma.proposerSidebar.net": "neto",
  "uma.proposerSidebar.won": "ganadas",
  "uma.proposerSidebar.lost": "perdidas",
  "uma.proposerSidebar.proposalOutcomes": "Resultado de propuestas",
  "uma.proposerSidebar.disputeOutcomes": "Resultado de disputas",
  "uma.proposerSidebar.noDisputes": "Sin disputas presentadas",
  // Dispute sidebar (modal)
  "uma.dsb.commitDetail": "Detalle del Commit",
  "uma.dsb.disputeDetail": "Detalle de la Disputa",
  "uma.dsb.unknownDispute": "Disputa desconocida",
  "uma.dsb.tab.result": "Resultado",
  "uma.dsb.tab.details": "Detalles",
  "uma.dsb.tab.lifecycle": "Ciclo de vida",
  "uma.dsb.tab.summary": "Resumen IA",
  "uma.dsb.tab.comments": "Comentarios Discord",
  "uma.dsb.readMore": "Leer más",
  "uma.dsb.showLess": "Ver menos",
  // Lifecycle stages
  "uma.dsb.lc.proposal": "Propuesta",
  "uma.dsb.lc.dispute": "Disputa",
  "uma.dsb.lc.dvmVote": "Votación DVM",
  "uma.dsb.lc.resolution": "Resolución",
  "uma.dsb.lc.settlement": "Liquidación",
  "uma.dsb.lc.commitPhase": "Fase Commit — votos cifrados enviados",
  "uma.dsb.lc.revealPhase": "Fase Reveal — votos descifrados y contados",
  // Outcome explanations
  "uma.dsb.outcome.yes": "Sí",
  "uma.dsb.outcome.no": "No",
  "uma.dsb.outcome.tooEarly": "Demasiado pronto",
  "uma.dsb.outcome.unknown": "Desconocido / 50-50",
  "uma.dsb.outcome.earlyRequest": "Petición temprana / Demasiado pronto",
  // Vote stats
  "uma.dsb.stat.commitAddrs": "Direcciones únicas de commit",
  "uma.dsb.stat.revealAddrs": "Direcciones únicas de reveal",
  "uma.dsb.stat.tokensCommitted": "Tokens comprometidos",
  "uma.dsb.stat.tokensRevealed": "Tokens revelados",
  "uma.dsb.stat.voteRolls": "Rondas de votación",
  "uma.dsb.stat.totalSlashed": "Total slashed",
  "uma.dsb.stat.totalCorrectVotes": "Votos correctos",
  "uma.dsb.tip.tokensCommitted": "Total de tokens UMA en stake durante la fase commit.",
  "uma.dsb.tip.tokensRevealed": "Total de tokens UMA revelados. Determina si se cumple el quórum (GAT).",
  "uma.dsb.tip.totalSlashed": "Total de tokens UMA penalizados a votantes incorrectos o ausentes. Se redistribuyen a los correctos como recompensa.",
  "uma.dsb.tip.totalCorrect": "Tokens UMA que votaron por el resultado ganador. Estos votantes reciben las recompensas del slashing.",
  "uma.dsb.tip.commitAddrs": "Número de wallets que enviaron votos cifrados durante la fase commit.",
  "uma.dsb.tip.revealAddrs": "Número de wallets que revelaron sus votos. Los votos no revelados sufren slashing.",
  "uma.dsb.tip.voteRolls": "Veces que esta votación no alcanzó quórum o consenso y pasó a la siguiente ronda. Máx. 4 rondas.",
  "uma.dsb.tip.participation": "Métricas de votación ponderadas por tokens. Quórum (GAT) = mínimo de tokens que deben votar para que el resultado sea válido. Consenso (SPAT) = el resultado líder necesita ~65% de los tokens votados. Si alguno falla, la votación pasa a la siguiente ronda (máx. 4).",
  "uma.dsb.tip.quorum": "Global Access Threshold (GAT) — mínimo de tokens UMA que deben votar para que el resultado sea válido.",
  "uma.dsb.tip.consensus": "Stake Participation Agreement Threshold (SPAT) — el resultado líder debe alcanzar este umbral de tokens para que el resultado pase.",
  "uma.dsb.tip.slashing": "UMA penaliza a los votantes que votan incorrectamente o no revelan sus votos. Los tokens penalizados se redistribuyen a los correctos como recompensa.",
  "uma.dsb.tip.wrongVoteSlash": "Porcentaje de tokens en stake perdidos por UMA al votar el resultado incorrecto.",
  "uma.dsb.tip.noVoteSlash": "Porcentaje perdido por UMA al hacer commit pero no revelar. Suele ser mayor que el slash por voto incorrecto — revela siempre tus votos.",
  // Result tab
  "uma.dsb.signIn": "Inicia sesión para acceso completo",
  "uma.dsb.participation": "Participación",
  "uma.dsb.quorum": "Quórum ({pct}%)",
  "uma.dsb.consensus": "Consenso ({pct}%)",
  "uma.dsb.leading": "Líder: {outcome}",
  "uma.dsb.slashing": "Impacto del Slashing",
  "uma.dsb.stat.wrongVoteSlash": "Slash voto incorrecto/token",
  "uma.dsb.stat.noVoteSlash": "Slash sin voto/token",
  "uma.dsb.roll": "{count} ronda",
  "uma.dsb.rolls": "{count} rondas",
  "uma.dsb.noVoteData.disputed": "Los datos de votación aparecerán cuando los votantes empiecen el commit.",
  "uma.dsb.noVoteData": "No hay datos de votación disponibles para esta disputa.",
  "uma.dsb.notFound": "Disputa no encontrada.",
  // Details tab
  "uma.dsb.viewOnOracle": "Ver en Oracle UI ↗",
  "uma.dsb.description": "Descripción",
  "uma.dsb.seeOnPolymarket": "Ver en Polymarket",
  "uma.dsb.additionalContext": "Contexto adicional",
  "uma.dsb.votingOptions": "Opciones de voto",
  "uma.dsb.timestamp": "Marca temporal",
  "uma.dsb.links": "Enlaces",
  "uma.dsb.seeOnFrenflow": "Ver en FrenFlow",
  "uma.dsb.oracleRequest": "Solicitud al Optimistic Oracle",
  // Lifecycle tab
  "uma.dsb.lc.title": "Cronología de resolución",
  "uma.dsb.lc.current": "Actual",
  "uma.dsb.lc.proposalWindow": "Ventana de 2 horas",
  "uma.dsb.lc.dvmTime": "~48 horas",
  "uma.dsb.lc.proposalDesc": "Respuesta propuesta con un bono en USDC.e. Se abre una ventana de 2 horas para que cualquiera pueda disputarla.",
  "uma.dsb.lc.disputeDesc": "Alguien impugnó la propuesta depositando un bono igual. Escalado al DVM (Data Verification Mechanism) de UMA.",
  "uma.dsb.lc.dvmDesc": "Los holders de tokens UMA votan la respuesta correcta mediante un proceso commit-reveal en dos fases.",
  "uma.dsb.lc.resolutionDesc": "Resultado ganador determinado según los votos. Los bonos se distribuyen al ganador.",
  "uma.dsb.lc.settlementDesc": "El mercado se liquida on-chain. Los tokens ganadores se canjean por $1.00 y los perdedores por $0.00.",
  // Bond block
  "uma.dsb.bond.title": "Bonos y economía",
  "uma.dsb.bond.proposer": "Bono del proponente",
  "uma.dsb.bond.disputer": "Bono del disputador",
  "uma.dsb.bond.totalAtStake": "Total en juego",
  "uma.dsb.bond.winner": "Ganador",
  "uma.dsb.bond.winnerDesc": "recupera su bono + la mitad del bono del perdedor como recompensa.",
  "uma.dsb.bond.loser": "Perdedor",
  "uma.dsb.bond.loserDesc": "pierde todo su bono. La mitad va al ganador, la otra mitad al UMA Store.",
  // Outcomes block
  "uma.dsb.outcomes.title": "Qué significa cada resultado para los traders",
  "uma.dsb.outcomes.yesDesc": "Los tokens \"Sí\" se canjean a $1.00. Los tokens \"No\" valen $0.00.",
  "uma.dsb.outcomes.noDesc": "Los tokens \"No\" se canjean a $1.00. Los tokens \"Sí\" valen $0.00.",
  "uma.dsb.outcomes.tooEarlyDesc": "Pregunta hecha demasiado pronto. El mercado vuelve a operar — sin liquidación inmediata.",
  "uma.dsb.outcomes.unknownDesc": "No puede determinarse. Tanto los tokens \"Sí\" como \"No\" se canjean a $0.50.",
  // DVM Voting block
  "uma.dsb.dvm.title": "Cómo funciona la votación DVM",
  "uma.dsb.dvm.row1Title": "Voto ponderado por tokens",
  "uma.dsb.dvm.row1Desc": "Los holders de UMA hacen stake para votar. A más tokens en stake, más poder de voto.",
  "uma.dsb.dvm.row2Title": "Commit-reveal en dos fases",
  "uma.dsb.dvm.row2Desc": "Los votantes envían primero votos cifrados (Commit) y después los descifran (Reveal). Evita la copia de votos.",
  "uma.dsb.dvm.row3Title": "Quórum y Consenso",
  "uma.dsb.dvm.row3Desc": "Debe votar un mínimo de tokens (GAT) y el resultado líder necesita ≥65% de acuerdo (SPAT).",
  "uma.dsb.dvm.row4Title": "Slashing",
  "uma.dsb.dvm.row4Desc": "Los votantes incorrectos o ausentes pierden parte de su UMA en stake. Esos tokens van a los votantes correctos como recompensa.",
  "uma.dsb.dvm.row5Title": "Rondas",
  "uma.dsb.dvm.row5Desc": "Si no se alcanza el quórum o el consenso, la votación pasa a la siguiente ronda. Máximo 4 rondas antes de que la solicitud se elimine.",
  // Learn more
  "uma.dsb.learnMore": "Lee siempre las reglas de resolución antes de operar en mercados disputados. El resultado depende de la redacción exacta de la pregunta y los criterios.",
  "uma.dsb.learnMore.polymarket": "Docs de resolución de Polymarket →",
  "uma.dsb.learnMore.uma": "Docs del protocolo UMA →",
  "uma.dsb.learnMore.umip": "UMIP-107 →",
  // AI Summary tab
  "uma.dsb.aiWarning": "Aviso: resumen generado por IA a partir de comentarios de Discord, sin verificación de hechos. Puede no representar a los votantes de UMA o a Risk Labs. Los comentaristas pueden intentar influir en los votos por interés propio.",
  "uma.dsb.ai.p1": "No (p1: 0)",
  "uma.dsb.ai.p2": "Sí (p2: 1)",
  "uma.dsb.ai.p3": "Petición temprana (p3: 0.5)",
  "uma.dsb.ai.p4": "Demasiado pronto / Custom (p4)",
  "uma.dsb.ai.uncategorized": "Discusión general",
  // Comments tab
  "uma.dsb.commentsWarning": "Aviso: estos comentarios provienen del servidor público de Discord del protocolo UMA y pueden no reflejar la opinión del equipo de UMA.",
  "uma.dsb.noComments": "Aún no hay comentarios para esta disputa.",
  "uma.dsb.joinDiscord": "Únete a la discusión en Discord",
  // Time formatters
  "uma.dsb.time.justNow": "ahora mismo",
  "uma.dsb.time.mAgo": "hace {n}m",
  "uma.dsb.time.hAgo": "hace {n}h",
  "uma.dsb.time.dAgo": "hace {n}d",
  "deposit.transferCrypto": "Transferir cripto",
  "deposit.frenflowBalance": "Saldo en FrenFlow:",
  "deposit.tab.buy": "Comprar",
  "deposit.tab.polymarket": "Polymarket",
  "deposit.tab.bridge": "Bridge",
  "deposit.tab.buyDesc": "Tarjeta, wallet o exchange",
  "deposit.tab.tipDesc": "Envía una propina",
  "deposit.tab.bridgeDesc": "Desde otra red",
  "deposit.method.card.title": "Comprar con tarjeta",
  "deposit.method.card.desc": "Visa, Mastercard, Apple Pay, Google Pay",
  "deposit.method.connectWallet.title": "Conectar wallet",
  "deposit.method.connectWallet.desc": "MetaMask, Phantom, Coinbase y más",
  "deposit.method.transferWallet.title": "Transferir desde wallet",
  "deposit.method.transferWallet.desc": "Envía desde tu wallet conectada",
  "deposit.method.receive.title": "Recibir en Polygon",
  "deposit.method.receive.desc": "Copia tu dirección o QR — envía USDC en Polygon",
  "deposit.note.directToWallet": "Los fondos van directos a tu wallet de trading en Polygon (pUSD)",
  "deposit.note.startTrading": "Empieza a operar al instante tras la compra",
  "deposit.selectChain": "Selecciona una cadena…",
  "deposit.tip.step1": "Abre tu wallet de FrenFlow en Polymarket",
  "deposit.tip.step2": "Pulsa \"Send Tip\" e introduce la cantidad",
  "deposit.tip.step3": "El pUSD llega a tu wallet de trading",
  "deposit.tip.hint": "Pista",
  "deposit.tip.yourWallet": "Tu wallet en Polymarket",
  "deposit.tip.imageAlt": "Tu perfil de Polymarket con Send Tip",
  "deposit.tip.openOnPolymarket": "Abrir en Polymarket",
  "deposit.tip.walletLabel": "Wallet",
  "deposit.bridge.tokens": "Tokens",
  "deposit.bridge.chains": "Cadenas",
  "deposit.bridge.minPrefix": "Mín.",
  "deposit.bridge.depositAddress": "Tu dirección de depósito",
  "deposit.bridge.termsApply": "Se aplican términos",
  "deposit.bridge.copyAddress": "Copiar dirección",
  "deposit.bridge.copied": "Copiada",
  "deposit.bridge.arrivesIn": "Llega en menos de 5 min · Se convierte a pUSD en Polygon automáticamente",
  "deposit.bridge.notYourWallet": "Esta es la dirección del bridge para esta transferencia, no la de tu wallet de FrenFlow.",
  "deposit.bridge.empty": "Selecciona una cadena y un token para generar tu dirección de depósito",

  // ── Pin your prices modal (footer cog) ─────────────────────────────
  "tickerModal.title": "Fija tus precios",
  "tickerModal.subtitle": "Elige qué precios cripto se muestran en el footer. Arrástralas para reordenar.",
  "tickerModal.placeholder.add": "Añade un ticker",
  "tickerModal.placeholder.full": "Quita uno para añadir otro",
  "tickerModal.add": "Añadir",
  "tickerModal.reset": "Restablecer",
  "tickerModal.done": "Hecho",
  "tickerModal.helpIntro": "Escribe cualquier ticker de Binance — p. ej.",
  "tickerModal.helpOutro": "es un par spot operable en Binance antes de fijarlo.",
  "tickerModal.helpCheck": ". Comprobamos que",
  "tickerModal.limitReached": "Has alcanzado el límite de {max} tickers. Quita uno con el icono de papelera de arriba para liberar un hueco.",
  "tickerModal.limit.prefix": "Has alcanzado el límite de {max} tickers. Quita uno con",
  "tickerModal.limit.suffix": "para liberar un hueco.",
  "tickerModal.error.keepOne": "Deja al menos una moneda en el ticker.",
  "tickerModal.error.tooLong": "El símbolo es demasiado largo.",
  "tickerModal.error.alreadyAdded": "{symbol} ya está en el ticker.",
  "tickerModal.error.limit": "Límite de {max} monedas. Quita una primero.",
  "tickerModal.error.noPair": "Binance no tiene el par {symbol}/USDT.",
  "tickerModal.error.noPrice": "Binance no devolvió precio para {symbol}.",
  "tickerModal.error.validation": "La validación falló. Inténtalo de nuevo.",
  "tickerModal.dragLabel": "Arrastrar {symbol}",
  "tickerModal.removeLabel": "Quitar {symbol}",
  "tickerModal.closeLabel": "Cerrar",

  // ── Filters / sort bar ─────────────────────────────────────────────
  "filterBar.status": "Estado",
  "filterBar.statusActive": "Activos",
  "filterBar.statusClosed": "Cerrados",
  "filterBar.sort": "Ordenar",
  "filterBar.filters": "Filtros",
  "filterBar.advancedFilters": "Filtros avanzados",
  "filterBar.advancedFiltersDesc": "Afina tu búsqueda de mercados",
  "filterBar.searchCategories": "Buscar categorías…",
  "filterBar.applyFilters": "Aplicar filtros",
  "filterBar.clearAll": "Borrar todo",
  "filterBar.savedFilters": "Filtros guardados",
  "filterBar.reverseSort": "Invertir orden",
  "filterBar.sort.volume24hr": "Volumen 24h",
  "filterBar.sort.volume": "Volumen total",
  "filterBar.sort.liquidity": "Liquidez",
  "filterBar.sort.newest": "Más nuevos",
  "filterBar.sort.endingSoon": "Cierran pronto",
  "filterBar.sort.competitive": "Competitivos",
  "filterBar.sort.trending": "Tendencias",
  "filterBar.sort.volatile": "Volátiles",
  "filterBar.sort.new": "Nuevos",
  "filterBar.sort.closingSoon": "Cierran pronto",
  "filterBar.sort.eventVolume": "Volumen",
  "filterBar.sort.priceBalance": "50-50",
  "filterBar.sort.relevance": "Relevancia",
  "filterBar.sort.hot": "Hot",
  "filterBar.sort.all": "Todos",
  "filters.categoriesCount": "Categorías ({count})",
  "filters.mode.and": "Y",
  "filters.mode.or": "O",
  "filters.mode.include": "Incluir",
  "filters.mode.exclude": "Excluir",
  "filters.section.endDate": "Fecha de cierre",
  "filters.section.priceRange": "Rango de precio (YES)",
  "filters.section.liquidity": "Liquidez",
  "filters.section.volume24h": "Volumen 24h",
  "filters.section.maxSpread": "Spread máximo",
  "filters.section.totalVolume": "Volumen total",
  "filters.section.totalVolumeNote": "Usa las opciones de orden de arriba para ordenar por volumen total",
  "filters.section.rewardsOnly": "Solo con recompensas",
  "filters.section.rewardsOnlyDesc": "Mostrar solo mercados con recompensas de liquidez",
  "filters.range.min": "mín.",
  "filters.range.max": "máx.",
  "filters.range.to": "a",
  "filters.range.minimum": "mínimo",
  "filters.range.maximum": "máximo",
  "filters.range.daysShort": "días",
  "filters.range.zeroDays": "0 días",
  "filters.range.maxDays": "365 días",
  "filters.preset.lessOneDay": "< 1 día",
  "filters.preset.lessSevenDays": "< 7 días",
  "filters.preset.lessThirtyDays": "< 30 días",
  "filters.footer.reset": "Restablecer",
  "filters.footer.save": "Guardar",
  "filters.footer.cancel": "Cancelar",
  "filters.footer.apply": "Aplicar filtros",
  "filters.footer.filterNamePlaceholder": "Nombre del filtro…",
  "viewMode.grid": "Vista de cuadrícula",
  "viewMode.list": "Vista de lista",
  "table.market": "Mercado",
  "table.price": "Precio",
  "table.change1h": "1h %",
  "table.change24h": "24h %",
  "table.change7d": "7d %",
  "table.chart7d": "Gráfico (7d)",
  "table.smartMoney": "Smart $",
  "table.roi": "ROI",
  "table.volume24h": "Vol 24h",
  "table.volume": "Volumen",
  "table.liquidity": "Liquidez",
  "table.total": "Total",
  "table.hot": "Hot",
  "table.hotTooltip": "Mercado disputado — la opción líder está entre 45-55%",
  "table.live": "En vivo",
  "table.rewards": "Recompensas",
  "table.rewardsTooltip": "${rate}/día en recompensas de liquidez",
  "table.today": "Hoy",
  "table.markets": "{n} mercados",
  "table.expand": "Ver más",
  "table.collapse": "Ver menos",
  "table.daysLeft": "{n}d restantes",
  "table.timeLeft": "{value} restante",
  "elon.tweetCounter": "Contador de Tweets",
  "elon.live": "EN VIVO",
  "elon.tweetCount": "Total de Tweets",
  "elon.endsIn": "Termina en",
  "elon.view": "Ver",
  "elon.volume": "Volumen",
  "elon.pickBracket": "Elige tu rango",
  "elon.leading": "🔥 En cabeza",
  "elon.passed": "✓ Superado",
  "elon.tweets": "tweets",
  "elon.empty": "Los mercados de contador de tweets aparecerán aquí cuando estén disponibles",
  "elon.subcat.all": "Todos los mercados",
  "elon.subcat.tweets": "Contador de Tweets",
  "elon.subcat.tesla": "Tesla",
  "elon.subcat.spacex": "SpaceX",
  "elon.subcat.xai": "xAI / Grok",
  "elon.subcat.doge": "DOGE",
  "elon.tracker.retry": "Reintentar",
  "elon.tracker.newTweet": "¡NUEVO TWEET!",
  "elon.tracker.tweetCount": "Total de tweets",
  "elon.tracker.timeLeft": "Tiempo restante",
  "elon.tracker.progress": "Progreso",
  "elon.tracker.progressPace": "Progreso: {progress}%   Ritmo: {pace} tweets",
  "elon.tracker.speedTitle": "Velocidad para alcanzar el objetivo",
  "elon.tracker.pass": "Pasado",
  "elon.tracker.current": "Actual",
  "elon.tracker.na": "N/D",
  "elon.tracker.timePerTweet": "{mins} min {secs} seg / tweet",
  "elon.tracker.liveSpeed": "Velocidad en vivo",
  "elon.tracker.timeSinceLast": "Tiempo desde la última actividad",
  "elon.tracker.activityCount": "Nº de actividades en el último período",
  "elon.tracker.activities": "{count} actividades",
  "elon.tracker.lastOne": "Última, {tz}",
  "elon.tracker.postingSpeed": "Velocidad de publicación en el último período",
  "elon.tracker.perActivity": "{time} / actividad",
  "elon.tracker.screenTime": "Tiempo de adicción a la pantalla",
  "elon.tracker.firstOne": "Primera, {tz}",
  "elon.tracker.heatmapTitle": "Tweets (divididos por horas)",
  "elon.tracker.repliesIncluded": "Incluir respuestas",
  "elon.tracker.focusDay": "Día destacado",
  "elon.tracker.legendET": "Hora del Este",
  "elon.tracker.legendLT": "Tu hora local según tu ubicación",
  "elon.tracker.legendStart": "El período del mercado empieza aquí",
  "elon.tracker.legendNow": "Hora actual",
  "elon.tracker.colDate": "Fecha",
  "elon.tracker.colTotal": "Total",
  "elon.tracker.refreshNote": "Actualización de datos: cada ~30 segundos",
  "elon.tracker.recentPosts": "Publicaciones recientes",
  "elon.tracker.colCount": "Nº de tweet",
  "elon.tracker.colTimestamp": "Fecha y hora",
  "elon.tracker.colType": "Tipo",
  "elon.tracker.colId": "ID básico / ID X Tracker",
  "elon.tracker.colContent": "Contenido",
  "elon.tracker.typeReply": "Respuesta",
  "elon.tracker.typeTweet": "Tweet / Repost / Cita",
  "elon.tracker.none": "Ninguno",
  "elon.tracker.perPage": "{n} / página",
  "elon.tracker.lastUpdated": "Última actualización: {time}",
  "elon.tracker.ended": "Finalizado",
  "elon.tracker.day": "día",
  "elon.tracker.days": "días",
  "common.loadMore": "Cargar más",
  "common.loading": "Cargando…",
  "common.tryAgain": "Reintentar",
  "common.fetchEventsError": "No se pudieron cargar los eventos",
  "common.all": "Todos",

  // ── SEO / títulos pestaña navegador y descripciones ─────────────
  "seo.home.title": "FrenFlow — Copy trading en Polymarket, Kalshi y más",
  "seo.home.description": "Copia a los mejores traders de Polymarket y Kalshi en menos de un segundo. Leaderboards on-chain verificados, whale tracking y self-custody.",
  "seo.markets.title": "Mercados de predicción — Explora y opera eventos de Polymarket",
  "seo.markets.description": "Explora miles de mercados de predicción en Polymarket. Filtra por categoría, sigue eventos en tendencia y opera política, deportes, cripto y más con precios y analíticas en tiempo real.",
  "seo.elon.title": "Mercados de Elon Musk",
  "seo.elon.description": "Opera mercados de predicción sobre Elon Musk en FrenFlow. Contadores de tweets, Tesla, SpaceX, xAI y más.",
  "seo.crypto.title": "Mercados cripto",
  "seo.crypto.description": "Opera mercados de predicción cripto en FrenFlow. Bitcoin, Ethereum, Solana y más.",
  "seo.elections.title": "Mercados de elecciones — Opera elecciones de EE.UU. y del mundo",
  "seo.elections.description": "Opera mercados de predicción sobre elecciones en FrenFlow. Presidenciales de EE.UU., política europea, Latinoamérica y más. Cuotas y analíticas en tiempo real.",
  "seo.forYou.title": "Para ti — Mercados de predicción personalizados",
  "seo.forYou.description": "Descubre mercados de predicción adaptados a tus intereses. Recomendaciones con IA sobre Polymarket de política, deportes, cripto y más.",
  "seo.trending.title": "Mercados de predicción en tendencia — Lo más caliente ahora",
  "seo.trending.description": "Descubre los mercados de predicción más calientes ahora mismo. Mira qué es tendencia en Polymarket con volumen y movimientos de precio en tiempo real.",
  "seo.trending.descriptionN": "Descubre {n} mercados de predicción calientes ahora mismo. Mira qué es tendencia en Polymarket con volumen, movimientos de precio y actividad de trading en tiempo real.",
  "seo.breakingPage.title": "Mercados de última hora — Eventos en directo",
  "seo.breakingPage.description": "Opera sobre noticias de última hora y eventos en directo. Mercados de predicción en tiempo real para los últimos desarrollos en Polymarket.",
  "seo.breakingPage.descriptionN": "Opera en {n} mercados de predicción de última hora. Cuotas en tiempo real para eventos en directo y los últimos desarrollos en Polymarket.",
  "seo.new.title": "Nuevos mercados de predicción — Recién añadidos",
  "seo.new.description": "Descubre los mercados de predicción más nuevos de Polymarket. Sé el primero en operar mercados frescos con oportunidades tempranas.",
  "seo.new.descriptionN": "Descubre {n} mercados de predicción recién añadidos en Polymarket. Sé el primero en operar mercados frescos con cuotas y oportunidades tempranas.",
  "seo.endingSoon.title": "Terminan pronto — Mercados que se resuelven hoy",
  "seo.endingSoon.description": "Opera mercados de predicción que terminan pronto. Bitcoin, cripto, deportes y más — se resuelven hoy en Polymarket.",
  "seo.endingSoon.descriptionN": "Opera en {n} mercados de predicción que terminan pronto. Bitcoin, Ethereum, deportes y más — se resuelven hoy en Polymarket.",
  "seo.categoryFallback.title": "Mercados de {label}",
  "seo.categoryFallback.description": "Opera mercados de predicción de {label} en FrenFlow. Cuotas en tiempo real y liquidez profunda impulsadas por Polymarket.",
  "seo.cat.titleSuffix": " — Opera con dinero real",
  "seo.cat.dynamicPrefix": "Explora {n} mercados de predicción de {label} activos. ",
  "seo.cat.politics.title": "Mercados de predicción de política",
  "seo.cat.politics.description": "Opera mercados de predicción políticos con dinero real. Elecciones de EE.UU., legislación del Congreso, órdenes ejecutivas, decisiones del Tribunal Supremo y política global. Cuotas en tiempo real impulsadas por Polymarket.",
  "seo.cat.sports.title": "Mercados de predicción deportivos",
  "seo.cat.sports.description": "Mercados de predicción deportivos — opera NFL, NBA, Premier League, La Liga, Champions League, UFC, F1 y más. Cuotas en tiempo real, liquidez profunda. Opera resultados con dinero real en FrenFlow.",
  "seo.cat.esports.title": "Mercados de predicción de esports",
  "seo.cat.esports.description": "Mercados de predicción de esports — opera CS2, League of Legends, Dota 2, Valorant, Overwatch y más. Cuotas en tiempo real, liquidez profunda. Opera resultados con dinero real en FrenFlow.",
  "seo.cat.crypto.title": "Mercados de predicción cripto",
  "seo.cat.crypto.description": "Mercados de predicción cripto — objetivos de precio de Bitcoin, hitos de Ethereum, eventos de altcoins, resultados DeFi y decisiones regulatorias. Cuotas en vivo impulsadas por Polymarket en FrenFlow.",
  "seo.cat.finance.title": "Mercados de predicción de finanzas",
  "seo.cat.finance.description": "Mercados de predicción financieros — decisiones de tipos de la Reserva Federal, hitos de bolsa, resultados de OPV, sorpresas de earnings e indicadores macroeconómicos. Opera con dinero real en FrenFlow.",
  "seo.cat.elections.title": "Mercados de predicción de elecciones",
  "seo.cat.elections.description": "Mercados de predicción electorales — presidenciales, congresuales y gubernamentales de EE.UU. y elecciones globales. Cuotas en tiempo real, sentimiento del votante y probabilidades de resultado en FrenFlow.",
  "seo.cat.geopolitics.title": "Mercados de predicción geopolíticos",
  "seo.cat.geopolitics.description": "Mercados de predicción geopolíticos — conflictos internacionales, acuerdos comerciales, sanciones, decisiones de la OTAN, resoluciones de la ONU y negociaciones diplomáticas. Cuotas en tiempo real en FrenFlow.",
  "seo.cat.economy.title": "Mercados de predicción de economía",
  "seo.cat.economy.description": "Mercados de predicción económicos — crecimiento del PIB, tasas de inflación, datos de desempleo, confianza del consumidor, tendencias del mercado inmobiliario y política de bancos centrales. Opera dinero real en FrenFlow.",
  "seo.cat.tech.title": "Mercados de predicción de tecnología",
  "seo.cat.tech.description": "Mercados de predicción tecnológicos — avances en IA, lanzamientos de productos, adquisiciones de startups, rankings de app stores, guerra de chips y resultados de las big tech. Cuotas en vivo en FrenFlow.",
  "seo.cat.culture.title": "Mercados de predicción de cultura y entretenimiento",
  "seo.cat.culture.description": "Mercados de predicción de cultura pop — Oscars, Grammys, taquilla, renovación de series, eventos de famosos, momentos virales y resultados de la industria del entretenimiento en FrenFlow.",
  "seo.cat.world.title": "Mercados de predicción de actualidad mundial",
  "seo.cat.world.description": "Mercados de predicción sobre actualidad mundial — eventos globales, crisis humanitarias, cumbres climáticas, tratados internacionales, hitos de exploración espacial y desarrollos transfronterizos en FrenFlow.",
  "seo.cat.elon.title": "Mercados de predicción de Elon Musk",
  "seo.cat.elon.description": "Mercados de predicción sobre Elon Musk — objetivos de Tesla, lanzamientos de SpaceX, cambios en X, hitos de Neuralink, decisiones del DOGE y resultados de tweets de Musk en FrenFlow.",
  "seo.cat.climate-science.title": "Mercados de predicción de clima y ciencia",
  "seo.cat.climate-science.description": "Mercados de predicción de clima y ciencia — pronósticos diarios de temperatura, terremotos, huracanes, volcanes, pandemias, eventos espaciales y récords climáticos en FrenFlow.",
  "seo.cat.weather-science.title": "Mercados de predicción de clima y ciencia",
  "seo.cat.weather-science.description": "Mercados de predicción de clima y ciencia — pronósticos diarios de temperatura, terremotos, huracanes, volcanes, pandemias, eventos espaciales y récords climáticos en FrenFlow.",
  "seo.cat.mentions.title": "Mercados de predicción de menciones",
  "seo.cat.mentions.description": "Mercados de predicción sobre menciones en redes sociales, contenido viral y temas en tendencia. Sigue de qué habla internet y opera resultados con dinero real en FrenFlow.",
  "seo.cat.macro.title": "Mercados de predicción macro",
  "seo.cat.macro.description": "Mercados de predicción macroeconómicos — previsiones de PIB, balanzas comerciales, gasto del consumidor, índices de manufactura y panorama económico global. Opera con dinero real en FrenFlow.",
  "seo.cat.fed-rates.title": "Mercados de predicción de tipos de la Fed",
  "seo.cat.fed-rates.description": "Mercados de predicción sobre los tipos de interés de la Reserva Federal — resultados de reuniones del FOMC, bajadas y subidas de tipos, dot plot y decisiones de política monetaria. Cuotas en vivo en FrenFlow.",
  "seo.cat.trump.title": "Mercados de predicción de Trump",
  "seo.cat.trump.description": "Mercados de predicción sobre la presidencia de Trump — órdenes ejecutivas, nombramientos de gabinete, decisiones políticas, procedimientos legales y eventos políticos. Cuotas en tiempo real en FrenFlow.",
  "seo.cat.iran.title": "Mercados de predicción de Irán",
  "seo.cat.iran.description": "Mercados de predicción sobre Irán — negociaciones nucleares, sanciones, tensiones geopolíticas, desarrollos del régimen, objetivos de producción de petróleo y resultados diplomáticos. Cuotas en tiempo real en FrenFlow.",

  // SEO de páginas (layouts + páginas individuales)
  "seo.activity.title": "Actividad — Capa social de los mercados de predicción",
  "seo.activity.description": "Llamadas de trading, alertas de ballenas e insights de mercado. El motor social que impulsa las decisiones en los mercados de predicción.",
  "seo.bubbleflow.title": "BubbleFlow — Visualización en vivo de mercados de predicción | FrenFlow",
  "seo.bubbleflow.description": "Explora los mercados de predicción como burbujas interactivas. Mira los mercados en tendencia, volúmenes y precios en tiempo real. Arrastra, lanza y descubre mercados en Polymarket.",
  "seo.copytrading.title": "Bot de Copy Trading de Polymarket 2026 — Auto-Copia",
  "seo.copytrading.description": "Bot de copy trading de Polymarket con ejecución en el Bloque 0. Copia automáticamente a los mejores traders de mercados de predicción en tiempo real, rastrea wallets de ballenas y opera vía Telegram. Self-custody, 1% taker / 0.5% maker, sin cuota mensual.",
  "seo.degenbox.title": "Degenbox — Sigue 8 mercados de Polymarket a la vez",
  "seo.degenbox.description": "Tu dashboard personal de trading en Polymarket. Sigue hasta 8 mercados simultáneamente con orderbooks en tiempo real, trades recientes y gráficos de precio. Personaliza tu vista con drag & drop.",
  "seo.elonTracker.title": "Tracker de tweets de Elon Musk",
  "seo.elonTracker.description": "Seguimiento en tiempo real de los tweets de Elon Musk. Notificaciones en vivo, mapas de calor por hora y gráficos acumulados.",
  "seo.eventLayout.title": "Evento",
  "seo.eventLayout.description": "Mira y opera mercados de predicción para este evento en Polymarket.",
  "seo.frenlinks.title": "FrenLinks — Envía shares a cualquiera | FrenFlow",
  "seo.frenlinks.description": "Envía shares de mercados de predicción a cualquiera con un enlace simple. Regala posiciones a tus frens al instante con FrenLinks.",
  "seo.geopoliticsLayout.title": "Inteligencia geopolítica — Mapa de conflictos y tensiones en tiempo real",
  "seo.geopoliticsLayout.description": "Sigue conflictos globales, tensiones diplomáticas y puntos calientes bilaterales en tiempo real. Impulsado por datos de GDELT con mapas interactivos, hotspots y arcos de tensión.",
  "seo.hyperliquid.title": "Mercados de predicción de Hyperliquid · FrenFlow",
  "seo.hyperliquid.description": "Opera ya los mercados de predicción HIP-4 de Hyperliquid en FrenFlow. La primera interfaz externa para el binario diario de BTC en HyperCore — autocustodia y wrap a USDH automático.",
  "seo.portfolio.title": "Cartera — Sigue tus posiciones de Polymarket",
  "seo.portfolio.description": "Gestiona tus posiciones de Polymarket y tu copy trading. Sigue tu P&L, mira posiciones abiertas y monitoriza tu cartera de mercados de predicción en tiempo real.",
  "seo.post.title": "Publicación - FRENFLOW",
  "seo.post.description": "Mira la publicación en FRENFLOW.",
  "seo.openInterest.title": "Open Interest de mercados de predicción | FrenFlow",
  "seo.openInterest.description": "Datos de open interest y volumen en tiempo real entre mercados de predicción. Sigue las métricas combinadas de Kalshi y Polymarket con treemaps interactivos, gráficos y datos en bruto.",
  "seo.predictor.title": "Predictor con IA — Análisis de mercados para Polymarket",
  "seo.predictor.description": "Análisis y predicciones de mercados con IA para eventos de Polymarket. Obtén insights basados en datos sobre los resultados de los mercados de predicción.",
  "seo.profileLayout.title": "Perfil",
  "seo.profileLayout.description": "Mira el perfil de usuario en FrenFlow.",
  "seo.rewards.title": "Recompensas",
  "seo.rewards.description": "Gana Fren Points y abre Mystery Chests. Sistema de recompensas demostrablemente justo con premios en USDC.",
  "seo.settings.title": "Ajustes de cuenta",
  "seo.settings.description": "Gestiona los ajustes de tu cuenta de FrenFlow. Configura preferencias de trading, notificaciones y ajustes de perfil.",
  "seo.sportsLayout.title": "Mercados de predicción deportivos",
  "seo.sportsLayout.description": "Opera eventos deportivos en directo. Marcadores en tiempo real, cuotas y herramientas de trading para mercados de predicción deportivos.",
  "seo.swipe.title": "Swipe Markets — Descubre mercados de predicción",
  "seo.swipe.description": "Desliza entre mercados de predicción para descubrir nuevas oportunidades. Una forma rápida de encontrar mercados que encajen con tus intereses en Polymarket.",
  "seo.uma.title": "UMA Checker — Panel de disputas",
  "seo.uma.description": "Sigue las disputas del oráculo UMA, votaciones DVM y leaderboards de votantes para los mercados de predicción de Polymarket.",
  "seo.traders.title": "Leaderboard de top traders",
  "seo.traders.description": "Descubre los mejores traders de Polymarket. Sigue P&L, win rate, volumen y copia los trades de los mejores.",
  "seo.feedback.title": "Feedback",
  "seo.feedback.description": "Consigue ayuda, reporta bugs y sugiere features para FrenFlow.",
  "seo.contact.title": "Contacto — FrenFlow",
  "seo.contact.description": "Contacta con el equipo de FrenFlow. Soporte, partnerships y consultas generales.",
  "seo.theFrens.title": "The Frens — Comunidad",
  "seo.theFrens.description": "Conoce a la comunidad de FrenFlow. Top traders, influencers y partners.",
  "seo.affiliate.title": "Programa de afiliados — FrenFlow",
  "seo.affiliate.description": "Únete al programa de afiliados de FrenFlow. Gana comisiones refiriendo nuevos traders a la plataforma.",
  "seo.arbitrage.title": "Arbitraje — Encuentra mercados mal valorados",
  "seo.arbitrage.description": "Descubre oportunidades de arbitraje entre los mercados de Polymarket. Encuentra outcomes mal valorados y opera con confianza.",
  "seo.blogIndex.title": "Blog — FrenFlow",
  "seo.blogIndex.description": "Blog de FrenFlow: análisis de mercados, novedades de producto, entrevistas a traders e insights sobre mercados de predicción.",
  "sports.live": "EN VIVO",
  "sports.final": "FINAL",
  "sports.tbd": "TBD",
  "sports.volShort": "Vol.",
  "sports.draw": "Empate",
  "sports.noProps": "No hay props ni futuros disponibles",
  "sports.fetchError": "No se pudieron cargar los datos deportivos",
  "sports.retry": "Reintentar",
  "sports.watchStream": "Ver stream",
  "sports.gameXofY": "Partida {current} / {total}",
  "sports.startingSoon": "Empiezan pronto ({n})",
  "crypto.up": "Sube",
  "crypto.down": "Baja",
  "crypto.resolving": "Resolviendo",
  "crypto.priceToBeat": "Precio a batir",
  "crypto.target": "Objetivo",
  "crypto.currentPrice": "Precio actual",
  "crypto.current": "Actual",
  "crypto.finalPrice": "Precio final",
  "crypto.resolvesIn": "Resuelve en",
  "crypto.resolves": "Resuelve",
  "crypto.resolvedAt": "Resuelto a las",
  "crypto.loadingPriceData": "Cargando datos de precios…",
  "crypto.filtersHeader": "Filtros cripto",
  "crypto.commoditiesFiltersHeader": "Filtros materias primas",
  "crypto.tf.all": "Todos",
  "crypto.tf.5m": "5 min",
  "crypto.tf.15m": "15 min",
  "crypto.tf.1h": "1 hora",
  "crypto.tf.4h": "4 horas",
  "crypto.tf.daily": "Diario",
  "crypto.tf.weekly": "Semanal",
  "crypto.tf.monthly": "Mensual",
  "crypto.tf.yearly": "Anual",
  "crypto.tf.pre-market": "Pre-Mercado",
  "crypto.tf.etf": "ETF",
  "crypto.mt.all": "Todos",
  "crypto.mt.above-below": "Sobre/Bajo",
  "crypto.mt.up-down": "Sube/Baja",
  "crypto.etf.all": "Todos",
  "crypto.etf.ibit": "IBIT",
  "crypto.etf.fbtc": "FBTC",
  "crypto.etf.gbtc": "GBTC",
  "crypto.etf.ethe": "ETHE",
  "crypto.etf.inflows": "Entradas",
  "crypto.etf.outflows": "Salidas",
  "crypto.marketOverview": "Resumen de mercado",
  "crypto.toggleOverview": "Mostrar/ocultar resumen",
  "crypto.fearGreedIndex": "Índice de miedo y codicia",
  "crypto.now": "Ahora",
  "crypto.nowColon": "Ahora: ",
  "crypto.yesterday": "Ayer",
  "crypto.lastWeek": "Última semana",
  "crypto.lastMonth": "Último mes",
  "crypto.weekShort": "Semana",
  "crypto.monthShort": "Mes",
  "crypto.historical": "Histórico",
  "crypto.overTime": "Evolución",
  "crypto.exploreTop100": "Explora las Top 100 monedas en",
  "crypto.fg.extremeFear": "Miedo extremo",
  "crypto.fg.fear": "Miedo",
  "crypto.fg.neutral": "Neutral",
  "crypto.fg.greed": "Codicia",
  "crypto.fg.extremeGreed": "Codicia extrema",
  "finance.upcomingEarnings": "Próximos resultados",
  "finance.sectorPerformance": "Rendimiento por sector",
  "finance.commodities": "Materias primas",
  "finance.eps": "BPA",
  "finance.rev": "Ing.",
  "geopol.title": "Monitor geopolítico",
  "geopol.subtitle": "Datos de conflictos y tensiones diplomáticas en tiempo real desde GDELT",
  "geopol.live": "En vivo",
  "geopol.eventsTracked": "{n} eventos rastreados",
  "geopol.fullMap": "Mapa completo",
  "geopol.conflictZones": "Zonas de conflicto",
  "geopol.topRegion": "Región principal",
  "geopol.conflictMentions": "Menciones de conflicto",
  "geopol.diplomaticTensions": "Tensiones diplomáticas",
  "geopol.last24h": "24h",
  "geopol.mostActive": "más activa",
  "geopol.inNews": "en noticias",
  "geopol.hotspots": "Puntos calientes",
  "geopol.activeCount": "{n} activos",
  "geopol.noHotspots": "No hay puntos calientes activos",

  // ── Página de Ajustes ────────────────────────────────────────────
  "settings.title": "Ajustes",
  "settings.subtitle": "Gestiona tu cuenta, trading y notificaciones.",
  "settings.nav.profile": "Perfil",
  "settings.nav.trading": "Trading",
  "settings.nav.wallet": "Wallets",
  "settings.nav.notifications": "Notificaciones",
  "settings.nav.connections": "Conexiones",
  "settings.nav.invites": "Invitaciones",
  "settings.profile.editCover": "Editar portada",
  "settings.profile.changeAvatar": "Cambiar avatar",
  "settings.profile.pickFren": "Elige un Fren o sube tu propia foto",
  "settings.profile.upload": "Subir",
  "settings.profile.displayName": "Nombre",
  "settings.profile.displayNamePh": "Tu nombre",
  "settings.profile.username": "Usuario",
  "settings.profile.usernamePh": "usuario",
  "settings.profile.bio": "Bio",
  "settings.profile.bioPh": "Cuéntanos algo sobre ti...",
  "settings.profile.save": "Guardar perfil",
  "settings.profile.saving": "Guardando...",
  "settings.trading.tradingStatus": "Estado del trading",
  "settings.trading.platformConnections": "Conexiones de plataforma",
  "settings.trading.enable": "Activar",
  "settings.trading.enabling": "Activando...",
  "settings.trading.active": "Activo",
  "settings.trading.inactive": "Inactivo",
  "settings.trading.revoke": "Revocar",
  "settings.trading.cancel": "Cancelar",
  "settings.trading.deletePolyCreds": "¿Borrar credenciales de Polymarket?",
  "settings.trading.revokePredict": "¿Revocar acceso de trading de Predict.fun?",
  "settings.trading.revokeKalshi": "¿Revocar acceso de trading de Kalshi?",
  "settings.trading.verifyKyc": "Verificar KYC",
  "settings.trading.signing": "Firmando...",
  "settings.trading.reenable": "Reactivar",
  "settings.trading.autoRedeem": "Auto-cobrar",
  "settings.trading.autoRedeemDesc": "Convertir ganancias a pUSD cuando los mercados se resuelvan",
  "settings.trading.privateTrading": "Trading privado",
  "settings.trading.privateTradingDesc": "Oculta tus trades manuales y de copy del feed público de actividad",
  "settings.trading.slippage": "Tolerancia de slippage",
  "settings.trading.slippageDesc": "Desviación máxima de precio para órdenes de mercado",
  "settings.trading.painFloor": "Pérdida tolerada",
  "settings.trading.painFloorDesc": "Permite copias cuando la pérdida absoluta es menor a este valor, aunque el slippage exceda tu cap. Aplica a todos los leaders de copytrading.",
  "settings.trading.marketOrderType": "Tipo de orden de mercado",
  "settings.trading.marketOrderTypeDesc": "Cómo se ejecutan tus órdenes de mercado",
  "settings.trading.fakTitle": "Fill and Kill (FAK)",
  "settings.trading.fakDesc": "Llena tanto como sea posible a los mejores precios disponibles y cancela cualquier porción no llenada",
  "settings.trading.fokTitle": "Fill or Kill (FOK)",
  "settings.trading.fokDesc": "Ejecuta toda la orden inmediatamente al precio especificado o la cancela por completo",
  "settings.trading.quickBuy": "Importes de compra rápida",
  "settings.trading.quickBuyDesc": "Botones personalizados de acceso rápido en el flujo de trading",
  "settings.trading.quickBuySave": "Guardar",
  "settings.trading.quickBuyHint": "Configura hasta 3 importes rápidos ($1-$10.000). Déjalos vacíos para usar los predeterminados.",
  "settings.trading.updating": "Actualizando...",
  "settings.trading.onboardingIncomplete": "Onboarding incompleto",
  "settings.trading.revokeHyperliquid": "¿Desactivar el trading en Hyperliquid? Tus fondos permanecen en HyperCore — solo se revoca el permiso de la agent wallet.",
  "settings.wallet.withdraw": "Retirar",
  "settings.wallet.withdrawArbitrum": "Retirar a Arbitrum",
  "settings.wallet.enablePolymarket": "Activar trading en Polymarket →",
  "settings.wallet.enableHyperliquid": "Activar trading en Hyperliquid →",
  "settings.wallet.usdcHypercore": "USDC en HyperCore (master de Arbitrum)",
  "settings.wallet.enableFirst": "Activa el trading primero",
  "settings.wallet.nothingToWithdraw": "Nada que retirar",
  "settings.toast.updateFailed": "No se pudo actualizar el ajuste",
  "settings.toast.quickBuySaved": "Importes rápidos guardados",
  "settings.toast.slippageSet": "Slippage fijado en {value}%",
  "settings.toast.slippageSetSource": "Slippage de {label} fijado en {value}%",
  "settings.toast.slippageRange": "Introduce un valor entre 0,1 y 100",
  "settings.toast.orderTypeSet": "Órdenes de mercado ahora {type}",
  "settings.toast.orderTypeFok": "Fill-Or-Kill",
  "settings.toast.orderTypeFak": "Fill-And-Kill",
  "settings.toast.autoRedeemOn": "Auto-cobro activado",
  "settings.toast.autoRedeemOff": "Auto-cobro desactivado",
  "settings.toast.kycFailed": "No se pudo iniciar la verificación KYC",
  "settings.toast.kalshiReenabled": "Trading en Kalshi reactivado",
  "settings.toast.reenableFailed": "No se pudo reactivar",
  "settings.toast.revokeHlFailed": "No se pudo revocar Hyperliquid",
  "settings.toast.resetDefault": "Restablecido al valor por defecto",
  "settings.toast.resetFailed": "No se pudo restablecer",
  "settings.toast.connected": "{label} conectado",
  "settings.toast.connectFailed": "Error al conectar {label}: {error}",
  "settings.aria.toggleAutoRedeem": "Alternar auto-cobro",
  "settings.aria.togglePrivateTrading": "Alternar trading privado",
  "settings.aria.toggleWebAlerts": "Alternar alertas web",
  "settings.aria.toggleLiveTicker": "Alternar ticker en vivo",
  "settings.aria.toggleNewsFeed": "Alternar feed de noticias",
  "settings.aria.togglePriceAlerts": "Alternar alertas de precio",
  "settings.aria.togglePush": "Alternar notificaciones push",
  "settings.aria.toggleCopytrading": "Alternar alertas de copytrading",
  "settings.aria.toggleWalletTracker": "Alternar alertas del wallet tracker",
  "settings.aria.toggleItem": "Alternar notificaciones de {label}",
  "settings.notif.webAlerts": "Alertas web",
  "settings.notif.webAlertsDesc": "Alertas del navegador en tiempo real",
  "settings.notif.liveTicker": "Ticker en vivo",
  "settings.notif.liveTickerDesc": "Precios de mercado en la parte inferior",
  "settings.notif.newsFeed": "Noticias",
  "settings.notif.newsFeedDesc": "Popups de noticias cripto",
  "settings.notif.priceAlerts": "Alertas de precio",
  "settings.notif.priceAlertsDesc": "Notificaciones de precio de mercado",
  "settings.notif.push": "Notificaciones push",
  "settings.notif.pushSubscribed": "Recibiendo alertas push",
  "settings.notif.pushDenied": "Pulsa para reintentar — revisa los permisos del navegador/SO",
  "settings.notif.pushNative": "Alertas nativas del navegador ({state})",
  "settings.notif.bravePushHint": "Brave bloquea push por defecto. Actívalo en brave://settings/privacy → \"Use Google services for push messaging\", reinicia Brave, y vuelve a intentar. O usa notificaciones por Telegram — funcionan en cualquier dispositivo sin configurar el navegador.",
  "settings.notif.webApp": "Web App",
  "settings.notif.webAppDesc": "Elige qué notificaciones aparecen en la campana del header",
  "settings.notif.copytrading": "Copytrading",
  "settings.notif.copytradingDesc": "Trades, fills, resueltos",
  "settings.notif.walletTracker": "Wallet Tracker",
  "settings.notif.walletTrackerDesc": "Actividad de wallets seguidas",
  "settings.notif.telegram": "Telegram",
  "settings.notif.telegramDesc": "Elige qué notificaciones recibir por Telegram",
  "settings.notif.copyTrade": "Copy trade",
  "settings.notif.copyTradeDesc": "Trade copiado del líder",
  "settings.notif.copySkips": "Avisos de copia saltada",
  "settings.notif.copySkipsDesc": "Por qué una copia se saltó (muy pequeña, slippage, auto-pausa)",
  "settings.notif.limitFilled": "Límite ejecutado",
  "settings.notif.limitFilledDesc": "La orden se ejecuta",
  "settings.notif.limitExpired": "Límite expirado",
  "settings.notif.limitExpiredDesc": "La orden expira sin ejecutarse",
  "settings.notif.resolved": "Resuelto",
  "settings.notif.resolvedDesc": "El mercado se resuelve",
  "settings.notif.lowBalance": "Saldo bajo",
  "settings.notif.lowBalanceDesc": "El saldo es demasiado bajo",
  "settings.notif.slippage": "Slippage",
  "settings.notif.slippageDesc": "Aviso de slippage alto",
  "settings.wallet.pUsdPolygon": "pUSD en Polygon",
  "settings.wallet.usdcSolana": "USDC en Solana",
  "settings.wallet.eoaSweep.title": "${amount} en tu wallet de login",
  "settings.wallet.eoaSweep.body": "{tokens} están en tu wallet de firma, fuera de la wallet de trading. Muévelos para poder operar.",
  "settings.wallet.eoaSweep.button": "Enviar a wallet de trading →",
  "settings.wallet.eoaSweep.sending": "Enviando...",
  "settings.wallet.loadingSolana": "Cargando wallet de Solana...",
  "settings.wallet.exportKeys": "Exportar claves privadas",
  "settings.wallet.exportKeysDesc": "Tus wallets embebidas de Privy. La wallet EVM controla tu Safe de Polymarket y se comparte entre cadenas EVM.",
  "settings.wallet.evmWallet": "Wallet EVM",
  "settings.wallet.solanaWallet": "Wallet de Solana",
  "settings.wallet.export": "Exportar",
  "settings.wallet.warnPrivKey": "Cualquiera con acceso a tu clave privada puede controlar tus fondos. Nunca la compartas con nadie.",
  "settings.wallet.warnNoStore": "FrenFlow no almacena ni tiene acceso a tus claves privadas. Nadie de FrenFlow te las pedirá nunca.",
  "settings.connections.title": "Cuentas conectadas",
  "settings.connections.connect": "Conectar",
  "settings.connections.notConnected": "Sin conectar",
  "settings.connections.googleBenefit": "Inicio de sesión más rápido con Google SSO",
  "settings.connections.twitterBenefit": "Comparte tus trades y haz crecer tu audiencia",
  "settings.connections.telegramBenefit": "Tradea y recibe alertas desde nuestro bot de Telegram",
  "settings.connections.emailBenefit": "Recibe confirmaciones de trades y recuperación de cuenta",
  "settings.connections.recommended": "Recomendado",
  "settings.connections.email": "Email",
  "settings.connections.externalWallet": "Wallet externa",
  "settings.connections.externalWalletDesc": "Conecta MetaMask, Coinbase, etc.",
  "settings.invites.title": "Tus invitaciones",
  "settings.invites.subtitle": "Invita a amigos a FrenFlow",
  "settings.invites.unlimited": "∞ ilimitado",
  "settings.invites.left": "{n} restantes",
  "settings.invites.generate": "Generar código de invitación",
  "settings.invites.usedBy": "Usado por {who}",
  "settings.invites.someone": "alguien",
  "settings.invites.notUsed": "Sin usar",
  "settings.invites.empty": "Aún no has generado invitaciones. Pulsa el botón de arriba para crear una.",
  "settings.invites.failed": "No se pudieron cargar las invitaciones",
  "bridge.balance.available": "Saldo disponible",
  "bridge.balance.moveFunds": "Mover fondos",
  "bridge.balance.converting": "Convirtiendo...",
  "bridge.balance.convertUsdce": "Convertir {amount} USDC.e a saldo de trading",
  "bridge.balance.convertUsdc": "Convertir {amount} USDC a saldo de trading",
  "bridge.balance.convertPusdBack": "Convertir {amount} pUSD de vuelta a USDC.e",
  "bridge.balance.wrapFailed": "Conversión fallida: {error}",
  "bridge.balance.unwrapFailed": "Desconversión fallida: {error}",
  "bridge.balance.toastConverted": "${amount} {asset} convertidos a pUSD — listos para operar",
  "bridge.modal.title": "Mover fondos",
  "bridge.modal.titleProgress": "Moviendo fondos...",
  "bridge.modal.titleComplete": "¡Transferencia completada!",
  "bridge.modal.from": "Desde",
  "bridge.modal.to": "Hasta",
  "bridge.modal.amount": "Cantidad",
  "bridge.modal.max": "MÁX",
  "bridge.modal.minAmount": "Mínimo $5,00",
  "bridge.modal.insufficient": "Saldo insuficiente",
  "bridge.modal.gettingRoute": "Buscando la mejor ruta...",
  "bridge.modal.youReceive": "Recibirás",
  "bridge.modal.fee": "Comisión",
  "bridge.modal.estimatedTime": "Tiempo estimado",
  "bridge.modal.minutes": "~{n} min",
  "bridge.modal.selectDestination": "Seleccionar destino",
  "bridge.modal.selectPlaceholder": "Seleccionar",
  "bridge.modal.signing": "Firmando y aprobando...",
  "bridge.modal.approved": "Aprobado — transfiriendo fondos...",
  "bridge.modal.bridging": "Puente entre cadenas — casi listo...",
  "bridge.modal.canClose": "Puedes cerrar esta ventana — te avisaremos cuando termine.",
  "bridge.modal.addedTo": "añadido a {market}",
  "bridge.modal.done": "Hecho",
  "bridge.modal.transferFailed": "Transferencia fallida",
  "bridge.modal.somethingWrong": "Algo salió mal. Tus fondos están seguros.",
  "bridge.modal.tryAgain": "Reintentar",
  "weather.tempHigh": "Temp. máx.",
  "weather.tempLow": "Temp. mín.",
  "news.tab": "Noticias",
  "news.cryptoTitle": "Noticias cripto",
  "news.live": "En vivo",
  "news.closeTitle": "Cerrar noticias",
  "news.openTitle": "Abrir noticias",
  "sports.liveLabel": "En vivo",
  "sports.liveSportMarkets": "Mercados deportivos en vivo ({n})",
  "sports.liveEsportsMarkets": "Mercados de esports en vivo ({n})",
  "sport.soccer": "Fútbol",
  "sport.basketball": "Baloncesto",
  "sport.football": "Fútbol americano",
  "sport.baseball": "Béisbol",
  "sport.hockey": "Hockey",
  "sport.tennis": "Tenis",
  "sport.golf": "Golf",
  "sport.boxing": "Boxeo",
  "sport.mma": "MMA",
  "sport.ufc": "UFC",
  "sport.motorsports": "Motor",
  "sport.chess": "Ajedrez",
  "sport.pickleball": "Pickleball",
  "sport.table-tennis": "Tenis de mesa",
  "sport.cricket": "Críquet",
  "sport.rugby": "Rugby",
  "sport.esports": "Esports",
  "sport.winter-olympics": "Olimpiadas de invierno",

  // ── Tooltips (event / market cards, badges) ────────────────────────
  "tooltip.addToWatchlist": "Añadir a la watchlist",
  "tooltip.removeFromWatchlist": "Quitar de la watchlist",
  "tooltip.notInterested": "No me interesa",
  "tooltip.boosted": "Boosted — operando este mercado ganas recompensas extra",
  "tooltip.liquidity": "Liquidez",
  "tooltip.liquidityHelp": "Profundidad disponible en el orderbook",
  "tooltip.openInterest": "Interés abierto",
  "tooltip.openInterestHelp": "USDC bloqueado en posiciones abiertas",
  "tooltip.tradersCount": "{count} traders",
  "tooltip.smartMoneyEarly": "Aún hay menos de 5 smart wallets operando este mercado",
  "smartMoney.label": "Smart Money",
  "smartMoney.shortLabel": "Smart $",
  "smartMoney.outcome": "Resultado: {label}",
  "smartMoney.wallets": "Smart wallets: {count}",
  "smartMoney.netPositioning": "Posicionamiento neto: {pct}% {side}",
  "smartMoney.volume": "Volumen smart: {volume}",
  "smartMoney.avgRoi": "ROI medio: {roi}",
  "smartMoney.avgWinRate": "Tasa de acierto media: {rate}",
  "smartMoney.summaryYesNo": "Smart money: {yes} YES · {no} NO · {volume} de volumen",
  "smartMoney.boxSubtitle": "Qué están haciendo las wallets con mejor rendimiento en este mercado",
  "smartMoney.boxSubtitleOutcome": "Wallets con mejor rendimiento — señal más fuerte en «{label}»",
  "smartMoney.statWallets": "Smart wallets",
  "smartMoney.statPositioning": "Posicionamiento",
  "smartMoney.statVolume": "Volumen smart",
  "smartMoney.statRoi": "ROI medio",
  "smartMoney.statWinRate": "Tasa de acierto",
  "smartMoney.topHolders": "Top smart wallets",
  "smartMoney.tipWhat": "Lo que las wallets con mejor rendimiento («smart money») están haciendo en este mercado: posicionamiento, tamaño y acierto histórico.",
  "smartMoney.tipPositioning": "Porcentaje de wallets smart money que son compradoras netas de cada lado en el outcome líder. La barra divide SÍ vs NO.",
  "smartMoney.tipWallets": "Número de wallets con mejor rendimiento («smart») activas en este outcome.",
  "smartMoney.tipVolume": "Volumen total en USD operado por estas smart wallets en este outcome.",
  "smartMoney.tipRoi": "Retorno medio de la inversión de estas smart wallets en sus posiciones resueltas.",
  "smartMoney.tipWinRate": "Porcentaje de posiciones resueltas de estas smart wallets que acabaron ganando.",
  "smartMoney.tipHolders": "Las smart wallets con mayores posiciones en el outcome líder. Toca para ver el perfil de la wallet.",
  "tooltip.deadline.next": "Siguiente: {label}",
  "tooltip.deadline.resolves": "Se resuelve el {date}",
  "tooltip.deadline.daysLeftPct": "Quedan {days} · {pct}% transcurrido",
  "tooltip.deadline.more": "+{n} más",
  "eventCard.moreOutcomes": "+{n} resultados más",
  "eventCard.moreMarkets": "+{n} mercados más",
  "eventCard.viewAllMarkets": "Ver los {n} mercados",

  // ── Category labels ────────────────────────────────────────────────
  "category.trending": "Tendencias",
  "category.new": "Nuevos",
  "category.breaking": "Última hora",
  "category.for-you": "Para ti",
  "category.ending-soon": "Cierran pronto",
  "category.politics": "Política",
  "category.sports": "Deportes",
  "category.crypto": "Cripto",
  "category.crypto-prices": "Cripto",
  "category.finance": "Finanzas",
  "category.fed-rates": "Tipos de la Fed",
  "category.geopolitics": "Geopolítica",
  "category.world": "Mundo",
  "category.tech": "Tecnología",
  "category.business": "Empresas",
  "category.climate": "Clima",
  "category.climate-science": "Clima y ciencia",
  "category.weather-science": "Clima y ciencia",
  "category.pop-culture": "Cultura pop",
  "category.movies": "Cine",
  "category.music": "Música",
  "category.health": "Salud",
  "category.science": "Ciencia",
  "category.gaming": "Gaming",
  "category.esports": "Esports",
  "category.elections": "Elecciones",
  "category.mentions": "Menciones",
  "category.elon": "Elon",
  "category.trump": "Trump",
  "category.biden": "Biden",
  "category.nfl": "NFL",
  "category.nba": "NBA",
  "category.mlb": "MLB",
  "category.nhl": "NHL",
  "category.soccer": "Fútbol",
  "category.mma": "MMA",
  "category.ufc": "UFC",
  "category.olympics": "Juegos Olímpicos",
  "category.tennis": "Tenis",
  "category.boxing": "Boxeo",
  "category.f1": "F1",
  "category.golf": "Golf",
  "category.cricket": "Críquet",
  "category.ai": "IA",
  "category.bookmarks": "Guardados",
  "category.culture": "Cultura",
  "category.entertainment": "Cultura",
  "category.economy": "Economía",
  "category.economics": "Economía",
  "category.commodities": "Materias primas",
  "category.companies": "Empresas",
  "category.financials": "Finanzas",
  "category.live": "En directo",
  "category.all": "Todos",
  "category.hourly": "Por hora",
  "category.daily": "Diario",
  "category.weekly": "Semanal",
  "category.monthly": "Mensual",
  "category.elon-musk": "Elon Musk",
  "category.tech-science": "Tecnología y ciencia",
  "category.middle-east": "Oriente Medio",
  "category.china": "China",
  "category.russia": "Rusia",
  "category.ukraine": "Ucrania",
  "category.spain": "España",
  "category.recession": "Recesión",
  "category.inflation": "Inflación",
  "category.stocks": "Bolsa",
  "category.tesla": "Tesla",
  "category.apple": "Apple",
  "category.spacex": "SpaceX",
  "category.nasa": "NASA",
  "category.nobel": "Nobel",
  "category.oscars": "Óscars",
  "category.grammys": "Grammys",
  "category.la-liga": "La Liga",
  "category.premier-league": "Premier League",
  "category.champions-league": "Champions League",
  "category.world-cup": "Mundial",
  "category.euro": "Eurocopa",
  "category.copa-america": "Copa América",
  "category.lol": "League of Legends",
  "category.cs": "CS",
  "category.dota": "Dota",
  "category.valorant": "Valorant",
  "category.hurricanes": "Huracanes",
  "category.wildfires": "Incendios",
  "category.earthquakes": "Terremotos",
  "category.tv-shows": "Series",

  // ── Subcategories — politics ─────────────────────────────────────
  "category.election-2024": "Elecciones 2024",
  "category.election-2026": "Elecciones 2026",
  "category.election-2028": "Elecciones 2028",
  "category.elections-2024": "Elecciones 2024",
  "category.elections-2026": "Elecciones 2026",
  "category.swing-states": "Estados clave",
  "category.polls": "Encuestas",
  "category.president": "Presidente",
  "category.presidents": "Presidentes",
  "category.congress": "Congreso",
  "category.senate": "Senado",
  "category.house": "Cámara",
  "category.supreme-court": "Tribunal Supremo",
  "category.fed": "Fed",
  "category.harris": "Harris",
  "category.vance": "Vance",
  "category.musk": "Musk",
  "category.putin": "Putin",
  "category.modi": "Modi",
  "category.xi": "Xi Jinping",
  "category.zelensky": "Zelenski",
  "category.netanyahu": "Netanyahu",
  "category.israel": "Israel",
  "category.iran": "Irán",
  "category.gaza": "Gaza",
  "category.palestine": "Palestina",
  "category.taiwan": "Taiwán",
  "category.korea": "Corea",
  "category.north-korea": "Corea del Norte",

  // ── Subcategories — crypto ───────────────────────────────────────
  "category.bitcoin": "Bitcoin",
  "category.ethereum": "Ethereum",
  "category.solana": "Solana",
  "category.dogecoin": "Dogecoin",
  "category.ripple": "Ripple",
  "category.btc": "BTC",
  "category.eth": "ETH",
  "category.sol": "SOL",
  "category.halving": "Halving",
  "category.etf": "ETF",
  "category.spot-etf": "ETF spot",
  "category.bull-market": "Mercado alcista",
  "category.bear-market": "Mercado bajista",
  "category.altcoins": "Altcoins",
  "category.stablecoins": "Stablecoins",
  "category.memecoins": "Memecoins",
  "category.defi": "DeFi",
  "category.nfts": "NFTs",
  "category.btc-price": "Precio BTC",
  "category.eth-price": "Precio ETH",
  "category.sol-price": "Precio SOL",

  // ── Subcategories — sports leagues ────────────────────────────────
  "category.super-bowl": "Super Bowl",
  "category.world-series": "World Series",
  "category.nba-finals": "Finales de la NBA",
  "category.nba-mvp": "MVP de la NBA",
  "category.nfl-mvp": "MVP de la NFL",
  "category.nfl-week": "Semana NFL",
  "category.stanley-cup": "Stanley Cup",
  "category.serie-a": "Serie A",
  "category.bundesliga": "Bundesliga",
  "category.ligue-1": "Ligue 1",
  "category.mls": "MLS",
  "category.europa-league": "Europa League",
  "category.wimbledon": "Wimbledon",
  "category.us-open": "US Open",
  "category.french-open": "Roland Garros",
  "category.australian-open": "Open de Australia",
  "category.atp": "ATP",
  "category.wta": "WTA",
  "category.grand-slam": "Grand Slam",
  "category.formula-1": "Fórmula 1",
  "category.moto-gp": "MotoGP",
  "category.nascar": "NASCAR",
  "category.cricket-world-cup": "Mundial de críquet",
  "category.ipl": "IPL",

  // ── Subcategories — econ / finance ────────────────────────────────
  "category.fed-meeting": "Reunión de la Fed",
  "category.cpi": "IPC",
  "category.gdp": "PIB",
  "category.jobs-report": "Datos de empleo",
  "category.unemployment": "Desempleo",
  "category.interest-rates": "Tipos de interés",
  "category.recession-2026": "Recesión 2026",
  "category.bonds": "Bonos",
  "category.sp500": "S&P 500",
  "category.nasdaq": "Nasdaq",
  "category.dow": "Dow Jones",

  // ── Finance sidebar / pestañas subcat ────────────────────────────
  "category.earnings": "Resultados",
  "category.indicies": "Índices",
  "category.forex": "Divisas",
  "category.collectibles": "Coleccionables",
  "category.acquisitions": "Adquisiciones",
  "category.earnings-calls": "Conferencias de resultados",
  "category.ipo": "OPVs",
  "category.prediction-markets": "Mercados de predicción",
  "category.treasuries": "Bonos del Tesoro",
  "category.up-or-down": "Sube/Baja",
  "category.updown": "Sube/Baja",
  "category.above": "Sobre/Bajo",
  "category.below": "Sobre/Bajo",
  "category.above-below": "Sobre/Bajo",
  "category.price-range": "Rango de precio",
  "category.hit-price": "Toca el precio",
  "category.daily-close": "Cierre diario",
  "category.sp-500": "S&P 500",
  "category.gold": "Oro",
  "category.silver": "Plata",
  "category.oil": "Petróleo",
  "category.uranium": "Uranio",
  "category.dow-jones": "Dow Jones",
  "category.russell": "Russell 2000",
  "category.nya": "NYA",
  "category.ftse": "FTSE 100",
  "category.hangseng": "Hang Seng",
  "category.kospi": "KOSPI",
  "category.tsla": "Tesla",
  "category.nvda": "NVIDIA",
  "category.app-store": "App Store",
  "category.ipos": "OPVs",
  "category.fda": "FDA",
  "category.microstrategy": "MicroStrategy",
  "category.big-tech": "Big Tech",
  "category.tiktok": "TikTok",
  "category.trade-war": "Guerra comercial",
  "category.macro": "Macro",
  "category.macro-indicators": "Indicadores macro",
  "category.global-rates": "Tipos globales",
  "category.taxes": "Impuestos",
  "category.tsa": "TSA",
  "category.housing": "Vivienda",
  "category.temperature": "Temperatura",
  "category.high-temperature": "Temperatura máxima",
  "category.low-temperature": "Temperatura mínima",
  "category.precipitation": "Precipitación",
  "category.tornadoes": "Tornados",
  "category.volcanoes": "Volcanes",
  "category.pandemics": "Pandemias",
  "category.global": "Global",

  // ── Subcategories — culture / entertainment ──────────────────────
  "category.box-office": "Taquilla",
  "category.streaming": "Streaming",
  "category.netflix": "Netflix",
  "category.spotify": "Spotify",
  "category.taylor-swift": "Taylor Swift",
  "category.celebrity": "Famosos",
  "category.awards": "Premios",
  "category.emmys": "Emmys",
  "category.golden-globes": "Globos de Oro",

  // ── Subcategories — tech ─────────────────────────────────────────
  "category.openai": "OpenAI",
  "category.gpt": "GPT",
  "category.gpt-5": "GPT-5",
  "category.gpt-6": "GPT-6",
  "category.meta": "Meta",
  "category.google": "Google",
  "category.microsoft": "Microsoft",
  "category.nvidia": "Nvidia",
  "category.amazon": "Amazon",

  // ── Subcategories — weather / climate ────────────────────────────
  "category.weather": "Tiempo",
  "category.hurricane": "Huracán",
  "category.wildfire": "Incendio forestal",
  "category.snowfall": "Nevada",
  "category.heatwave": "Ola de calor",
  "category.tornado": "Tornado",
  "category.flood": "Inundación",
  "category.drought": "Sequía",
  "category.rainfall": "Precipitación",
  "category.sea-level": "Nivel del mar",
  "category.global-warming": "Calentamiento global",
  "category.climate-change": "Cambio climático",
  "category.air-quality": "Calidad del aire",
  "category.storm": "Tormenta",
  "category.atlantic-hurricane": "Huracán del Atlántico",
  "category.pacific-hurricane": "Huracán del Pacífico",
  "category.snow": "Nieve",
  "category.rain": "Lluvia",
  "category.weather-alerts": "Alertas meteorológicas",
  "tooltip.totalVolume": "Volumen total: {volume}",
  "tooltip.volume24h": "Volumen 24h: {volume}",
  "tooltip.readAnalysis": "Leer el análisis",
  "tooltip.boostedLabel": "Boosted",
  "tooltip.liquidityRewards": "Recompensas de liquidez",
  "tooltip.lpRewardsBlurb": "Gana recompensas colocando órdenes límite cerca del punto medio.",
  "tooltip.lpRewards": "Recompensas",
  "tooltip.lpMaxSpread": "Spread máximo",
  "tooltip.lpMinShares": "Acciones mínimas",
  "tooltip.editTickerCoins": "Editar monedas del ticker",
  "tooltip.selectLanguage": "Seleccionar idioma",
  "tooltip.theme": "Tema",
  "tooltip.darkMode": "Modo oscuro",
  "tooltip.lightMode": "Modo claro",

  // ── Home / landing ─────────────────────────────────────────────────
  // Hero
  "home.hero.statusPill": "Beta abierta · En vivo",
  "home.hero.headingLine1": "La capa social y",
  "home.hero.headingLine2": "de ejecución",
  "home.hero.headingLine3": "para mercados de predicción.",
  "home.hero.subhead": "FrenFlow es la plataforma de copytrading para Polymarket: replica las entradas y salidas de los mejores traders en tu propia wallet self-custody en menos de 1s. El mismo copytrading con un toque en Kalshi, Predict.fun e Hyperliquid.",
  "home.hero.cta.start": "Empezar a operar",
  "home.hero.cta.explore": "Explorar mercados",
  "home.hero.tradeAcross": "Opera en",
  "home.hero.backedBy": "Con tecnología de",
  // Counters
  "home.counters.volumeTraded": "Volumen operado",
  "home.counters.activeTraders": "Traders activos",
  // Stats row
  "home.stats.addressableLiquidity": "Liquidez accesible",
  "home.stats.addressableHint": "En Polymarket · Kalshi · Hyperliquid",
  "home.stats.acrossVenues": "2 exchanges, una app",
  "home.stats.venuesValue": "1 UI",
  "home.stats.venuesHint": "Polymarket · Kalshi",
  "home.stats.avgExecution": "Ejecución media",
  "home.stats.avgExecutionHint": "Copia en menos de un segundo",
  "home.stats.totalVolume": "Volumen total",
  "home.stats.totalVolumeHint": "Operado en FrenFlow",
  // Bottom strip
  "home.strip.liveTrades": "Operaciones en directo",
  // Showcase cards
  "home.card.topMover": "Mayor movimiento · 24h",
  "home.card.topMoverFallback": "Mayor movimiento",
  "home.card.topTrader": "Mejor trader · 30d",
  "home.card.topTraderFallback": "Mejor trader",
  "home.card.copiers": "{count} copiando",
  "home.card.verifiedTrader": "Trader verificado",
  "home.card.pnl30d": "PnL 30d",
  "home.card.live": "En directo",
  "home.card.liveNow": "Ahora mismo",
  "home.card.whaleAlert": "Alerta de ballena",
  "home.card.tier.megaWhale": "Mega ballena",
  "home.card.tier.whale": "Ballena",
  "home.card.tier.dolphin": "Delfín",
  "home.card.historicalAccuracy": "{pct}% acierto histórico",
  "home.card.bought": "compró {outcome} en",
  "home.card.sold": "vendió {outcome} en",
  "home.card.movesNow": "Movimientos ahora",
  "home.card.smartMoneyPrefix": "Smart money:",
  "home.card.wallets": "{count} wallets",
  "home.card.traderFallback": "Trader",
  // Copy-centric showcase cards
  "home.card.sameBlock": "Copia · sub-segundo",
  "home.card.sameBlock.leader": "El líder opera",
  "home.card.sameBlock.you": "Tú lo replicas",
  "home.card.sameBlock.latency": "<1s",
  "home.card.sameBlock.sub": "en tu wallet sin custodia",
  "home.card.mostCopied": "Más copiado",
  "home.card.copyCta": "Copiar a este trader",
  "home.card.topToCopy": "Mejores traders para copiar",
  "home.card.seeAll": "Ver ranking",
  "home.card.roi": "ROI",
  // MarketPulse
  "home.pulse.eyebrow": "Pulso del mercado",
  "home.pulse.title": "Los mercados que se mueven ahora.",
  "home.pulse.body": "Las operaciones más grandes, los que más suben y las preguntas en las que la smart money está cargando sin hacer ruido.",
  "home.pulse.cta": "Ver todos los mercados",
  // WhosWinning
  "home.winning.eyebrow": "Quién está ganando",
  "home.winning.title": "Copia a los traders que de verdad ganan.",
  "home.winning.body": "Wallets reales, PnL real, en tiempo real. Pulsa cualquier trader para replicar sus posiciones en cuanto abra una nueva.",
  "home.winning.cta": "Ranking completo",
  "home.winning.copiers": "{count} copiando",
  "home.winning.trades": "{count} operaciones",
  "home.winning.winPct": "{pct}% acierto",
  "home.winning.viewProfile": "Ver perfil →",
  // ProductFeatures
  "home.features.eyebrow": "Lo que hemos construido",
  "home.features.title": "Copytrading, y todo lo que lo rodea.",
  "home.features.body": "El copytrading es el núcleo. La prueba social, la ejecución self-custody y el seguimiento de wallets son todo lo que hace que copiar a los mejores traders funcione de verdad.",
  "home.features.copy.eyebrow": "Copytrading",
  "home.features.copy.title": "Replica a los mejores traders en cuanto abren una posición.",
  "home.features.copy.body": "Elige un líder, fija tu stake y cada movimiento que haga se replica en tu wallet en tiempo real — con las mismas reglas de gestión de riesgo que pondrías para ti.",
  "home.features.copy.bullet1": "Stake fijo o % de saldo",
  "home.features.copy.bullet2": "Límites por operación, control de slippage, follow-sells",
  "home.features.copy.bullet3": "Copia en menos de un segundo en el CLOB de Polymarket",
  "home.features.copy.cta": "Empezar copytrading",
  "home.features.social.eyebrow": "Social",
  "home.features.social.title": "Un feed pensado para mercados de predicción, no para memes.",
  "home.features.social.body": "Las operaciones verificadas se convierten en ideas que se pueden compartir. Sigue traders, publica opiniones con un mercado adjunto y descubre quién está cazando los movimientos más grandes.",
  "home.features.social.bullet1": "Posts de operaciones verificadas con odds en vivo",
  "home.features.social.bullet2": "Alertas de ballenas y movimientos de mercado en el mismo timeline",
  "home.features.social.bullet3": "Recompensas para los autores cuyos posts generan volumen",
  "home.features.social.cta": "Ver el feed",
  "home.features.exec.eyebrow": "Ejecución",
  "home.features.exec.title": "Routing self-custody en Polymarket, Kalshi e Hyperliquid.",
  "home.features.exec.body": "Una UI, tres exchanges, cero intermediarios reteniendo tus fondos. Enrutamos cada operación al CLOB correcto con la wallet correcta — tus llaves, nuestra velocidad.",
  "home.features.exec.bullet1": "Safe wallets gestionadas por Privy en Polygon (Polymarket)",
  "home.features.exec.bullet2": "Solana vía DFlow (Kalshi) · wallets HyperCore (Hyperliquid)",
  "home.features.exec.bullet3": "Builder fee del CLOB — 1% taker / 0.5% maker (verificado en DeFiLlama)",
  "home.features.exec.cta": "Operar ahora",
  "home.features.track.eyebrow": "Tracking",
  "home.features.track.title": "No te pierdas lo que está haciendo la smart money.",
  "home.features.track.body": "Configura alertas de precio, sigue cualquier wallet, observa los flujos de ballenas en tiempo real y recibe pings en Telegram en cuanto tus apuestas se mueven. Para los traders que se lo toman en serio.",
  "home.features.track.bullet1": "Wallet tracker — cualquier wallet de Polymarket, en directo",
  "home.features.track.bullet2": "Señales de smart money — quién acierta de forma consistente",
  "home.features.track.bullet3": "Alertas de precio + analítica de PnL en Telegram",
  "home.features.track.cta": "Abrir el tracker",
  // Visual mocks
  "home.visual.copy.topLeader": "Top líder · 187 copiando",
  "home.visual.copy.justOpened": "Acaba de abrir",
  "home.visual.copy.bought": "Compró",
  "home.visual.copy.boughtSuffix": "en \"¿Será Scream 7 la más taquillera de 2026?\" a 0,7¢",
  "home.visual.copy.mirroring": "Replicando ahora en 187 wallets",
  "home.visual.copy.executed": "Copia ejecutada en 187/187 wallets",
  "home.visual.social.followers": "1,2M seguidores",
  "home.visual.social.verifiedTrade": "Trade verificado",
  "home.visual.social.topLeader": "Top líder",
  "home.visual.social.elonBody": "Cargando YES en \"La Fed baja tipos en marzo\"",
  "home.visual.social.whaleBody": "Acaba de comprar $50K de NO en \"Trump 2028\"",
  "home.visual.social.threadBody": "Hilo: 3 mercados que cierran el viernes que merece la pena seguir",
  "home.visual.social.insight": "Insight",
  "home.visual.exec.tagline": "Una wallet · Tres exchanges · Cero custodios",
  "home.visual.exec.connected": "Conectado",
  "home.visual.exec.endToEnd": "de media en ejecución end-to-end",
  "home.visual.track.whaleAlert": "Alerta de ballena",
  "home.visual.track.whaleBody": "0xa1b2… abrió $250K YES — hace 2m",
  "home.visual.track.smartMoney": "Smart money",
  "home.visual.track.smartBody": "7 top performers en largo en 'Bitcoin 150K $'",
  "home.visual.track.priceAlert": "Alerta de precio",
  "home.visual.track.priceBody": "YES en 'Recortes Fed' acaba de cruzar 62¢",
  "home.visual.track.pnl": "PnL",
  "home.visual.track.pnlBody": "Tus posiciones copiadas suben +$1.284 hoy",
  // Why FrenFlow
  "home.why.eyebrow": "Por qué FrenFlow",
  "home.why.title": "Hecho para traders que se lo toman en serio.",
  "home.why.r1.title": "La ejecución más rápida en Polymarket",
  "home.why.r1.body": "Copia en menos de un segundo. Firmamos por nuestro lado con KMS para que tus operaciones caigan en el mismo bloque que las de la ballena.",
  "home.why.r1.stat": "~620ms media",
  "home.why.r2.title": "Self-custody, verificado on-chain",
  "home.why.r2.body": "Tus fondos, tus llaves. Nuestro contrato FeeCollector es open-source y está listado en DeFiLlama — sin promesas, solo código.",
  "home.why.r2.stat": "Verificado en DefiLlama",
  "home.why.r3.title": "Multi-exchange desde el día uno",
  "home.why.r3.body": "Polymarket (Polygon) y Kalshi (Solana vía DFlow). Una UI, un bot de Telegram, dos pools de liquidez.",
  "home.why.r3.stat": "2 exchanges",
  // FAQ
  "home.faq.eyebrow": "Preguntas frecuentes",
  "home.faq.title": "Todo lo que querrías saber antes de registrarte.",
  "home.faq.q1": "¿Qué es FrenFlow?",
  "home.faq.a1": "FrenFlow es una plataforma multi-venue para mercados de predicción. Combina una UI de trading, un bot de Telegram y wallets self-custody con un feed social nativo para que puedas operar en Polymarket, Kalshi, Predict.fun e Hyperliquid HIP-4, copiar a los mejores traders y compartir posiciones verificadas — todo desde una sola cuenta. FrenFlow funciona con tecnología de DexTools.",
  "home.faq.q2": "¿Es seguro usar FrenFlow?",
  "home.faq.a2": "Sí. FrenFlow es non-custodial — nunca custodiamos los fondos de los usuarios. Las wallets de trading son Safes gestionadas por Privy en Polygon (Polymarket), wallets embebidas de Solana (Kalshi vía DFlow), EOAs en BSC (Predict.fun) y cuentas de Hyperliquid HyperCore. El contrato FeeCollector que cobra nuestra comisión es open-source, auditado y está verificado en DeFiLlama. Todas las operaciones se liquidan on-chain en el exchange subyacente — FrenFlow solo enruta órdenes.",
  "home.faq.q3": "¿Cómo funciona el copy trading en Polymarket?",
  "home.faq.a3": "Elige un líder en el ranking de FrenFlow, configura el modo de stake (USD fijo o % de tu saldo) y ajusta los límites por operación. Cuando el líder abra o cierre una posición, FrenFlow firma y ejecuta la misma operación en tu wallet self-custody en menos de un segundo — con límites opcionales de slippage, tamaño y follow-sells. La latencia media de copia es de ~620ms.",
  "home.faq.q4": "¿Qué mercados de predicción soporta FrenFlow?",
  "home.faq.a4": "FrenFlow soporta cuatro venues hoy: Polymarket (Polygon, USDC), Kalshi vía DFlow (Solana, USDC), Predict.fun (BSC, USDT) e Hyperliquid HIP-4 (HyperCore). Los cuatro son accesibles desde el mismo modal de trading unificado, así que puedes mover capital donde haya mejor liquidez sin gestionar cuentas o wallets separadas.",
  "home.faq.q5": "¿FrenFlow es una alternativa a Polymarket?",
  "home.faq.a5": "FrenFlow es una capa agregadora sobre Polymarket y los otros venues de mercados de predicción — no un exchange que compite. Sigues operando contra el mismo order book de Polymarket, con la misma liquidez y la misma liquidación on-chain, pero ganas copy trading, tracking de ballenas, señales de smart money, un bot de Telegram y acceso unificado a Kalshi, Predict.fun e Hyperliquid HIP-4 desde la misma UI.",
  "home.faq.q6": "¿FrenFlow tiene bot de Telegram?",
  "home.faq.a6": "Sí. El bot de Telegram de FrenFlow soporta trading completo, copy trading, wallet tracker y alertas de precio en todos los venues soportados. Comparte el mismo backend y las mismas wallets self-custody que la web, así que tus posiciones y ajustes están sincronizados entre ambos.",
  "home.faq.q7": "¿Qué comisiones cobra FrenFlow?",
  "home.faq.a7": "1% taker y 0.5% maker en trades de Polymarket, cobrados como builder fee directamente al nivel del CLOB — sin comisiones de servicio extra, sin comisiones de depósito, retiro, suscripción ni membresía. Mismo modelo para copy trading. Los ingresos netos se publican públicamente en DeFiLlama.",
  "home.faq.q8": "¿Para quién está hecho FrenFlow?",
  "home.faq.a8": "FrenFlow está hecho para traders activos de mercados de predicción que quieren ejecución en menos de un segundo, tracking de smart money y copy trading multi-venue sin renunciar al self-custody. Si operas Polymarket habitualmente, copias wallets ballena o cazas edge entre Kalshi / Predict.fun / Hyperliquid HIP-4, FrenFlow es el terminal unificado.",
  "home.faq.q9": "¿Es seguro el copy trading en Polymarket?",
  "home.faq.a9": "El copy trading en FrenFlow es non-custodial: tus fondos están en una wallet que solo tú controlas y FrenFlow solo ejecuta las operaciones que autorizas — nunca puede retirar tu dinero. El riesgo real es el del trading en sí: un líder puede perder y el rendimiento pasado nunca garantiza resultados futuros. Mantienes el control con límites de tamaño por operación, topes de stake y la opción de dejar de copiar cuando quieras. Revisa el historial completo de un líder antes de copiarlo.",
  "home.faq.q10": "¿Puedo perder dinero haciendo copy trading?",
  "home.faq.a10": "Sí. El copy trading replica posiciones reales en mercados de predicción reales, así que puedes perder tu stake si las operaciones copiadas resuelven en contra. Copiar a un trader rentable no importa una garantía — incluso las mejores wallets tienen rachas perdedoras. Ajusta tu stake de copia a lo que puedas permitirte perder y usa los topes por operación de FrenFlow para acotar el riesgo en cada posición.",
  // Closing CTA
  "home.closing.title.line1": "Empieza a copiar donde",
  "home.closing.title.line2": "ya está la smart money.",
  "home.closing.body": "Únete a los traders que copian las wallets de Polymarket que más ganan — fija tu stake, replica cada movimiento y mantén tus fondos en una wallet que solo tú controlas.",
  "home.closing.cta.start": "Empezar a copiar",
  "home.closing.cta.markets": "Explorar mercados",
  "home.closing.cta.leaderboard": "Ver el ranking",
  "home.blog.eyebrow": "Del blog",
  "home.blog.title": "Aprende a copiar a los mejores en mercados de predicción",
  "home.blog.cta": "Ver todos los posts",
  "home.blog.readMore": "Leer",
  "home.closing.note": "Gratis para empezar · Non-custodial · Copia con un toque · Sin instalar nada",

  // ── Profile widget (sidebar) ───────────────────────────────────────
  "profile.userFallback": "Usuario",
  "profile.levelInfoAria": "Información de nivel",
  "profile.levelSystem": "Sistema de niveles",
  "profile.levelInfo.lifetimeFp": "FP totales",
  "profile.levelInfo.determinesLevel": "determinan tu nivel",
  "profile.levelInfo.earnFp": "Gana FP operando ($1 = 0,1 FP) y publicando",
  "profile.levelInfo.higherAuraPrefix": "Más",
  "profile.levelInfo.higherAuraSuffix": "= mejor multiplicador de FP",
  "profile.levelInfo.toNextLevel": "{fp} FP para el nivel {level}",
  "profile.levelInfo.maxLevel": "¡Nivel máximo alcanzado!",
  "profile.followers": "Seguidores",
  "profile.following": "Siguiendo",
  "profile.aura": "Aura",
  "profile.fp": "FP",
  "profile.tooltip.followers": "Seguidores: usuarios que te siguen para ver tu actividad y trades compartidos.",
  "profile.tooltip.following": "Siguiendo: usuarios a los que sigues. Ve su actividad en tu feed social.",
  "profile.tooltip.aura": "Aura: tu reputación en la comunidad. Gana Aura con likes en posts y predicciones acertadas. Más Aura = mejor multiplicador de FP.",
  "profile.tooltip.fp": "Fren Points (FP): puntos gastables para Mystery Chests y recompensas. Gana FP operando ($1 = 0,1 FP) y publicando contenido.",
  "profile.addFunds": "Añadir fondos",
  "profile.levelUp.title": "¡SUBES DE NIVEL!",
  "profile.levelUp.subtitle": "Has alcanzado",
  "profile.levelUp.tapToClose": "Pulsa para cerrar",

  // ── For You page ────────────────────────────────────────────────────
  "forYou.title": "Para ti",
  "forYou.subtitle": "Personalizado según tu actividad",
  "forYou.howItWorks": "Cómo funciona Para ti",
  "forYou.trendingEvents": "Eventos en tendencia",
  "forYou.recommended": "Recomendado para ti",
  "forYou.bookmarks": "Tus marcadores",
  "forYou.frensTrading": "Tus frens están operando",
  "forYou.viewAll": "Ver todo →",
  "forYou.refreshing": "Actualizando recomendaciones...",
  "forYou.refreshed": "Recomendaciones actualizadas",
  "forYou.frenBadgeCopy": "COPIA",
  "forYou.frenBadgeTracked": "RASTREADO",
  "forYou.frenBadgeFren": "FREN",
  "forYou.bought": "Compró {outcome}",
  "forYou.sold": "Vendió {outcome}",
  "forYou.unknownMarket": "Mercado desconocido",
  "forYou.unknownOutcome": "Desconocido",
  "forYou.timeNow": "ahora",
  "forYou.timeMinutes": "{n}m",
  "forYou.timeHours": "{n}h",
  "forYou.timeDays": "{n}d",

  // ── Sort pills (markets carousels / lists) ──────────────────────────
  "sort.volume24hr": "Volumen 24h",
  "sort.volume": "Volumen total",
  "sort.liquidity": "Liquidez",
  "sort.newest": "Más recientes",
  "sort.endingSoon": "Cierre próximo",
  "sort.competitive": "Reñidos",

  // ── Breaking page ───────────────────────────────────────────────────
  "breaking.live": "En directo",
  "breaking.short": "Lo más reciente",
  "breaking.title": "Mercados en movimiento",
  "breaking.subtitle": "Los mercados que más se han movido en las últimas 24 horas",
  "breaking.empty": "No hay mercados en movimiento ahora mismo",
  "breaking.subcat.all": "Todos",
  "breaking.subcat.politics": "Política",
  "breaking.subcat.world": "Mundo",
  "breaking.subcat.sports": "Deportes",
  "breaking.subcat.crypto": "Cripto",
  "breaking.subcat.finance": "Finanzas",
  "breaking.subcat.tech": "Tecnología",
  "breaking.subcat.culture": "Cultura",

  // ── Telegram bot modal ──────────────────────────────────────────────
  "tgBot.title": "Opera desde Telegram",
  "tgBot.titleLink": "Vincular Telegram",
  "tgBot.intro": "FrenFlow en movimiento sin salir de Telegram. El mismo saldo, los mismos mercados — desde el chat puedes comprar, copiar y gestionar posiciones en segundos.",
  "tgBot.introLink": "Vincula tu Telegram una sola vez y el bot reconocerá tu cuenta de FrenFlow automáticamente — mismo saldo, mismos mercados, listo para operar en segundos.",
  "tgBot.feature.trade.title": "Opera cualquier mercado",
  "tgBot.feature.trade.body": "Compra y vende en Polymarket — mismo routing, mismos precios.",
  "tgBot.feature.copy.title": "Copia a traders",
  "tgBot.feature.copy.body": "Replica a líderes en automático y recibe un aviso en cada movimiento.",
  "tgBot.feature.manage.title": "Gestiona posiciones",
  "tgBot.feature.manage.body": "Alertas de precio, reclamar ganancias, retiros — desde el chat.",
  "tgBot.feature.wallet.title": "Sin wallet nueva",
  "tgBot.feature.wallet.body": "Misma cuenta que la web. Vincúlala una vez y listo.",
  "tgBot.cta.open": "Abrir en Telegram",
  "tgBot.cta.linkAndOpen": "Vincular Telegram",
  "tgBot.cta.linking": "Vinculando Telegram...",
  "tgBot.linkHint": "Vincularemos tu Telegram una sola vez para que el bot reconozca tu cuenta — sin code de acceso.",
  "tgBot.linkError": "No se pudo vincular Telegram. Inténtalo de nuevo.",
  "tgBot.openError": "No se pudo abrir el flujo de vinculación. Inténtalo de nuevo.",
  "tgBot.close": "Cerrar",

  // ── Public profile page ─────────────────────────────────────────────
  "profile.tabs.wall": "Muro",
  "profile.tabs.achievements": "Logros",
  "profile.tabs.pits": "Pits",
  "profile.pits.emptyTitle": "Aún no está en ningún Pit",
  "profile.pits.emptyDesc": "Los Pits son micro-comunidades públicas para traders. Los pits públicos a los que se una este usuario aparecerán aquí.",
  "profile.pits.explore": "Explorar Pits →",
  "profile.edit.invalidImage": "Selecciona un archivo de imagen",
  "profile.edit.imageTooLarge": "La imagen debe pesar menos de 5MB",
  "profile.edit.coverTooLarge": "La portada debe pesar menos de 10MB",
  "profile.edit.notAuthed": "No autenticado",
  "profile.edit.uploadImageFailed": "No se pudo subir la imagen",
  "profile.edit.setAvatarFailed": "No se pudo establecer el avatar",
  "profile.edit.uploadCoverFailed": "No se pudo subir la portada",
  "profile.edit.socialLinks": "Redes sociales",
  "profile.edit.viewMore": "Ver más",
  "profile.edit.viewLess": "Ver menos",
  "profile.edit.position": "Posición",
  "profile.stats.followers": "Seguidores",
  "profile.stats.following": "Siguiendo",
  "profile.stats.copying": "Copiando",
  "profile.stats.frenCopiers": "Copiadores",
  "profile.stats.points": "Puntos",
  "profile.stats.aura": "Aura",
  "profile.stats.totalVolume": "Volumen total",
  "profile.stats.achievements": "Logros",
  "profile.joined": "Se unió en {date}",
  "profile.actions.follow": "Seguir",
  "profile.actions.following": "Siguiendo",
  "profile.actions.copyTrader": "Copiar trader",
  "profile.actions.copy": "Copiar",
  "profile.actions.copying": "Copiando",
  "profile.actions.stopping": "Parando...",
  "profile.actions.signInToFollow": "Inicia sesión para seguir a usuarios",
  "profile.modal.followers": "Seguidores",
  "profile.modal.following": "Siguiendo",
  "profile.modal.noFollowers": "Aún no tienes seguidores",
  "profile.modal.noFollowing": "Aún no sigues a nadie",
  "profile.edit.button": "Editar perfil",
  "profile.edit.title": "Editar perfil",
  "profile.edit.displayName": "Nombre",
  "profile.edit.displayNamePh": "Tu nombre",
  "profile.edit.username": "Usuario",
  "profile.edit.usernamePh": "usuario",
  "profile.edit.usernameHint": "3-30 caracteres: letras, números y guiones bajos",
  "profile.edit.bio": "Bio",
  "profile.edit.bioPh": "Cuéntanos algo sobre ti...",
  "profile.edit.save": "Guardar cambios",
  "profile.edit.saving": "Guardando...",
  "profile.edit.cancel": "Cancelar",
  "profile.edit.failed": "No se pudo actualizar el perfil",

  // ── Achievements section ────────────────────────────────────────────
  "achievements.title": "Logros",
  "achievements.unlockedOf": "{n} de {total} desbloqueados",
  "achievements.closest": "Más cerca de desbloquear",
  "achievements.almostThere": "Ya casi",
  "achievements.nothingHere": "Aquí no hay nada todavía",
  "achievements.unlocked": "✓ Desbloqueado",
  "achievements.complete": "{p}% completado",
  "achievements.tab.all": "Todos",
  "achievements.tab.unlocked": "Desbloqueados",
  "achievements.tab.progress": "En progreso",
  "achievements.tab.locked": "Bloqueados",
  // Wall (UserTimeline)
  "wall.placeholder": "¿En qué piensas? Pega el link de un mercado o evento para que se muestre la preview.",
  "wall.post": "Publicar",
  "wall.posting": "Publicando...",
  "wall.loadingMore": "Cargando más…",
  "wall.bought": "Comprado",
  "wall.sold": "Vendido",
  "wall.leader": "Líder",
  "wall.copy": "Copia",
  "wall.anonymous": "Anónimo",
  "wall.deletePost": "Eliminar post",
  "wall.sharePost": "Compartir post",
  "wall.removePreview": "Quitar preview",
  "wall.marketPreview": "Preview de mercado",
  "wall.eventMarkets": "Evento · {n} mercados",
  "wall.moreMarkets": "+{n} más",
  "wall.vol": "Vol: {v}",
  "wall.inPit": "en {name}",
  "wall.empty": "Aún no hay posts.",
  "wall.emptyOwner": "Aún no hay posts. ¡Comparte el primero!",
  "wall.yourAvatar": "Tu avatar",
  "common.scrollLeft": "Desplazar a la izquierda",
  "common.scrollRight": "Desplazar a la derecha",

  // Volume badge tooltip
  "volumeBadge.moreToUnlock": "Faltan ${amount} para desbloquear",
  "volumeBadge.desc.og": "Miembro Genesis",
  "volumeBadge.desc.trader": "$1K+ de volumen",
  "volumeBadge.desc.whale": "$10K+ de volumen",
  "volumeBadge.desc.shark": "$50K+ de volumen",
  "volumeBadge.desc.legend": "$100K+ de volumen",

  // Profile sidebar widgets
  "sidebar.trending.title": "Mercados Trending",
  "sidebar.trending.volume": "Volumen 24h",
  "sidebar.trending.empty": "No hay mercados trending",
  "sidebar.trending.vol": "{v} vol",
  "sidebar.suggested.title": "Sugerencias para seguir",
  "sidebar.suggested.empty": "No hay sugerencias disponibles",
  "sidebar.suggested.followers": "{n} seguidores",
  "sidebar.suggested.follower": "{n} seguidor",
  "sidebar.suggested.follow": "Seguir",
  "sidebar.suggested.following": "Siguiendo",
  "sidebar.mostCopied.title": "Más copiados",
  "sidebar.mostCopied.window": "24h",
  "sidebar.mostCopied.empty": "No hay traders",
  "sidebar.mostCopied.copy": "Copiar",

  // Profile sidebar collapse
  "profile.sidebar.hide": "Ocultar barra lateral",
  "profile.sidebar.show": "Mostrar barra lateral",

  "achievements.category.Special": "Especial",
  "achievements.category.Mastery": "Maestría",
  "achievements.category.Onboarding": "Bienvenida",
  "achievements.category.Markets": "Plataformas",
  "achievements.category.Volume": "Volumen",
  "achievements.category.Trade Size": "Tamaño de trade",
  "achievements.category.Milestones": "Hitos",
  "achievements.category.Social": "Social",
  "achievements.category.Copy Trading": "Copytrading",
  "achievements.category.Watchlist": "Watchlist",
  "achievements.category.Influence": "Influencia",
  "achievements.category.Pits": "Pits",
  "achievements.category.Referrals": "Referidos",

  // ── Catálogo de logros (nombre / descripción / requisito) ──────────
  "ach.og.name": "OG FREN",
  "ach.og.desc": "Miembro Genesis — pionero de FrenFlow",
  "ach.og.req": "Tener un OG Pass NFT",
  "ach.hat_trick.name": "Hat Trick",
  "ach.hat_trick.desc": "Vinculaste wallets de Polymarket, Solana y Hyperliquid",
  "ach.hat_trick.req": "Las 3 wallets conectadas",
  "ach.welcome_aboard.name": "Bienvenido",
  "ach.welcome_aboard.desc": "Pon un avatar y una bio",
  "ach.welcome_aboard.req": "Añade avatar + bio",
  "ach.verified_telegram.name": "Vinculado",
  "ach.verified_telegram.desc": "Vinculaste tu cuenta de Telegram",
  "ach.verified_telegram.req": "Vincula Telegram",
  "ach.funded.name": "Operado",
  "ach.funded.desc": "Hiciste tu primer trade",
  "ach.funded.req": "Opera 1 vez",
  "ach.profile_showoff.name": "Perfil con Estilo",
  "ach.profile_showoff.desc": "Pon una imagen de portada en tu perfil",
  "ach.profile_showoff.req": "Añade una portada",
  "ach.tastemaker_like.name": "Tastemaker",
  "ach.tastemaker_like.desc": "Diste tu primer like",
  "ach.tastemaker_like.req": "Da 1 like",
  "ach.conversation_starter.name": "Iniciador de Conversación",
  "ach.conversation_starter.desc": "Escribiste tu primer comentario",
  "ach.conversation_starter.req": "Comenta 1 vez",
  "ach.followed_someone.name": "Seguiste a Alguien",
  "ach.followed_someone.desc": "Seguiste a tu primer usuario",
  "ach.followed_someone.req": "Sigue a 1 usuario",
  "ach.first_watch.name": "Curioso",
  "ach.first_watch.desc": "Añadiste un mercado a tu watchlist",
  "ach.first_watch.req": "Vigila 1 mercado",
  "ach.first_deposit.name": "Primer Depósito",
  "ach.first_deposit.desc": "Hiciste tu primer bridge entre cadenas",
  "ach.first_deposit.req": "Haz 1 bridge",
  "ach.pit_joiner.name": "Te Uniste a un Pit",
  "ach.pit_joiner.desc": "Te uniste a tu primer Pit",
  "ach.pit_joiner.req": "Únete a 1 Pit",
  "ach.shared_frenlink.name": "FrenLink Compartido",
  "ach.shared_frenlink.desc": "Creaste tu primer FrenLink",
  "ach.shared_frenlink.req": "Crea 1 FrenLink",
  "ach.beneficiary.name": "Beneficiario",
  "ach.beneficiary.desc": "Reclamaste el FrenLink de otro",
  "ach.beneficiary.req": "Reclama 1 FrenLink",
  "ach.watcher_25.name": "Vigilante",
  "ach.watcher_25.desc": "Sigues 25 mercados",
  "ach.watcher_25.req": "Vigila 25 mercados",
  "ach.watcher_100.name": "Curador",
  "ach.watcher_100.desc": "Sigues 100 mercados",
  "ach.watcher_100.req": "Vigila 100 mercados",
  "ach.trend_setter_1.name": "Marca Tendencia",
  "ach.trend_setter_1.desc": "Alguien está copiando tus trades",
  "ach.trend_setter_1.req": "1 copiador",
  "ach.trend_setter_5.name": "Imán de Copiadores",
  "ach.trend_setter_5.desc": "5 personas copian tus trades",
  "ach.trend_setter_5.req": "5 copiadores",
  "ach.trend_setter_25.name": "Whale Alpha",
  "ach.trend_setter_25.desc": "25 personas copian tus trades",
  "ach.trend_setter_25.req": "25 copiadores",
  "ach.trend_setter_100.name": "Leyenda del Copy",
  "ach.trend_setter_100.desc": "100 personas copian tus trades",
  "ach.trend_setter_100.req": "100 copiadores",
  "ach.followers_5.name": "Magnético",
  "ach.followers_5.desc": "5 personas siguen tu perfil",
  "ach.followers_5.req": "5 seguidores",
  "ach.followers_25.name": "Favorito del Público",
  "ach.followers_25.desc": "25 personas siguen tu perfil",
  "ach.followers_25.req": "25 seguidores",
  "ach.followers_100.name": "Estrella",
  "ach.followers_100.desc": "100 personas siguen tu perfil",
  "ach.followers_100.req": "100 seguidores",
  "ach.karma_100.name": "Forjador de Karma",
  "ach.karma_100.desc": "Ganaste 100 de karma",
  "ach.karma_100.req": "100 de karma",
  "ach.karma_500.name": "Jefe del Karma",
  "ach.karma_500.desc": "Ganaste 500 de karma",
  "ach.karma_500.req": "500 de karma",
  "ach.karma_1000.name": "Lord del Aura",
  "ach.karma_1000.desc": "Ganaste 1.000 de karma",
  "ach.karma_1000.req": "1.000 de karma",
  "ach.karma_5000.name": "Nivel Dios",
  "ach.karma_5000.desc": "Ganaste 5.000 de karma",
  "ach.karma_5000.req": "5.000 de karma",
  "ach.pit_master_10.name": "Pit Master",
  "ach.pit_master_10.desc": "Tu Pit alcanzó 10 miembros",
  "ach.pit_master_10.req": "10 miembros en tu Pit",
  "ach.pit_master_50.name": "Pit King",
  "ach.pit_master_50.desc": "Tu Pit alcanzó 50 miembros",
  "ach.pit_master_50.req": "50 miembros en tu Pit",
  "ach.pit_master_100.name": "Pit Emperor",
  "ach.pit_master_100.desc": "Tu Pit alcanzó 100 miembros",
  "ach.pit_master_100.req": "100 miembros en tu Pit",
  "ach.liked_10.name": "Apreciado",
  "ach.liked_10.desc": "Recibiste 10 likes en tus posts",
  "ach.liked_10.req": "10 likes recibidos",
  "ach.liked_100.name": "Querido",
  "ach.liked_100.desc": "Recibiste 100 likes en tus posts",
  "ach.liked_100.req": "100 likes recibidos",
  "ach.liked_500.name": "Adorado",
  "ach.liked_500.desc": "Recibiste 500 likes en tus posts",
  "ach.liked_500.req": "500 likes recibidos",
  "ach.hot_post.name": "Post Caliente",
  "ach.hot_post.desc": "Uno de tus posts alcanzó 25 likes",
  "ach.hot_post.req": "25 likes en un solo post",
  "ach.viral_post.name": "Viral",
  "ach.viral_post.desc": "Uno de tus posts alcanzó 100 likes",
  "ach.viral_post.req": "100 likes en un solo post",
  "ach.discussed_10.name": "Comentado",
  "ach.discussed_10.desc": "10 comentarios recibidos en tus posts",
  "ach.discussed_10.req": "10 comentarios recibidos",
  "ach.discussed_50.name": "Debatido",
  "ach.discussed_50.desc": "50 comentarios recibidos en tus posts",
  "ach.discussed_50.req": "50 comentarios recibidos",
  "ach.ach_hunter_15.name": "Cazalogros",
  "ach.ach_hunter_15.desc": "Desbloqueaste 15 logros",
  "ach.ach_hunter_15.req": "Desbloquea 15 logros",
  "ach.ach_hunter_40.name": "Coleccionista",
  "ach.ach_hunter_40.desc": "Desbloqueaste 40 logros",
  "ach.ach_hunter_40.req": "Desbloquea 40 logros",
  "ach.ach_hunter_75.name": "Completista",
  "ach.ach_hunter_75.desc": "Desbloqueaste 75 logros",
  "ach.ach_hunter_75.req": "Desbloquea 75 logros",
  "ach.true_completionist.name": "Completista Total",
  "ach.true_completionist.desc": "Desbloqueaste todos los logros del catálogo",
  "ach.true_completionist.req": "Desbloquea el 100% de logros",
  "ach.market_polymarket.name": "Polymarket",
  "ach.market_polymarket.desc": "Hiciste un trade en Polymarket",
  "ach.market_polymarket.req": "1 trade en Polymarket",
  "ach.market_kalshi.name": "Kalshi",
  "ach.market_kalshi.desc": "Hiciste un trade en Kalshi",
  "ach.market_kalshi.req": "1 trade en Kalshi",
  "ach.market_predictfun.name": "Predict Fun",
  "ach.market_predictfun.desc": "Hiciste un trade en Predict Fun",
  "ach.market_predictfun.req": "1 trade en Predict Fun",
  "ach.market_hyperliquid.name": "Hyperliquid",
  "ach.market_hyperliquid.desc": "Hiciste un trade en Hyperliquid",
  "ach.market_hyperliquid.req": "1 trade en Hyperliquid",
  "ach.omni_trader.name": "Omni Trader",
  "ach.omni_trader.desc": "Operaste en cada plataforma soportada",
  "ach.omni_trader.req": "1 trade en cada plataforma",
  "ach.active_trader_500.name": "Trader Activo",
  "ach.active_trader_500.desc": "Alcanzaste $500 de volumen",
  "ach.active_trader_500.req": "$500+ de volumen",
  "ach.trader_1k.name": "Trader",
  "ach.trader_1k.desc": "Alcanzaste $1.000 de volumen",
  "ach.trader_1k.req": "$1.000+ de volumen",
  "ach.whale_10k.name": "Whale",
  "ach.whale_10k.desc": "Alcanzaste $10.000 de volumen",
  "ach.whale_10k.req": "$10.000+ de volumen",
  "ach.shark_50k.name": "Shark",
  "ach.shark_50k.desc": "Alcanzaste $50.000 de volumen",
  "ach.shark_50k.req": "$50.000+ de volumen",
  "ach.legend_100k.name": "Leyenda",
  "ach.legend_100k.desc": "Alcanzaste $100.000 de volumen",
  "ach.legend_100k.req": "$100.000+ de volumen",
  "ach.mega_shark_250k.name": "Mega Shark",
  "ach.mega_shark_250k.desc": "Alcanzaste $250.000 de volumen",
  "ach.mega_shark_250k.req": "$250.000+ de volumen",
  "ach.mogul_1m.name": "Magnate",
  "ach.mogul_1m.desc": "Alcanzaste $1.000.000 de volumen",
  "ach.mogul_1m.req": "$1M+ de volumen",
  "ach.magnate_5m.name": "Imperio",
  "ach.magnate_5m.desc": "Alcanzaste $5.000.000 de volumen",
  "ach.magnate_5m.req": "$5M+ de volumen",
  "ach.size_50.name": "Trade de $50",
  "ach.size_50.desc": "Hiciste un trade de $50+",
  "ach.size_50.req": "Trade individual ≥ $50",
  "ach.size_100.name": "Trade de $100",
  "ach.size_100.desc": "Hiciste un trade de $100+",
  "ach.size_100.req": "Trade individual ≥ $100",
  "ach.size_200.name": "Trade de $200",
  "ach.size_200.desc": "Hiciste un trade de $200+",
  "ach.size_200.req": "Trade individual ≥ $200",
  "ach.size_500.name": "Trade de $500",
  "ach.size_500.desc": "Hiciste un trade de $500+",
  "ach.size_500.req": "Trade individual ≥ $500",
  "ach.size_1k.name": "Trade de $1K",
  "ach.size_1k.desc": "Hiciste un trade de $1.000+",
  "ach.size_1k.req": "Trade individual ≥ $1.000",
  "ach.size_5k.name": "Trade de $5K",
  "ach.size_5k.desc": "Hiciste un trade de $5.000+",
  "ach.size_5k.req": "Trade individual ≥ $5.000",
  "ach.size_10k.name": "Trade de $10K",
  "ach.size_10k.desc": "Hiciste un trade de $10.000+",
  "ach.size_10k.req": "Trade individual ≥ $10.000",
  "ach.size_25k.name": "Trade de $25K",
  "ach.size_25k.desc": "Hiciste un trade de $25.000+",
  "ach.size_25k.req": "Trade individual ≥ $25.000",
  "ach.first_trade.name": "Primeros Pasos",
  "ach.first_trade.desc": "Completaste tu primer trade",
  "ach.first_trade.req": "Haz 1 trade",
  "ach.10_trades.name": "Empezando",
  "ach.10_trades.desc": "Completaste 10 trades",
  "ach.10_trades.req": "Haz 10 trades",
  "ach.50_trades.name": "Activo",
  "ach.50_trades.desc": "Completaste 50 trades",
  "ach.50_trades.req": "Haz 50 trades",
  "ach.100_trades.name": "Pro Trader",
  "ach.100_trades.desc": "Completaste 100 trades",
  "ach.100_trades.req": "Haz 100 trades",
  "ach.250_trades.name": "Veterano",
  "ach.250_trades.desc": "Completaste 250 trades",
  "ach.250_trades.req": "Haz 250 trades",
  "ach.500_trades.name": "Picapedrero",
  "ach.500_trades.desc": "Completaste 500 trades",
  "ach.500_trades.req": "Haz 500 trades",
  "ach.1000_trades.name": "Máquina",
  "ach.1000_trades.desc": "Completaste 1.000 trades",
  "ach.1000_trades.req": "Haz 1.000 trades",
  "ach.first_post.name": "Voz Escuchada",
  "ach.first_post.desc": "Publicaste tu primer post",
  "ach.first_post.req": "Publica 1 post",
  "ach.10_posts.name": "Colaborador",
  "ach.10_posts.desc": "Publicaste 10 posts",
  "ach.10_posts.req": "Publica 10 posts",
  "ach.50_posts.name": "Influencer",
  "ach.50_posts.desc": "Publicaste 50 posts",
  "ach.50_posts.req": "Publica 50 posts",
  "ach.100_posts.name": "Líder de Opinión",
  "ach.100_posts.desc": "Publicaste 100 posts",
  "ach.100_posts.req": "Publica 100 posts",
  "ach.comments_10.name": "Charlatán",
  "ach.comments_10.desc": "Escribiste 10 comentarios",
  "ach.comments_10.req": "10 comentarios",
  "ach.comments_50.name": "Debatidor",
  "ach.comments_50.desc": "Escribiste 50 comentarios",
  "ach.comments_50.req": "50 comentarios",
  "ach.comments_100.name": "Pregonero",
  "ach.comments_100.desc": "Escribiste 100 comentarios",
  "ach.comments_100.req": "100 comentarios",
  "ach.likes_given_10.name": "Tastemaker Pro",
  "ach.likes_given_10.desc": "Diste 10 likes",
  "ach.likes_given_10.req": "10 likes dados",
  "ach.likes_given_50.name": "Animador",
  "ach.likes_given_50.desc": "Diste 50 likes",
  "ach.likes_given_50.req": "50 likes dados",
  "ach.follows_5.name": "Networker",
  "ach.follows_5.desc": "Sigues a 5 usuarios",
  "ach.follows_5.req": "Sigue a 5 usuarios",
  "ach.follows_25.name": "Curador",
  "ach.follows_25.desc": "Sigues a 25 usuarios",
  "ach.follows_25.req": "Sigue a 25 usuarios",
  "ach.pit_founder.name": "Fundador de Pit",
  "ach.pit_founder.desc": "Creaste tu propio Pit",
  "ach.pit_founder.req": "Crea 1 Pit",
  "ach.pit_hopper.name": "Saltador de Pits",
  "ach.pit_hopper.desc": "Miembro de 5 Pits distintos",
  "ach.pit_hopper.req": "Únete a 5 Pits",
  "ach.first_copy.name": "Copión",
  "ach.first_copy.desc": "Empezaste a copiar a tu primer trader",
  "ach.first_copy.req": "Copia a 1 trader",
  "ach.5_copies.name": "Diversificador",
  "ach.5_copies.desc": "Copiando 5 traders distintos",
  "ach.5_copies.req": "Copia a 5 traders",
  "ach.10_copies.name": "Constructor de Cartera",
  "ach.10_copies.desc": "Copiando 10 traders distintos",
  "ach.10_copies.req": "Copia a 10 traders",
  "ach.25_copies.name": "Cazador de Ballenas",
  "ach.25_copies.desc": "Copiando 25 traders distintos",
  "ach.25_copies.req": "Copia a 25 traders",
  "ach.copytrades_10.name": "Copy Cat",
  "ach.copytrades_10.desc": "10 copy trades ejecutados para ti",
  "ach.copytrades_10.req": "10 copy trades",
  "ach.copytrades_100.name": "Mirror Pro",
  "ach.copytrades_100.desc": "100 copy trades ejecutados para ti",
  "ach.copytrades_100.req": "100 copy trades",
  "ach.copytrades_1000.name": "Piloto Automático",
  "ach.copytrades_1000.desc": "1.000 copy trades ejecutados para ti",
  "ach.copytrades_1000.req": "1.000 copy trades",
  "ach.loyal_copier_30.name": "Copy Fiel",
  "ach.loyal_copier_30.desc": "Copiando al mismo trader 30 días",
  "ach.loyal_copier_30.req": "30 días copiando",
  "ach.devoted_copier_90.name": "Copy Devoto",
  "ach.devoted_copier_90.desc": "Copiando al mismo trader 90 días",
  "ach.devoted_copier_90.req": "90 días copiando",
  "ach.referral_1.name": "Primera Invitación",
  "ach.referral_1.desc": "Invitaste a tu primer fren a FrenFlow",
  "ach.referral_1.req": "Invita a 1 fren",
  "ach.referral_5.name": "Conector",
  "ach.referral_5.desc": "Invitaste a 5 frens a FrenFlow",
  "ach.referral_5.req": "Invita a 5 frens",
  "ach.referral_10.name": "Networker",
  "ach.referral_10.desc": "Invitaste a 10 frens a FrenFlow",
  "ach.referral_10.req": "Invita a 10 frens",
  "ach.referral_25.name": "Reclutador",
  "ach.referral_25.desc": "Invitaste a 25 frens a FrenFlow",
  "ach.referral_25.req": "Invita a 25 frens",
  "ach.referral_50.name": "Embajador",
  "ach.referral_50.desc": "Invitaste a 50 frens a FrenFlow",
  "ach.referral_50.req": "Invita a 50 frens",
  "ach.referral_100.name": "Leyenda",
  "ach.referral_100.desc": "Invitaste a 100 frens a FrenFlow",
  "ach.referral_100.req": "Invita a 100 frens",
  "ach.referral_250.name": "Kingmaker",
  "ach.referral_250.desc": "Invitaste a 250 frens a FrenFlow",
  "ach.referral_250.req": "Invita a 250 frens",
  "ach.referral_500.name": "Mítico",
  "ach.referral_500.desc": "Invitaste a 500 frens a FrenFlow",
  "ach.referral_500.req": "Invita a 500 frens",
  "ach.referral_1000.name": "Movimiento",
  "ach.referral_1000.desc": "Invitaste a 1.000 frens a FrenFlow",
  "ach.referral_1000.req": "Invita a 1.000 frens",

  // ───────── /traders — list, filters, grid, modals ─────────
  // Skeleton + pagination
  "traders.col.trader": "Trader",
  "traders.col.pnl": "PnL",
  "traders.col.volume": "Volumen",
  "traders.col.balance": "Saldo",
  "traders.col.followers": "Seguidores",
  "traders.col.profit": "Beneficio",
  "traders.col.predictions": "Predicciones",
  "traders.col.style": "Estilo",
  "traders.col.chart": "Gráfica",
  "traders.col.roi": "ROI",
  "traders.col.win": "% Acierto",
  "traders.tip.win": "Porcentaje de trades resueltos que cerraron en ganancia",
  "traders.col.edge": "Edge",
  "traders.col.bigWin": "Mejor jugada",
  "traders.col.pf": "PF",
  "traders.col.avgBuy": "Compra media",
  "traders.col.maxDD": "Caída máx",
  "traders.col.posValue": "Valor pos.",
  "traders.col.watch": "Seguir",
  "traders.col.copy": "Copiar",
  "traders.col.x": "X",
  "traders.pager.prev": "Anterior",
  "traders.pager.next": "Siguiente",
  "traders.pager.showing": "Mostrando {range}",
  "traders.pager.showingOfTraders": "Mostrando {range} de {total} traders",

  // Filters
  "traders.filter.all": "Todos",
  "traders.filter.watchlist": "Lista",
  "traders.filter.favorites": "Favoritos",
  "traders.filter.whales": "Ballenas",
  "traders.filter.degens": "Degens",
  "traders.filter.contrarian": "Contrarios",
  "traders.filter.value": "Valor",
  "traders.filter.conviction": "Convicción",
  "traders.filter.marketMakers": "Market Makers",
  "traders.filter.profitable": "Rentables",
  "traders.filter.highRoi": "ROI alto",
  "traders.filter.sharp": "Sharp",
  "traders.filter.hft": "HFT Bots",
  "traders.filter.showHftBots": "Mostrar bots HFT",
  "traders.filter.hideHftBots": "Ocultar bots HFT",
  "traders.filter.xOnly": "Solo",
  "traders.filter.xOnly.tooltip.on": "Mostrando solo traders con X vinculado",
  "traders.filter.xOnly.tooltip.off": "Mostrar solo traders con X vinculado",
  "traders.window.allTime": "Todo",
  "traders.window.1d": "1D",
  "traders.window.7d": "7D",
  "traders.window.30d": "30D",
  "traders.window.kalshi.all": "Todo",
  "traders.window.kalshi.daily": "Diario",
  "traders.window.kalshi.weekly": "Semanal",
  "traders.window.kalshi.monthly": "Mensual",
  "traders.window.kalshi.yearly": "Anual",
  "traders.sort.profit": "Beneficio",
  "traders.sort.label": "Ordenar",
  "traders.sort.quality": "Calidad",
  "traders.sort.volume": "Volumen",
  "traders.sort.predictions": "Predicciones",
  "traders.sort.rank": "Ranking",
  "traders.sort.pnl": "PnL",
  "traders.sort.roi": "ROI",
  "traders.sort.winRate": "Win Rate",
  "traders.sort.profitFactor": "Profit Factor",
  "traders.view.grid": "Vista cuadrícula",
  "traders.view.list": "Vista lista",
  "traders.results.suffix": "traders",

  // Grid empty states
  "traders.empty.watchlist.title": "Aún no tienes favoritos",
  "traders.empty.watchlist.desc": "Marca traders con la estrella para añadirlos a tus favoritos y volver a ellos en un clic.",
  "traders.empty.watchlist.cta": "Explorar traders",
  "traders.empty.profitable.title": "No se encontraron traders rentables",
  "traders.empty.profitable.desc": "Prueba a ajustar los demás filtros o vuelve más tarde.",
  "traders.empty.whales.title": "No hay ballenas aquí",
  "traders.empty.whales.desc": "Las ballenas son traders con saldo de $50K+. Pueden estar en otras categorías de mercado.",
  "traders.empty.highVolume.title": "No hay traders de alto volumen",
  "traders.empty.highVolume.desc": "Los traders de alto volumen tienen $100K+ en volumen total. Prueba a quitar algún filtro.",
  "traders.empty.default.title": "No se encontraron traders",
  "traders.empty.default.desc": "Prueba a ajustar tu búsqueda o filtros para encontrar lo que buscas.",
  "traders.empty.clearFilters": "Limpiar filtros",

  // Grid tooltips
  "traders.tip.pnl": "Ganancia o pérdida total (realizada + no realizada)",
  "traders.tip.roi": "Retorno sobre inversión — beneficio / volumen operado",
  "traders.tip.edge": "Edge de entrada — negativo = compras por debajo del promedio del mercado (bueno)",
  "traders.tip.pf": "Profit factor — ganancias brutas / pérdidas brutas. >1.05 = rentable, 1.0-1.05 = empate (rojo), <1.0 = pierde dinero (rojo).",
  "traders.tip.avgBuy": "Precio medio de compra en todas las posiciones (0-100¢)",
  "traders.tip.posValue": "Valor actual de todas las posiciones abiertas",
  "traders.tip.watchAdd": "Añadir a la lista",
  "traders.tip.watchRemove": "Quitar de la lista",
  "traders.tip.favAdd": "Añadir a favoritos",
  "traders.tip.favRemove": "Quitar de favoritos",
  "traders.tip.copyTable": "Copiar la estrategia de este trader",
  "traders.tip.copyMobile": "Copiar trader",
  "traders.tip.copyWallet": "Copiar dirección de wallet",
  "traders.tip.toggleWatchlist": "Alternar lista",
  "traders.tip.compareInList": "En comparación",
  "traders.tip.compareAdd": "Añadir a comparar",
  "traders.tip.compareLimit": "Límite de comparación alcanzado",
  "traders.tip.xOnHandle": "@{handle} en X",

  // Card stats labels
  "traders.card.profit": "Beneficio",
  "traders.card.rank": "Ranking",
  "traders.card.volume": "Volumen",
  "traders.card.copyNow": "Copiar ahora",
  "traders.card.followers": "Seguidores",
  "traders.card.predictions": "Predicciones",
  "traders.card.winRate": "Win rate",
  "traders.card.trades": "Trades",
  "traders.card.roi": "ROI",
  "traders.card.pnl": "PnL",
  "traders.card.edge": "Edge",
  "traders.card.pf": "PF",
  "traders.card.posValue": "Valor pos.",
  "traders.card.maxDrawdown": "Caída máx.",
  "traders.card.beFirst": "Sé el primero",
  "traders.card.tradesSuffix": "trades",
  "traders.hftBadge": "BOT HFT",
  "traders.hftBadgeTooltip": "Bot de trading de alta frecuencia",
  "traders.hftBadgeTooltipWithRate": "Bot de trading de alta frecuencia — {rate} trades/hora",
  "traders.badge.high": "ALTO",
  "traders.badge.consistent": "CONSISTENTE",
  "traders.badge.whale": "Ballena",
  "traders.badge.shark": "Tiburón",
  "traders.badge.dolphin": "Delfín",
  "traders.badge.fish": "Pez",
  "traders.badge.shrimp": "Gamba",

  // AdvancedFiltersDrawer
  "traders.adv.title": "Filtros avanzados",
  "traders.adv.activeBadge": "{count} activos",
  "traders.adv.reset": "Reset",
  "traders.adv.apply": "Aplicar",
  "traders.adv.style": "Estilo de trading",
  "traders.adv.window": "Ventana temporal",
  "traders.adv.metrics": "Rangos de métricas",
  "traders.adv.range.pnl": "PnL ($)",
  "traders.adv.range.volume": "Volumen ($)",
  "traders.adv.range.trades": "Trades",
  "traders.adv.range.roi": "ROI (decimal)",
  "traders.adv.range.pf": "Profit Factor",
  "traders.adv.range.winRate": "Win Rate (0-1)",
  "traders.adv.range.avgTrade": "Trade medio ($)",
  "traders.adv.range.walletAge": "Antigüedad wallet (días)",
  "traders.adv.range.entryEdge": "Edge de entrada",
  "traders.adv.window.allTime": "Todo el tiempo",
  "traders.adv.window.30d": "30 días",
  "traders.adv.window.7d": "7 días",
  "traders.adv.window.24h": "24 horas",
  "traders.adv.range.min": "Mín",
  "traders.adv.range.max": "Máx",
  "traders.adv.filters": "Filtros",

  // CopyTraderModal — header
  "traders.copy.editTitle": "Editar configuración",
  "traders.copy.copyTitle": "Copiar a @{username}",
  "traders.copy.subtitle": "Configura tu estrategia de copia",

  // CopyTraderModal — validation
  "traders.copy.err.invalidAmount": "Introduce un importe válido",
  "traders.copy.err.maxAmount": "El importe máximo es $10.000",
  "traders.copy.err.pctMin": "El porcentaje debe ser al menos 1%",
  "traders.copy.err.pctMax": "El porcentaje no puede superar el 100%",
  "traders.copy.err.balanceBelowMin": "Con tu balance de ${balance}, ${copy} por copia está por debajo del mínimo de $1. Deposita o sube el %.",
  "traders.copy.err.maxStake": "El máximo es $10.000",
  "traders.copy.err.sellPctMin": "Debe ser al menos 1%",
  "traders.copy.err.sellPctMax": "No puede superar el 100%",
  "traders.copy.err.amountGtZero": "El importe debe ser mayor que 0",
  "traders.copy.err.entriesMin": "Debe ser al menos 1",
  "traders.copy.err.slippageRange": "El slippage debe estar entre 0,1% y 50%",
  "traders.copy.err.dailyMin": "El límite diario debe ser al menos $1",
  "traders.copy.err.positiveNumber": "Debe ser un número positivo",
  "traders.copy.err.positivePct": "Debe ser un porcentaje positivo",
  "traders.copy.err.notLoggedIn": "Necesitas iniciar sesión antes de copiar",

  // CopyTraderModal — loading / success
  "traders.copy.loading.save": "Guardando cambios...",
  "traders.copy.loading.start": "Iniciando copia...",
  "traders.copy.success.editTitle": "¡Configuración actualizada!",
  "traders.copy.success.startTitle": "¡Ya estás copiando!",
  "traders.copy.success.editMsg": "Tu configuración de copia se ha actualizado",
  "traders.copy.success.startMsg": "Cada trade que haga @{username} se copiará",

  // CopyTraderModal — copy mode
  "traders.copy.mode.title": "Modo de copia",
  "traders.copy.mode.fixed": "Fijo",
  "traders.copy.mode.fixedDesc": "Mismo $ en cada trade",
  "traders.copy.mode.percentage": "Saldo",
  "traders.copy.mode.percentageDesc": "Escala contigo",
  "traders.copy.mode.percentageLegacy": "% Leader",
  "traders.copy.mode.percentageLegacyDesc": "% del trade del leader",
  "traders.copy.mode.mirror": "Mirror",
  "traders.copy.mode.mirrorDesc": "Mismo % de riesgo",
  "traders.copy.mode.betaBadge": "Beta",
  "traders.copy.mode.comingSoon": "Pronto",
  "traders.copy.mode.tooltip": "Cómo se calcula el tamaño de tu copia. Pasa el cursor por cada opción para los detalles.",
  "traders.copy.mode.fixedTooltip": "Cantidad fija por trade. Tú pones el valor en $ (ej $5) y cada copia usa exactamente eso, sin importar el tamaño del trade del leader ni tu balance.",
  "traders.copy.mode.percentageTooltip": "Porcentaje de tu balance. El tamaño de la copia escala con tu pUSD disponible. Ejemplo: 10% con balance $50 = $5 por copia.",
  "traders.copy.mode.percentageLegacyTooltip": "Legacy: porcentaje del tamaño del trade del leader. Ejemplo: 5% de un trade de $200 = $10. Cambia a Balance para que escale con tu propio balance.",
  "traders.copy.mode.mirrorTooltip": "Espeja el sizing relativo del leader. Si apuesta 5% de su balance, tú apuestas 5% del tuyo. Cap por defecto 10% de tu balance por trade.",
  "traders.copy.mode.legacyNotice": "Estás en el modo antiguo: el 5% se aplica al trade del leader — si el leader mueve $20, tú copias solo $1. Cambia para que cada copia se calcule sobre tu saldo.",
  "traders.copy.mode.legacySwitch": "Cambiar a % de mi saldo",
  "traders.copy.mode.mirrorBanner.title": "Mirror replica el sizing relativo del leader",
  "traders.copy.mode.mirrorBanner.body": "Si el leader pone el 5% de su balance en un trade, tú pones el 5% del tuyo.\nSi pone el 30% (alta convicción), tú pones el 30%.\n\nTope automático: 10% de tu balance por trade (ajustable 5–25%).\nEl sistema pausa el copy si pierdes el 20% acumulado con este leader.\n\nRecomendado solo para leaders con historial probado de gestión de riesgo.",
  "traders.copy.mode.mirrorBanner.checkbox": "He leído y entiendo el riesgo. Mi tope automático me protege al 10% por trade por defecto.",
  "traders.copy.mode.mirrorBanner.required": "Confirma el aviso de riesgo de Mirror para continuar.",
  "traders.copy.mode.mirrorCapLabel": "Tope por trade",
  "traders.copy.mode.mirrorCapTip": "Porcentaje máximo de tu balance que Mirror puede desplegar en una copia. Slider 5–25%, default 10%.",
  "traders.copy.mode.mirrorCapAdvancedMarker": "avanzado",
  "traders.copy.mode.mirrorCapAdvancedWarning": "Por encima del 15%, una sola racha de pérdidas puede llevarse el 40%+ de tu balance. ¿Continuar?",
  "traders.copy.mode.mirrorCapPreview": "Con tu balance de ${balance}, máximo ${cap} por trade. Tras 3 pérdidas consecutivas = -{drawdown}% de drawdown.",
  "traders.copy.mode.mirrorCapPreviewNoBalance": "El tope se activará cuando deposites pUSD en la wallet.",
  "traders.copy.mode.mirrorCapInvalid": "El tope debe estar entre 5% y 25%.",
  "traders.copy.mode.mirrorAutoBumpLabel": "Subir a $1 cuando el tope deje la copia por debajo del mínimo de Polymarket",
  "traders.copy.mode.mirrorAutoBumpDesc": "Solo dispara si la copia calculada es ≥ $0.50 Y el leader puso ≥ 2% de su balance en el trade. Desactivado por defecto para preservar el sizing relativo de Mirror.",
  "traders.copy.mode.mirrorPrecheck.title": "Comprobación de sizing",
  "traders.copy.mode.mirrorPrecheck.dismiss": "Ocultar",
  "traders.copy.mode.mirrorPrecheck.row.leaderBalance": "Balance del leader",
  "traders.copy.mode.mirrorPrecheck.row.medianTrade": "Trade típico del leader",
  "traders.copy.mode.mirrorPrecheck.row.yourMatch": "Tu copia equivalente",
  "traders.copy.mode.mirrorPrecheck.row.polyMin": "Mínimo Polymarket",
  "traders.copy.mode.mirrorPrecheck.row.minFire": "Trade mínimo para disparar",
  "traders.copy.mode.mirrorPrecheck.row.capture": "Captura estimada",
  "traders.copy.mode.mirrorPrecheck.belowThreshold.tag": "bajo el umbral de {threshold}",
  "traders.copy.mode.mirrorPrecheck.belowThreshold.verdict": "Mirror NO copiará ningún trade de este leader (umbral mínimo {threshold}).",
  "traders.copy.mode.mirrorPrecheck.smallTrades.verdict": "Las copias bajo $1 (mínimo Polymarket) se saltarán.",
  "traders.copy.mode.mirrorPrecheck.smallTradesBump.verdict": "Con auto-bump activo, las copias en [$0.50, $1) se suben a $1 si el leader puso ≥2% de su balance.",
  "traders.copy.mode.mirrorPrecheck.recommend.short": "Deposita al menos {recommended} para capturar el ~80%.",
  "traders.copy.mode.mirrorPrecheck.alternative": "Usar % Saldo en su lugar",
  "traders.copy.mode.mirrorForecast.label": "Captura estimada",
  "traders.copy.mode.mirrorForecast.copiesPerHour": "~{copies} copias/h",
  "traders.copy.mode.mirrorForecast.avgCopy": "media {avg}",
  "traders.copy.mode.mirrorForecast.medianTrade": "Trade típico del leader {trade}",
  "traders.copy.mode.mirrorForecast.noData": "Aún no hay suficiente histórico del leader",
  "traders.copy.mode.mirrorForecast.belowThresholdShort": "Leader con balance bajo {threshold}. No se copiará ningún trade.",
  "traders.copy.mode.mirrorRisk.short": "Mirror copia el % de riesgo del leader. Tu tope del {cap} te protege.",
  "traders.copy.mode.mirrorRisk.expand": "Cómo funciona",
  "traders.copy.mode.mirrorRisk.showLess": "Ver menos",
  "traders.copy.mode.mirrorRisk.ack": "Entiendo el riesgo",
  "traders.copy.mode.mirrorCapInline": "Máx {cap}/trade · -{drawdown}% tras 3 pérdidas",
  "traders.copy.mode.mirrorAutoBumpShort": "Subir copias pequeñas a $1",
  "traders.copy.amount.fixed": "Importe por trade",
  "traders.copy.amount.percentage": "Stake",
  "traders.copy.amount.percentageLegacy": "Stake",
  "traders.copy.amount.fixedBumpWarn": "⚠ Se ejecutará a ~$1.10 por el mínimo de $1 de Polymarket.",
  "traders.copy.amount.autoBumpLabel": "Auto-financiar trades por debajo de $1",
  "traders.copy.amount.autoBumpDescOn": "Las copias <$1 suben a $1 para ejecutarse.",
  "traders.copy.amount.autoBumpDescOff": "⚠️ Las copias <$1 se omiten.",
  "traders.copy.amount.autoBumpBadgeActive": "Activo",
  "traders.copy.amount.autoBumpBadgeOff": "Off",
  "traders.copy.preset.title": "Estilo de ejecución",
  "traders.copy.preset.tooltip": "Qué tan agresivo persigues el precio del leader. Pasa el cursor por la i de cada opción para ver los detalles.",
  "traders.copy.preset.safe": "Safe",
  "traders.copy.preset.safeSub": "estricto",
  "traders.copy.preset.safeTooltip": "Cancela copias cuando el precio se ha movido en tu contra. Perderás algunas pero nunca pagarás mucho más que el leader.",
  "traders.copy.preset.normal": "Normal",
  "traders.copy.preset.normalSub": "default",
  "traders.copy.preset.normalTooltip": "La mayoría de copias se ejecutan. Solo cancela cuando el precio se ha movido bastante en contra. Recomendado para casi todos.",
  "traders.copy.preset.aggressive": "Aggressive",
  "traders.copy.preset.aggressiveSub": "amplio",
  "traders.copy.preset.aggressiveTooltip": "Casi todas las copias se ejecutan, incluso cuando el mercado se ha movido. Puede que termines pagando bastante más que el leader en trades volátiles.",
  "traders.copy.preset.migratedBanner": "Sustituimos los controles antiguos de slippage por Estilo de ejecución. Según tu configuración anterior elegimos {{preset}} — revísalo y dale a Guardar para confirmar.",
  // ── Onboarding wizard (CopyOnboardingWizard.tsx). 3-step flow on
  // first follow. Strings short porque el modal tiene 512px de ancho.
  "traders.copy.wizard.stepCounter": "Paso {n}",
  "traders.copy.wizard.stepOf": "de {total}",
  "traders.copy.wizard.step1.title": "Cantidad",
  "traders.copy.wizard.step1.subtitle": "¿Cuánto quieres poner en cada copia?",
  "traders.copy.wizard.step2.title": "Estilo",
  "traders.copy.wizard.step2.subtitle": "Qué tan agresivo persigues el precio del leader",
  "traders.copy.wizard.step3.title": "Confirmar",
  "traders.copy.wizard.step3.subtitle": "Revisa y empieza a copiar",
  "traders.copy.wizard.copyMode": "Modo de copia",
  "traders.copy.wizard.executionMode": "Modo de ejecución",
  "traders.copy.wizard.fixed": "Cantidad fija",
  "traders.copy.wizard.balance": "Balance",
  "traders.copy.wizard.fixedDesc": "Misma cantidad en cada copia",
  "traders.copy.wizard.balanceDesc": "Escala con tu balance",
  "traders.copy.wizard.recommended": "Recomendado",
  "traders.copy.wizard.amountLabel": "Cantidad por copia",
  "traders.copy.wizard.percentLabel": "% del balance por copia",
  "traders.copy.wizard.helperFixed": "Cada copia será exactamente ${amount}",
  "traders.copy.wizard.helperBalance": "Cada copia usa el {amount}% de tu balance actual",
  "traders.copy.wizard.helperMirror": "Cada copia iguala la convicción del leader, nunca por encima del {amount}% de tu balance",
  "traders.copy.wizard.exampleBalance": "Ejemplo: con un balance de $100 al {pct}%, cada copia es ${value}.",
  "traders.copy.wizard.leaderBuys": "El leader compra ${amount}",
  "traders.copy.wizard.youCopy": "→ tú copias ${amount}",
  "traders.copy.wizard.balancePreview": "Tu balance: ${balance} · suficiente para ~{count} copias",
  "traders.copy.wizard.portfolio": "portfolio",
  "traders.copy.wizard.preset.safe": "Seguro",
  "traders.copy.wizard.preset.normal": "Normal",
  "traders.copy.wizard.preset.aggressive": "Agresivo",
  "traders.copy.wizard.preset.safeTip": "Cancela copias cuando el precio se ha movido en contra. Menos fills, nunca pagas de más.",
  "traders.copy.wizard.preset.normalTip": "La mayoría de copias se ejecutan. Solo cancela cuando el precio se ha movido mucho en contra.",
  "traders.copy.wizard.preset.aggressiveTip": "Casi todas las copias se ejecutan. Puedes pagar bastante más en trades volátiles.",
  "traders.copy.wizard.table.preset": "Modo",
  "traders.copy.wizard.table.maxLoss": "Pérdida máx",
  "traders.copy.wizard.table.maxPrice": "Precio máx",
  "traders.copy.wizard.table.fills": "Fills",
  "traders.copy.wizard.table.noCap": "sin tope",
  "traders.copy.wizard.table.leader": "del leader",
  "traders.copy.wizard.table.protection": "Protección de precio",
  "traders.copy.wizard.table.protectStrict": "Estricta",
  "traders.copy.wizard.table.protectBalanced": "Equilibrada",
  "traders.copy.wizard.table.protectOff": "Sin límite",
  "traders.copy.wizard.summary.copyMode": "Modo de copia",
  "traders.copy.wizard.summary.perCopy": "Por copia",
  "traders.copy.wizard.summary.style": "Estilo",
  "traders.copy.wizard.summary.autoBump": "Auto bump <$1",
  "traders.copy.wizard.on": "Activo",
  "traders.copy.wizard.off": "Off",
  "traders.copy.wizard.next.title": "Qué pasa ahora",
  "traders.copy.wizard.next.b1": "Vigilamos a {handle} 24/7",
  "traders.copy.wizard.next.b2": "Las copias se ejecutan automáticamente en segundos",
  "traders.copy.wizard.next.b3": "Pausa o cambia ajustes cuando quieras",
  "traders.copy.wizard.footerLock": "Los fondos quedan en tu wallet — nunca los tenemos nosotros.",
  "traders.copy.wizard.back": "Atrás",
  "traders.copy.wizard.continue": "Continuar",
  "traders.copy.wizard.startCopying": "Empezar a copiar",
  "traders.copy.wizard.starting": "Iniciando…",
  "traders.copy.wizard.useAdvanced": "Usar configuración avanzada",
  "traders.copy.wizard.thisTrader": "este trader",
  "traders.copy.wizard.traderFallback": "trader",
  "traders.copy.wizard.done.title": "Copia iniciada",
  "traders.copy.wizard.done.desc": "Tu follow está activo. El próximo trade de este leader se copiará automáticamente.",
  "traders.copy.wizard.failedCopy": "No se pudo iniciar la copia",
  "traders.copy.wizard.invalidAmount": "Introduce una cantidad válida",
  "traders.copy.wizard.hftTitle": "Este trader hace ~{tph} trades/hora",
  "traders.copy.wizard.hftTitleGeneric": "Este trader es un bot de alta frecuencia",
  "traders.copy.wizard.hftDesc": "Los bots HFT disparan cientos de operaciones diminutas — las comisiones y el slippage se acumulan rápido y la mayoría de copiadores acaba perdiendo. Puedes pausar en Copy Settings cuando quieras.",
  "traders.copy.wizard.hftAck": "Entiendo que es un bot de alta frecuencia y que la mayoría de copiadores pierde dinero.",
  "traders.copy.painFloorChipLabel": "Pérdida tolerada",
  "traders.copy.painFloorChipScope": "todos los leaders",
  "traders.copy.painFloorChipEdit": "Editar en Ajustes",
  "traders.copy.amount.autoPennyLabel": "Auto-ajustar slippage",
  "traders.copy.amount.autoPennyDescOn": "Los mercados penny (<$0.15) tienen un cap más flexible.",
  "traders.copy.amount.autoPennyDescOff": "⚠️ Mismo cap en todos los mercados (más skips en penny).",
  "traders.copy.amount.autoPennyTooltip": "En mercados de bajo precio (<$0.15), el límite de slippage se relaja automáticamente para que un movimiento pequeño de precio (unos céntimos) no cancele cada copia. Desmarca para mantener el mismo límite estricto en todos los mercados.",
  "traders.copy.amount.pennyCapLabel": "Ajustar hasta",
  "traders.copy.amount.pennyCapTooltip": "Cuánto puede subir tu copia por encima del precio del leader en mercados penny (<$0.15). 100% = tolerancia normal (default). 500% = permite pagar hasta 6× el precio del leader. Solo aplica si Auto-ajustar está ON.",
  "traders.copy.amount.pennyCapUnlimited": "Sin límite",
  "traders.copy.amount.pennyCapWarnConfirm": "Estás permitiendo que tu copia entre hasta un {pct}% por encima del precio del leader en mercados penny — aproximadamente {mult}× el precio del leader.\n\nSi el outcome resuelve a $0 puedes perder casi todo el capital del trade. Esto NO mejora PnL automáticamente — solo úsalo si copias un leader cuyo edge persiste tras el move del precio (ej: bots de arbitraje pre-resolución).\n\n¿Confirmas?",
  "traders.copy.amount.pennyCapWarnInline": "Pagas hasta un {pct}% sobre el leader (~{mult}× su precio). Solo merece la pena con leaders tipo arbitraje — si no, espera drawdowns más grandes.",
  "traders.copy.amount.pennyCapNoCapLabel": "Sin límite — fillea a cualquier precio (power-user)",
  "traders.copy.amount.pennyCapNoCapDesc": "Tu copia entra al precio que ofrezca el mercado en mercados de bajo precio — sin protección de slippage. Como mucho pierdes el Max/trade ($) por copia. Solo merece la pena con leaders tipo arbitraje.",
  "traders.copy.amount.pennyCapNoCapConfirm": "¿Activar modo Sin límite?\n\nTu copia se llenará a CUALQUIER precio que ofrezca el mercado en mercados <$0.15. Ejemplo: leader compra a $0.015 → tu copia podría fillear a $0.50 o más.\n\nPérdida máx. por copia = tu valor Max/trade ($). Esto es modo power-user — úsalo solo si entiendes que el edge del leader persiste tras el move del precio.\n\n¿Confirmas?",
  "ct.pennySlip.bannerCapTitle": "Penny slippage agresivo activo en {n} leader(s)",
  "ct.pennySlip.bannerUnlimitedTitle": "Penny slippage sin cap activo en {n} leader(s)",
  "ct.pennySlip.entryUnlimited": "sin cap",
  "ct.unlimitedBadge.tooltip": "Slippage sin tope\n\nEste leader copia a cualquier precio en mercados de bajo precio (<$0.15).",
  "traders.curated.recommended": "Recomendados",
  "traders.curated.trending": "Trending",
  "traders.curated.mostCopied": "Más copiados",
  "traders.curated.whales": "Whales",
  "traders.curated.lowRisk": "Constantes",
  "traders.curated.hft": "HFT Bots",
  "traders.curated.hftHint": "Cientos de trades/hora. Muchas copias se saltarán — quema capital rápido.",
  "traders.curated.new": "Nuevos",
  "traders.curated.heading": "Descubre traders",
  "traders.copy.amount.pennyCapHintBody": "Últimos 7d: {n} copias en penny markets fallaron por slippage. Subir el cap a {pct}% habría capturado ~75% de ellas.",
  "traders.copy.amount.pennyCapHintApply": "Aplicar {pct}%",
  "traders.copy.amount.autoBumpTooltip": "Polymarket rechaza trades menores de $1. Cuando tu % daría una copia más pequeña, la subimos hasta $1 para que se ejecute igual. A cambio gastarás un poco más que tu % en esos trades pequeños.",
  "traders.copy.amount.fixedTip": "El importe exacto en dólares que invertirás cada vez que el trader abra una posición.",
  "traders.copy.amount.percentageTip": "Porcentaje de tu saldo disponible que se invierte en cada copia — el tamaño del trade del leader da igual.",
  "traders.copy.amount.percentageLegacyTip": "Porcentaje del tamaño del trade del leader — trades pequeños del leader producen copias pequeñas (un trade de $20 × 5% = $1).",
  "traders.copy.maxStake": "Máx por trade",
  "traders.copy.maxStakeTip": "Límite de seguridad: importe máximo en cualquier trade individual, sea cual sea el modo.",

  // DepositWrapBanner — USDC.e/native → pUSD wrap (wave 16/17)
  "wallet.wrapBanner.body": "en tu wallet de trading no está listo para tradear. Conviértelo a pUSD para que copytrading pueda usarlo.",
  "wallet.wrapBanner.autoWrapInfo": "A partir de ahora, los pagos de Polymarket se convertirán automáticamente a pUSD sin que tengas que hacer nada.",
  "wallet.wrapBanner.cta": "Convertir todo a pUSD →",
  "wallet.wrapBanner.wrapping": "Convirtiendo...",
  "wallet.wrapBanner.successWithAutoWrap": "Convertido a pUSD ✓ Desde ahora, los pagos de Polymarket se auto-convertirán. Copytrading se reanuda en la próxima señal.",
  "wallet.wrapBanner.partialSuccess": "Convertidos {done} de {total}. Revisa Recent Trades.",

  "traders.copy.minFloor.fixedTooLow.title": "No se ejecutará ningún trade.",
  "traders.copy.minFloor.fixedTooLow.body": "El tamaño mínimo de orden es $1. Sube tu stake a $1 o más para que el trade se ejecute.",
  "traders.copy.minFloor.percentage.title": "Tamaño mínimo de orden: $1.",
  "traders.copy.minFloor.percentage.bodyWithCap": "Con {pct}%, trades del leader menores de ${threshold} calcularían menos de $1 y se skipean (motivo visible en Recent Trades). Tu cap (${cap}) marca el techo.",
  "traders.copy.minFloor.percentage.bodyNoCap": "Con {pct}%, trades del leader menores de ${threshold} calcularían menos de $1 y se skipean (motivo visible en Recent Trades).",
  "traders.copy.minFloor.fixedOk": "Por encima del mínimo de $1 por orden.",
  "traders.copy.err.belowPolyMin": "Mínimo $1 por orden.",

  "traders.copy.placeholder.off": "Off",

  "traders.copy.amount.previewBalance": "Con tu balance de ${balance}, copiarías ~${copy} por trade del leader.",
  "traders.copy.amount.previewBelowMin": "Con tu balance de ${balance}, ${copy} por copia está por debajo del mínimo de $1.",
  "traders.copy.amount.previewBelowMinAction": "Deposita o sube el % para copiar a este trader.",
  "traders.copy.amount.previewBelowMinRaisePct": "Sube el % a al menos {minPct}% para copiar a este trader.",
  "traders.copy.amount.previewBelowMinTopUp": "Deposita en tu wallet para copiar a este trader (mínimo $1 de saldo).",
  "traders.copy.amount.previewBumpActive": "${balance} × {pct}% = ${copy} → auto-ajustado a $1. Los trades se ejecutarán al mínimo de la CLOB.",
  "traders.copy.amount.previewNoBalance": "Deposita en tu wallet para ver el tamaño de cada copia.",

  // CopyTraderModal — sell mode
  "traders.copy.sell.title": "Cuando el trader vende",
  "traders.copy.sell.tooltip": "Qué pasa cuando el trader al que copias vende su posición.",
  "traders.copy.sell.mirror": "Mirror %",
  "traders.copy.sell.mirrorDesc": "Mismo % que el trader",
  "traders.copy.sell.all": "Vender todo",
  "traders.copy.sell.allDesc": "Cerrar la posición entera",
  "traders.copy.sell.fixed": "Fijo $",
  "traders.copy.sell.fixedDesc": "Vender importe fijo",
  "traders.copy.sell.custom": "% personalizado",
  "traders.copy.sell.customDesc": "Define tu propio %",
  "traders.copy.sell.manual": "Manual",
  "traders.copy.sell.manualDesc": "- Nunca vender automático, tú controlas las salidas",
  "traders.copy.sell.amountFixed": "Importe a vender ($)",
  "traders.copy.sell.amountPct": "Porcentaje a vender (%)",

  // CopyTraderModal — entry mode
  "traders.copy.entry.title": "Entradas por mercado",
  "traders.copy.entry.tooltip": "Cuántas veces copiar entradas en el mismo mercado. Evita sobreexposición.",
  "traders.copy.entry.all": "Todas",
  "traders.copy.entry.allDesc": "Cada entrada",
  "traders.copy.entry.first": "Primera",
  "traders.copy.entry.firstDesc": "1 por mercado",
  "traders.copy.entry.limit": "Límite",
  "traders.copy.entry.limitDesc": "Define el máximo",

  // CopyTraderModal — collapsibles
  "traders.copy.advanced": "Ajustes avanzados",
  "traders.copy.risk": "Ajustes de riesgo",
  "traders.copy.customBadge": "Editado",

  // CopyTraderModal — risk: limits
  "traders.copy.risk.limits": "Límites",
  "traders.copy.risk.dailyVolume": "Volumen diario $",
  "traders.copy.risk.dailyVolumeTip": "Volumen máximo total que puedes copiar al día. Pon 0 para ilimitado.",
  "traders.copy.risk.maxPositions": "Posiciones máx",
  "traders.copy.risk.maxPositionsTip": "Número máximo de posiciones abiertas a la vez. Pon 0 para ilimitado.",
  "traders.copy.risk.budgetCap": "Presupuesto máx $",
  "traders.copy.risk.budgetCapTip": "Tope del capital que has puesto (coste) en todas las posiciones abiertas. Una posición que vale 4x sigue contando por lo que pagaste — esto es un tope de dinero invertido, no de exposición actual. Los trades nuevos se omiten al alcanzarlo.",

  // CopyTraderModal — risk: trade size
  "traders.copy.risk.tradeSize": "Filtro de BUY del leader",
  "traders.copy.risk.minTrade": "BUY mín del leader $",
  "traders.copy.risk.minTradeTip": "Omite BUYs cuando el notional del leader es menor a este valor. Útil para ignorar el ruido. Los SELLs siempre se procesan.",
  "traders.copy.risk.maxTrade": "BUY máx del leader $",
  "traders.copy.risk.maxTradeTip": "Omite BUYs cuando el notional del leader supera este valor. Útil cuando el leader va muy fuerte en una sola jugada. Los SELLs siempre se procesan.",

  // CopyTraderModal — risk: market quality (mig 254)
  "traders.copy.risk.marketQuality": "Filtro de calidad del market",
  "traders.copy.risk.minOdds": "Odds mín %",
  "traders.copy.risk.minOddsTip": "Omite BUYs cuando el precio actual del market está por debajo de este porcentaje. Ej: 5 omite apuestas <5¢ (muy out-of-the-money). Los SELLs siempre se procesan.",
  "traders.copy.risk.maxOdds": "Odds máx %",
  "traders.copy.risk.maxOddsTip": "Omite BUYs cuando el precio actual del market está por encima de este porcentaje. Ej: 95 omite markets casi resueltos (>95¢). Los SELLs siempre se procesan.",
  "traders.copy.risk.minLiquidity": "Liquidez mín $",
  "traders.copy.risk.minLiquidityTip": "Omite BUYs en markets con orderbook fino (liquidez de Polymarket por debajo de este importe). Ayuda a no comerse spread en markets pequeños.",
  "traders.copy.risk.minVolume24h": "Volumen 24h mín $",
  "traders.copy.risk.minVolume24hTip": "Omite BUYs en markets con poca actividad reciente (volumen 24h de Polymarket por debajo de este importe). Filtra markets abandonados o pre-launch.",
  "traders.copy.risk.maxDaysExpiry": "Días máx hasta vencimiento",
  "traders.copy.risk.maxDaysExpiryTip": "Omite BUYs en markets que se resuelven más allá de este número de días. Evita capital atado en posiciones de muy larga duración.",
  "traders.copy.risk.daysSuffix": "d",

  // CopyTraderModal — risk: exposure
  "traders.copy.risk.exposure": "Exposición",
  "traders.copy.risk.perEvent": "Por evento $",
  "traders.copy.risk.perEventTip": "Tope del coste total entre todos los mercados del mismo evento. (Coste, no valor actual — una posición ganadora cuenta por lo que pagaste.)",
  "traders.copy.risk.maxEvents": "Eventos máx",
  "traders.copy.risk.maxEventsTip": "Tope al número de eventos distintos en los que estás a la vez. Mantiene la exposición enfocada cuando el leader opera muchos mercados.",

  // CopyTraderModal — risk: auto-pause
  "traders.copy.risk.autoPause": "Auto-pausa",
  "traders.copy.risk.lossPct": "Pérdida %",
  "traders.copy.risk.lossPctTip": "Pausa la copia cuando tu pérdida realizada total alcance este % del volumen.",
  "traders.copy.risk.profitPct": "Beneficio %",
  "traders.copy.risk.profitPctTip": "Pausa la copia automáticamente cuando tu beneficio realizado alcance este % del volumen. Te ayuda a asegurar ganancias.",
  "traders.copy.risk.dailyUsd": "Diario $",
  "traders.copy.risk.dailyUsdTip": "Pausa la copia cuando las pérdidas diarias realizadas alcancen este importe. Se reinicia cada día a medianoche UTC.",
  "traders.copy.risk.weeklyUsd": "Semanal $",
  "traders.copy.risk.weeklyUsdTip": "Pausa la copia cuando las pérdidas de los últimos 7 días superen este importe. Avanza día a día.",

  // CopyTraderModal — slippage
  "traders.copy.slip.title": "Protección de slippage",
  "traders.copy.slip.tooltip": "Cancela trades si el precio se mueve demasiado. El fallback de límite usa órdenes limit en vez de cancelar.",
  "traders.copy.slip.maxPct": "Slippage máx %",
  "traders.copy.slip.tightWarn.title": "Cap muy estricto:",
  "traders.copy.slip.tightWarn.body": "Con {pct}% tus BUYs se skipearán en mercados volátiles o penny.",
  "traders.copy.hftWarn.title": "Trader de alta frecuencia",
  "traders.copy.hftWarn.body": "~{tradesPerHour} trades/hora. Muchas copias se saltarán.",
  "traders.copy.hftWarn.tooltip": "Este leader hace ~{tradesPerHour} trades/hora ({trades24h} en 24h). Esta actividad excede el copytrading típico; muchas copias se saltarán por límites de firmas. Considera un leader menos activo.",
  "traders.copy.hftWarn.ack": "Lo entiendo y aun así quiero seguir.",
  "traders.copy.hftWarn.edu.show": "Pros y contras de copiar un bot",
  "traders.copy.hftWarn.edu.hide": "Ocultar pros y contras",
  "traders.copy.hftWarn.edu.prosTitle": "Por qué puede funcionar",
  "traders.copy.hftWarn.edu.pro1": "Los bots suelen tener un edge estadístico real y un win-rate alto — capturan micro-ineficiencias que un humano no puede seguir.",
  "traders.copy.hftWarn.edu.consTitle": "Por qué a los copiadores muchas veces no",
  "traders.copy.hftWarn.edu.con1": "Volumen enorme de trades — cada copia paga una fee, que puede comerse todo el margen.",
  "traders.copy.hftWarn.edu.con2": "Operan en mercados finos con spreads mínimos; tus fills son peores (latencia + slippage), así que su edge no se transfiere 1:1.",
  "traders.copy.hftWarn.edu.con3": "Rotación constante de posiciones y muchas notificaciones.",
  "traders.copy.hftWarn.edu.con4": "Su P&L no predice el tuyo — depende de una velocidad de fill que tu copia no tiene.",
  "traders.copy.lossWarn.title": "Los copiadores han perdido dinero con este trader.",
  "traders.copy.lossWarn.body": "Nuestra simulación de copia{roi} marca esta wallet como no copiable — su ventaja no sobrevive al copiarse. Prueba uno del tab Recommended.",
  "traders.copy.lossWarn.ack": "Entiendo que los copiadores han perdido dinero copiando a este trader.",
  "traders.tab.recommended": "Recomendados",
  "traders.tab.leaderboard": "Leaderboard",
  "traders.curator.empty": "No hay traders copiables ahora mismo — vuelve tras la próxima curación.",
  "traders.curator.teaserTitle": "Regístrate para ver todos los traders copiables",
  "traders.curator.teaserCta": "Registrarse gratis",
  "traders.copy.slip.tieredTitle": "Slippage por tramos %",
  "traders.copy.slip.tieredTip": "Define límites de slippage personalizados por rango de precio — los tokens caros toleran menos slippage que los baratos. Déjalo vacío para usar el slippage global.",
  "traders.copy.slip.tier.high": "40¢+ (alto)",
  "traders.copy.slip.tier.mid": "15-40¢ (medio)",
  "traders.copy.slip.tier.low": "<15¢ (bajo)",

  // CopyTraderModal — footer
  "traders.copy.btn.saving": "Guardando...",
  "traders.copy.btn.save": "Guardar cambios",
  "traders.copy.btn.start": "Empezar a copiar",
  "traders.copy.btn.copy": "Copiar",
  "traders.copy.btn.loginRequired": "Inicia sesión para empezar a copiar",
  "traders.copy.btn.signInPrefix": "Inicia sesión para copiar",
  "traders.copy.btn.signInAction": "Inicia sesión",
  "traders.copy.balance.available": "Saldo",
  "traders.copy.leaderPortfolio": "Portfolio",
  "traders.copy.btn.depositFirst": "Deposita en tu wallet para empezar a copiar — mínimo $1.",

  // ───── /traders/[identifier] — profile page ─────
  // UserProfileClient (errors)
  "traders.profile.notFound": "Trader no encontrado",
  "traders.profile.fetchError": "Error al obtener los datos del trader",
  "traders.profile.loadError": "Error al cargar el perfil",
  "traders.profile.errorTitle": "Trader no encontrado",
  "traders.profile.errorDesc": "Este perfil no existe o no se pudo cargar.",
  "traders.profile.browse": "Explorar traders",
  "traders.profile.goHome": "Ir al inicio",
  // UserProfile (header + stats)
  "traders.profile.back": "Volver",
  "traders.profile.stat.volume": "Volumen",
  "traders.profile.stat.points": "Puntos",
  "traders.profile.stat.positions": "Posiciones",
  "traders.profile.stat.followers": "Seguidores",
  "traders.profile.stat.rank": "Ranking",
  "traders.profile.stat.biggestWin": "Mayor jugada",
  "traders.profile.stat.markets": "Mercados",
  "traders.profile.stat.predictions": "Predicciones",
  "traders.profile.stat.posValue": "Valor posiciones",
  "traders.profile.stat.winRate": "Win Rate",
  "traders.profile.aka.tooltip": "Ver usernames anteriores",
  "traders.profile.aka": "AKA",
  "traders.profile.previously": "Anteriormente conocido como:",
  "traders.profile.copyCta": "Copiar a este trader",
  "traders.profile.stopping": "Deteniendo...",
  "traders.profile.copying": "Copiando",
  "traders.profile.tab.profile": "Perfil",
  "traders.profile.xLink": "@{handle} en X",
  "traders.profile.copiers.one": "trader está",
  "traders.profile.copiers.many": "traders están",
  "traders.profile.copiers.suffix": "ya copiando",
  "traders.profile.copiers.first": "Sé el primero en copiar",
  "traders.card.copying": "copiando",
  "traders.profile.joined": "Se unió en {date}",
  "traders.profile.views": "{count} vistas",
  "traders.profile.volByCategory": "Volumen por categoría",
  "traders.profile.totalVol": "Vol. total",
  "traders.profile.badges": "Insignias",
  "traders.profile.marketsByCategory": "Mercados por categoría",
  "traders.profile.predictionsCount": "Predicciones ({count})",
  "traders.profile.totalDonut": "Total",
  "traders.profile.showLess": "Ver menos",
  "traders.profile.viewAll": "Ver las {count} categorías",
  "traders.profile.performance": "Rendimiento",
  "traders.profile.netPnl": "PnL Neto",
  "traders.profile.toast.comingSoon": "Próximamente",
  "traders.profile.toast.comingSoonDesc": "El copy trading en {platform} llega pronto. ¡Mantente atento!",
  "traders.profile.viewBscScan": "Ver en BscScan",
  "traders.profile.viewPolygonscan": "Ver en Polygonscan",
  "traders.profile.viewOnPolymarket": "Ver en Polymarket",
  "traders.profile.viewOnKalshi": "Ver en Kalshi",
  "traders.profile.viewOnPredict": "Ver en Predict.fun",
  // TraderAboutCard
  "traders.about.title": "Acerca de",
  "traders.about.autoGenerated": "Generado automáticamente",
  "traders.about.markets": "Mercados",
  // TraderPortfolio
  "traders.port.tab.positions": "Posiciones",
  "traders.port.tab.activity": "Actividad",
  "traders.port.tab.comments": "Comentarios",
  "traders.port.tab.volume": "Volumen",
  "traders.port.tab.similar": "Wallets similares",
  "traders.port.tab.new": "Nuevo",
  "traders.port.filter.all": "Todos",
  "traders.port.filter.open": "Abiertas",
  "traders.port.filter.resolved": "Resueltas",
  "traders.port.filter.active": "Activas",
  "traders.port.filter.closed": "Cerradas",
  "traders.port.searchPlaceholder": "Buscar posiciones...",
  "traders.port.portfolioValue": "Valor del portfolio",
  "traders.port.empty.positions": "No se encontraron posiciones",
  "traders.port.col.market": "Mercado",
  "traders.port.col.avg": "Promedio",
  "traders.port.col.current": "Actual",
  "traders.port.col.shares": "Shares",
  "traders.port.col.value": "Valor",
  "traders.port.col.pnl": "PnL",
  "traders.port.showMore": "Ver más ({n} restantes)",
  "traders.port.shares": "shares",
  "traders.port.empty.closed": "No hay posiciones cerradas",
  "traders.port.col.result": "Resultado",
  "traders.port.col.totalTraded": "Total operado",
  "traders.port.col.amountWon": "Importe ganado",
  "traders.port.result.won": "Ganada",
  "traders.port.result.lost": "Perdida",
  "traders.port.empty.activity": "Sin actividad",
  "traders.port.col.type": "Tipo",
  "traders.port.col.amount": "Importe",
  "traders.port.col.time": "Tiempo",
  "traders.port.activity.rewards": "Recompensas",
  "traders.port.activity.rewards4": "Recompensas 4%",
  "traders.port.activity.referralReward": "Recompensa por referido",
  "traders.port.activity.referralRewardDesc": "Pago de afiliado de Polymarket",
  "traders.port.activity.yield": "Rendimiento de saldo",
  "traders.port.activity.yieldDesc": "4% APR sobre el saldo en USDC",
  "traders.port.activity.unknownMarket": "Mercado desconocido",
  "traders.port.activity.sharesSuffix": "{n} shares",
  "traders.port.badge.reward": "Recompensa",
  "traders.port.badge.referral": "Referido",
  "traders.port.badge.yield": "Yield",
  "traders.port.badge.redeem": "Canjear",
  "traders.port.badge.buy": "Compra",
  "traders.port.badge.sell": "Venta",
  "traders.port.badge.split": "Split",
  "traders.port.badge.merge": "Merge",
  "traders.port.badge.convert": "Convertir",
  "traders.port.badge.rebate": "Rebate",
  "traders.port.empty.comments": "Sin comentarios en Polymarket",
  // TraderPnLChart
  "traders.chart.title": "Beneficio/Pérdida",
  "traders.chart.noData": "No hay datos de PnL disponibles",
  "traders.chart.loadFail": "Error al cargar el historial de P&L",
  "traders.chart.noTradingHistory": "No hay historial de trading para este periodo",
  "traders.chart.tooltip.pnl": "P&L",
  "traders.chart.display.day": "Último día",
  "traders.chart.display.week": "Última semana",
  "traders.chart.display.month": "Último mes",
  "traders.chart.display.year": "Último año",
  "traders.chart.display.all": "Todo el tiempo",
  // WalletPnLEnriched
  "traders.pnlb.title": "Desglose P&L",
  "traders.pnlb.realized": "Realizado",
  "traders.pnlb.unrealized": "No realizado",
  // WalletVolumeChart
  "traders.vol.gran.hourly": "Por hora",
  "traders.vol.gran.daily": "Diario",
  "traders.vol.gran.weekly": "Semanal",
  "traders.vol.totalVolume": "Volumen total",
  "traders.vol.buyVolume": "Volumen compra",
  "traders.vol.sellVolume": "Volumen venta",
  "traders.vol.totalTrades": "Trades totales",
  "traders.vol.dataPoints": "Puntos de datos",
  "traders.vol.buy": "Compra",
  "traders.vol.sell": "Venta",
  // UserTimeline (extras)
  "traders.timeline.justNow": "ahora mismo",
  "traders.timeline.mAgo": "hace {n}m",
  "traders.timeline.hAgo": "hace {n}h",
  "traders.timeline.dAgo": "hace {n}d",
  "traders.timeline.postFailed": "No se pudo crear la publicación",
  "traders.timeline.copied": "copiado",
  // MarketPerformance
  "traders.mperf.title": "Rendimiento por mercado",
  "traders.mperf.tradesSuffix": "trades",
  "traders.mperf.wrSuffix": "% WR",
  "traders.mperf.roiSuffix": "% ROI",
  "traders.mperf.entryEdge": "Edge de entrada: {pct}%",
  // RecentTrades
  "traders.recent.title": "Actividad reciente",
  "traders.recent.now": "ahora",
  "traders.recent.tradesSuffix": "trades",
  "traders.recent.buy": "COMPRA",
  "traders.recent.sell": "VENTA",
  "traders.recent.yes": "SÍ",
  "traders.recent.no": "NO",
  // TraderRelatedArticles
  "traders.articles.thisTrader": "este trader",
  "traders.articles.title": "Noticias sobre {name}",
  // PredexonStatsPanel
  "traders.psp.style.whale": "Ballena",
  "traders.psp.style.marketMaker": "Market Maker",
  "traders.psp.style.active": "Activo",
  "traders.psp.style.holder": "Holder",
  "traders.psp.style.degen": "Degen",
  "traders.psp.style.conviction": "Convicción",
  "traders.psp.style.contrarian": "Contrario",
  "traders.psp.style.value": "Valor",
  "traders.psp.section.performance": "Rendimiento",
  "traders.psp.section.tradingStyle": "Estilo de trading",
  "traders.psp.row.volume": "Volumen",
  "traders.psp.row.roi": "ROI",
  "traders.psp.row.winRate": "Win Rate",
  "traders.psp.row.wl": "G / P",
  "traders.psp.row.trades": "Trades",
  "traders.psp.row.profitFactor": "Profit Factor",
  "traders.psp.row.entryEdge": "Edge de entrada",
  "traders.psp.row.netFees": "Fees netas",
  "traders.psp.row.bestTrade": "Mejor trade",
  "traders.psp.row.worstTrade": "Peor trade",
  "traders.psp.row.streaks": "Rachas",
  "traders.psp.row.avgHold": "Hold medio",
  "traders.psp.row.walletAge": "Antigüedad wallet",
  "traders.psp.row.positions": "Posiciones",
  // CompareTraders
  "traders.compare.profit": "Beneficio",
  "traders.compare.roi": "ROI",
  "traders.compare.volume": "Volumen",
  "traders.compare.pnl": "PnL",
  "traders.compare.vs": "VS",
  "traders.compare.selectTrader": "Selecciona un trader para comparar...",
  "traders.compare.cancel": "Cancelar",
  "traders.compare.tapAnother": "Pulsa otro trader para comparar",
  "traders.compare.copyTrader": "Copiar trader",
  "traders.compare.analyzing": "Analizando traders...",
  "traders.compare.results": "Resultados",
  "traders.compare.winner": "— Ganador",
  // PortfolioTracker
  "traders.tracker.loadFail": "Error al cargar el portfolio",
  "traders.tracker.noData": "No hay datos del portfolio",
  "traders.tracker.tryAgain": "Reintentar",
  "traders.tracker.title": "Mi portfolio",
  "traders.tracker.lastUpdated": "Última actualización: {time}",
  "traders.tracker.refresh": "Actualizar",
  "traders.tracker.portfolioValue": "Valor del portfolio",
  "traders.tracker.totalPnl": "P&L total",
  "traders.tracker.realized": "Realizado: {amount}",
  "traders.tracker.unrealized": "No realizado: {amount}",
  "traders.tracker.roi": "ROI",
  "traders.tracker.activePositions": "Posiciones activas",
  "traders.tracker.pnlHistory": "Historial P&L",
  "traders.tracker.change": "Cambio: {currency} ({percent})",
  "traders.tracker.tooltipPnl": "P&L",
  "traders.tracker.byCategory": "Por categoría",
  // SimilarWallets
  "traders.similar.intro": "Wallets que operan los mismos mercados — ordenadas por solapamiento de portfolio. Pulsa cualquier fila para explorar esa wallet.",
  "traders.similar.col.wallet": "Wallet",
  "traders.similar.col.shared": "Compartidos",
  "traders.similar.col.overlap": "Solape",
  "traders.similar.col.theirMarkets": "Sus mercados",
  "traders.similar.col.totalPnl": "PnL total",
  "traders.similar.col.roi": "ROI",
  "traders.similar.col.winRate": "Win Rate",
  "traders.similar.tip.shared": "Número de mercados que ambas wallets han operado",
  "traders.similar.tip.overlap": "Porcentaje de los mercados del trader que esta wallet también opera",
  "traders.similar.tip.theirMarkets": "Número total de mercados que esta wallet ha operado",
  "traders.similar.tip.totalPnl": "Beneficio o pérdida total de todos los tiempos para esta wallet",
  "traders.similar.tip.roi": "Retorno sobre inversión — beneficio relativo al volumen operado",
  "traders.similar.tip.winRate": "Porcentaje de posiciones cerradas que fueron rentables",
  // Error boundaries
  "traders.err.title": "Algo ha ido mal",
  "traders.err.profileDesc": "No pudimos cargar este perfil de trader. Inténtalo de nuevo.",
  "traders.err.pageDesc": "No pudimos cargar la página de traders. Inténtalo de nuevo.",
  "traders.err.tryAgain": "Reintentar",
  "traders.err.goHome": "Ir al inicio",

  // ───── /the-frens ─────
  "frens.download": "Descargar",

  // ───── Misc small pages ─────
  "common.redirecting": "Redirigiendo...",
  // /telegram/login
  "tgLogin.init": "Inicializando...",
  "tgLogin.switching": "Cambiando de cuenta...",
  "tgLogin.authTg": "Autenticando con Telegram...",
  "tgLogin.loading": "Cargando...",
  "tgLogin.clickToLogin": "Pulsa abajo para iniciar sesión...",
  "tgLogin.connecting": "Conectando tu cuenta...",
  "tgLogin.errNoTg": "No se pudo obtener el ID de Telegram. Inténtalo de nuevo desde el bot.",
  "tgLogin.errAuth": "Error de autenticación. Inténtalo de nuevo.",
  "tgLogin.errOpenFromTg": "Abre esta página desde Telegram",
  "tgLogin.success": "¡Cuenta conectada correctamente!",
  "tgLogin.errFail": "Error al conectar la cuenta",
  "tgLogin.errGeneric": "Ocurrió un error",
  "tgLogin.cta": "Iniciar sesión con Telegram",
  "tgLogin.returning": "Volviendo al bot...",
  "tgLogin.errorLabel": "Error",
  "tgLogin.tryAgain": "Reintentar",
  "tgLogin.poweredBy": "Con tecnología de Privy",
  // /restricted
  "restricted.title": "Contenido no disponible",
  "restricted.desc": "Los mercados de predicción de Kalshi no están disponibles en tu región por restricciones regulatorias.",
  "restricted.cta": "Ir a Polymarket",
  "restricted.footer": "Polymarket y el resto de funciones siguen estando disponibles.",
  // /traders/cohorts
  "cohorts.back": "Volver a Traders",
  "cohorts.title": "Cohortes por estilo de trading",
  "cohorts.desc": "Compara el rendimiento de los 8 estilos de trading en Polymarket. Datos con tecnología Predexon.",
  "cohorts.empty": "Datos de cohortes no disponibles. Requiere el plan Predexon Dev+.",
  "cohorts.tradersCount": "{count} traders",
  "cohorts.stat.avgPnl": "PnL medio",
  "cohorts.stat.winRate": "Win Rate",
  "cohorts.stat.avgVol": "Vol medio",
  "cohorts.stat.avgTrades": "Trades medios",
  "cohorts.avgPnlSmall": "PnL medio",

  // ───── UMA — VoterProfile ─────
  "uma.voter.back": "Volver al ranking",
  "uma.voter.notFound": "Votante no encontrado.",
  "uma.voter.staked": "En stake",
  "uma.voter.apy": "APY",
  "uma.voter.accuracy": "Precisión",
  "uma.voter.slashed": "Penalizado",
  "uma.voter.correct": "Correcto",
  "uma.voter.wrong": "Incorrecto",
  "uma.voter.noVote": "Sin voto",
  "uma.voter.disputesInvolved": "Disputas en las que participó",

  // ───── UMA — AnalyticsDashboard ─────
  "uma.analytics.loadFail": "Error al cargar analíticas: {error}",
  "uma.analytics.participation": "Tasa de participación",
  "uma.analytics.turnout": "Participación de votantes",
  "uma.analytics.bondSizes": "Tamaños de bono (USD)",
  "uma.analytics.monthly": "Disputas mensuales",
  "uma.analytics.slashing": "Slashing en el tiempo",

  // ───── UMA — DisputeDetail ─────
  "uma.detail.back": "Volver a disputas",
  "uma.detail.notFound": "Disputa no encontrada.",
  "uma.detail.proposed": "Propuesto",
  "uma.detail.bond": "Bono",
  "uma.detail.proposer": "Proponente",
  "uma.detail.disputer": "Disputador",
  "uma.detail.voteBreakdown": "Desglose de votos",
  "uma.detail.voters": "Votantes",
  "uma.detail.tokensRevealed": "Tokens revelados",
  "uma.detail.participation": "Participación",
  "uma.detail.winner": "Ganador",
  "uma.detail.voter": "votante",
  "uma.detail.votersPlural": "votantes",
  "uma.detail.revealedVotes": "Votos revelados ({n})",
  "uma.detail.col.address": "Dirección",
  "uma.detail.col.tokens": "Tokens",
  "uma.detail.col.vote": "Voto",
  "uma.detail.col.status": "Estado",
  "uma.detail.status.correct": "Correcto",
  "uma.detail.status.wrong": "Incorrecto",
  "uma.detail.status.pending": "Pendiente",
  "uma.detail.unrevealed": "Votantes sin revelar ({n})",
  "uma.detail.unrevealedDesc": "Hicieron commit pero aún no han revelado",
  "uma.detail.col.stake": "Stake",
  "uma.detail.col.tokensCommitted": "Tokens comprometidos",

  // ───── /traders/category ─────
  "tradersCat.crumb.home": "Inicio",
  "tradersCat.crumb.traders": "Traders",
  "tradersCat.crumb.categories": "Categorías",
  "tradersCat.title": "Categorías de traders en Polymarket",
  "tradersCat.desc": "Explora los traders más rentables de Polymarket organizados por categoría de mercado. Cada categoría muestra traders ordenados por beneficio que se especializan en ese tipo de mercado de predicción.",
  "tradersCat.tradersCount": "{count} traders",
  "tradersCat.empty": "Aún no hay categorías disponibles.",
  "tradersCat.browseAll": "Explorar todos los traders",
  "tradersCat.about": "Sobre las categorías de traders de Polymarket",
  "tradersCat.aboutDesc": "Los traders de Polymarket suelen especializarse en categorías concretas donde tienen experiencia. FrenFlow registra en qué categorías opera cada trader y te permite explorar los mejores en cada área. Si te interesan política, deportes, cripto o finanzas — encuentra a los traders con mejor track record y copia sus estrategias.",
  // /traders/category/[category]
  "tradersCat.detail.title": "Top traders de {cat} en Polymarket",
  "tradersCat.detail.descHas": "{n} traders especializados en mercados de predicción de {cat}, ordenados por beneficio. Sigue sus posiciones y copia sus trades en FrenFlow.",
  "tradersCat.detail.descEmpty": "Aún no se encontraron traders en mercados de {cat}.",
  "tradersCat.detail.empty": "No se encontraron traders en esta categoría.",
  "tradersCat.detail.volSuffix": "vol",
  "tradersCat.detail.winSuffix": "% win",
  "tradersCat.detail.moreCats": "Más categorías de traders",
  "tradersCat.detail.seoTitle": "Trading de mercados de predicción de {cat}",
  "tradersCat.detail.seoDesc": "{cat} es una de las categorías más activas de Polymarket, donde los traders apuestan sobre resultados relacionados con eventos de {cat2}.",
  "tradersCat.detail.seoTracks": "FrenFlow sigue a {n}+ traders especializados en mercados de {cat}.",
  "tradersCat.detail.seoCopy": "Puedes seguir a cualquier trader para ver sus posiciones en tiempo real, o configurar copy trading para replicar sus operaciones automáticamente.",

  // ───── /pits ─────
  // Discovery
  "pits.title": "Pits",
  "pits.intro": "Encuentra a tu tribu. Comparte trades, llamadas y alpha en comunidades creadas en torno a intereses comunes.",
  "pits.create": "Crear un pit",
  "pits.myPits": "Mis pits",
  "pits.searchPlaceholder": "Buscar pits…",
  "pits.sort.trending": "Trending",
  "pits.sort.mostMembers": "Más miembros",
  "pits.sort.newest": "Más nuevos",
  "pits.section.searchResults": "Resultados de búsqueda",
  "pits.section.trending": "Trending",
  "pits.section.mostPopular": "Más populares",
  "pits.section.newest": "Más nuevos",
  "pits.empty.search": "Ningún pit coincide con tu búsqueda",
  "pits.empty.title": "Aún no hay nada por aquí",
  "pits.empty.desc": "Sé el primero en crear un pit y empezar una comunidad.",
  // Card
  "pits.card.private": "Privado",
  "pits.card.noDescription": "Sin descripción.",
  // Image picker
  "pits.img.errImageOnly": "Solo se permiten archivos de imagen",
  "pits.img.errMaxSize": "Máx. {mb}MB",
  "pits.img.altAvatar": "vista previa avatar",
  "pits.img.altBanner": "vista previa banner",
  "pits.img.change": "Cambiar",
  "pits.img.removeAvatar": "Quitar avatar",
  "pits.img.removeBanner": "Quitar banner",
  "pits.img.dropOrClick": "Arrastra o pulsa",
  "pits.img.errSaveFirst": "Guarda primero el grupo para subir imágenes.",
  "pits.img.errOnlyImages": "Solo se permiten archivos de imagen",
  "pits.img.noAvatar": "Aún no hay avatar",
  "pits.img.noBanner": "Aún no hay banner",
  "pits.img.uploadAvatar": "Subir avatar",
  "pits.img.uploadBanner": "Subir banner",
  "pits.img.saveFirstHint": "Guarda primero el grupo para habilitar las subidas.",
  // Invite Accept
  "pits.invite.loading": "Cargando…",
  "pits.invite.title": "Estás invitado",
  "pits.invite.signInDesc": "Inicia sesión para aceptar la invitación.",
  "pits.invite.signIn": "Iniciar sesión",
  "pits.invite.joining": "Entrando al pit…",
  "pits.invite.success": "¡Estás dentro! Redirigiendo…",
  "pits.invite.errFallback": "No se pudo aceptar la invitación",
  // Header
  "pits.header.errInviteOnly": "Este pit es solo por invitación. Pide a un admin un enlace de invitación.",
  "pits.header.errCouldNotJoin": "No se pudo unir",
  "pits.header.confirmLeave": "¿Salir de {name}?",
  "pits.header.errCouldNotLeave": "No se pudo salir",
  "pits.header.altCover": "portada de {name}",
  "pits.header.role.owner": "Propietario",
  "pits.header.role.admin": "Admin",
  "pits.header.role.mod": "Mod",
  "pits.header.role.member": "Miembro",
  "pits.header.private": "Privado",
  "pits.header.public": "Público",
  "pits.header.settings": "Ajustes",
  "pits.header.requestInvite": "Pedir invitación",
  "pits.header.join": "Unirse al pit",
  "pits.header.leave": "Salir",
  "pits.header.created": "Creado el {date}",
  "pits.header.member": "Miembro",
  "pits.header.members": "Miembros",
  "pits.header.post": "Post",
  "pits.header.posts": "Posts",
  "pits.header.access.invite": "Solo invitación",
  "pits.header.access.open": "Abierto",
  "pits.header.access": "Acceso",
  // Shell tabs
  "pits.tab.posts": "Posts",
  "pits.tab.members": "Miembros",
  "pits.tab.about": "Acerca de",
  // Sidebar
  "pits.sidebar.topMarkets": "Top mercados de este pit",
  "pits.sidebar.col.posts": "Posts",
  "pits.sidebar.post": "post",
  "pits.sidebar.posts": "posts",
  "pits.sidebar.vol24h": "{v} vol 24h",
  "pits.sidebar.activeMembers": "Miembros activos",
  "pits.sidebar.noMembers": "Aún no hay miembros.",
  "pits.sidebar.anonymous": "Anónimo",
  "pits.sidebar.postCount": "{n} {label}",
  "pits.sidebar.seeAll": "Ver todos los miembros",
  // About tab
  "pits.about.title": "Acerca de",
  "pits.about.noDesc": "No se ha proporcionado descripción.",
  "pits.about.stats": "Stats",
  "pits.about.details": "Detalles",
  "pits.about.visibility": "Visibilidad",
  "pits.about.visPrivateDesc": "Solo invitación — únicamente los miembros ven los posts",
  "pits.about.visPublicDesc": "Cualquiera puede ver; los miembros pueden publicar",
  "pits.about.created": "Creado",
  "pits.about.tags": "Etiquetas",
  "pits.about.slug": "Slug",
  "pits.about.links": "Enlaces",
  // Members tab
  "pits.members.empty": "Aún no hay miembros",
  // Composer
  "pits.composer.placeholder": "Comparte algo con el pit…",
  "pits.composer.posting": "Publicando…",
  "pits.composer.post": "Publicar",
  // Members leaderboard
  "pits.lb.title": "Miembros",
  "pits.lb.descSortable": "Ordenable por estadísticas de Polymarket",
  "pits.lb.descLoading": "Cargando estadísticas…",
  "pits.lb.totalSuffix": "{n} en total",
  "pits.lb.col.member": "Miembro",
  "pits.lb.col.style": "Estilo",
  "pits.lb.col.volume": "Volumen",
  "pits.lb.col.pnl": "PnL",
  "pits.lb.col.win": "Win",
  "pits.lb.col.trades": "Trades",
  "pits.lb.col.posts": "Posts",
  "pits.lb.col.joined": "Se unió",
  "pits.lb.empty": "Aún no hay miembros.",
  "pits.lb.anonymous": "Anónimo",
  // Posts tab
  "pits.posts.justNow": "ahora mismo",
  "pits.posts.mAgo": "hace {m}m",
  "pits.posts.hAgo": "hace {h}h",
  "pits.posts.dAgo": "hace {d}d",
  "pits.posts.errOnlyDomains": "Solo se permiten enlaces a {domains}",
  "pits.posts.confirmDelete": "¿Eliminar este post?",
  "pits.posts.errDeleteStatus": "No se pudo eliminar el post ({status})",
  "pits.posts.errNetworkDelete": "Error de red al eliminar el post",
  "pits.posts.signInToVote": "Inicia sesión para votar posts",
  "pits.posts.wall": "Muro",
  "pits.posts.altYourAvatar": "Tu avatar",
  "pits.posts.placeholder": "¿Qué tienes en mente? ¡Pega un enlace de mercado o evento para mostrar una vista previa!",
  "pits.posts.eventMarkets": "Evento · {n} mercados",
  "pits.posts.marketPreview": "Vista previa de mercado",
  "pits.posts.removePreview": "Quitar vista previa",
  "pits.posts.moreMarkets": "+{n} más",
  "pits.posts.volPreview": "Vol {v}",
  "pits.posts.charCount": "{n}/500",
  "pits.posts.posting": "Publicando…",
  "pits.posts.post": "Publicar",
  "pits.posts.signInToPost": "Inicia sesión para publicar en este pit.",
  "pits.posts.joinPrivate": "Únete a este pit privado para publicar (necesita invitación).",
  "pits.posts.joinPublic": "Únete a este pit para publicar.",
  "pits.posts.empty.title": "Aún no hay posts",
  "pits.posts.empty.beFirst": "Sé el primero en compartir algo",
  "pits.posts.empty.joinToStart": "Únete para empezar la conversación",
  "pits.posts.user": "Usuario",
  "pits.posts.anonymous": "Anónimo",
  "pits.posts.deletePost": "Eliminar post",
  "pits.posts.sold": "Vendió",
  "pits.posts.bought": "Compró",
  "pits.posts.sharePost": "Compartir post",
  // Create pit
  "pits.create.loading": "Cargando…",
  "pits.create.gateTitle": "Crear un pit",
  "pits.create.gateDesc": "Inicia sesión para crear tu propio pit en FrenFlow.",
  "pits.create.signIn": "Iniciar sesión",
  "pits.create.browseInstead": "Explorar pits en su lugar",
  "pits.create.errName": "El nombre debe tener al menos 3 caracteres",
  "pits.create.errSlug": "El slug debe tener 3–64 caracteres (a–z, 0–9, guiones) y empezar por letra o número",
  "pits.create.progressCreating": "Creando pit…",
  "pits.create.progressUploadAvatar": "Subiendo avatar…",
  "pits.create.progressUploadBanner": "Subiendo banner…",
  "pits.create.progressUploadImages": "Subiendo imágenes…",
  "pits.create.progressDone": "Listo. Redirigiendo…",
  "pits.create.title": "Crear un pit",
  "pits.create.subtitle": "Un espacio para que tu tribu comparta trades, llamadas e ideas. La parte derecha se actualiza según escribes.",
  "pits.create.section.identity": "Identidad",
  "pits.create.field.name": "Nombre",
  "pits.create.placeholder.name": "p.ej. Polymarket Whales",
  "pits.create.field.slug": "URL",
  "pits.create.placeholder.slug": "polymarket-whales",
  "pits.create.hint.slug": "3–64 caracteres · a–z, 0–9, guiones",
  "pits.create.section.imagery": "Imágenes",
  "pits.create.field.banner": "Banner",
  "pits.create.hint.banner": "Ratio 3:1, máx. 10MB · JPG/PNG/WebP",
  "pits.create.upload": "Subir",
  "pits.create.field.avatar": "Avatar",
  "pits.create.hint.avatar": "Cuadrado, máx. 5MB · JPG/PNG/WebP",
  "pits.create.uploadAvatar": "Subir avatar",
  "pits.create.changeAvatar": "Cambiar avatar",
  "pits.create.removeBtn": "Quitar",
  "pits.create.section.description": "Descripción",
  "pits.create.field.description": "¿De qué va este pit?",
  "pits.create.placeholder.description": "Ballenas intercambiando alpha en Polymarket. Capturas de PnL bienvenidas.",
  "pits.create.section.visibility": "Visibilidad",
  "pits.create.vis.public": "Público",
  "pits.create.vis.publicDesc": "Cualquiera puede encontrarlo y unirse",
  "pits.create.vis.private": "Privado",
  "pits.create.vis.privateDesc": "Solo invitación",
  "pits.create.section.tags": "Etiquetas",
  "pits.create.field.tags": "Temas",
  "pits.create.hint.tags": "Separadas por comas · máx. 10",
  "pits.create.placeholder.tags": "crypto, deportes, ballenas",
  "pits.create.section.social": "Redes sociales",
  "pits.create.submit": "Crear pit",
  "pits.create.submitDefault": "Creando…",
  "pits.create.cancel": "Cancelar",
  "pits.create.preview": "Vista previa",
  "pits.create.previewName": "Tu pit",
  "pits.create.createdToday": "Creado hoy",
  "pits.create.preview.member": "Miembro",
  "pits.create.preview.posts": "Posts",
  "pits.create.preview.access": "Acceso",
  // Settings
  "pits.settings.checkPerms": "Comprobando permisos…",
  "pits.settings.gateSignIn": "Inicia sesión para gestionar este pit",
  "pits.settings.gateNoPerm": "No tienes permisos para gestionar este pit",
  "pits.settings.nav.profile": "Perfil",
  "pits.settings.nav.members": "Miembros",
  "pits.settings.nav.invites": "Invitaciones",
  "pits.settings.nav.danger": "Zona peligrosa",
  "pits.settings.backTo": "Volver a {name}",
  "pits.settings.title": "Ajustes del pit",
  "pits.settings.subtitle": "Gestiona perfil, miembros, invitaciones y acceso de {name}.",
  "pits.settings.back": "Volver",
  "pits.settings.uploading": "Subiendo…",
  "pits.settings.editCover": "Editar portada",
  "pits.settings.removeCover": "Quitar portada",
  "pits.settings.changeAvatar": "Cambiar avatar",
  "pits.settings.imgHint": "Avatar: cuadrado, máx. 5MB · Portada: ratio 3:1, máx. 10MB · JPG/PNG/WebP",
  "pits.settings.field.name": "Nombre",
  "pits.settings.visibility": "Visibilidad",
  "pits.settings.public": "Público",
  "pits.settings.private": "Privado",
  "pits.settings.field.description": "Descripción",
  "pits.settings.placeholder.description": "¿De qué va este pit?",
  "pits.settings.field.tags": "Etiquetas",
  "pits.settings.placeholder.tags": "crypto, ballenas, alpha (separadas por comas, máx. 10)",
  "pits.settings.social": "Redes sociales",
  "pits.settings.saved": "Guardado",
  "pits.settings.saving": "Guardando…",
  "pits.settings.save": "Guardar cambios",
  "pits.settings.failed": "Falló",
  "pits.settings.confirmBan": "¿Banear a este usuario del pit?",
  "pits.settings.confirmRemove": "¿Quitar a este miembro?",
  "pits.settings.loadingMembers": "Cargando miembros…",
  "pits.settings.makeAdmin": "Hacer admin",
  "pits.settings.mod": "Mod",
  "pits.settings.demote": "Degradar",
  "pits.settings.ban": "Banear",
  "pits.settings.kick": "Expulsar",
  "pits.settings.invite.create": "Crear enlace de invitación",
  "pits.settings.invite.maxUses": "Usos máximos (opcional)",
  "pits.settings.invite.expiresHrs": "Caduca en horas (opcional)",
  "pits.settings.invite.never": "nunca",
  "pits.settings.invite.creating": "Creando…",
  "pits.settings.invite.createBtn": "Crear",
  "pits.settings.invite.usesLabel": "{used}/{max} usos",
  "pits.settings.invite.expires": "caduca el {date}",
  "pits.settings.invite.copy": "Copiar enlace",
  "pits.settings.invite.revoke": "Revocar",
  "pits.settings.invite.empty": "Aún no hay invitaciones.",
  "pits.settings.invite.confirmRevoke": "¿Revocar esta invitación?",
  "pits.settings.danger.title": "Eliminar este pit",
  "pits.settings.danger.desc": "Esto elimina permanentemente el pit, todos los miembros, posts e invitaciones. No se puede deshacer.",
  "pits.settings.danger.typeToConfirm": "Escribe {slug} para confirmar:",
  "pits.settings.danger.deleting": "Eliminando…",
  "pits.settings.danger.delete": "Eliminar el pit permanentemente",

  // ───── /claim/[shortCode] (FlowLinks) ─────
  "claim.countdown.expired": "Caducado",
  "claim.countdown.day": "Día",
  "claim.countdown.days": "Días",
  "claim.countdown.hour": "Hora",
  "claim.countdown.hours": "Horas",
  "claim.countdown.min": "Min",
  "claim.countdown.sec": "Seg",
  "claim.countdown.expiresIn": "Caduca en",
  "claim.steps.verifyingLink": "Verificando enlace",
  "claim.steps.transferring": "Transfiriendo shares",
  "claim.steps.done": "¡Listo!",
  "claim.err.verifyTasks": "Error al verificar las tareas sociales",
  "claim.err.linkNotFound": "Enlace no encontrado",
  "claim.err.loading": "Error al cargar el enlace",
  "claim.err.claiming": "Error al reclamar",
  "claim.err.processing": "Error al procesar el claim",
  "claim.err.lotteryTimeout": "Timeout en el resultado de la lotería — prueba a refrescar",
  "claim.title.tryLuck": "Prueba suerte",
  "claim.title.openGift": "Abre tu regalo",
  "claim.title.claiming": "Reclamando shares...",
  "claim.title.rollingPrize": "Tirando tu premio...",
  "claim.title.drawing": "Sorteando...",
  "claim.title.congrats": "🎉 ¡Enhorabuena fren!",
  "claim.title.lost": "Esta vez no",
  "claim.title.notAvailable": "Enlace no disponible",
  "claim.goHome": "Ir al inicio",
  "claim.lottery.rollingInfo": "Tirando 1-{odds}... Necesitas un 1 para ganar",
  "claim.random.prizeRange": "Rango del premio: {min} — {max} shares",
  "claim.msg.receivedFrom": "Has recibido de @{name}",
  "claim.msg.betterLuck": "Más suerte la próxima vez",
  "claim.msg.givingAway": "Tu fren @{name} está regalando",
  "claim.msg.sentYou": "Tu fren @{name} te ha enviado",
  "claim.polymarketShares": "shares de Polymarket",
  "claim.lotteryBadge": "1 entre {odds} de probabilidad",
  "claim.randomBadge": "Todos ganan — la cantidad es aleatoria",
  "claim.estValue": "≈ ${amount} de valor",
  "claim.social.unlock": "Completa para desbloquear",
  "claim.social.counter": "{verified}/{total}",
  "claim.social.requiredVerify": "Requerido para verificar tareas",
  "claim.social.connectPlatform": "Conectar {platform}",
  "claim.social.connect": "Conectar",
  "claim.social.follow": "Sigue a @{username}",
  "claim.social.repostUser": "Repostea el post de @{username}",
  "claim.social.repost": "Repostea este post",
  "claim.social.likeUser": "Da like al post de @{username}",
  "claim.social.like": "Da like a este post",
  "claim.social.joinGuild": "Únete a {name}",
  "claim.social.joinDiscord": "Únete al servidor de Discord",
  "claim.social.joinTelegramTitle": "Únete a {title}",
  "claim.social.joinTelegram": "Únete al grupo de Telegram",
  "claim.social.doIt": "Hacerlo",
  "claim.social.verifying": "Verificando...",
  "claim.social.verifyAll": "Verificar todo",
  "claim.cta.viewPortfolio": "Ver tu portfolio",
  "claim.lottery.label": "Lotería",
  "claim.lottery.chance": "{n}% de probabilidad",
  "claim.random.label": "Premio aleatorio",
  "claim.random.range": "{min} — {max} shares",
  "claim.spots.claimed": "Plazas reclamadas",
  "claim.spots.counter": "{claimed}/{max}",
  "claim.spots.left": "{n} disponibles",
  "claim.status.expired": "Este enlace ha caducado",
  "claim.status.unavailable": "Este enlace ya no está disponible",
  "claim.status.noShares": "No quedan shares",
  "claim.btn.connectToClaim": "Conéctate para reclamar",
  "claim.btn.notAvailable": "No disponible",
  "claim.btn.enterLottery": "Entrar en la lotería",
  "claim.btn.openPrize": "Abrir tu premio",
  "claim.btn.claimShares": "Reclamar shares",
  "claim.btn.entering": "Entrando...",
  "claim.btn.processing": "Procesando...",
  "claim.lottery.drawing": "Sorteando...",
  "claim.lottery.revealing": "Revelando...",
  "claim.lottery.oddsInfo": "1 entre {odds} ({pct}%)",
  "claim.lottery.youWon": "¡Has ganado!",
  "claim.lottery.sharesYours": "{n} shares son tuyas",
  "claim.lottery.notThisTime": "Esta vez no",
  "claim.lottery.betterLuck": "Más suerte la próxima vez",

  // ───── /coming-soon ─────
  "comingSoon.tagline": "Los mercados de predicción son más divertidos con frens.",
  "comingSoon.label": "Próximamente",
  "comingSoon.successTitle": "¡Estás dentro!",
  "comingSoon.formTitle": "Únete a la lista de espera",
  "comingSoon.formDesc": "Sé el primero en operar con tus frens",
  "comingSoon.emailPlaceholder": "Introduce tu email",
  "comingSoon.acceptIntro": "Acepto la",
  "comingSoon.privacyPolicy": "Política de Privacidad",
  "comingSoon.acceptAnd": "y los",
  "comingSoon.terms": "Términos",
  "comingSoon.submit": "Conseguir acceso anticipado",
  "comingSoon.joining": "Uniéndote...",
  "comingSoon.noSpam": "Sin spam, nunca. Date de baja cuando quieras.",
  "comingSoon.followX": "Síguenos en X",
  "comingSoon.joinTg": "Únete a los anuncios de Telegram",
  "comingSoon.adminAccess": "Acceso admin",
  "comingSoon.password": "Contraseña",
  "comingSoon.enter": "Entrar",
  "comingSoon.errInvalid": "Contraseña incorrecta",
  "comingSoon.errGeneric": "Algo salió mal",

  // ───── /gate ─────
  "gate.title": "Restringido",
  "gate.desc": "Introduce la contraseña de acceso para continuar.",
  "gate.placeholder": "Contraseña",
  "gate.submit": "Entrar",
  "gate.invalid": "Contraseña incorrecta",
  "gate.stagingTitle": "Acceso Staging",
  "gate.stagingDesc": "Introduce la contraseña para continuar",
  "gate.verifying": "Verificando...",
  "gate.tooMany": "Demasiados intentos. Inténtalo más tarde.",
  "gate.wrong": "Contraseña incorrecta",
  "gate.errGeneric": "Algo salió mal",

  // ───── /telegram/miniapp ─────
  "tgmini.tagline": "Opera más inteligente con tus frens",
  "tgmini.initializing": "Inicializando...",
  "tgmini.connectingPrivy": "Conectando con Privy...",
  "tgmini.linkingAccount": "Vinculando tu cuenta...",
  "tgmini.statusLine": "Estado: {s}",
  "tgmini.connected": "¡Conectado!",
  "tgmini.linkedSuccess": "Cuenta vinculada con éxito",
  "tgmini.returnToBot": "Volver al bot",
  "tgmini.checkingStatus": "Comprobando estado",
  "tgmini.verifyingSetup": "Verificando tu configuración de trading...",
  "tgmini.tradingEnabled": "¡Trading activado!",
  "tgmini.allSetTrade": "Todo listo para empezar a operar.",
  "tgmini.settingUpTrading": "Configurando trading",
  "tgmini.pleaseWait": "Por favor espera...",
  "tgmini.creatingWallet": "Creando wallet",
  "tgmini.registeringSigner": "Registrando firmante",
  "tgmini.readyToTrade": "Listo para operar",
  "tgmini.setupFailed": "Configuración fallida",
  "tgmini.tryAgain": "Reintentar",
  "tgmini.completeSetup": "Completar configuración",
  "tgmini.completeSetupDesc": "Tu wallet está lista, pero necesitamos terminar de configurar las credenciales de trading.",
  "tgmini.enableTrading": "Activar trading",
  "tgmini.enableTradingDesc": "Completa la configuración inicial para empezar a operar en Polymarket.",
  "tgmini.gaslessSetup": "Configuración sin gas (pagamos las fees)",
  "tgmini.securePrivy": "Firma segura con Privy TEE",
  "tgmini.takes30s": "Tarda ~30 segundos",
  "tgmini.fundCard": "Recargar con tarjeta",
  "tgmini.fundCardDesc": "Compra USDC directamente con tu tarjeta de crédito o débito.",
  "tgmini.instantPurchase": "Compra instantánea",
  "tgmini.sentToWallet": "Enviado a tu wallet en Polygon",
  "tgmini.poweredPrivy": "Powered by Privy",
  "tgmini.buyUsdc": "Comprar USDC",
  "tgmini.close": "Cerrar",
  "tgmini.logout": "Cerrar sesión",
  "tgmini.logoutDesc": "¿Seguro que quieres cerrar sesión? Tendrás que volver a iniciar sesión para usar FrenFlow.",
  "tgmini.confirmLogout": "Confirmar cierre de sesión",
  "tgmini.cancel": "Cancelar",
  "tgmini.loggedOut": "Sesión cerrada",
  "tgmini.loggedOutDesc": "Has cerrado sesión correctamente.",
  "tgmini.openingWallet": "Abriendo ajustes de wallet...",
  "tgmini.errorTitle": "Error",
  "tgmini.openFromTg": "Por favor, abre esto desde Telegram",
  "tgmini.debugLog": "Log de depuración ({n} entradas)",

  // ── Profile widget (logged-out hero card) ──────────────────────────
  "profileWidget.liveBeta": "En vivo · Beta abierta",
  "profileWidget.headlineTop": "El nuevo hogar de",
  "profileWidget.headlineBottom": "los mercados de predicción.",
  "profileWidget.kpi": "Opera Polymarket y Kalshi · Copia a los mejores traders en <1s · Autocustodia.",
  "profileWidget.cta": "Empezar",
  "profileWidget.trust": "Asegurado por Privy",

  // ── Price Alert modal ──────────────────────────────────────────────
  "priceAlert.title": "Alerta de precio",
  "priceAlert.loadingAlerts": "Cargando alertas...",
  "priceAlert.activeAlerts": "Alertas activas ({count})",
  "priceAlert.createNewAlert": "Crear nueva alerta",
  "priceAlert.outcome": "Resultado",
  "priceAlert.yes": "Sí",
  "priceAlert.no": "No",
  "priceAlert.alertWhen": "Avisar cuando el precio esté",
  "priceAlert.above": "Por encima",
  "priceAlert.below": "Por debajo",
  "priceAlert.targetPrice": "Precio objetivo",
  "priceAlert.helperAbove": "Actual: {current}¢ · Rango: {min}¢ - 99¢",
  "priceAlert.helperBelow": "Actual: {current}¢ · Rango: 1¢ - {max}¢",
  "priceAlert.errorRange": "El precio debe estar entre 1¢ y 99¢",
  "priceAlert.errorTargetAbove": "El objetivo debe ser superior al precio actual ({current}¢)",
  "priceAlert.errorTargetBelow": "El objetivo debe ser inferior al precio actual ({current}¢)",
  "priceAlert.errorCreate": "No se pudo crear la alerta",
  "priceAlert.errorTimeout": "Tiempo de espera agotado. Inténtalo de nuevo.",
  "priceAlert.success": "¡Alerta creada! Te avisaremos cuando el precio alcance tu objetivo.",
  "priceAlert.creating": "Creando...",
  "priceAlert.createAlert": "Crear alerta",

  // ── Slippage warning (shared, market + sports) ─────────────────────
  "trade.slippageWarn.title": "Tolerancia de slippage alta",
  "trade.slippageWarn.body": "Tu configuración de {pct}% permite que esta orden se rellene a cualquier precio hasta {maxCents}¢, muy por encima de los {shownCents}¢ mostrados.",
  "trade.slippageWarn.cta": "Bajar en Settings",

  // ── Trading Panel (sidebar) ────────────────────────────────────────
  "tradingPanel.placeTrade": "Operar",
  "tradingPanel.resolution": "Resolución",
  "tradingPanel.modeMarket": "Mercado",
  "tradingPanel.modeLimit": "Límite",
  "tradingPanel.tradeSettings": "Ajustes de la operación",
  "tradingPanel.slippageSettings": "Ajustes de slippage",
  "tradingPanel.slippageAutoHint": "El slippage se adapta a la liquidez del mercado en cada trade. Puede llegar al 50% en mercados con poca liquidez.",
  "tradingPanel.slippage": "Slippage",
  "tradingPanel.slippageCustom": "Personalizado",
  "tradingPanel.slippageCustomAria": "Porcentaje de slippage personalizado",
  "tradingPanel.slippageError": "No se pudo actualizar el slippage",
  "tradingPanel.slippageHint.polymarket": "Desviación máxima de precio en órdenes de mercado antes de rechazar la ejecución. Mayor = más probabilidad de llenar en mercados volátiles.",
  "tradingPanel.slippageHint.hyperliquid": "Desviación máxima al alza en órdenes IOC de HIP-4 antes de que HL rechace la ejecución. Mayor = más probabilidad de llenar en spreads volátiles.",
  "tradingPanel.slippageHint.kalshi": "Desviación máxima de precio en órdenes de mercado de Kalshi antes de revertir el swap. Mayor = más probabilidad de llenar con libros finos.",
  "tradingPanel.slippageHint.predict": "Desviación máxima de precio aceptada en órdenes de mercado de Predict antes de rechazar la orden.",
  "tradingPanel.orderType": "Tipo de orden",
  "tradingPanel.orderTypeError": "No se pudo actualizar el tipo de orden",
  "tradingPanel.fak.title": "Fill and Kill",
  "tradingPanel.fak.desc": "Llena lo que haya disponible ahora y cancela el resto.",
  "tradingPanel.fok.title": "Fill or Kill",
  "tradingPanel.fok.desc": "Llena la orden completa o se cancela del todo.",

  // ── Amount Field ───────────────────────────────────────────────────
  "amountField.amount": "Cantidad",
  "amountField.amountSlider": "Control deslizante de cantidad",
  "amountField.max": "Máx",
  "amountField.editQuickAmounts": "Editar cantidades rápidas",
  "amountField.needMoreFunds": "Te faltan fondos — haz clic para ver detalles",
  "amountField.notEnoughShares": "No tienes suficientes shares",
  "amountField.notEnoughSharesDesc": "Solo tienes {max} shares. Limitando a {target}.",
  "amountField.thisAmount": "esta cantidad",
  "amountField.sharesUnit": "{n} shares",
  "amountField.exceedsPosition": "Excede tu posición · máx {max} shares",
  "amountField.insufficientBalance": "Saldo insuficiente · te faltan ${diff}",
  "amountField.balanceTooltip": "Saldo: ${max}",
  "amountField.sharesToSell": "Shares a vender",
  "amountField.sharesUnitLabel": "Shares",
  "amountField.onlyAvailable": "Solo hay {n} shares disponibles",
  "positionImpact.small": "Operación pequeña",
  "positionImpact.reasonable": "Tamaño razonable",
  "positionImpact.large": "Grande para tu saldo",
  "positionImpact.major": "Posición enorme — ¿seguro?",

  // ── Profit Fantasy ─────────────────────────────────────────────────
  "profitFantasy.youReceive": "Recibirás",
  "profitFantasy.potentialWin": "Ganancia potencial",
  "profitFantasy.moonshot": "Moonshot",

  // ── Smart Money inline pill ────────────────────────────────────────
  "smartMoney.pillLabel": "Smart $",
  "smartMoney.pillWallets": "wallets",
  "smartMoney.flip": "Cambiar →",
  "smartMoney.match": "Igualar ${n}",

  // ── Live Trade Ticker ──────────────────────────────────────────────
  "liveTicker.bought": "compró",
  "liveTicker.sold": "vendió",
  "liveTicker.anon": "anon",
  "liveTicker.agoSeconds": "hace {n}s",
  "liveTicker.agoMinutes": "hace {n}m",
  "liveTicker.agoHours": "hace {n}h",

  // ── Sell Context ───────────────────────────────────────────────────
  "sellContext.dontOwn": "No tienes shares de {outcome} en este mercado",
  "sellContext.youOwn": "Tienes",

  // ── Primary CTA ────────────────────────────────────────────────────
  "primaryCTA.marketClosed": "Mercado cerrado",
  "primaryCTA.signIn": "Iniciar sesión",
  "primaryCTA.placingOrder": "Enviando orden…",
  "primaryCTA.retry": "Reintentar",
  "trade.polymarketIssue": "Polymarket está teniendo problemas ahora mismo — las órdenes pueden ir lentas o fallar. Tus fondos están seguros (no se cobró nada). Reinténtalo en unos minutos.",
  "trade.polymarketStatusLink": "Estado de Polymarket ↗",
  "primaryCTA.enterAmount": "Introduce una cantidad",
  "primaryCTA.setLimitPrice": "Pon un precio límite",
  "primaryCTA.exceedsPosition": "Excede la posición",
  "primaryCTA.addMore": "Añade ${diff} más",
  "primaryCTA.limitPrefix": "Límite ",
  "primaryCTA.sellAction": "Vender",
  "primaryCTA.buyAction": "Comprar",

  // ── My Positions ───────────────────────────────────────────────────
  "myPositions.title": "Mis posiciones",
  "myPositions.startTrading": "Empezar a operar",
  "myPositions.noPositions": "Aún no tienes posiciones",
  "myPositions.invested": "Invertido",
  "myPositions.value": "Valor",
  "myPositions.resolving": "Resolviendo",
  "myPositions.waitingSettlement": "Esperando liquidación",
  "myPositions.readyToClaim": "+${n} listos para reclamar",
  "myPositions.claim": "Reclamar",
  "myPositions.won": "Ganado",
  "myPositions.lost": "Perdido",
  "myPositions.pending": "Pendiente",
  "myPositions.sell": "Vender",
  "myPositions.share": "Compartir",
  "myPositions.net": "Neto",
  "myPositions.shares": "Shares",
  "myPositions.pnl": "PnL",
  "myPositions.cost": "Coste ${n}",

  // ── Open Orders ────────────────────────────────────────────────────
  "openOrders.title": "Órdenes abiertas",
  "openOrders.cancelling": "Cancelando...",
  "openOrders.cancelAll": "CANCELAR TODO",
  "openOrders.noOpen": "Sin órdenes abiertas",
  "openOrders.col.market": "Mercado",
  "openOrders.col.side": "Lado",
  "openOrders.col.outcome": "Resultado",
  "openOrders.col.price": "Precio",
  "openOrders.col.filled": "Llenado",
  "openOrders.col.total": "Total",
  "openOrders.cancelOrder": "Cancelar orden",
  "openOrders.earningRewards": "Esta orden está ganando recompensas de liquidez de Polymarket",
  "openOrders.validUntilCancelled": "Todas las órdenes son válidas hasta cancelarlas",

  // ── Scenario Bar ───────────────────────────────────────────────────
  "scenarioBar.ifYes": "Si SÍ",
  "scenarioBar.ifNo": "Si NO",

  // ── Sports Event page ──────────────────────────────────────────────
  "sportsEvent.loadingChart": "Cargando gráfico...",
  "sportsEvent.live": "En vivo",
  "sportsEvent.final": "Final",
  "sportsEvent.livestream": "Retransmisión",
  "sportsEvent.notHeadToHead": "Este mercado no es un enfrentamiento directo.",
  "sportsEvent.whale": "BALLENA",
  "sportsEvent.smart": "SMART",
  "sportsEvent.viewOnPolymarket": "Ver en Polymarket",
  "sportsEvent.watchStream": "Ver retransmisión",
  "sportsEvent.killTotals": "Total de bajas",
  "sportsEvent.totalGames": "Total",
  "sportsEvent.setPriceAlert": "Crear alerta de precio",
  "sportsEvent.alert": "Alerta",

  // ── Sports Order Book ──────────────────────────────────────────────
  "sportsOrderBook.title": "Libro de órdenes",
  "sportsOrderBook.mid": "Medio",
  "sportsOrderBook.midLabel": "MEDIO",
  "sportsOrderBook.spread": "Spread",
  "sportsOrderBook.colPrice": "Precio",
  "sportsOrderBook.colSize": "Tamaño",
  "sportsOrderBook.colTotal": "Total ($)",
  "sportsOrderBook.noAsks": "Sin asks",
  "sportsOrderBook.noBids": "Sin bids",

  // ── Sports Hero ────────────────────────────────────────────────────
  "sportsHero.vol": "Vol",
  "sportsHero.liquidity": "Liquidez",
  "sportsHero.whaleTooltip": "Mercado ballena: {wallets} wallets smart · ${vol}M de volumen smart",
  "sportsHero.smartTooltip": "Mercado con mucho smart money: {wallets} wallets smart lo siguen",
  "sportsHero.kalshiAlsoExact": "También se opera en Kalshi (coincidencia exacta)",
  "sportsHero.kalshiAlsoRelated": "También se opera en Kalshi (relacionado)",
  "sportsHero.kalshiMore": "+{n} más",
  "sportsHero.addWatchlist": "Añadir a watchlist",
  "sportsHero.removeWatchlist": "Quitar de watchlist",
  "sportsHero.week": "Semana {n}",
  "sportsHero.vs": "VS",
  "sportsHero.liveBadge": "EN VIVO",
  "sportsHero.setLabel": "Set {n}: {score}",
  "sportsHero.setCurrent": "Set {n}",
  "sportsHero.sets": "Sets {home}–{away}",
  "sportsHero.game": "Juego {n}",
  "sportsHero.bestOf": "Al mejor de {n}",
  "sportsHero.map": "Mapa {home}–{away}",
  "sportsHero.series": "Serie {home}–{away}",
  "sportsHero.gameWord": "Juego",
  "sportsHero.mapWord": "Mapa",

  // ── Period long names (sports) ────────────────────────────────────
  "period.FT": "Tiempo completo",
  "period.VFT": "Tiempo completo (tras prórroga)",
  "period.HT": "Descanso",
  "period.ET": "Prórroga",
  "period.AET": "Tras prórroga",
  "period.PEN": "Penaltis",
  "period.Q1": "1er cuarto",
  "period.Q2": "2º cuarto",
  "period.Q3": "3er cuarto",
  "period.Q4": "4º cuarto",
  "period.OT": "Prórroga",
  "period.1H": "1ª parte",
  "period.2H": "2ª parte",
  "period.P1": "1er periodo",
  "period.P2": "2º periodo",
  "period.P3": "3er periodo",

  // ── Sports Market Labels (cleanMarketLabel) ───────────────────────
  "marketLabel.moneyline": "Moneyline",
  "marketLabel.totalGames": "Total Juegos",
  "marketLabel.totalMaps": "Total Mapas",
  "marketLabel.totalPrefix": "Total {what}",
  "marketLabel.mapHandicap": "Hándicap de mapas",
  "marketLabel.gameHandicap": "Hándicap de juegos",
  "marketLabel.spread": "Spread",
  "marketLabel.spreadValue": "Spread {value}",
  "marketLabel.totalCorners": "Total de córners",
  "marketLabel.firstBlood": "Primera sangre",
  "marketLabel.killTotalsLabel": "Total de bajas",
  "marketLabel.killTotalsValue": "Total de bajas {value}",
  "marketLabel.bothTeamsToScore": "Ambos equipos marcan",
  "marketLabel.bothTeamsBaron": "Ambos equipos matan al Barón",
  "marketLabel.bothTeamsDragon": "Ambos equipos matan al Dragón",
  "marketLabel.bothTeamsInhibitors": "Ambos equipos destruyen inhibidores",
  "marketLabel.oddEvenKills": "Total de bajas par/impar",
  "marketLabel.pentaKill": "Algún jugador hace penta kill",
  "marketLabel.quadraKill": "Algún jugador hace quadra kill",
  "marketLabel.firstSetWinner": "Ganador del 1er set",
  "marketLabel.secondSetWinner": "Ganador del 2º set",
  "marketLabel.thirdSetWinner": "Ganador del 3er set",
  "marketLabel.setWinner": "Ganador del set",
  "marketLabel.matchWinner": "Ganador del partido",
  "marketLabel.gameWinner": "Ganador del juego",
  "marketLabel.gameNWinner": "Ganador del juego {n}",
  "marketLabel.mapNWinner": "Ganador del mapa {n}",
  "marketLabel.nrfi": "Sin carrera en la 1ª entrada",
  "marketLabel.yrfi": "Carrera en la 1ª entrada",
  "marketLabel.oddEvenRounds": "Total de rondas par/impar",

  // ── Sidebar OrderBook (market/[slug]/components/OrderBook) ────────
  "orderBook.title": "Libro de órdenes",
  "orderBook.loading": "Cargando libro...",
  "orderBook.trade": "Operar {label}",
  "orderBook.colPrice": "Precio",
  "orderBook.colShares": "Shares",
  "orderBook.colTotal": "Total",
  "orderBook.asks": "Asks",
  "orderBook.bids": "Bids",
  "orderBook.last": "Último:",
  "orderBook.spread": "Spread:",
  "orderBook.levels": "{n} niveles",
  "orderBook.rewardsBand": "banda de recompensas",
  "orderBook.rewardsBandTooltip": "Banda de recompensas {low}¢ – {high}¢ · pool de ${rate}/día",
  "orderBook.liveUpdates": "Actualizaciones en vivo",

  // ── Player Profile (sports/players/[league]/[slug]) ───────────────
  "playerProfile.back": "Atrás",
  "playerProfile.live": "En vivo",
  "playerProfile.vs": "vs",
  "playerProfile.unknown": "Desconocido",
  "playerProfile.hot": "HOT",
  "playerProfile.hotTooltip": "Igualado {pct}/100 — cerca del 50/50, se espera mucha actividad",
  "playerProfile.playerCountOne": "{n} jugador",
  "playerProfile.playerCountMany": "{n} jugadores",
  "playerProfile.tile.tradingVolume": "Volumen operado",
  "playerProfile.tile.activeMarkets": "Mercados activos",
  "playerProfile.tile.winRate": "Aciertos",
  "playerProfile.tile.record": "Récord",
  "playerProfile.tile.nationality": "Nacionalidad",
  "playerProfile.ai.heading": "Análisis del enfrentamiento",
  "playerProfile.ai.readMore": "Leer análisis completo",
  "playerProfile.ai.hide": "Ocultar análisis",
  "playerProfile.ai.disclaimer": "Generado a partir de operaciones on-chain, resultados recientes y el historial cara a cara. No es asesoramiento financiero.",
  "playerProfile.matchHistory.title": "Historial de partidos",
  "playerProfile.matchHistory.colDate": "Fecha",
  "playerProfile.matchHistory.colMatch": "Partido",
  "playerProfile.matchHistory.colScore": "Marcador",
  "playerProfile.matchHistory.colTournament": "Torneo",
  "playerProfile.upcoming.title": "Próximos",
  "playerProfile.upcoming.empty": "No hay próximos partidos programados.",
  "playerProfile.more.title": "Más de {league}",
  "playerProfile.roster.title": "Plantilla",
  "playerProfile.roster.active": "{n} activos",
  "playerProfile.roster.colPlayer": "Jugador",
  "playerProfile.roster.colName": "Nombre",
  "playerProfile.roster.colRole": "Rol",
  "playerProfile.roster.colCountry": "País",
  "playerProfile.showLess": "Ver menos",
  "playerProfile.loadMore": "Ver más ({n})",
  "playerProfile.time.inMinutes": "En {n} min",
  "playerProfile.time.inHours": "En {n} h",
  "playerProfile.time.today": "Hoy {time}",
  "playerProfile.time.tomorrow": "Mañana {time}",
  "playerProfile.about.title": "Acerca de {name}",
  "playerProfile.about.p1": "{name} {ticker} es jugador profesional de {league} y se sigue en FrenFlow con cuotas en vivo, contexto cara a cara y análisis del enfrentamiento asistido por IA, todo a partir de mercados de predicción on-chain.",
  "playerProfile.about.p2": "Sigue el próximo partido de {name}, sus resultados recientes y las cuotas del mercado en esta página. FrenFlow agrega datos en vivo de Polymarket junto con nuestro propio scoring de IA, para que los precios reflejen lo que el mercado realmente piensa — no las cuotas estáticas de las casas.",
  "playerProfile.faq.title": "Preguntas frecuentes",
  "playerProfile.faq.q1": "¿Qué mercados de {league} hay disponibles para {name} en FrenFlow?",
  "playerProfile.faq.a1": "Cada partido que juega {name} abre un mercado moneyline (ganador) y un conjunto de mercados derivados — ganadores de set, total de juegos, resultado exacto y líneas over/under. Todos resuelven en el mismo contrato on-chain de Polymarket, por lo que los precios se mantienen ajustados y las operaciones liquidan al instante.",
  "playerProfile.faq.q2": "¿Cómo funcionan los mercados de predicción de {league} en FrenFlow?",
  "playerProfile.faq.a2": "Cada mercado es un par binario \"Sí/No\" u \"Over/Under\". Compras shares del lado que crees que se resolverá como cierto; si aciertas, cada share paga 1$, y la diferencia entre tu precio de compra y el pago es tu beneficio. La liquidez está en libros de órdenes on-chain compartidos, así que el precio que ves es el precio al que operas.",
  "playerProfile.faq.q3": "¿Cómo se resuelven los mercados de {name}?",
  "playerProfile.faq.a3": "Los mercados se resuelven según los resultados oficiales de {league}. El lado ganador cobra automáticamente en cuanto el partido es final y la fuente de resolución confirma el resultado — sin reclamaciones ni acrobacias de plazos.",
  "playerProfile.faq.q4": "¿Por qué son fiables las cuotas de {league} en FrenFlow?",
  "playerProfile.faq.a4": "Porque hay dinero real en juego. Los traders ajustan los precios en tiempo real cuando aparecen noticias — lesiones, cambios de superficie, cambios de momentum. FrenFlow muestra esos precios junto a un análisis del enfrentamiento con IA para que puedas contrastar el mercado con las señales subyacentes.",
  "playerProfile.faq.q5": "¿Qué factores influyen en las cuotas de {name}?",
  "playerProfile.faq.a5": "La forma reciente, el historial cara a cara, la preferencia de superficie, el estado físico, la dificultad del cuadro y las noticias en vivo (retiradas, walkovers, clima). Cada nueva información mueve el libro de órdenes en segundos.",
  "playerProfile.faq.q6": "¿Puedo seguir las cuotas y resultados de {name} sin operar?",
  "playerProfile.faq.a6": "Sí — puedes añadir cualquier mercado a tu watchlist con el botón de marcador y mantendremos visibles los precios y resultados sin necesidad de operar. Configura alertas de precio desde el modal de trading si quieres recibir notificación cuando un outcome alcance un objetivo.",
  "playerProfile.faq.q7": "¿Cómo empiezo a operar en partidos de {name}?",
  "playerProfile.faq.a7": "Conecta una wallet, deposita USDC en tu cuenta de trading de FrenFlow y pulsa cualquier outcome en esta página o en la página del partido. El modal de trading gestiona los approvals automáticamente — no necesitas salir de la app.",
  "playerProfile.faq.q8": "¿Qué significa un precio de 65¢ para \"{ticker}\"?",
  "playerProfile.faq.a8": "El mercado está fijando una probabilidad implícita del 65% de que {ticker} gane el outcome resuelto. Compra a 65¢, gana 35¢ si se resuelve a tu favor, pierde 65¢ si no. El mercado es la probabilidad consenso entre todos los traders activos.",
  "playerProfile.faq.q9": "¿Qué es FrenFlow?",
  "playerProfile.faq.a9": "FrenFlow es una plataforma social de mercados de predicción construida sobre Polymarket. Reunimos en un único sitio la liquidez on-chain más profunda, copy trading, análisis de enfrentamientos con IA y un UX de trading limpio — para deportes, crypto, política y cultura.",

  // ── Wallet onboarding + migración + retiro ────────────────────────
  "onboarding.close": "Cerrar",
  "onboarding.setup.title": "Configurando tu wallet",
  "onboarding.setup.waitingForWallet": "Creando tu wallet segura...",
  "onboarding.setup.fallbackMsg": "Un momento…",
  "onboarding.setup.step.creatingWallet": "Creando wallet",
  "onboarding.setup.step.authorizing": "Autorizando",
  "onboarding.setup.step.finalTouches": "Últimos retoques",
  "onboarding.fund.title": "Wallet lista",
  "onboarding.fund.subtitle": "Tu cartera de trading: añade fondos para empezar.",
  "onboarding.fund.acceptedTokens": "Solo en la red Polygon. Envía USDC / USDC.e / pUSD.\nOtros tokens u otras redes se pierden para siempre.",
  "onboarding.fund.addressLabel": "Tu dirección",
  "onboarding.fund.copy": "Copiar dirección",
  "onboarding.fund.copied": "Copiada",
  "onboarding.fund.methodsLabel": "Tienes varias formas de fondear tu wallet — elige una:",
  "onboarding.fund.haveCrypto": "¿Ya tienes cripto? Recibe o haz bridge",
  "onboarding.fund.haveCryptoTitle": "¿Ya tienes cripto?",
  "onboarding.fund.haveCryptoSub": "Recibe o haz bridge",
  "onboarding.fund.networkWarn": "Solo red Polygon.",
  "onboarding.fund.networkWarnRest": "Enviar desde Ethereum, BSC, Arbitrum o cualquier otra red = fondos perdidos para siempre.",
  "onboarding.fund.waiting": "Esperando fondos…",
  "onboarding.fund.fundLater": "Depositaré después",
  "onboarding.fund.intentLeader": "Sigues a {name} — añade fondos para copiar su próximo trade.",
  "onboarding.fund.intentMarket": "Añade fondos para tomar posición en «{name}».",
  "onboarding.funded.title": "Ya tienes fondos.",
  "onboarding.funded.amountReady": "{amount} listos para operar.",
  "onboarding.funded.cta1": "Haz tu primera predicción.",
  "onboarding.funded.browse": "Ver mercados",
  "onboarding.funded.stay": "Quedarme aquí",
  "onboarding.error.title": "Configuración fallida",
  "onboarding.error.fallback": "Algo ha ido mal. ¿Reintentar?",
  "onboarding.error.tryAgain": "Reintentar",
  "onboarding.error.reportIssue": "Reportar incidencia",
  "onboarding.error.persistentHint": "Si sigue fallando, recarga la página y avísanos por feedback.",

  "migration.intro.title": "Actualización de wallet",
  "migration.intro.subtitle": "Polymarket ha actualizado el sistema de wallets. Configuramos tu nueva wallet y movemos tus fondos en 30 segundos.",
  "migration.intro.note": "Las posiciones abiertas se quedan en tu wallet actual — puedes venderlas o redimirlas como siempre. Solo se mueve el efectivo.",
  "migration.intro.cta": "Empezar migración",
  "migration.intro.helper": "Necesario para seguir operando. Sin gas, ~30 segundos.",
  "migration.deploying.title": "Configurando tu nueva wallet",
  "migration.deploying.fallbackMsg": "Un momento…",
  "migration.deploying.dontClose": "No cierres esta pestaña.",
  "migration.confirm.title": "Nueva wallet lista",
  "migration.confirm.subtitleWithCash": "Mueve tus {amount} a la nueva wallet para seguir operando.",
  "migration.confirm.subtitleEmpty": "Tu Safe está vacía — completa la migración para empezar a operar.",
  "migration.confirm.row.pusd": "pUSD",
  "migration.confirm.row.usdce": "USDC.e",
  "migration.confirm.row.total": "Total a mover",
  "migration.confirm.newWalletLabel": "Tu nueva wallet",
  "migration.confirm.copy": "Copiar dirección",
  "migration.confirm.copied": "Copiada",
  "migration.confirm.warn": "Las posiciones abiertas se quedan en tu wallet actual (puedes venderlas/redimirlas con normalidad). Ahora solo se mueve el efectivo.",
  "migration.confirm.ctaMove": "Mover {amount} a la nueva wallet",
  "migration.confirm.ctaComplete": "Completar migración",
  "migration.moving.title": "Moviendo tus fondos",
  "migration.moving.subtitle": "Transfiriendo on-chain — unos 30 segundos.",
  "migration.moving.dontClose": "No cierres esta pestaña.",
  "migration.done.title": "Migración completada",
  "migration.done.amountReady": "{amount} listos en tu nueva wallet.",
  "migration.done.emptyReady": "Tu nueva wallet está lista. Opera con normalidad.",
  "migration.done.continue": "Continuar",
  "migration.error.title": "Migración fallida",
  "migration.error.fallback": "Algo ha ido mal. ¿Reintentar?",
  "migration.error.safeNote": "Tus fondos están seguros — siguen en tu wallet original.",
  "migration.error.tryAgain": "Reintentar",

  "polyWithdraw.title": "Retirar de Polymarket",
  "polyWithdraw.submitted.title": "Retiro enviado",
  "polyWithdraw.submitted.subtitle": "USDC.e está en camino. Confirmación en ~30 segundos en Polygon.",
  "polyWithdraw.viewTx": "Ver en Polygonscan ↗",
  "polyWithdraw.intro": "Envía USDC.e desde tu wallet de trading a cualquier dirección de Polygon. Si tienes pUSD se desenvuelve automáticamente en la misma transacción. Llega en ~30 segundos.",
  "polyWithdraw.feeLine.lead": "FrenFlow cobra 0%",
  "polyWithdraw.feeLine.rest": "en retiros. El gas de la red lo paga el relayer (sin gas para ti).",
  "polyWithdraw.row.usdce": "USDC.e (libre)",
  "polyWithdraw.row.pusd": "pUSD (auto-unwrap)",
  "polyWithdraw.row.available": "Disponible para retirar",
  "polyWithdraw.fields.destLabel": "Destino en Polygon",
  "polyWithdraw.fields.destHelp": "Por defecto tu EOA de Privy. Cambia para enviar a un CEX u otra wallet.",
  "polyWithdraw.fields.amountLabel": "Importe (USD)",
  "polyWithdraw.fields.max": "Máx ({amount})",
  "polyWithdraw.fields.amountHelp": "Mín $1 · Máx $5.000 por retiro · Límite diario $10.000",
  "polyWithdraw.submitting": "Enviando…",
  "polyWithdraw.submittingMsg": "Enviando retiro…",
  "polyWithdraw.cta": "Retirar",
  "polyWithdraw.error.positive": "Introduce un importe positivo",
  "polyWithdraw.error.min": "El retiro mínimo es $1",
  "polyWithdraw.error.max": "El retiro máximo es $5.000 por transacción",
  "polyWithdraw.error.invalidDest": "Dirección de destino inválida",
  "polyWithdraw.error.balanceNotLoaded": "Saldo no cargado todavía — vuelve a intentarlo",
  "polyWithdraw.error.maxWithdrawable": "Máximo a retirar: {amount}",
  "polyWithdraw.error.fallback": "Retiro fallido ({status})",
  "market.chance": "probabilidad",
  "market.outcome": "Resultado",
  "market.volume": "Volumen",
  "market.liquidity": "Liquidez",
  "market.buy": "Comprar",
  "market.goToLiveMarket": "Ir al mercado en vivo",
  "placeTrade.resolvedWinner": "Resuelto · Ganador",
  "placeTrade.redeemPayout": "Los tenedores de esta opción pueden canjear sus shares por $1,00 cada una.",
  "placeTrade.awaitingResolution": "Esperando resolución",
  "placeTrade.verifyingOutcome": "Verificando resultado",
  "placeTrade.verifyingOutcomeDesc": "Trading cerrado. La fuente de resolución está determinando el ganador — normalmente se resuelve en menos de un minuto.",
  "chartError.failedToLoad": "No se pudo cargar {chart}",
  "chartError.failedToLoadGeneric": "El gráfico no se pudo cargar",
  "chartError.retry": "Reintentar",
  "chartError.pnl": "el P&L",
  "chartError.traderPnl": "el P&L del trader",
  "common.loadingChart": "Cargando gráfico…",
  "common.loadingTrades": "Cargando operaciones…",
  "common.loadingOrderbook": "Cargando orderbook…",
  "common.live": "En vivo",
  "common.view": "Ver",
  "common.viewAll": "Ver todos",
  "common.viewAllOutcomes": "Ver todos los resultados",
  "marketHero.resolved": "RESUELTO",
  "marketHero.active": "ACTIVO",
  "marketStatus.resolved": "RESUELTO",
  "marketChart.priceChart": "Gráfico de precio",
  "marketChart.priceHistory": "Historial de precios",
  "marketChart.historical": "Histórico",
  "marketResolution.rules": "Reglas de resolución",
  "marketResolution.umaResolution": "Resolución UMA",
  "rewardsBadge.youEarnedToday": "Ganaste hoy",
  "rewardsBadge.makerRebatePortion": "Porción de rebate maker",
  "rewardsBadge.placeLimitOrderHint": "Coloca una orden límite dentro de la banda del spread para empezar a ganar.",
  "tradingPanel.tradeError": "Error de operación",
  "tradingPanel.oneClickHint": "Opera con un clic, sin modales",
  "portfolio.balance.available": "Disponible",
  "portfolio.balance.open": "Abierto",
  "portfolio.balance.claim": "Por reclamar",
  "portfolio.balance.total": "Saldo total",
  "portfolio.balance.inPositions": "En posiciones",
  "portfolio.balance.toClaim": "Por reclamar",
  "activity.categories": "Categorías",
  "activity.loadingMore": "Cargando más…",
  "activity.tradeCall.title": "Nueva Trade Call",
  "activity.tradeCall.subtitle": "Comparte tu alpha con la comunidad",
  "activity.shareToEarn.title": "Comparte y gana",
  "activity.shareToEarn.newBadge": "Nuevo",
  "activity.shareToEarn.earn": "Gana",
  "activity.shareToEarn.ofFees": "de las fees",
  "activity.shareToEarn.tagline.before": "Cuando alguien opera un mercado mediante",
  "activity.shareToEarn.tagline.yourPost": "tu post",
  "activity.shareToEarn.tagline.after": ", tú cobras.",
  "activity.shareToEarn.step1": "Publica una operación o tesis en cualquier mercado",
  "activity.shareToEarn.step2": "Tus frens descubren y operan mediante tu post",
  "activity.shareToEarn.step3": "Cobras el 10% de cada fee que paguen",
  "activity.shareToEarn.cta": "Opera y comparte",
  "activity.composer.placeholder": "¿Qué piensas? Comparte una opinión o trade…",
  "activity.composer.cta": "Publicar",
  "mostCopied.title": "Más copiados",
  "mostCopied.window24h": "24h",
  "mostCopied.noTraders": "No se encontraron traders",
  "mostCopied.copyButton": "Copiar",
  "activity.filter.following": "Siguiendo",
  "activity.viewToggle.expanded": "Vista expandida",
  "activity.viewToggle.compact": "Vista compacta",
  "activity.filter.all": "Todos",
  "activity.filter.trades": "Operaciones",
  "activity.filter.copies": "Copias",
  "activity.filter.watchlist": "Watchlist",
  "activity.filter.opinions": "Opiniones",
  "activity.filter.whales": "Ballenas",
  "activity.filter.fresh": "Wallets nuevas",
  "activity.filter.moves": "Movimientos",
  "activity.filter.new": "Nuevos",
  "activity.newMarket.label": "Nuevo mercado",
  "activity.newEvent.label": "Nuevo evento",
  "activity.move.pump": "Subida",
  "activity.move.dump": "Bajada",
  "activity.action.closed": "Cerrado",
  "activity.action.copied": "copió a",
  "activity.tier.mega_whale": "Mega Ballena",
  "activity.tier.whale": "Ballena",
  "activity.tier.dolphin": "Delfín",
  "activity.tier.fish": "Pez Grande",
  "activity.tier.fresh": "Wallet Nueva",
  "activity.badge.tracked": "En seguimiento",
  "activity.badge.copy": "Copia",
  "activity.badge.marketClosed": "Cerrado",
  "activity.empty.following.title": "Aún no sigues a ningún trader",
  "activity.empty.following.desc": "Sigue traders para ver su actividad aquí y copiar sus operaciones automáticamente.",
  "activity.empty.watchlist.title": "Aún no tienes favoritos",
  "activity.empty.watchlist.desc": "Marca traders con la estrella desde el leaderboard para seguir sus operaciones aquí.",
  "activity.empty.trades.title": "Sin operaciones todavía",
  "activity.empty.trades.desc": "Tus operaciones ejecutadas aparecerán aquí. Empieza a operar en cualquier mercado para ver tu historial.",
  "activity.empty.copies.title": "Sin copy trades todavía",
  "activity.empty.copies.desc": "Cuando sigas a un trader y este abra una operación, tus copias aparecerán aquí.",
  "activity.empty.opinions.title": "Sin opiniones todavía",
  "activity.empty.opinions.desc": "Comparte tus predicciones y análisis de mercado con la comunidad.",
  "activity.empty.whales.title": "Sin actividad de ballenas detectada",
  "activity.empty.whales.desc": "Las operaciones grandes de las top wallets aparecerán aquí cuando sucedan.",
  "activity.empty.moves.title": "Sin movimientos de mercado ahora",
  "activity.empty.moves.desc": "Los movimientos de precio significativos en los mercados aparecerán aquí.",
  "activity.empty.new.title": "Sin mercados nuevos",
  "activity.empty.new.desc": "Los nuevos mercados de predicción aparecerán aquí cuando se publiquen.",
  "activity.empty.default.title": "Sin actividad todavía",
  "activity.empty.default.desc": "La actividad aparecerá aquí a medida que suceda.",
  "activity.empty.browseTraders": "Explorar traders",
  "activity.empty.findTraders": "Encontrar traders que seguir",
  "activity.empty.exploreMarkets": "Explorar mercados",
  "activity.empty.createPost": "Crear publicación",
  "activity.empty.viewAll": "Ver toda la actividad",
  "createPost.yourAvatar": "Tu avatar",
  "createPost.greeting": "¡Hey {name}!",
  "createPost.whatsYourPrediction": "¿Cuál es tu predicción?",
  "createPost.shareYourTake": "Comparte tu opinión",
  "createPost.publicFeed": "Feed público",
  "createPost.placeholder.alpha": "Suelta tu alpha aquí…",
  "createPost.placeholder.market": "Creo que este mercado va a…",
  "createPost.placeholder.event": "Este evento va a ser una locura porque…",
  "createPost.pasteHint": "Pega un enlace de mercado para añadir una vista previa",
  "createPost.postButton": "Publicar",
  "activity.action.bought": "compró",
  "activity.action.sold": "vendió",
  "activity.group.justNow": "Justo ahora",
  "activity.group.minutesAgo": "Hace minutos",
  "activity.group.oneHourAgo": "Hace 1 hora",
  "activity.group.hoursAgo": "Hace {n} horas",
  "activity.group.yesterday": "Ayer",
  "activity.group.daysAgo": "Hace {n} días",
  "activity.group.lastWeek": "Semana pasada",
  "activity.group.older": "Anterior",
  "activity.copyButton.copy": "Copiar",
  "activity.copyButton.pause": "Pausar",
  "activity.copyButton.resume": "Reanudar",
  "activityDetail.post": "Publicación",
  "activityDetail.leader": "Líder",
  "activityDetail.copy": "Copia",
  "activityDetail.bought": "Compró",
  "activityDetail.sold": "Vendió",
  "activityDetail.postedIn": "Publicado en",
  "activityDetail.tradeThisMarket": "Operar este mercado",
  "activity.eventLabel.megaWhale": "MEGA BALLENA",
  "activity.eventLabel.whale": "BALLENA",
  "activity.eventLabel.dolphin": "DELFÍN",
  "activity.eventLabel.bigFish": "PEZ GORDO",
  "activity.eventLabel.trade": "OPERACIÓN",
  "activity.eventLabel.post": "PUBLICACIÓN",
  "activity.eventLabel.copy": "COPIA",
  "activity.eventLabel.win": "GANANCIA",
  "activity.eventLabel.loss": "PÉRDIDA",
  "activity.eventLabel.pump": "PUMP",
  "activity.eventLabel.dump": "DUMP",
  "activity.eventLabel.resolved": "RESUELTO",
  "activity.eventLabel.newMarket": "MERCADO NUEVO",
  "activity.eventLabel.follow": "SEGUIR",
  "activity.eventLabel.watchlist": "WATCHLIST",
  "activity.eventLabel.article": "ARTÍCULO",
  "comments.title": "Comentarios",
  "comments.sort.featured": "Destacados",
  "comments.sort.top": "Top",
  "comments.sort.recent": "Recientes",
  "comments.placeholder": "Comparte tus ideas…",
  "comments.signInPrompt": "Inicia sesión para unirte a la conversación",
  "comments.signIn": "Iniciar sesión",
  "comments.empty": "Aún no hay comentarios. ¡Sé el primero en compartir tus ideas!",
  "comments.cancel": "Cancelar",
  "comments.reply": "Responder",
  "comments.submitButton": "Comentar",
  "comments.kbdHint.before": "Pulsa",
  "comments.kbdHint.after": "para enviar",
  "marketCard.archived": "Archivado",
  "marketCard.closedHeyFren": "¡Eh fren, el mercado está cerrado!",
  "activity.reachedEnd": "Has llegado al final",
  "home.tape.boughtOn": "compró {outcome} en",
  "home.tape.soldOn": "vendió {outcome} en",
  "home.tape.copiedOn": "copió una operación en",
  "home.tape.shares": "shares",
  "install.title": "Instala FrenFlow",
  "install.subtitleMobile": "Opera mercados de predicción en cualquier sitio, en cualquier momento",
  "install.subtitleDesktop": "La mejor experiencia de trading en mercados de predicción",
  "install.button": "Instalar app",
  "install.maybeLater": "Quizá luego",
  "install.iosPrompt.before": "Pulsa",
  "install.iosPrompt.then": "luego",
  "install.iosPrompt.addToHome": "«Añadir a pantalla de inicio»",
  "cookies.message": "Usamos cookies para analizar el uso de la web y mejorar tu experiencia.",
  "cookies.policyLink": "Ver nuestra Política de cookies",
  "cookies.accept": "Aceptar",
  "uma.signInForFullAccess": "Inicia sesión para acceso completo",
  "copytrading.hero.badge.long": "Ejecución Block 0 — Copia operaciones antes de que lleguen a la blockchain",
  "copytrading.hero.badge.short": "Block 0 — Velocidad mempool",
  "copytrading.hero.headline.desktop": "Bot de Copy Trading para Polymarket",
  "copytrading.hero.headline.mobilePrefix": "Bot de Polymarket, copia como",
  "copytrading.hero.headline.desktopLikePrefix": "Auto-copia como",
  "copytrading.hero.rotating.desktop.1": "los top traders de Polymarket",
  "copytrading.hero.rotating.desktop.2": "estrategias ganadoras",
  "copytrading.hero.rotating.desktop.3": "predicciones rentables",
  "copytrading.hero.rotating.mobile.1": "un pro",
  "copytrading.hero.rotating.mobile.2": "los mejores",
  "copytrading.hero.subtitle": "El único bot de copy trading que ejecuta en el mismo bloque que el líder. Block 0. Sin retraso.",
  "copytrading.hero.ctaPrimary": "Empezar a copy tradear",
  "copytrading.hero.ctaSecondary": "Gana FREN Points",
  "copytrading.hero.subcopy": "No-custodial. Ejecución en el mismo bloque. Tus fondos siempre en tu wallet.",
  "copytrading.hero.rating.short": "valoración",
  "copytrading.hero.rating.long": "de traders en Polymarket",
  "stats.headline.before": "Confianza de los",
  "stats.headline.accent": "traders más listos",
  "stats.subtitle": "Únete a miles de traders que sacan beneficio del copy trading en Polymarket",
  "stats.footnote": "Cifras actualizadas en tiempo real. Todas las operaciones verificadas on-chain.",
  "stats.totalVolume.label": "Volumen total copiado",
  "stats.totalVolume.desc": "Operaciones ejecutadas a través de FrenFlow",
  "stats.activeTraders.label": "Traders activos",
  "stats.activeTraders.desc": "Usuarios copiando estrategias a diario",
  "stats.activeMarkets.label": "Mercados activos",
  "stats.activeMarkets.desc": "Eventos de Polymarket disponibles para operar",
  "stats.tradesExecuted.label": "Operaciones ejecutadas",
  "stats.tradesExecuted.desc": "Total de operaciones copiadas hasta hoy",
  "how.eyebrow": "Cómo funciona el copy trading en Polymarket",
  "how.headline.before": "Empieza a copy tradear en Polymarket en",
  "how.headline.accent": "4 pasos sencillos",
  "how.subtitle": "No hace falta experiencia. Configura tu bot de copy trading de Polymarket en menos de 5 minutos.",
  "how.bottomNote": "¿Listo para copiar a los top traders de Polymarket? Empieza tu camino en los mercados de predicción hoy.",
  "how.step1.title": "Regístrate y conecta",
  "how.step1.desc": "Crea tu cuenta FrenFlow en segundos con email o Telegram. Configuramos automáticamente una wallet no-custodial segura para Polymarket.",
  "how.step2.title": "Encuentra top traders",
  "how.step2.desc": "Explora nuestro leaderboard de Polymarket con los mejores traders ordenados por ROI, o pega cualquier dirección de wallet ballena para copiarla.",
  "how.step3.title": "Copia en Block 0",
  "how.step3.desc": "Tus operaciones se ejecutan en el mismo bloque que el líder — no varios bloques después. Las detectamos desde el mempool antes de que la transacción se mine.",
  "how.step4.title": "Sigue y gana",
  "how.step4.desc": "Monitoriza tus posiciones de Polymarket, sigue las analíticas de rendimiento y gana FREN points con cada copy trade.",
  "features.eyebrow": "Por qué FrenFlow",
  "features.headline.before": "El único bot de Polymarket con copy trading",
  "features.headline.after": "",
  "features.subtitle": "Otros bots copian operaciones varios bloques después. Nosotros copiamos en el mismo bloque — porque detectamos las operaciones antes de que se minen.",
  "features.f1.title": "Ejecución Block 0",
  "features.f1.highlight": "Mismo bloque que el líder",
  "features.f1.desc": "Tu copy trade aterriza en el mismo bloque que el original. No 1 bloque después, ni 5 — el mismo. Esa es la diferencia FrenFlow.",
  "features.f2.title": "Detección en mempool",
  "features.f2.highlight": "Antes de llegar a la cadena",
  "features.f2.desc": "Interceptamos las operaciones desde el mempool — antes de que se minen. Otros bots esperan la confirmación on-chain. Nosotros no esperamos.",
  "features.f3.title": "Whale tracking",
  "features.f3.desc": "Sigue y copia cualquier wallet ballena de Polymarket. Rastrea el smart money y replica las estrategias ganadoras de los mercados de predicción.",
  "features.f4.title": "No-custodial y seguro",
  "features.f4.desc": "Tus fondos se quedan en tu wallet. Nuestro bot de Polymarket ejecuta operaciones pero nunca custodia tu cripto. Transparencia total.",
  "topTraders.eyebrow": "Leaderboard de Polymarket",
  "topTraders.headline.before": "Copia a los",
  "topTraders.headline.accent": "mejores traders de Polymarket",
  "topTraders.subtitle": "Sigue wallets ballena y copia estrategias probadas en mercados de predicción con un solo clic. Analíticas de traders en tiempo real.",
  "topTraders.badge.topVolume": "Top Volumen",
  "topTraders.badge.risingStar": "Promesa",
  "topTraders.badge.consistent": "Consistente",
  "topTraders.totalVolume": "Volumen total",
  "topTraders.pnl": "PnL",
  "topTraders.markets": "Mercados",
  "topTraders.viewProfile": "Ver perfil",
  "topTraders.bottom.title": "Explora más traders de Polymarket",
  "topTraders.bottom.subtitle": "Explora nuestro leaderboard completo de Polymarket con más de 100 wallets ballena, analíticas de mercados de predicción y stats de copy trading",
  "topTraders.bottom.cta": "Ver leaderboard completo",
  "mobileShowcase.eyebrow": "El mejor bot de Telegram para Polymarket",
  "mobileShowcase.headline.before": "Una sola cuenta.",
  "mobileShowcase.headline.accent": "Web + Telegram",
  "mobileShowcase.headline.after": "sincronizadas.",
  "mobileShowcase.subtitle1": "Regístrate con Telegram y obtén acceso instantáneo en ambas plataformas. Misma wallet, mismos ajustes, mismos traders seguidos. Configura el copy trading en la web, ejecuta y monitoriza desde Telegram - todo se mantiene en sincronía.",
  "mobileShowcase.subtitle2": "Abre @FrenFlowBot, inicia sesión con tu cuenta de Telegram y todo tu setup de FrenFlow está listo. Sin configuración extra.",
  "mobileShowcase.faq1.q": "¿Los ajustes se sincronizan entre web y Telegram?",
  "mobileShowcase.faq1.a": "¡Sí! FrenFlow lo mantiene todo sincronizado. Sigue a un trader desde la web, aparece en Telegram. Cambia tu cantidad de stake desde Telegram, se actualiza en la web. Misma wallet, mismos ajustes, mismos traders seguidos - siempre sincronizado en tiempo real.",
  "mobileShowcase.faq2.q": "¿Cómo conecto mi cuenta de Telegram?",
  "mobileShowcase.faq2.a": "Simplemente regístrate o inicia sesión con Telegram como método de autenticación. Tu cuenta FrenFlow se vinculará automáticamente con tu Telegram, dándote acceso instantáneo a @FrenFlowBot con todos tus ajustes ya configurados.",
  "mobileShowcase.faq3.q": "¿Qué puedo hacer desde el bot de Telegram?",
  "mobileShowcase.faq3.a": "¡Todo! Consulta tu cartera y PnL, sigue/deja de seguir traders, pausa/reanuda copy trading, ajusta cantidades de stake, deposita y retira USDC, recibe notificaciones instantáneas de operaciones — copy trading completo en Polymarket desde tu móvil.",
  "saasShowcase.headline.before": "IA que",
  "saasShowcase.headline.accent": "opera por ti",
  "saasShowcase.subtitle": "Olvídate del caos de operar manualmente. La IA de FrenFlow analiza el rendimiento de los traders como un experto, predice movimientos ganadores y te ayuda a entender cómo va realmente tu cartera.",
  "saasShowcase.feature1.title": "Análisis automatizado de traders",
  "saasShowcase.feature1.desc": "La IA explica el rendimiento de los traders, win rates y patrones sin hojas de cálculo.",
  "saasShowcase.close": "Cerrar",
  "pricing.eyebrow": "Recompensas",
  "pricing.headline.before": "Múltiples formas de",
  "pricing.headline.accent": "ganar recompensas",
  "pricing.subtitle": "Opera en Polymarket, refiere a tus amigos y construye tu reputación para maximizar tus ganancias.",
  "pricing.frenpoints.name": "Gana FrenPoints",
  "pricing.frenpoints.subtitle": "Opera, comparte y sube de nivel",
  "pricing.frenpoints.f1": "0.1 FP por cada $1 operado en Polymarket",
  "pricing.frenpoints.f2": "0.5 FP por publicación con enlaces de mercados (3/día máx.)",
  "pricing.frenpoints.f3": "Daily Spin: gana 1–100 FP cada 24 horas",
  "pricing.frenpoints.f4": "Rachas de operaciones: $100+/día desbloquea hitos de FP",
  "pricing.frenpoints.cta": "Empezar a operar",
  "pricing.referral.name": "Programa de referidos",
  "pricing.referral.subtitle": "Gana con tu red",
  "pricing.referral.f1": "10% de comisión vitalicia sobre las fees de tus referidos",
  "pricing.referral.f2": "+10 FP de bonus cuando un referido alcance $100 de volumen",
  "pricing.referral.f3": "Dashboard en tiempo real: clics, registros y ganancias",
  "pricing.referral.f4": "Comparte vía X, Telegram o enlace de invitación personalizado",
  "pricing.referral.badge": "Gana ingresos pasivos",
  "pricing.referral.cta": "Obtener enlace de referido",
  "pricing.aura.name": "Aura y niveles",
  "pricing.aura.subtitle": "Aumenta tu reputación, multiplica tus ganancias",
  "pricing.aura.f1": "5 niveles de Aura con multiplicadores de hasta 1.25x",
  "pricing.aura.f2": "31 niveles con insignias: Bronce → Diamante",
  "pricing.aura.f3": "El cap diario de FP escala de 200 a 400 FP/día",
  "pricing.aura.f4": "Gana Aura operando, publicando y refiriendo",
  "pricing.aura.cta": "Ver niveles",
  "pricing.auraTiers.label": "Niveles de Aura:",
  "pricing.auraTiers.list": "Newbie (1.0x) → Fren (1.1x) → OG (1.15x) → Whale (1.2x) → Legend (1.25x)",
  "pricing.auraTiers.decay": "El Aura decae 0.5%/semana si estás inactivo durante 7+ días — mantente activo para conservar tu multiplicador",
  "pricing.ogPass.suffix": "111 pases genesis gratis para los early supporters",
  "testimonials.eyebrow": "Testimonios",
  "testimonials.headline.before": "Wall of",
  "testimonials.headline.accent": "Love",
  "testimonials.subtitle": "Traders de Polymarket que usan FrenFlow y la están petando con copy trading automatizado.",
  "testimonials.tradersCount": "500+ traders",
  "testimonials.role.cryptoTrader": "Trader de cripto",
  "testimonials.role.defiInvestor": "Inversor DeFi",
  "testimonials.role.polymarketPro": "Polymarket Pro",
  "testimonials.role.predictionMarketsTrader": "Trader de mercados de predicción",
  "testimonials.role.quantTrader": "Trader cuantitativo",
  "testimonials.role.riskManager": "Gestor de riesgo",
  "testimonials.role.formerManualTrader": "Ex-trader manual",
  "testimonials.role.professionalBettor": "Apostador profesional",
  "testimonials.r1.t1": "Seguir a los top traders con FrenFlow ha triplicado mis retornos en Polymarket. La IA acierta de forma consistente.",
  "testimonials.r1.t2": "Era escéptico con el copy trading, pero las analíticas de FrenFlow me convencieron. +47% en 2 meses.",
  "testimonials.r1.t3": "El enfoque no-custodial me convenció. Mis fondos nunca salen de mi wallet. Inteligente y seguro.",
  "testimonials.r1.t4": "La mejor herramienta para Polymarket que he usado. Configúralo y olvídate. El bot lo gestiona todo perfectamente.",
  "testimonials.r2.t1": "El setup tardó 3 minutos. Empecé a copiar operaciones de inmediato. ROI positivo desde el primer día.",
  "testimonials.r2.t2": "La función de diversificación de cartera es genial. Sigo a 5 traders con estrategias diferentes.",
  "testimonials.r2.t3": "Probé el trading manual durante meses. La IA de FrenFlow lo hace mejor. Cambiar fue la mejor decisión.",
  "testimonials.r2.t4": "Copia en tiempo real, cero lag. La ejecución es impecable. Súper recomendable para cualquiera en Polymarket.",
  "faq.headline.before": "Copy trading en Polymarket",
  "faq.subtitle": "Todo lo que necesitas saber sobre el copy trading en mercados de predicción con FrenFlow.",
  "faq.section.bot": "Bot de Polymarket",
  "faq.section.markets": "Trading en mercados de predicción",
  "faq.section.settings": "Ajustes de copy trading",
  "faq.section.fees": "Fees y precios",
  "faq.section.security": "Seguridad y wallet",
  "faq.section.start": "Cómo empezar",
  "faq.bot.q1": "¿Existe un bot de Polymarket?",
  "faq.bot.a1": "¡Sí! FrenFlow es el bot líder de Polymarket para copy trading automatizado. Nuestro bot te permite copiar automáticamente a los top traders de mercados de predicción, seguir wallets ballena y ejecutar operaciones automáticamente 24/7 sin intervención manual.",
  "faq.bot.q2": "¿Qué es el copy trading en Polymarket?",
  "faq.bot.a2": "El copy trading en Polymarket te permite replicar automáticamente las operaciones de traders exitosos en mercados de predicción. Cuando un top trader compra shares YES o NO en cualquier evento de Polymarket, el bot de FrenFlow copia esa operación a tu wallet al instante.",
  "faq.bot.q3": "¿Cómo funciona el bot de auto-trade en Polymarket?",
  "faq.bot.a3": "El bot de FrenFlow monitoriza a los traders que sigues en tiempo real. Cuando hacen una operación, nuestro sistema automatizado la replica al instante en tu wallet con la cantidad de stake que hayas elegido. Sin trading manual.",
  "faq.markets.q1": "¿Qué son los mercados de predicción?",
  "faq.markets.a1": "Los mercados de predicción como Polymarket te permiten operar sobre el resultado de eventos del mundo real. Compra shares YES si crees que algo va a pasar, NO si crees que no. Los shares ganadores pagan $1 cada uno. FrenFlow hace accesible el trading en mercados de predicción dejándote copiar a traders experimentados.",
  "faq.markets.q2": "¿Es rentable el copy trading en mercados de predicción?",
  "faq.markets.a2": "Copiar a los top traders de Polymarket puede ser rentable si sigues a ganadores consistentes. FrenFlow muestra el ROI, win rate e historial de volumen de cada trader para que tomes decisiones informadas. El rendimiento pasado no garantiza resultados futuros.",
  "faq.markets.q3": "¿Qué mercados puedo copy tradear?",
  "faq.markets.a3": "FrenFlow soporta todos los mercados de predicción de Polymarket: política, deportes, precios cripto, entretenimiento, eventos mundiales y más. Cuando el trader que sigues apuesta en cualquier mercado, la operación se copia automáticamente en tu cuenta.",
  "faq.settings.q1": "¿Cómo configuro la cantidad de stake en copy trading?",
  "faq.settings.a1": "Elige entre Cantidad fija (p. ej., $10 por operación copiada) o modo Porcentaje (p. ej., 5% de tu balance). Configura un stake máximo por operación para controlar el riesgo. Ajusta estos parámetros cuando quieras desde tu dashboard de copy trading.",
  "faq.settings.q2": "¿FrenFlow también copia órdenes de venta?",
  "faq.settings.a2": "¡Sí! Activa 'Follow Sells' para salir automáticamente de posiciones cuando el trader copiado venda. Esto te ayuda a asegurar beneficios o cortar pérdidas al mismo tiempo que los traders experimentados de Polymarket.",
  "faq.settings.q3": "¿Puedo pausar o detener el copy trading?",
  "faq.settings.a3": "Por supuesto. Pausa cualquier trader con un clic, ajusta tus parámetros de copia o deja de seguirlo por completo. Mantienes el control total sobre tu estrategia de copy trading en Polymarket en todo momento.",
  "faq.fees.q1": "¿Cuánto cuesta el copy trading en Polymarket?",
  "faq.fees.a1": "FrenFlow cobra la builder fee de Polymarket a nivel de CLOB: 1% taker / 0.5% maker. La misma tarifa para operaciones manuales y copiadas — sin service fee añadida, sin suscripciones mensuales, sin fees ocultas, sin depósitos mínimos. También cubrimos todas las fees de gas de blockchain por ti.",
  "faq.fees.q2": "¿Hay fees de trading en Polymarket?",
  "faq.fees.a2": "Polymarket tiene sus propias fees de trading (típicamente 1-2% de spread). Son estándar para todos los usuarios de Polymarket y separadas de la builder fee de FrenFlow (1% taker / 0.5% maker).",
  "faq.fees.q3": "¿Hay un mínimo para empezar a copy tradear?",
  "faq.fees.a3": "No se requiere depósito mínimo. Sin embargo, recomendamos al menos $50-100 USDC para diversificar correctamente entre varios traders y mercados de predicción.",
  "faq.security.q1": "¿Es seguro el copy trading en Polymarket?",
  "faq.security.a1": "FrenFlow usa una arquitectura no-custodial - tus fondos se quedan en tu propia wallet, gestionada con Privy. Solo podemos ejecutar las operaciones que autorices. No podemos retirar ni transferir tu USDC.",
  "faq.security.q2": "¿Cómo funciona la wallet cripto?",
  "faq.security.a2": "Cuando te registras, FrenFlow crea una embedded wallet segura en Polygon. Es no-custodial (tú tienes las claves) y funciona sin gestionar seed phrases. Tu USDC siempre está bajo tu control.",
  "faq.security.q3": "¿Puedo retirar de mi wallet de copy trading?",
  "faq.security.a3": "Sí, retira tu USDC cuando quieras a cualquier wallet externa de Polygon. Las posiciones abiertas en Polymarket deben resolverse o venderse antes. FrenFlow nunca bloquea tus fondos.",
  "faq.start.q1": "¿Cómo empiezo a copy tradear en Polymarket?",
  "faq.start.a1": "1) Regístrate con email o Telegram - creamos tu wallet automáticamente. 2) Deposita USDC en Polygon. 3) Activa el trading con un clic. 4) Elige traders para copiar desde nuestro leaderboard. Empieza a copiar operaciones en mercados de predicción en menos de 5 minutos.",
  "faq.start.q2": "¿Qué necesito para copy tradear en mercados de predicción?",
  "faq.start.a2": "Solo una cuenta de email o Telegram para registrarte. FrenFlow gestiona la creación de la wallet automáticamente. Después deposita USDC en Polygon y ya estás listo para copiar a los traders de Polymarket.",
  "faq.start.q3": "¿Puedo usar FrenFlow en móvil?",
  "faq.start.a3": "¡Sí! FrenFlow funciona en cualquier dispositivo. Usa nuestro bot de Telegram (@FrenFlowBot) para copy trading en móvil, o accede a la web app desde cualquier navegador. Gestiona tus copy trades de Polymarket desde donde quieras.",
  "blogCarousel.eyebrow": "Aprende",
  "blogCarousel.headline.before": "Domina el",
  "blogCarousel.headline.accent": "copy trading",
  "blogCarousel.subtitle": "Guías expertas, estrategias e insights para ayudarte a triunfar en Polymarket.",
  "blogCarousel.viewAll": "Ver todos los artículos",
  "blogCarousel.readMore": "Leer más",
  "blogCarousel.tag.predictionMarkets": "Mercados de predicción",
  "blogCarousel.tag.cryptoTrading": "Trading cripto",
  "blogCarousel.tag.earnMoney": "Ganar dinero",
  "blogCarousel.tag.trading": "Trading",
  "blogCarousel.tag.tradingStrategies": "Estrategias de trading",
  "blogCarousel.tag.arbitrage": "Arbitraje",
  "blogCarousel.post1.title": "¿Qué es Polymarket? La guía completa de los mercados de predicción",
  "blogCarousel.post1.excerpt": "Descubre todo sobre Polymarket, la mayor plataforma descentralizada de mercados de predicción del mundo.",
  "blogCarousel.post2.title": "Cómo ganar dinero compartiendo tus predicciones de Polymarket",
  "blogCarousel.post2.excerpt": "Descubre cómo monetizar tu experiencia en mercados de predicción con el sistema de referidos de FrenFlow.",
  "blogCarousel.post3.title": "Estrategias de arbitraje en Polymarket: guía completa 2026",
  "blogCarousel.post3.excerpt": "Aprende las estrategias de arbitraje más rentables: hedging delta-neutral, oportunidades cross-platform y más.",
  "finalCta.headline": "¿Listo para copiar a los mejores traders de Polymarket?",
  "finalCta.subtitle.before": "Control total. Copy trading automatizado con",
  "finalCta.subtitle.bold1": "analíticas de IA",
  "finalCta.subtitle.middle": "y",
  "finalCta.subtitle.bold2": "gestión de cartera",
  "finalCta.subtitle.after": "Opera como un pro, sin el trabajo manual.",
  "finalCta.cta": "Empezar a copy tradear ya",
  "finalCta.noCard": "Sin tarjeta. Sin compromiso.",
  "finalCta.trusted": "El mejor bot de Polymarket, con la confianza de 500+ traders",
  "mentions.empty": "No hay eventos de menciones disponibles",
  "mentions.live": "EN VIVO",
  "mentions.new": "NUEVO",
  "mentions.volSuffix": "vol",
  "mentions.marketCountOne": "{n} mercado",
  "mentions.marketCountMany": "{n} mercados",
  "mentions.viewFullEvent": "Ver evento completo",
  "blog.trending": "Tendencias",
  "blog.searchPlaceholder": "Buscar artículos…",
  "blog.allTag": "Todos",
  "blog.readMore": "Leer más",
  "blog.empty.search": "No hay artículos que coincidan con «{q}».",
  "blog.empty.tag": "No se encontraron artículos para «{tag}».",
  "blog.empty.none": "No se encontraron artículos.",
  "blog.loadMore": "Cargar más artículos",
  "blog.endAll": "Has visto todos los artículos ({n})",
  "blog.endAllFeatured": "Has visto todos los artículos ({n} + 1 destacado)",
  "blog.endTag": "Has visto todos los artículos de «{tag}» ({n})",
  "blog.h1.before": "El",
  "blog.h1.after": "Blog",
  "blog.subtitle": "Guías de Polymarket, estrategias de copy trading e insights de expertos para que operes más inteligente.",
  "blog.cat.traders": "Traders",
  "blog.cat.markets": "Mercados",
  "blog.cat.strategies": "Estrategias",
  "blog.cat.news": "Noticias",
  "blog.cat.guides": "Guías",
  "blog.cat.education": "Educación",
  "blog.cat.tools": "Herramientas",
  "blog.tag.prediction-markets": "Mercados de predicción",
  "blog.tag.polymarket-sports-betting": "Apuestas deportivas en Polymarket",
  "blog.tag.polymarket-whale-trader": "Trader ballena de Polymarket",
  "blog.tag.sports-prediction-markets": "Mercados de predicción deportivos",
  "blog.tag.trading": "Trading",
  "blog.tag.on-chain": "On-Chain",
  "blog.tag.copy-trading": "Copy trading",
  "blog.tag.crypto-trading": "Trading cripto",
  "blog.tag.earn-money": "Ganar dinero",
  "blog.tag.trading-strategies": "Estrategias de trading",
  "blog.tag.arbitrage": "Arbitraje",
  "blogPost.minRead": "{n} min de lectura",
  "blogPost.relatedArticles": "Artículos relacionados",
  "blogPost.authorName": "Equipo FrenFlow",
  "blogPost.authorRole": "Expertos en mercados de predicción",
  "blogPost.authorBio": "Construyendo el futuro del copy trading en Polymarket. Ayudamos a los traders a descubrir oportunidades y automatizar sus estrategias en mercados de predicción.",
  "blogTraderCard.profit": "Beneficio",
  "blogTraderCard.volume": "Volumen",
  "blogTraderCard.copy": "Copiar",
  "blogTraderCard.profile": "Perfil",
  "finalCtaBlog.headline": "¿Listo para copy tradear como un pro?",
  "finalCtaBlog.subtitle": "Sigue las mejores wallets de Polymarket. Copia cada posición automáticamente. Ejecución en el mismo bloque.",
  "finalCtaBlog.cta": "Empezar a copiar ya",
  "mobileStickyBlogCta.title": "Copia a los Top Predictors",
  "mobileStickyBlogCta.subtitle": "Ejecución Block 0 en FrenFlow",
  "mobileStickyBlogCta.cta": "Empezar",
  "blogSidebar.headline": "Copia a los Top Predictors. Mismo bloque.",
  "blogSidebar.subtitle": "Sigue las mejores wallets de Polymarket y copia automáticamente cada posición que abran — ejecutado en el mismo bloque.",
  "blogSidebar.bullet1": "Ejecución Block 0 — mismo bloque que el líder",
  "blogSidebar.bullet2": "Cero gas fees — lo cubrimos todo",
  "blogSidebar.bullet3": "No-custodial — tus fondos, tu wallet",
};

const CATALOGS: Record<Locale, Catalog> = { en, es };

export function translate(locale: Locale, key: string): string {
  return CATALOGS[locale]?.[key] ?? CATALOGS.en[key] ?? key;
}
